/**
 * NOLAS 2026 — © Mika (Mikaël FOURNY) pour eln.
 * src/i18n/fr.ts
 *
 * Rôle :
 *   Catalogue UNIQUE de toutes les chaînes UI en français de France.
 *   Aucune chaîne UI ne doit jamais être écrite en dur dans un composant —
 *   tout passe ici, ce qui permet :
 *     1. de garantir le wording français strict (cf. CLAUDE.md §15.5),
 *     2. de greper anti-anglicisme (cf. §15.5.3),
 *     3. de faire un audit linguistique en un seul fichier,
 *     4. de réutiliser le catalogue dans NOLAS / un futur salon.
 *
 * Convention de clés : `<domaine>.<contexte>.<libellé>` en kebab-case.
 *   ex: `auth.login.title`, `workshop.signup.confirm`, `media.webradio.live`.
 *
 * @author Mika
 * @since 2026-05-04
 */

// ──────────────────────────────────────────────────────────────────────────
// SOUS-CATALOGUES PAR CHANTIER (lot CIRFA 2026-08-03)
// Ce fichier fait ~6 700 lignes : c'est LA dépendance partagée du dépôt. Les
// nouveaux chantiers écrivent leurs chaînes dans leur PROPRE fichier, fusionné
// ci-dessous. Rien ne change pour les consommateurs : `messagesFr` reste un
// objet plat unique. Leçon du lot Console C9 — les chantiers menés en
// parallèle ne se gênent pas sur leurs fichiers, ils se gênent sur les fichiers
// communs. Un catalogue par chantier supprime la collision par construction.
// ──────────────────────────────────────────────────────────────────────────
import { messagesFormStatsFr } from './fr.formStats';
import { messagesRosterFr } from './fr.roster';
import { messagesTransportFr } from './fr.transport';
import { messagesTwoFactorFr } from './fr.twoFactor';
// 2026-09-28 : application d'authentification des participants (raccourci,
// le courriel reste toujours). Mikael - chef de projet - 2026-09-28
import { messagesTwoFactorParticipantFr } from './fr.twoFactorParticipant';

/**
 * Catalogue des messages français-France pour react-intl.
 * Réutilisable tel quel dans un autre salon — adapter `app.*`
 * et les libellés métier spécifiques au salon NOLAS.
 */
const messagesCoreFr: Record<string, string> = {
  // ───── App ───────────────────────────────────────────────────────────
  'app.name': '{eventName}',
  'app.subtitle': 'Salon du Recrutement et de l’Emploi — Votre avenir commence ICI !',
  // LOT 1 : `app.publisher`, `app.eventDate`, `app.eventVenue` supprimées
  // (mortes — aucune référence hors fr.ts).
  'app.author': 'Mika (Mikaël FOURNY)',
  'app.year': '2026',

  // ───── Navigation principale ─────────────────────────────────────────
  'nav.home': 'Accueil',
  'nav.programme': 'Programme',
  'nav.workshops': '{lexWorkshopPluralCap}',
  'nav.map': 'Plan',
  'nav.partners': 'Partenaires',
  'nav.media': 'Médias',
  'nav.webradio': 'Webradio',
  'nav.webtv': 'WebTV',
  'nav.videos': 'Vidéos',
  'nav.myBadge': 'Mon badge',
  'nav.myProgramme': 'Mon programme',
  'nav.myFavorites': 'Mes favoris',
  'nav.myProfile': 'Mon profil',
  'nav.notifications': 'Notifications',
  'nav.contact': 'Nous contacter',
  'nav.about': 'À propos',
  'nav.legal': 'Mentions légales',
  'nav.accessibility': 'Accessibilité',
  'nav.privacy': 'Confidentialité',

  // ───── Boutons ───────────────────────────────────────────────────────
  'btn.continue': 'Continuer',
  'btn.cancel': 'Annuler',
  'btn.confirm': 'Valider',
  'btn.save': 'Enregistrer',
  'btn.send': 'Envoyer',
  'btn.delete': 'Supprimer',
  'btn.edit': 'Modifier',
  'btn.close': 'Fermer',
  'btn.back': 'Retour',
  'btn.next': 'Suivant',
  'btn.previous': 'Précédent',
  'btn.search': 'Rechercher',
  'btn.filter': 'Filtrer',
  'btn.sort': 'Trier',
  'btn.signin': 'Connexion',
  'btn.signup': "S'inscrire",
  'btn.signout': 'Déconnexion',
  'btn.scan': 'Scanner',
  'btn.share': 'Partager',
  'btn.download': 'Télécharger',
  'btn.print': 'Imprimer',
  'btn.exportPdf': 'Exporter en PDF',
  'btn.addFavorite': 'Ajouter aux favoris',
  'btn.removeFavorite': 'Retirer des favoris',
  'btn.installApp': "Installer l'application",

  // ───── États génériques ──────────────────────────────────────────────
  'state.loading': 'Chargement…',
  'state.pleaseWait': 'Veuillez patienter…',
  'state.empty': 'Aucun élément à afficher.',
  'state.error': 'Une erreur est survenue.',
  'state.networkError': 'Connexion impossible. Vérifiez votre réseau.',
  'state.success': 'Enregistré.',
  'state.confirmed': 'Confirmé.',
  'state.offline': 'Vous êtes hors ligne.',
  'state.disabled': 'Indisponible.',

  // ───── Inscription / rôles ───────────────────────────────────────────
  'role.visitor': 'Visiteur',
  'role.exhibitor': 'Exposant',
  'role.vip': 'VIP',
  'role.specificPublic.press': 'Presse',
  'role.specificPublic.formateur': 'Formateur',
  'role.specificPublic.researcher': 'Chercheur',
  'role.student': 'Élève',
  'role.child': 'Enfant',
  'role.staff': "Équipe d'organisation",
  'role.admin': 'Administrateur',

  // ───── Atelier / programme ───────────────────────────────────────────
  'workshop.title': '{lexWorkshopCap}',
  'workshop.list.title': '{lexWorkshopAllDefPluralCap}',
  'workshop.signup.action': "S'inscrire à {lexWorkshopDem}",
  'workshop.signup.confirmed': 'Inscription confirmée.',
  'workshop.signup.waitlist': 'Vous êtes sur liste d’attente.',
  'workshop.signup.cancel': 'Se désinscrire',
  'workshop.signup.conflict': 'Conflit d’horaire avec {lexWorkshopOtherIndef}.',
  'workshop.capacity': '{filled} / {total} inscrits',
  'workshop.full': 'Complet',
  'workshop.programme.global': 'Programme officiel',
  'workshop.programme.mine': 'Mon programme',

  // ───── Médias ────────────────────────────────────────────────────────
  'media.webradio.live': 'Webradio en direct',
  'media.webtv.live': 'WebTV en direct',
  'media.replay': 'Replay',
  'media.replay.list': 'Replays disponibles',
  'media.captions.on': 'Sous-titres activés',
  'media.captions.off': 'Sous-titres désactivés',

  // ───── Partenaires ───────────────────────────────────────────────────
  'partners.title': 'Nos partenaires',
  'partners.category.institutional': 'Institutionnels',
  'partners.category.academic': 'Académiques',
  'partners.category.edtech': 'EdTech',
  'partners.category.associative': 'Associations',
  'partners.category.media': 'Médias',
  'partners.category.financier': 'Financiers',
  'partners.category.collectivity': 'Collectivités',

  // FIX 2026-05-07 — clé manquante pour BandeauPartenaires (footer perm.)
  'footer.partners.heading':       'En partenariat avec',

  // ───── Carte / présence ──────────────────────────────────────────────
  'map.title': 'Plan du {eventPlace}',
  'map.searchPlaceholder': 'Trouver un stand, un partenaire, {lexWorkshopIndef}…',
  'map.youAreHere': 'Vous êtes ici',
  'map.density.low': 'Peu de monde',
  'map.density.medium': 'Affluence moyenne',
  'map.density.high': 'Très fréquenté',

  // ───── Notifications ─────────────────────────────────────────────────
  // FIX 2026-05-06 (S15bis) : `notifications.title` retiré ici — doublé
  // ligne ~775 par le bloc S9 NotificationsPrefsPage avec une valeur plus
  // contextuelle (« Mes notifications »). On garde la version S9.
  'notifications.empty': 'Aucune notification.',
  'notifications.subscribe': 'Activer les notifications',
  'notifications.unsubscribe': 'Désactiver les notifications',

  // ───── RGPD / consentement ───────────────────────────────────────────
  'consent.general.label':
    "J'accepte que mes données soient traitées pour l'organisation {eventTypeOf} {eventName}.",
  'consent.tracking.label':
    'J’accepte le suivi anonymisé en temps réel pour estimer la fréquentation des stands.',
  'consent.image.label':
    'J’accepte d’être photographié(e) ou filmé(e) pendant l’événement.',
  'consent.required': 'Ce consentement est obligatoire pour continuer.',

  // ───── Pied de page (permanent) ──────────────────────────────────────
  'footer.organizer': 'Organisé dans le cadre du Salon {eventName} du Recrutement et de l’Emploi',
  // FIX 2026-05-07 (retour Mika) — wording officiel : éditeur (organisateur)
  // puis auteur Mikaël FOURNY, séparés par tiret demi-cadratin.
  // Cohérent avec le copyright ligne suivante.
  // 2026-09-09 (Mika) — signature professionnelle : etablissement + lien
  // vers le profil LinkedIn. Le pseudo « Mika » reste dans les en-tetes de
  // code, il n'a pas sa place devant le public.
  'footer.author':
    'Application développée par <lien>Mikaël FOURNY — LPO La Briquerie</lien>',
  'footer.copyright': '© 2026 — {eventName} — Mikaël FOURNY — Tous droits réservés.',

  // ───── Erreurs HTTP / système ────────────────────────────────────────
  'error.404.title': 'Page introuvable',
  'error.404.message': "La page que vous recherchez n’existe pas ou a été déplacée.",
  'error.500.title': 'Erreur serveur',
  'error.500.message': 'Un incident est survenu de notre côté. Veuillez réessayer dans quelques minutes.',
  'error.offline.title': 'Mode hors ligne',
  'error.offline.message': "Vous n'êtes pas connecté(e). Certaines fonctionnalités sont limitées.",

  // ════════════════════════════════════════════════════════════════════════
  // Sprint S6 vague 1 — A:1 → A:5 (onboarding + inscription multi-rôles)
  // ════════════════════════════════════════════════════════════════════════

  // ───── A:1 — Splash ─────────────────────────────────────────────────
  'splash.brand.title': '{eventName}',
  'splash.brand.region': 'THIONVILLE',
  'splash.brand.tagline': 'Salon du Recrutement et de l’Emploi',
  'splash.loading': 'Chargement…',
  'splash.cta.enter': 'Commencer',
  'splash.author.line': 'Création Mikaël FOURNY',
  // LOT 1 : deux clés d'alt-text de logos institutionnels supprimées
  // (mortes — anciens logos résiduels, aucune référence hors fr.ts).

  // ───── A:2 — Onboarding (5 walks) ───────────────────────────────────
  'onboarding.skip': 'Passer',
  'onboarding.next': 'Suivant',
  'onboarding.previous': 'Précédent',
  'onboarding.start': 'Découvrir {eventTypeDef}',
  'onboarding.progress.aria': 'Étape {step} sur {total}',
  'onboarding.illustration.alt':
    "Illustration du salon NOLAS 2026, silhouettes de professionnels et espaces de rencontre, dans une palette chaleureuse rouge profond et crème.",
  // Walks
  'onboarding.walk1.title': 'Bienvenue {eventTypeAt} {eventName}',
  'onboarding.walk1.body':
    "Le rendez-vous de la citoyenneté numérique, de l’intelligence artificielle et des usages du numérique éducatif, {eventDateLong} au {eventPlace}.",
  'onboarding.walk2.title': "S’inscrire en quelques secondes",
  'onboarding.walk2.body':
    "Visiteur, exposant, élève, équipe : créez votre profil pour personnaliser votre parcours et accéder aux contenus qui vous concernent.",
  'onboarding.walk3.title': 'Mon badge, partout avec moi',
  'onboarding.walk3.body':
    "Votre QR code est conservé hors ligne, prêt à être scanné sur les stands. Vous pouvez aussi l’imprimer en format A6.",
  'onboarding.walk4.title': 'Scanner pour échanger',
  'onboarding.walk4.body':
    'Scannez les stands qui vous intéressent : votre parcours est enregistré et les exposants peuvent rebondir avec vous après {eventTypeDef}.',
  'onboarding.walk5.title': 'Programme et plan temps réel',
  'onboarding.walk5.body':
    "Inscrivez-vous {lexWorkshopAtPlural}, suivez votre programme personnalisé et localisez-vous sur la carte du {eventPlace}.",

  // ───── A:3 — Mon Espace (visiteur) ──────────────────────────────────
  'monEspace.title': 'Mon Espace',
  'monEspace.greeting': 'Bonjour, prêt(e) pour le salon ?',
  'monEspace.banner.live': 'En direct',
  'monEspace.banner.nextWorkshop': 'Prochain{lexWorkshopAgr} {lexWorkshopBare} dans {minutes} min',
  'monEspace.banner.nextWorkshop.fallback': '{lexWorkshopNoneCap} programmé prochainement',
  'monEspace.banner.nextWorkshop.detail': '{title} — {room}',
  'monEspace.tile.badge.title': 'Mon badge',
  'monEspace.tile.badge.aria': 'Ouvrir mon badge numérique',
  'monEspace.tile.programme.title': 'Mon programme',
  'monEspace.tile.programme.aria': 'Voir mon programme personnalisé',
  'monEspace.tile.map.title': 'Plan',
  'monEspace.tile.map.aria': 'Ouvrir le plan du {eventPlace}',
  'monEspace.tile.media.title': 'Médias',
  'monEspace.tile.media.aria': 'Webradio, WebTV et vidéos du salon',
  'monEspace.tile.partners.title': 'Partenaires',
  'monEspace.tile.partners.aria': 'Voir la liste des partenaires du salon',
  'monEspace.tile.notifications.title': 'Notifications',
  'monEspace.tile.notifications.aria': 'Mes notifications',
  'monEspace.notifications.unread': '{count} non {count, plural, one {lue} other {lues}}',
  'monEspace.menu.aria': 'Ouvrir le menu principal',
  'monEspace.back.aria': 'Retour à la page précédente',

  // ───── Commun (FIX 2026-05-17) ──────────────────────────────────────
  'common.loading': 'Chargement…',
  'common.retry': 'Réessayer',
  // FIX 2026-05-20 (Mika Message 8) — page d'accueil interstitielle.
  // FIX 2026-05-20 (Mika Message 9) — saut de ligne eyebrow + titre, italique
  // sur la citation (placeholders {br} et <em>…</em> ICU consommés par
  // <FormattedMessage values=…>).
  'accueilChoix.eyebrow':
    '{eventName} — {eventDate}{br}{eventPlace}',
  'accueilChoix.title': 'Bienvenue {eventTypeAt}{br}{eventName}',
  // FIX 2026-06-20 (finition LOT-H) : l'accroche est désormais éditable au
  // back-office (content.home.tagline, affichée via <ContentBlock> au-dessus) ;
  // le sous-titre ne porte plus que le descriptif du salon (pas de doublon).
  'accueilChoix.subtitle': 'Salon du Recrutement et de l’Emploi.',
  'accueilChoix.question': 'Avez-vous déjà un compte ?',
  // FIX 2026-05-21 (bug Mika — libellés tronqués « … » iPhone) :
  // raccourcir + autoriser wrap CSS local (cf. AccueilChoixPage.module.css).
  'accueilChoix.btn.login': 'Oui, j’ai un compte — Me connecter',
  'accueilChoix.btn.login.aria':
    'Me connecter avec mon adresse e-mail (j’ai déjà un compte)',
  'accueilChoix.btn.register':
    'Non — Je crée mon compte',
  'accueilChoix.btn.register.aria':
    'Je crée mon compte (je n’ai pas encore de compte)',
  'accueilChoix.hint':
    'Une question ? Contactez l’organisation à l’adresse {email}.',

  // ───── Contenu éditorial NOLAS (LOT-H) — replis i18n des <ContentBlock> ─
  // Ces clés sont le REPLI affiché tant que le réglage content.* est vide.
  // Valeurs = branding TEXTE officiel de l'affiche NOLAS V2 (déjà consigné
  // CLAUDE-NOLAS §0 + entrée M6 BRANDING ; identiques au seed V67) — ne rien
  // inventer. Modifiables au back-office via /admin/content.
  // FIX 2026-06-20 (finition LOT-H) — HomePage accroche + 4 verbes, Footer.
  'content.home.tagline': 'Votre avenir commence ICI !',
  'content.home.verb_1': 'Rencontrez des entreprises',
  'content.home.verb_2': 'Découvrez des métiers',
  'content.home.verb_3': 'Saisissez des opportunités',
  'content.home.verb_4': 'Construisez votre avenir',
  'content.footer.tagline': 'Votre avenir commence ICI !',
  'content.footer.event_date': '{eventDate}',
  'content.footer.event_place': '{eventPlace} — {eventPlaceAddress}',

  // ───── A:4 — Sélection du rôle ──────────────────────────────────────
  'role.select.title': 'Bienvenue au {eventName}',
  'role.select.intro':
    'Sélectionnez votre profil pour personnaliser votre expérience et accéder aux contenus qui vous sont dédiés.',
  // FIX 2026-05-17 (demande Mika) : connexion atteignable depuis l'entrée.
  'role.select.alreadyRegistered': 'Vous avez déjà un compte ?',
  'role.select.loginCta': 'Se connecter',
  'role.select.cta.iAm': 'Je suis {role, select, visitor {visiteur} exhibitor {exposant} vip {invité} press {journaliste} formateur {formateur} student {élève} child {enfant} staff {membre du staff} other {participant}}',
  'role.card.visitor.desc':
    'Découvrez les innovations et participez {lexWorkshopAtPlural} ouvert{lexWorkshopAgrPlural} à tous.',
  'role.card.exhibitor.desc':
    'Gérez votre stand, vos ressources et vos interactions avec le public.',
  'role.card.vip.title': 'Invité VIP',
  // FIX 2026-05-17 (Mika) : wording raccourci à la demande.
  'role.card.vip.desc': 'Réservé aux invités institutionnels.',
  'role.card.vip.cta': 'Accès VIP',
  'role.card.press.title': 'Presse',
  'role.card.press.desc':
    "Accréditation presse, dossiers et contacts officiels de l’événement.",
  'role.card.press.cta': 'Espace presse',
  'role.card.formateur.title': 'Formateur',
  'role.card.formateur.desc':
    "Gérez vos sessions, supports pédagogiques et groupes d’apprenants.",
  // FIX 2026-05-17 (demande Mika) : « Formateur » devient « Intervenant »
  // (même descriptif). Les clés .formateur.* sont conservées (anciens
  // liens /inscription/formateur restent valides — append-only §1.4).
  // 2026-08-31 (Mika) — carte ANIMATEUR : elle manquait, et son absence a
  // envoye une animatrice dans le formulaire « public specifique ».
  'role.card.workshopHost.title': 'Animateur {lexWorkshopDefPlural}',
  'role.card.workshopHost.desc':
    'Vous animez {lexWorkshopIndef} {eventTypeAt} {eventName}. Munissez-vous du code reçu de l’organisation — ou demandez un accès.',
  // ⚠ Description REECRITE le 2026-08-31. L'ancienne (« Gérez vos sessions,
  // supports pédagogiques et groupes d’apprenants ») décrivait mot pour mot
  // le métier d'un animateur d'atelier : c'est elle qui a détourné la
  // personne vers le mauvais parcours. Un libellé qui décrit le travail de
  // quelqu'un d'autre est un piège, pas une imprécision.
  'role.card.intervenant.title': 'Intervenant extérieur',
  'role.card.intervenant.desc':
    'Vous intervenez pour une institution, un organisme de formation ou un laboratoire, sans animer {lexWorkshopIndef} au programme.',
  'role.specificPublic.intervenant': 'Intervenant',
  // FIX 2026-05-17 (Mika) : nouveaux rôles enseignant + autre.
  'role.card.enseignant.title': 'Enseignant',
  'role.card.enseignant.desc':
    'Professionnel de l’éducation : inscrivez-vous, recevez votre badge et choisissez vos {lexWorkshopPlural}.',
  'role.card.autre.title': 'Autre',
  'role.card.autre.desc':
    'Vous ne vous reconnaissez pas dans les profils ci-dessus ? Inscrivez-vous ici.',
  'role.card.student.desc':
    'À partir de 3 ans : maternelle, élémentaire, collège ou lycée. Accédez à votre parcours et choisissez vos {lexWorkshopPlural}.',
  'role.card.child.desc':
    "Tout-petit de moins de 3 ans, accompagné d’un adulte responsable. À partir de 3 ans, choisissez « Élève ».",
  'role.card.staff.title': 'Équipe organisatrice',
  'role.card.staff.desc':
    "Accès restreint aux outils d’administration et de gestion de l’événement.",
  'role.card.staff.cta': 'Accès équipe',

  // ───── A:5 — Inscription dynamique ──────────────────────────────────
  'inscription.title': 'Inscription',
  'inscription.intro':
    'Veuillez compléter les informations suivantes pour finaliser votre accès au salon.',
  'inscription.progress.label': 'Étape {step} sur {total}',
  'inscription.field.firstName': 'Prénom',
  'inscription.field.lastName': 'Nom',
  'inscription.field.email': 'Adresse e-mail',
  'inscription.field.phone': 'Numéro de téléphone',
  'inscription.field.optional': '(optionnel)',
  // FIX 2026-05-17 (Mika) : champ fichier (autorisation parentale).
  'inscription.file.tooLarge':
    'Fichier trop volumineux (5 Mo maximum). Choisissez un PDF ou une image plus légère.',
  'inscription.field.required': 'Ce champ est obligatoire.',
  'inscription.field.email.invalid': 'Veuillez saisir une adresse e-mail valide.',
  'inscription.field.structure': 'Structure / établissement',
  'inscription.field.functionTitle': 'Fonction / classe',
  'inscription.consent.general': 'Consentement général',
  'inscription.consent.tracking': 'Suivi temps réel (optionnel)',
  'inscription.consent.tracking.label':
    "J’accepte le suivi anonymisé de ma présence par stand pour aider à estimer la fréquentation. Cette autorisation est facultative et peut être retirée à tout moment.",
  'inscription.cta.submit': 'Valider mon inscription',
  'inscription.cta.submitting': 'Enregistrement…',
  'inscription.success.title': 'Inscription confirmée',
  'inscription.success.message':
    'Vous êtes inscrit(e) {eventTypeAt} {eventName}. Votre badge est prêt dans Mon Espace.',
  'inscription.success.queued':
    'Connexion réseau indisponible. Votre inscription sera envoyée automatiquement au retour de connexion.',
  'inscription.error.generic':
    "Une erreur est survenue. Vos informations ont été conservées, vous pouvez réessayer.",
  // FIX 2026-05-17 (bug Mika : « Erreur HTTP 409 » brute affichée) :
  // adresse déjà inscrite → redirection vers la connexion par code
  // (récupération du badge) au lieu d'un message technique.
  'inscription.error.emailAlreadyUsed':
    'Cette adresse e-mail est déjà inscrite. Connectez-vous avec votre code pour retrouver votre badge.',
  'inscription.error.schemaUnavailable':
    "Impossible de charger le formulaire d’inscription. Veuillez réessayer dans quelques instants.",
  'inscription.role.unknown': 'Rôle inconnu',
  'inscription.field.label.aria': '{label}{required, select, true { (obligatoire)} other {}}',
  // FIX 2026-05-20 (BUG VIP 400 — CLAUDE.md §5/§24) : écran clair quand
  // un utilisateur arrive sur /inscription/vip SANS lien d'invitation
  // (?token=…). Évite l'« Erreur HTTP 400 » que retournait sinon
  // register/vip.php (token HMAC obligatoire). Wording français de France
  // strict §15.5 + contact officiel mikael.fourny@labriquerie.net (LOT 1).
  'inscription.vip.inviteOnly.title': 'Inscription sur invitation',
  'inscription.vip.inviteOnly.body':
    'L’inscription VIP nécessite un lien d’invitation envoyé par e-mail. Si vous avez reçu une invitation, ouvrez le lien depuis votre boîte de réception. Pour toute question : mikael.fourny@labriquerie.net.',
  'inscription.vip.inviteOnly.back': 'Choisir un autre rôle',

  // Logos institutionnels (alt-text WCAG)
  // LOT 1 : trois clés d'alt-text de logos institutionnels supprimées
  // (mortes — anciens logos résiduels, aucune référence hors fr.ts).
  'logos.france2030.alt': 'Logo France 2030',

  // ════════════════════════════════════════════════════════════════════════
  // Sprint S6 vague 2 — B:6 → B:10 (badge / scanner / programme / détail)
  // ════════════════════════════════════════════════════════════════════════

  // ───── B:6 — Mon badge ──────────────────────────────────────────────
  'badge.title': 'Mon badge',
  'badge.subtitle': 'Présentez ce badge sur les stands ou imprimez-le en A6.',
  'badge.brand': '{eventName}',
  'badge.qr.alt':
    'Code QR personnel à scanner par les exposants pour enregistrer votre passage.',
  'badge.role.line': '{role} — {structure}',
  'badge.role.line.noStructure': '{role}',
  'badge.event.date': '{eventDate}',
  'badge.event.venue': '{eventPlace}, {eventPlaceAddress}',
  'badge.action.print': 'Imprimer en A6',
  'badge.action.wallet': 'Ajouter à Wallet',
  'badge.action.share': 'Partager',
  'badge.action.print.aria': 'Imprimer mon badge au format A6',
  'badge.action.wallet.aria': 'Ajouter mon badge à Apple Wallet ou Google Wallet',
  'badge.action.share.aria': 'Partager mon badge',
  'badge.share.title': 'Mon badge {eventName}',
  'badge.share.text':
    'Voici mon badge pour {eventTypeDef} {eventName} du {eventDate} à {eventPlace}.',
  'badge.offline.note':
    'Mode hors ligne — votre QR reste valide et lisible par les exposants.',
  'badge.cache.unavailable':
    'Aucun badge disponible. Connectez-vous pour générer votre QR.',
  'badge.qr.generating': 'Génération de votre code QR…',
  'badge.qr.error': 'Impossible de générer le code QR. Veuillez réessayer.',

  // ───── B:7 — Scanner exposant ───────────────────────────────────────
  'scanner.title': 'Scanner un visiteur',
  'scanner.subtitle': 'Cadrez le QR du visiteur dans le rectangle.',
  'scanner.close.aria': 'Fermer le scanner et revenir à la page précédente',
  'scanner.flash.aria': 'Activer ou désactiver la lampe torche',
  'scanner.camera.flip.aria': 'Basculer entre la caméra avant et la caméra arrière',
  'scanner.manual.cta': 'Saisir manuellement le code',
  'scanner.permission.required.title': 'Caméra requise',
  'scanner.permission.required.body':
    "Pour scanner un visiteur, autorisez l'accès à la caméra dans les réglages de votre appareil, puis revenez sur cette page.",
  'scanner.permission.cta': "Demander l'accès à la caméra",
  'scanner.permission.denied':
    "L'accès à la caméra a été refusé. Vous pouvez l'autoriser dans les réglages de votre navigateur.",
  'scanner.unsupported.title': 'Scanner indisponible',
  'scanner.unsupported.body':
    "Votre appareil ne prend pas en charge la caméra ou le navigateur n'autorise pas le scan QR. Utilisez la saisie manuelle.",
  'scanner.success.title': 'Bienvenue {name}',
  'scanner.success.detail': '{stand} — {pavillon}',
  'scanner.error.unreadable': 'QR illisible. Rapprochez-vous du visiteur.',
  'scanner.error.notRegistered': 'Visiteur non inscrit au salon.',
  'scanner.error.notAuthorized': 'Stand non autorisé pour ce visiteur.',
  'scanner.error.queued':
    'Pointage enregistré hors ligne — synchronisation au retour de connexion.',
  'scanner.modal.title': 'Notes sur le visiteur',
  'scanner.modal.notes.label': 'Notes (facultatives)',
  'scanner.modal.notes.placeholder':
    "Ex. : intéressé par notre {lexWorkshopBare} IA, demande à recevoir la documentation…",
  'scanner.modal.notes.counter': '{count} / 280 caractères',
  'scanner.modal.interest.label': "Niveau d'intérêt",
  'scanner.modal.interest.value':
    '{value, plural, =1 {Très faible} =2 {Faible} =3 {Moyen} =4 {Élevé} other {Très élevé}}',
  'scanner.modal.cta.save': 'Enregistrer',
  'scanner.modal.cta.continue': 'Continuer à scanner',
  'scanner.toast.saved': 'Pointage enregistré.',
  'scanner.toast.queued': 'Pointage mis en file hors ligne.',
  'scanner.manual.placeholder': 'Code badge du visiteur (ex. NOLAS-A1B2C3)',
  'scanner.manual.confirm': 'Valider le code',

  // ───── B:8 — Programme global ───────────────────────────────────────
  'programme.title': 'Programme officiel',
  // FIX 2026-05-18 — outil de recherche d'ateliers (demande Mika).
  'programme.search.placeholder':
    'Rechercher {lexWorkshopIndef}, un intervenant, une salle…',
  'programme.search.aria': 'Rechercher {lexWorkshopIndef} dans le programme',
  'programme.filters.axis.label': 'Filtrer par thématique',
  'programme.filters.public.label': 'Filtrer par public',
  'programme.filters.reset': 'Réinitialiser les filtres',
  'programme.filters.activeCount':
    '{count, plural, one {# filtre actif} other {# filtres actifs}}',
  'programme.axis.citoyennete': 'Citoyenneté numérique',
  'programme.axis.ia': 'Intelligence artificielle',
  'programme.axis.usages': 'Usages du numérique',
  'programme.axis.inclusive': 'École inclusive',
  'programme.axis.webradio': 'Webradio',
  'programme.axis.webtv': 'WebTV',
  'programme.public.all': 'Tous',
  'programme.public.college': 'Collège',
  'programme.public.lycee': 'Lycée',
  'programme.public.grandPublic': 'Grand public',
  'programme.public.vip': 'VIP',
  'programme.public.enseignant': 'Enseignant',
  'programme.empty.title': '{lexWorkshopNoneCap} ne correspond à vos filtres',
  'programme.empty.cta': 'Réinitialiser les filtres',
  'programme.workshop.animatedBy': 'Animé par {speaker} — {structure}',
  'programme.workshop.room': '{room}',
  'programme.workshop.capacity': '{filled} / {total} inscrits',
  'programme.workshop.full': 'Complet — liste d’attente',
  'programme.workshop.alreadySignedUp': 'Inscrit·e',
  'programme.workshop.signup': "S'inscrire",
  'programme.loading': 'Chargement du programme…',
  'programme.error.title': 'Programme indisponible',
  'programme.error.body':
    "Impossible de charger le programme. Vérifiez votre connexion et réessayez.",
  'programme.lastUpdate': 'À jour à {time}',

  // ───── B:9 — Mon programme perso ────────────────────────────────────
  'monProgramme.title': 'Mon programme',
  // FIX 2026-05-18 — origine d'un atelier du programme perso.
  'monProgramme.source.signup': 'Inscrit·e',
  'monProgramme.source.common': 'Programme commun',
  // FIX 2026-05-18 — bloc programme commun (texte riche) du profil.
  'monProgramme.common.title': 'Le programme de la journée',
  'monProgramme.common.aria': 'Programme commun de votre profil',
  'monProgramme.export.cta': 'Exporter mon programme en PDF',
  'monProgramme.export.aria': 'Télécharger mon programme personnalisé en PDF',
  'monProgramme.conflict.title': 'Conflit d’horaire détecté',
  'monProgramme.conflict.body':
    'Vous êtes inscrit·e à {count} {lexWorkshopPlural} qui se chevauchent à {time}.',
  'monProgramme.empty.title': "Vous n'êtes inscrit·e à {lexWorkshopNone}",
  'monProgramme.empty.body':
    'Découvrez le programme officiel et inscrivez-vous {lexWorkshopAtPlural} qui vous intéressent.',
  'monProgramme.empty.cta': 'Voir le programme officiel',
  'monProgramme.next.label': 'Dans {minutes} min',
  'monProgramme.next.now': 'En cours',
  'monProgramme.section.upcoming': 'À venir',
  'monProgramme.section.past': 'Déjà passés',

  // ───── B:10 — Détail atelier ────────────────────────────────────────
  'atelier.back.aria': 'Revenir au programme',
  'atelier.notFound.title': '{lexWorkshopCap} introuvable',
  'atelier.notFound.body':
    "{lexWorkshopDemCap} n'existe pas ou a été retiré{lexWorkshopAgr} du programme.",
  'atelier.loading': "Chargement {lexWorkshopOfDef}…",
  'atelier.schedule': '{start} → {end}',
  'atelier.duration': 'Durée : {minutes} min',
  'atelier.room.label': 'Salle',
  'atelier.capacity.label': 'Capacité',
  'atelier.capacity.value': '{filled} sur {total} places',
  // FIX 2026-05-18 (bug Mika : « 0 sur 0 places / 0 % ») — capacité 0
  // = inscription libre (règle métier §10.6 « 0 = illimité »). On
  // n'affiche plus un faux « 0/0 » qui faisait croire à un atelier
  // complet/sans place.
  // FIX 2026-05-18 (précision Mika) : « pas de chiffre dans l'Excel »
  // = atelier SANS inscription nécessaire (et non « inscription
  // libre »). Wording corrigé partout où capacity=0.
  'atelier.capacity.unlimited': 'Accès libre — sans inscription',
  'atelier.capacity.unlimited.short': 'Sans inscription',
  'atelier.capacity.bar.aria':
    "Taux de remplissage : {filled} sur {total} places, soit {percent} %",
  'atelier.capacity.unlimited.aria':
    'Accès libre, aucune inscription nécessaire.',
  'atelier.level.label': 'Niveau',
  'atelier.tags.label': 'Thématiques',
  'atelier.description.title': 'Description',
  'atelier.speaker.title': 'Animé par',
  'atelier.speaker.bioPlaceholder': 'Présentation à venir.',
  'atelier.partner.title': 'Partenaire',
  'atelier.partner.cta': 'Voir la fiche du partenaire',
  'atelier.media.title': 'Replay disponible',
  'atelier.media.cta': 'Voir le replay',
  'atelier.cta.signup': "S'inscrire à {lexWorkshopDem}",
  'atelier.cta.cancel': 'Se désinscrire',
  'atelier.cta.waitlist': "Rejoindre la liste d'attente",
  'atelier.cta.full.label': 'Complet',
  'atelier.cta.signedUp': 'Inscrit·e à {lexWorkshopDem}',
  'atelier.cta.waitlistPos': 'Liste d’attente — position {position}',
  'atelier.favorite.add': 'Ajouter aux favoris',
  'atelier.favorite.remove': 'Retirer des favoris',
  'atelier.favorite.added': 'Ajouté à vos favoris.',
  'atelier.favorite.removed': 'Retiré de vos favoris.',
  'atelier.signup.confirmed': 'Inscription confirmée à « {title} ».',
  'atelier.signup.cancelled': 'Désinscription enregistrée.',
  'atelier.signup.queued':
    'Action enregistrée hors ligne — sera synchronisée au retour de connexion.',
  'atelier.signup.error': 'Impossible de finaliser votre inscription.',
  // FIX 2026-05-18 — visiteur non connecté (401) : la page atelier est
  // publique ; on invite à se connecter au lieu du message trompeur.
  'atelier.signup.authRequired':
    'Connectez-vous pour vous inscrire à {lexWorkshopIndef}. Redirection vers la connexion…',
  // FIX 2026-05-18 (Tableau V3) — créneaux + état complet par animation.
  'atelier.signup.full': 'Les inscriptions à cette animation sont complètes.',
  'atelier.cta.full': 'Inscriptions complètes',
  'atelier.slots.title': 'Créneaux',
  'atelier.slots.allFull':
    'Inscriptions complètes — tous les créneaux sont pleins.',
  'atelier.slot.full': 'Complet',
  'atelier.slot.open': 'Places disponibles',
  'atelier.slot.remaining':
    '{n, plural, =0 {Complet} one {# place restante} other {# places restantes}}',
  'atelier.past.label': '{lexWorkshopDemCap} est déjà passé{lexWorkshopAgr}',
  'atelier.past.noReplay': 'Pas de replay disponible.',

  // ════════════════════════════════════════════════════════════════════════
  // Sprint S6 vague 3 — B:11 + C:12 + C:15 + E:27 + E:29
  // ════════════════════════════════════════════════════════════════════════

  // ───── B:11 — Carte du SPOT de Thionville ────────────────────────────
  'carte.title': 'Plan du salon',
  'carte.search.placeholder': 'Rechercher un stand, un partenaire, {lexWorkshopIndef}…',
  'carte.search.aria': 'Rechercher un stand sur le plan',
  'carte.search.clear': 'Effacer la recherche',
  'carte.search.results.aria':
    '{count, plural, =0 {Aucun résultat} one {1 résultat} other {{count} résultats}}',
  'carte.search.empty': 'Aucun stand ne correspond à votre recherche.',
  'carte.legend.title': 'Légende',
  'carte.legend.free': 'Peu de monde',
  'carte.legend.busy': 'Affluence moyenne',
  'carte.legend.crowded': 'Très fréquenté',
  'carte.legend.saturated': 'Saturé',
  'carte.legend.youAreHere': 'Vous êtes ici',
  'carte.legend.favorite': 'Favori',
  'carte.youAreHere.aria': 'Votre dernier passage : {label}',
  'carte.density.aria':
    '{label} — {percent}% de la capacité, {count, plural, one {1 personne} other {{count} personnes}}',
  'carte.stand.aria': 'Stand {label}, zone {zone}',
  'carte.zone.aria': 'Zone {label}',
  'carte.panel.title': 'Détail du stand',
  'carte.panel.exposant.label': 'Exposant',
  'carte.panel.zone.label': 'Zone',
  'carte.panel.kind.label': 'Type',
  'carte.panel.kind.stand': 'Stand',
  'carte.panel.kind.atelier': '{lexWorkshopCap}',
  'carte.panel.kind.agora': 'Agora',
  'carte.panel.kind.experience': 'Expérience immersive',
  'carte.panel.kind.media': 'Média (Webradio / WebTV)',
  'carte.panel.kind.service': 'Service',
  'carte.panel.kind.porte': 'Entrée / sortie',
  'carte.panel.density.label': 'Affluence',
  'carte.panel.itinerary': 'Itinéraire (à venir)',
  'carte.panel.viewWorkshops': 'Voir {lexWorkshopDefPlural} liés',
  'carte.panel.close': 'Fermer le détail',
  'carte.actions.downloadPdf': 'Télécharger le plan PDF',
  'carte.actions.viewProgramme': 'Voir le programme du salon',
  'carte.sse.error.aria':
    'La connexion temps réel a été interrompue, l’affluence affichée peut être obsolète.',
  'carte.sse.connected.aria': 'Connexion temps réel active.',
  'carte.svg.loading': 'Chargement du plan…',
  'carte.svg.error':
    'Le plan n’a pas pu être chargé. Vérifiez votre connexion ou réessayez dans quelques instants.',

  // ───── C:12 — Médias accueil ─────────────────────────────────────────
  'medias.title': 'Médias',
  'medias.subtitle':
    'Webradio, WebTV et vidéos courtes {eventTypeOf} {eventName}.',
  'medias.tile.webradio.title': 'Webradio',
  'medias.tile.webradio.subtitle': 'En direct du {eventDate} et tous les replays',
  'medias.tile.webradio.cta': 'Écouter la Webradio',
  'medias.tile.webradio.aria': 'Ouvrir la Webradio {eventName}',
  'medias.tile.webtv.title': 'WebTV',
  'medias.tile.webtv.subtitle': 'Conférences et keynotes filmées',
  'medias.tile.webtv.cta': 'Voir la WebTV',
  'medias.tile.webtv.aria': 'Ouvrir la WebTV {eventName}',
  'medias.tile.videos.title': 'Vidéos courtes',
  'medias.tile.videos.subtitle':
    'Témoignages élèves, projets EdTech, présentations partenaires',
  'medias.tile.videos.cta': 'Voir les vidéos',
  'medias.tile.videos.aria': 'Voir les vidéos courtes du salon',
  'medias.badge.live': 'En direct',
  'medias.badge.replay': '{count} replays disponibles',
  'medias.badge.videos': '{count} vidéos',
  'medias.featured.title': 'À ne pas manquer',
  'medias.featured.duration': 'Durée : {duration}',
  'medias.featured.cta': 'Lire',
  'medias.featured.aria': 'Lire le média « {title} »',
  'medias.preEvent.title': 'Diffusions live à partir du {eventDate}',
  'medias.preEvent.body':
    'Les flux Webradio et WebTV démarreront en direct le matin du {eventDate}, depuis le {eventPlace}.',
  'medias.captions.note':
    'Sous-titres VTT activés sur toutes les conférences pour garantir l’accessibilité.',

  // ───── C:15 — Partenaires (enrichissement) ───────────────────────────
  'partners.subtitle': 'Salon {eventName} — Recrutement et Emploi',
  'partners.tab.all': 'Tous',
  'partners.empty':
    'Aucun partenaire ne correspond à cette catégorie pour le moment.',
  'partners.level.platine': 'Platine',
  'partners.level.gold': 'Or',
  'partners.level.silver': 'Argent',
  'partners.level.bronze': 'Bronze',
  'partners.level.partner': 'Partenaire',
  'partners.card.aria':
    'Partenaire {name}, catégorie {category}, niveau {level}',
  // FIX 2026-05-07 (retour Mika) — variante sans niveau pour quand
  // la feature flag `partners.show_levels` est désactivée (par défaut).
  'partners.card.aria.noLevel':
    'Partenaire {name}, catégorie {category}',
  'partners.card.viewDetail': 'Voir la fiche',
  'partners.count':
    '{count, plural, =0 {Aucun partenaire} one {1 partenaire} other {{count} partenaires}}',
  'partners.disclaimer':
    'Les partenaires officiels sont validés par l’organisation du salon.',

  // ───── E:27 — À propos ───────────────────────────────────────────────
  'about.title': 'À propos du salon NOLAS 2026',
  'about.intro':
    'L’application officielle du salon NOLAS 2026 — Recrutement, Emploi et Formation. Une matinée au SPOT de Thionville pour rencontrer recruteurs, organismes de formation et acteurs de l’emploi.',
  'about.toc.title': 'Sommaire',
  'about.toc.salon': 'Le salon',
  // Repli du sommaire (nav). LOT 5 : section #drane renommée #organisateur
  // (ce libellé la sert de repli) ; entrée « dispositif TNE » supprimée.
  'about.toc.drane': 'L’organisateur',
  'about.toc.figures': 'Chiffres clés',
  'about.toc.credits': 'Crédits',
  'about.section.salon.title': 'Le salon',
  'about.section.salon.p1':
    'Le salon NOLAS 2026 réunit la communauté éducative autour de trois axes : citoyenneté numérique, intelligence artificielle et usages du numérique éducatif. Élèves, enseignants, parents, partenaires et exposants se retrouvent au SPOT de Thionville le vendredi 9 octobre 2026, de 8 h 30 à 13 h.',
  'about.section.salon.p2':
    'L’application NOLAS 2026 vous accompagne avant, pendant et après la journée : programme personnalisé, badge numérique, scanner exposant, carte temps réel et accès aux médias produits sur place.',
  // LOT 5 : repli i18n de la section « L'organisateur » (#organisateur). Le
  // contenu réel est éditable au back-office (content.about.section.organisateur.title
  // et content.about.organisateur.p1). Section « dispositif TNE » (about.section.tne.*)
  // supprimée — résidu TNE Vosges hors sujet sur NOLAS.
  'about.section.drane.title': 'L’organisateur',
  'about.section.drane.p1':
    'Le salon NOLAS 2026 est organisé par eln.',
  // LOT 5 : repli i18n du TITRE du bloc « Chiffres clés » uniquement. Les 4
  // paires valeur/libellé sont désormais éditables au back-office
  // (content.about.figures.f1..f4.value/.label) et laissées VIDES — les
  // anciennes valeurs (27,3 M€, 75 %…) venaient du dispositif TNE Vosges,
  // FAUSSES pour NOLAS. Le bloc reste MASQUÉ tant qu'aucune valeur n'est saisie.
  'about.figures.title': 'Chiffres clés du NOLAS',
  // LOT 5 : repli i18n des « Crédits ». Bandeau multi-lignes supprimé
  // (éditeur / hébergeur / financement — la ligne financement « PIA 4 —
  // Territoires Numériques Éducatifs » était un résidu TNE Vosges). Le contenu
  // est éditable au back-office (content.about.credits.author).
  'about.section.credits.title': 'Crédits',
  'about.section.credits.author':
    'Application développée par Mikaël FOURNY — LPO La Briquerie, pour eln.',

  // ───── E:29 — Contact ────────────────────────────────────────────────
  'contact.title': 'Nous contacter',
  'contact.subtitle':
    'Une question, une demande presse, un retour d’accessibilité ? Nos équipes vous répondent sous 48 h ouvrables.',
  'contact.coordinates.title': 'Coordonnées',
  'contact.coordinates.address.label': 'Adresse postale',
  'contact.coordinates.address.value':
    'SPOT de Thionville — Place de la Liberté, 57100 Thionville',
  'contact.coordinates.email.label': 'Adresse e-mail',
  // FIX LOT 1 (2026-07-23, demande Mika) : l'ancienne adresse de contact
  // (résidu de l'ancien salon) ne doit plus figurer dans le code. Contact officiel
  // NOLAS = mikael.fourny@labriquerie.net. Affichée ET utilisée pour le lien
  // mailto: sur /contact (ContactPage.tsx).
  'contact.coordinates.email.value': 'mikael.fourny@labriquerie.net',
  'contact.coordinates.phone.label': 'Téléphone',
  'contact.coordinates.phone.value': '03 83 86 20 20',
  'contact.coordinates.hours.label': 'Horaires d’accueil',
  'contact.coordinates.hours.value':
    'Du lundi au vendredi, de 9 h à 12 h et de 14 h à 17 h.',
  'contact.form.title': 'Formulaire de contact',
  'contact.form.name.label': 'Nom et prénom',
  'contact.form.name.placeholder': 'Camille Dupont',
  'contact.form.email.label': 'Adresse e-mail',
  'contact.form.email.placeholder': 'camille.dupont@exemple.fr',
  'contact.form.subject.label': 'Sujet de votre message',
  'contact.form.subject.option.general': 'Question générale',
  'contact.form.subject.option.inscription': 'Inscription',
  'contact.form.subject.option.accessibilite': 'Accessibilité',
  'contact.form.subject.option.rgpd': 'Données personnelles (RGPD)',
  'contact.form.subject.option.presse': 'Presse',
  'contact.form.subject.option.autre': 'Autre',
  'contact.form.message.label': 'Votre message',
  'contact.form.message.placeholder':
    'Décrivez votre demande en quelques lignes…',
  'contact.form.message.counter': '{count} / 2000 caractères',
  'contact.form.consent.label':
    'J’accepte que mes données soient utilisées exclusivement pour traiter ma demande, conformément au RGPD.',
  'contact.form.required': 'Champ obligatoire.',
  'contact.form.email.invalid':
    'L’adresse e-mail saisie n’est pas valide.',
  'contact.form.message.tooLong':
    'Le message ne doit pas dépasser 2000 caractères.',
  'contact.form.message.tooShort':
    'Veuillez détailler votre demande (10 caractères minimum).',
  'contact.form.consent.required':
    'Le consentement RGPD est obligatoire pour envoyer le message.',
  'contact.form.submit': 'Envoyer le message',
  'contact.form.submitting': 'Envoi en cours…',
  'contact.form.success.title': 'Message envoyé',
  'contact.form.success.body':
    'Votre demande a bien été transmise. Nous vous répondrons sous 48 heures ouvrables.',
  'contact.form.queued':
    'Connexion réseau indisponible. Votre message sera transmis automatiquement au retour de connexion.',
  'contact.form.error':
    'L’envoi a échoué. Veuillez réessayer dans quelques instants.',
  'contact.form.replyDelay': 'Réponse sous 48 heures ouvrables.',

  // ════════════════════════════════════════════════════════════════════════
  // Sprint S8 — SSE présence temps réel + dernier check-in
  // ════════════════════════════════════════════════════════════════════════
  // Bandeau de statut affiché sur la CartePage selon le mode de connexion.
  // - sse : connexion temps réel active → pas de bandeau
  // - polling : SSE indisponible mais snapshot 8 s opérationnel
  // - simulated : tout est hors-ligne → simulation locale déterministe
  'carte.status.polling':
    'Connexion temps réel indisponible. L’affluence est mise à jour toutes les 8 secondes.',
  'carte.status.simulated':
    'Mode hors-ligne — l’affluence affichée est simulée à des fins d’aperçu et sera mise à jour dès que la connexion sera rétablie.',
  'carte.status.lastCheckin':
    'Votre dernier passage : {standId}. Le marqueur « Vous êtes ici » est actif sur la carte.',

  // ════════════════════════════════════════════════════════════════════════
  // Sprint S7 — Form builder dynamique (admin) + DynamicForm (composant)
  // Voir CDC §10.3, §10.16.3, REVIEW-CHECKLIST.md
  // ════════════════════════════════════════════════════════════════════════

  // ───── FormBuilderPage (admin) ──────────────────────────────────────
  'formBuilder.title': 'Form builder',
  'formBuilder.subtitle':
    "Modifiez en temps réel les champs du formulaire d'inscription pour chaque rôle. Les changements sont versionnés et publiés sans toucher au code.",
  'formBuilder.role.label': 'Rôle à éditer',
  'formBuilder.addField': 'Ajouter un champ',
  'formBuilder.fieldCount':
    '{count, plural, =0 {Aucun champ} one {1 champ} other {{count} champs}}',
  'formBuilder.empty.body':
    'Aucun champ pour ce rôle. Ajoutez votre premier champ pour démarrer.',

  'formBuilder.edit.panelTitle': 'Édition des champs',
  'formBuilder.preview.panelTitle': 'Aperçu en direct',
  'formBuilder.preview.disabled':
    'Les champs sont désactivés pour empêcher toute soumission accidentelle.',
  'formBuilder.preview.empty':
    'Ajoutez au moins un champ pour visualiser le formulaire.',

  'formBuilder.publish.cta': 'Publier la nouvelle version',
  'formBuilder.publish.success': 'Nouvelle version publiée avec succès.',
  'formBuilder.publish.error':
    "La publication a échoué. Vérifiez votre connexion et réessayez.",
  'formBuilder.publish.empty':
    'Impossible de publier un formulaire sans aucun champ.',

  'formBuilder.history.cta': 'Voir les versions',
  'formBuilder.history.title': 'Historique des versions',
  'formBuilder.history.empty':
    "Aucune version n'a été publiée pour ce rôle.",
  'formBuilder.history.current': 'Actuelle',
  'formBuilder.history.restore': 'Restaurer cette version',

  'formBuilder.error.versions':
    "Impossible de charger les versions. Vérifiez votre connexion ou votre session admin.",

  // ───── Lignes de champ (liste de gauche) ────────────────────────────
  'formBuilder.field.archived': 'archivé',
  'formBuilder.field.archive': 'Archiver',
  'formBuilder.field.unarchive': 'Réactiver',
  'formBuilder.field.required.aria': 'Champ obligatoire',
  'formBuilder.field.drag.aria':
    'Déplacer le champ « {label} » — utilisez les flèches haut et bas pour réordonner.',
  'formBuilder.field.edit.aria': 'Modifier le champ « {label} »',
  'formBuilder.field.archive.aria':
    'Archiver ou réactiver le champ « {label} »',

  // ───── Modale d'édition d'un champ ──────────────────────────────────
  'formBuilder.modal.add.title': 'Nouveau champ',
  'formBuilder.modal.edit.title': 'Modifier le champ',
  'formBuilder.modal.key.label': 'Clé technique',
  'formBuilder.modal.type.label': 'Type',
  'formBuilder.modal.label.label': 'Libellé affiché',
  'formBuilder.modal.helper.label': "Texte d'aide (optionnel)",
  'formBuilder.modal.placeholder.label': 'Placeholder (optionnel)',
  'formBuilder.modal.pattern.label': 'Regex de validation (optionnel)',
  'formBuilder.modal.required.label': 'Champ obligatoire',
  'formBuilder.modal.options.label': 'Options',
  'formBuilder.modal.option.add': 'Ajouter une option',
  'formBuilder.modal.option.value.aria': "Valeur de l'option",
  'formBuilder.modal.option.label.aria': "Libellé affiché de l'option",
  'formBuilder.modal.option.remove.aria': 'Supprimer cette option',
  'formBuilder.modal.error.key':
    'La clé doit commencer par une lettre minuscule et contenir 3 à 41 caractères (lettres, chiffres, tirets bas).',
  'formBuilder.modal.error.keyDup':
    'Cette clé est déjà utilisée par un autre champ.',
  'formBuilder.modal.error.label':
    'Le libellé est obligatoire.',
  'formBuilder.modal.error.options':
    'Au moins une option est requise pour ce type de champ.',

  // ───── Libellés des types de champs ─────────────────────────────────
  'formBuilder.fieldType.text': 'Texte court',
  'formBuilder.fieldType.email': 'Adresse e-mail',
  'formBuilder.fieldType.tel': 'Téléphone',
  'formBuilder.fieldType.textarea': 'Texte long',
  'formBuilder.fieldType.select': 'Liste déroulante',
  'formBuilder.fieldType.multiselect': 'Choix multiple (liste)',
  'formBuilder.fieldType.checkbox': 'Cases à cocher',
  'formBuilder.fieldType.radio': 'Choix unique (boutons radio)',
  'formBuilder.fieldType.date': 'Date',
  'formBuilder.fieldType.number': 'Nombre',
  'formBuilder.fieldType.consent': 'Consentement RGPD',
  'formBuilder.fieldType.file': 'Fichier',

  // ───── DynamicForm (composant réutilisable) ─────────────────────────
  'dynamicForm.submit.default': 'Valider',
  'dynamicForm.optional': '(facultatif)',

  // ═══════════════════════════════════════════════════════════════════════
  // Sprint S9 — Web Push (VAPID) — visiteur + admin (FIX 2026-05-13).
  // ═══════════════════════════════════════════════════════════════════════

  // ───── NotificationsPrefsPage (visiteur) ───────────────────────────
  'notifications.title': 'Mes notifications',
  'notifications.intro':
    'Activez les notifications pour ne rien manquer le {eventDate} : changements de programme, ouverture des inscriptions {lexWorkshopPlural}, alertes événement.',
  'notifications.unsupported':
    'Votre navigateur ne prend pas en charge les notifications push. Essayez Chrome, Edge, Firefox ou Safari à jour.',

  'notifications.toggle.sectionTitle': 'État de l’abonnement',
  'notifications.toggle.enable': 'Activer les notifications',
  'notifications.toggle.disable': 'Désactiver les notifications',
  'notifications.toggle.enable.aria':
    'Activer les notifications push sur cet appareil',
  'notifications.toggle.disable.aria':
    'Désactiver les notifications push sur cet appareil',
  'notifications.toggle.enabled': 'Notifications activées sur cet appareil.',
  'notifications.toggle.disabled': 'Notifications désactivées sur cet appareil.',
  'notifications.toggle.error':
    'Impossible de modifier l’abonnement. Réessayez plus tard.',
  'notifications.denied.help':
    'Vous avez refusé les notifications. Pour les réactiver, ouvrez les réglages de votre navigateur, autorisez les notifications pour ce site, puis rechargez la page.',

  'notifications.status.unsupported':
    'Web Push n’est pas pris en charge sur ce navigateur.',
  'notifications.status.ios-needs-install':
    'Sur iOS, vous devez d’abord ajouter cette application à l’écran d’accueil.',
  'notifications.status.default':
    'Les notifications ne sont pas encore activées.',
  'notifications.status.denied':
    'Les notifications sont bloquées par votre navigateur.',
  'notifications.status.granted-not-subscribed':
    'Les notifications sont autorisées, mais l’abonnement n’a pas encore été créé.',
  'notifications.status.subscribed':
    'Vous êtes abonné aux notifications sur cet appareil.',

  'notifications.categories.sectionTitle': 'Que souhaitez-vous recevoir ?',
  'notifications.categories.help':
    'Ces préférences sont enregistrées sur cet appareil uniquement. Elles n’affectent pas le serveur — pour le moment.',

  'notifications.cat.eventAlerts': 'Alertes événement',
  'notifications.cat.eventAlerts.helper':
    'Annonces urgentes le jour J : ouverture des portes, changement de salle, sécurité.',
  'notifications.cat.programmeChanges': 'Changements de programme',
  'notifications.cat.programmeChanges.helper':
    'Modifications d’horaire, ajouts ou annulations {lexWorkshopOfPluralBare} et de conférences.',
  'notifications.cat.workshops': '{lexWorkshopPluralCap}',
  'notifications.cat.workshops.helper':
    'Confirmations d’inscription, places libérées, rappels avant le démarrage.',
  'notifications.cat.broadcastOrganizer': 'Messages des organisateurs',
  'notifications.cat.broadcastOrganizer.helper':
    'Communications générales de l’organisation adressées à tous les inscrits.',

  'notifications.test.sectionTitle': 'Tester sur cet appareil',
  'notifications.test.help':
    'Envoyez-vous une notification de test pour vérifier que tout fonctionne correctement (caméra, son, vibration).',
  'notifications.test.cta': 'M’envoyer une notification de test',
  'notifications.test.sent':
    'Notification de test envoyée. Si vous ne la recevez pas, vérifiez vos réglages.',
  'notifications.test.error':
    'L’envoi de la notification de test a échoué.',
  'notifications.test.notSubscribed':
    'Vous devez d’abord activer les notifications pour effectuer un test.',

  // ───── PushComposerPage (admin) ────────────────────────────────────
  'pushComposer.title': 'Composer une notification',
  'pushComposer.subtitle':
    'Rédigez une notification push, choisissez sa cible et envoyez-la immédiatement ou programmez son envoi. Toutes les actions sont consignées dans le journal d’audit.',

  'pushComposer.legend.message': 'Message',
  'pushComposer.legend.target': 'Cible',
  'pushComposer.legend.schedule': 'Planification',

  'pushComposer.field.title': 'Titre (60 caractères maximum)',
  'pushComposer.field.body': 'Corps du message (200 caractères maximum)',
  'pushComposer.field.url': 'URL d’action (au tap sur la notification)',
  'pushComposer.field.icon': 'Icône personnalisée (chemin vers une image)',
  'pushComposer.field.urgency': 'Urgence',
  'pushComposer.field.topic': 'Topic (regroupement, optionnel)',
  'pushComposer.field.targetKind': 'Type de cible',
  'pushComposer.field.targetValue.group': 'Identifiant ou slug du groupe',
  'pushComposer.field.targetValue.user': 'Identifiant numérique de l’utilisateur',
  'pushComposer.field.targetValue.role': 'Rôle ciblé (visitor, exhibitor, vip…)',
  'pushComposer.field.scheduledAt': 'Date et heure d’envoi (optionnel)',
  'pushComposer.field.scheduledAt.helper':
    'Laissez vide pour envoyer immédiatement. La date est interprétée en heure de Paris.',

  'pushComposer.urgency.very-low': 'Très basse',
  'pushComposer.urgency.low': 'Basse',
  'pushComposer.urgency.normal': 'Normale',
  'pushComposer.urgency.high': 'Haute',

  'pushComposer.targetKind.broadcast': 'Tous les abonnés',
  'pushComposer.targetKind.group': 'Un groupe',
  'pushComposer.targetKind.user': 'Un utilisateur',
  'pushComposer.targetKind.role': 'Un rôle',

  'pushComposer.error.title':
    'Le titre est obligatoire et limité à 60 caractères.',
  'pushComposer.error.body':
    'Le message est obligatoire et limité à 200 caractères.',
  'pushComposer.error.targetValue':
    'Indiquez la valeur de la cible pour ce type.',
  'pushComposer.error.topic':
    'Le topic doit contenir 1 à 32 caractères alphanumériques (lettres, chiffres, tirets, soulignés).',

  'pushComposer.submit': 'Envoyer la notification',
  'pushComposer.send.scheduled':
    'Notification programmée. Le cron prendra le relais à l’heure prévue.',
  'pushComposer.send.sent':
    '{count, plural, =0 {Aucun abonné concerné, mais la notification est consignée.} one {Notification envoyée à 1 abonné.} other {Notification envoyée à {count} abonnés.}}',
  'pushComposer.send.error':
    'L’envoi a échoué. Vérifiez les paramètres et réessayez.',

  'pushComposer.preview.aria': 'Aperçu de la notification sur mobile',
  'pushComposer.preview.title': 'Aperçu',
  'pushComposer.preview.placeholderTitle': 'Titre de la notification',
  'pushComposer.preview.placeholderBody':
    'Corps du message qui apparaîtra sur l’appareil de l’utilisateur.',

  'pushComposer.history.title': 'Historique récent',
  'pushComposer.history.loading': 'Chargement de l’historique…',
  'pushComposer.history.error':
    'Impossible de charger l’historique. Vérifiez votre session admin.',
  'pushComposer.history.empty':
    'Aucune notification n’a encore été envoyée depuis cette console.',
  'pushComposer.history.scheduled': 'Programmée pour : {at}',

  'pushComposer.status.queued': 'En file',
  'pushComposer.status.sending': 'Envoi en cours',
  'pushComposer.status.sent': 'Envoyée',
  'pushComposer.status.failed': 'Échec',
  'pushComposer.status.cancelled': 'Annulée',

  // ═══════════════════════════════════════════════════════════════════════
  // Sprint S10 — Médias étendus (Webradio, WebTV, Vidéos courtes,
  // Détail média, Fiche partenaire). FIX 2026-05-15.
  // ═══════════════════════════════════════════════════════════════════════

  // ───── Webradio (C:13) ──────────────────────────────────────────────
  'webradio.title': 'Webradio',
  'webradio.player.aria': 'Lecteur audio Webradio {eventName}',
  'webradio.audio.aria': 'Lecteur audio — {title}',
  'webradio.audio.fallback':
    'Votre navigateur ne prend pas en charge la lecture audio HTML5.',
  'webradio.badge.live': 'En direct',
  'webradio.badge.offline': 'Hors antenne',
  'webradio.player.now': '« {title} » — animé par {host}',
  'webradio.player.noLive':
    'Aucune diffusion en direct pour le moment. Retrouvez toutes les rediffusions ci-dessous.',
  'webradio.qa.openButton': 'Poser une question à l’antenne',
  'webradio.qa.modal.title': 'Q&A en direct',
  'webradio.qa.modal.body':
    'La modération en direct est en cours d’intégration. Elle sera disponible dès le 9 octobre 2026, en lien avec l’équipe {lexWorkshopCap} Canopé 88. Merci de votre patience.',

  'webradio.tabs.aria': 'Onglets de la rubrique Webradio',
  'webradio.tabs.programme': 'Programme du flux',
  'webradio.tabs.replays': 'Rediffusions',

  'webradio.programme.title': 'Programme du flux — 9 octobre 2026',
  'webradio.programme.note':
    'Programme prévisionnel — susceptible d’ajustements le jour J.',
  'webradio.slot.0930.time': '09 h 30',
  'webradio.slot.0930.title': 'Ouverture officielle du salon',
  'webradio.slot.0930.host': 'eln',
  'webradio.slot.1100.time': '11 h 00',
  'webradio.slot.1100.title': 'Citoyenneté numérique en classe',
  'webradio.slot.1100.host': 'Élèves Ambassadeurs NOLAS',
  'webradio.slot.1400.time': '14 h 00',
  'webradio.slot.1400.title': 'L’IA, levier ou risque pour les apprentissages ?',
  'webradio.slot.1400.host': 'Université de Lorraine',
  'webradio.slot.1530.time': '15 h 30',
  'webradio.slot.1530.title': 'Usages du numérique éducatif — retours du terrain',
  'webradio.slot.1530.host': '{lexWorkshopCap} Canopé 88',

  'webradio.replays.title': 'Rediffusions disponibles',
  'webradio.replays.empty':
    'Aucune rediffusion publiée pour le moment. Revenez après le 9 octobre 2026.',
  'webradio.replay.aria': 'Écouter la rediffusion : {title}',
  'webradio.replay.meta': 'Animé par {host} — {duration}',

  'webradio.captions.note':
    'Les conférences de la WebTV sont sous-titrées en français (fichiers VTT) pour respecter les exigences d’accessibilité WCAG 2.1 AA.',

  // ───── WebTV (C:14) ────────────────────────────────────────────────
  'webtv.title': 'WebTV',
  'webtv.subtitle':
    'Conférences et keynotes filmées — disponibles en rediffusion avec sous-titres VTT.',
  'webtv.video.aria': 'Lecteur vidéo — {title}',
  'webtv.video.fallback':
    'Votre navigateur ne prend pas en charge la lecture vidéo HTML5.',
  'webtv.video.meta': 'Intervenant : {speaker} — Durée : {duration}',
  'webtv.captions.label': 'Sous-titres français',
  'webtv.captions.available': 'Sous-titres VTT disponibles',
  'webtv.preEvent.title': 'Les rediffusions arrivent bientôt',
  'webtv.preEvent.body':
    'Les conférences et keynotes filmées le {eventDate} seront publiées progressivement dès le lendemain (montage et sous-titrage).',
  'webtv.library.title': 'Toutes les rediffusions',
  'webtv.library.empty':
    'Aucune rediffusion ne correspond à ce filtre. Essayez « Toutes ».',
  'webtv.filters.aria': 'Filtres de la bibliothèque WebTV',
  'webtv.filter.all': 'Toutes',
  'webtv.filter.citoyennete': 'Citoyenneté numérique',
  'webtv.filter.ia': 'Intelligence artificielle',
  'webtv.filter.usages': 'Usages du numérique éducatif',
  'webtv.card.aria': 'Lire la conférence : {title}',
  'webtv.card.captionsTitle': 'Sous-titres disponibles',
  'webtv.card.detailLink': 'Voir le détail',
  'webtv.captions.note':
    'Toutes les conférences sont sous-titrées en français pour respecter les exigences d’accessibilité WCAG 2.1 AA.',

  // ───── Vidéos courtes ─────────────────────────────────────────────
  'videos.title': 'Vidéos courtes',
  'videos.subtitle':
    'Témoignages d’élèves ambassadeurs, projets EdTech, présentations partenaires — formats courts (3 à 5 minutes).',
  'videos.filters.aria': 'Filtres de la rubrique Vidéos',
  'videos.filter.all': 'Toutes',
  'videos.filter.eleves': 'Élèves ambassadeurs',
  'videos.filter.edtech': 'EdTech et innovation',
  'videos.filter.partenaires': 'Partenaires',
  'videos.preEvent.title': 'Les vidéos arrivent bientôt',
  'videos.preEvent.body':
    'Les capsules courtes sont en cours de production. Premières publications attendues sous 7 jours après l’événement.',
  'videos.card.aria': 'Lire la vidéo : {title}',
  'videos.captions.note':
    'Les capsules sont sous-titrées en français quand un fichier VTT est fourni par le producteur.',

  // ───── Détail média ───────────────────────────────────────────────
  'mediaDetail.error.title': 'Média indisponible',
  'mediaDetail.error.invalidId':
    'Identifiant de média manquant ou invalide.',
  'mediaDetail.error.notFound':
    'Ce média n’existe plus ou n’a pas pu être chargé.',
  'mediaDetail.meta':
    '{kind} — Intervenant : {speaker} — Durée : {duration}',
  'mediaDetail.kind.webradio_live': 'Webradio en direct',
  'mediaDetail.kind.webradio_replay': 'Rediffusion radio',
  'mediaDetail.kind.webtv_live': 'WebTV en direct',
  'mediaDetail.kind.webtv_replay': 'Rediffusion vidéo',
  'mediaDetail.kind.video_short': 'Capsule courte',
  'mediaDetail.kind.presentation_pdf': 'Présentation (PDF)',
  'mediaDetail.kind.photo_album': 'Album photo',
  'mediaDetail.audio.aria': 'Lecteur audio — {title}',
  'mediaDetail.audio.fallback':
    'Votre navigateur ne prend pas en charge la lecture audio HTML5.',
  'mediaDetail.video.aria': 'Lecteur vidéo — {title}',
  'mediaDetail.video.fallback':
    'Votre navigateur ne prend pas en charge la lecture vidéo HTML5.',
  'mediaDetail.captions.label': 'Sous-titres français',
  'mediaDetail.captions.available': 'Sous-titres VTT disponibles',
  'mediaDetail.description.title': 'Description et transcription',
  'mediaDetail.workshop.title': '{lexWorkshopCap} associé{lexWorkshopAgr}',
  'mediaDetail.partner.title': 'Partenaire associé',
  'mediaDetail.partner.aria': 'Voir la fiche du partenaire {name}',

  // ───── Fiche partenaire ───────────────────────────────────────────
  'fichePartenaire.error.title': 'Fiche partenaire indisponible',
  'fichePartenaire.error.invalidSlug':
    'Identifiant de partenaire manquant ou invalide.',
  'fichePartenaire.error.notFound':
    'Ce partenaire n’existe plus ou n’est pas publié.',
  'fichePartenaire.subtitle': '{category} — {level}',
  'fichePartenaire.description.title': 'Présentation',
  'fichePartenaire.website.title': 'Site web',
  'fichePartenaire.website.disclaimer':
    'En cliquant sur ce lien, vous quittez l’application {eventName} pour ouvrir le site externe du partenaire dans un nouvel onglet.',
  'fichePartenaire.contact.title': 'Contact',
  'fichePartenaire.contact.name': 'Nom',
  'fichePartenaire.contact.email': 'Courriel',
  'fichePartenaire.contact.phone': 'Téléphone',
  'fichePartenaire.workshops.title': '{lexWorkshopPluralCap} animé{lexWorkshopAgrPlural}',
  'fichePartenaire.media.title': 'Médias liés',
  'fichePartenaire.stand.title': 'Sur le plan',
  'fichePartenaire.stand.cta': 'Aller au stand sur le plan',
  'fichePartenaire.stand.notExhibitor':
    'Partenaire institutionnel — pas de stand physique sur le salon.',

  // ════════════════════════════════════════════════════════════════════════
  // Sprint S11 — CMS pages éditables (Tiptap + workflow draft → published)
  // Voir CDC §10.3 (CMS) + SPRINT-S11.md
  // ════════════════════════════════════════════════════════════════════════

  // ───── PageDynamiquePage (public /p/:slug) ──────────────────────────
  'cms.page.loading': 'Chargement de la page…',
  'cms.page.notFound':
    "Cette page n'existe pas ou a été retirée par l'équipe éditoriale.",
  'cms.page.error.title': 'Erreur de chargement',
  'cms.page.error.generic':
    "Impossible de charger cette page pour le moment. Réessayez dans quelques instants.",
  'cms.page.tiptap.unsupported':
    "Le format d'édition Tiptap JSON n'est pas encore pris en charge côté public. Le contenu Markdown reste accessible.",
  'cms.page.toc.title': 'Sommaire',
  'cms.page.toc.aria': 'Sommaire de la page',
  'cms.page.lastUpdate': 'Dernière mise à jour le {date}.',

  // ───── PagesListPage (admin) ────────────────────────────────────────
  'cms.list.title': 'Pages éditoriales',
  'cms.list.subtitle':
    'Gérez les pages institutionnelles, communiqués et contenus éditoriaux {eventTypeOf} {eventName}.',
  'cms.list.cta.create': 'Nouvelle page',
  'cms.list.create.defaultTitle': 'Nouvelle page (à compléter)',
  'cms.list.create.success': 'Brouillon créé avec succès.',
  'cms.list.create.error':
    "Impossible de créer la page. Vérifiez votre session admin et réessayez.",
  'cms.list.filters.aria': 'Filtres de la liste des pages',
  'cms.list.filter.status': 'Statut',
  'cms.list.count':
    '{count, plural, =0 {Aucune page} one {1 page} other {{count} pages}}',
  'cms.list.col.title': 'Titre',
  'cms.list.col.slug': 'Slug',
  'cms.list.col.status': 'Statut',
  'cms.list.col.lastEditor': 'Dernier éditeur',
  'cms.list.col.updatedAt': 'Mise à jour',
  'cms.list.col.actions': 'Actions',
  'cms.list.action.edit': 'Éditer',
  'cms.list.action.versions': 'Versions',
  'cms.list.action.viewPublic': 'Voir en ligne',
  'cms.list.empty':
    "Aucune page ne correspond à ce filtre. Créez une nouvelle page pour démarrer.",
  'cms.list.loading': 'Chargement des pages…',
  'cms.list.error.generic':
    "Impossible de charger la liste des pages. Vérifiez votre session admin.",

  // ───── Statuts (partagés) ───────────────────────────────────────────
  'cms.status.all': 'Tous les statuts',
  'cms.status.draft': 'Brouillon',
  'cms.status.review': 'En relecture',
  'cms.status.approved': 'Approuvée',
  'cms.status.published': 'Publiée',
  'cms.status.archived': 'Archivée',
  'cms.status.rejected': 'Rejetée',

  // ───── PageEditorPage (admin) ───────────────────────────────────────
  'cms.editor.title': 'Édition de la page',
  'cms.editor.loading': 'Chargement de la page…',
  'cms.editor.error.title': 'Erreur',
  'cms.editor.error.idInvalid': 'Identifiant de page invalide.',
  'cms.editor.error.notFound':
    "Cette page n'existe pas ou a été archivée.",
  'cms.editor.error.fetch':
    "Impossible de charger la page. Vérifiez votre session admin et réessayez.",
  'cms.editor.back': 'Retour à la liste',
  'cms.editor.viewVersions': 'Voir les versions',
  'cms.editor.field.slug': 'Slug',
  'cms.editor.field.status': 'Statut',
  'cms.editor.field.title': 'Titre',
  'cms.editor.field.excerpt': 'Résumé court',
  'cms.editor.field.metaTitle': 'Meta title (SEO)',
  'cms.editor.field.metaDesc': 'Meta description (SEO)',
  'cms.editor.field.featuredImage': "URL de l'image de couverture",
  'cms.editor.toolbar.aria': "Barre d'outils de l'éditeur",
  'cms.editor.toolbar.link': 'Lien',
  'cms.editor.toolbar.image': 'Image',
  'cms.editor.link.prompt':
    "Saisissez l'URL du lien (laissez vide pour retirer) :",
  'cms.editor.image.uploaded': 'Image insérée dans la page.',
  'cms.editor.image.error': "Échec de l'upload de l'image.",
  'cms.editor.panel.aria': "Zone d'édition Markdown / WYSIWYG",
  'cms.editor.panel.title': 'Éditeur',
  'cms.editor.preview.title': 'Aperçu',
  'cms.editor.action.saveDraft': 'Enregistrer le brouillon',
  'cms.editor.save.success': 'Brouillon enregistré.',
  'cms.editor.save.error':
    "Échec de l'enregistrement. Vérifiez votre connexion et réessayez.",
  'cms.editor.action.submitReview': 'Soumettre à relecture',
  'cms.editor.action.submitReview.success':
    'Page soumise à relecture.',
  'cms.editor.action.approve': 'Approuver',
  'cms.editor.action.approve.success': 'Page approuvée.',
  'cms.editor.action.publish': 'Publier',
  'cms.editor.action.publish.success': 'Page publiée. Visible par le public.',
  'cms.editor.workflow.error':
    "L'action n'a pas pu aboutir. Vérifiez l'état de la page et réessayez.",

  // ───── PageVersionsPage (admin) ─────────────────────────────────────
  'cms.versions.title': 'Historique des versions',
  'cms.versions.subtitle':
    "Chaque modification publiée crée un instantané restaurable. Les snapshots sont conservés sans suppression.",
  'cms.versions.back': "Retour à l'éditeur",
  'cms.versions.loading': "Chargement de l'historique…",
  'cms.versions.empty':
    "Aucune version archivée pour cette page. Le premier snapshot apparaîtra dès la prochaine publication.",
  'cms.versions.empty.snapshot':
    'Aucun extrait disponible pour cette version.',
  'cms.versions.author': 'Auteur :',
  'cms.versions.action.restore': 'Restaurer cette version',
  'cms.versions.error.idInvalid': 'Identifiant de page invalide.',
  'cms.versions.error.generic':
    "Impossible de charger les versions. Vérifiez votre session admin.",
  'cms.versions.restore.modal.title': 'Restaurer une version',
  'cms.versions.restore.modal.body':
    'Voulez-vous restaurer la version #{id} en tant que brouillon ?',
  'cms.versions.restore.modal.note':
    "La version actuellement publiée reste en ligne tant que vous n'avez pas re-publié le brouillon restauré.",
  'cms.versions.restore.modal.confirm': 'Oui, restaurer',
  'cms.versions.restore.success':
    'Version restaurée. Le brouillon est prêt à être édité.',
  'cms.versions.restore.error':
    "La restauration a échoué. Vérifiez votre session admin et réessayez.",

  // ─────────────────────────────────────────────────────────────────────
  // ───── Sprint S13 — Feature flags + Mode maintenance ─────────────────
  // ─────────────────────────────────────────────────────────────────────

  // ── Composant FeatureFlagToggle (réutilisable) ──────────────────────
  'featureFlags.toggle.aria':
    'Bascule {label} — actuellement {state, select, on {activé} other {désactivé}}',
  'featureFlags.toggle.lastChange':
    'Modifié par {who} le {when}',
  // FIX 2026-05-18 (audit Cowork Bug #11) : un updated_by/actor_id
  // NULL = action système (seed SQL, cron, migration) — cf. §29
  // « actor_id=NULL = système ». « Inconnu » suggérait à tort un
  // utilisateur fantôme en base.
  'featureFlags.toggle.unknown': 'Système',
  'featureFlags.state.enabled': 'Activé',
  'featureFlags.state.disabled': 'Désactivé',
  'featureFlags.badge.sensitive': 'Sensible',
  'featureFlags.badge.superAdmin': 'Super-admin',

  // ── Page admin /admin/feature-flags ─────────────────────────────────
  'featureFlagsAdmin.title': 'Feature flags',
  'featureFlagsAdmin.subtitle':
    "Activez ou désactivez chaque module sans rebuild. Les changements s'appliquent en moins de 60 secondes côté visiteurs (cache HTTP). Les flags sensibles (RGPD/IA) demandent une confirmation supplémentaire.",
  'featureFlagsAdmin.filters.aria': 'Filtres de la liste des flags',
  'featureFlagsAdmin.filter.search': 'Recherche',
  'featureFlagsAdmin.filter.search.placeholder':
    'Rechercher par clé, libellé ou description…',
  'featureFlagsAdmin.filter.category': 'Catégorie',
  'featureFlagsAdmin.filter.category.all': 'Toutes les catégories',
  'featureFlagsAdmin.filter.state': 'État',
  'featureFlagsAdmin.filter.state.all': 'Tous',
  'featureFlagsAdmin.filter.state.enabled': 'Activés',
  'featureFlagsAdmin.filter.state.disabled': 'Désactivés',
  'featureFlagsAdmin.count':
    '{count, plural, =0 {Aucun flag} one {# flag} other {# flags}} affiché(s)',
  'featureFlagsAdmin.loading': 'Chargement des flags…',
  'featureFlagsAdmin.empty':
    "Aucun flag ne correspond à ce filtre. Élargissez vos critères pour en voir d'autres.",
  'featureFlagsAdmin.error.generic':
    'Impossible de charger la liste des flags. Vérifiez votre session admin et réessayez.',
  'featureFlagsAdmin.toggle.success': 'Flag mis à jour.',
  'featureFlagsAdmin.toggle.error':
    "La mise à jour du flag a échoué. Vérifiez votre session admin et réessayez.",

  // ── Catégories (libellés FR) ────────────────────────────────────────
  'featureFlagsAdmin.category.feature': 'Fonctionnalités',
  'featureFlagsAdmin.category.section': "Sections de l'app",
  'featureFlagsAdmin.category.api': 'API',
  'featureFlagsAdmin.category.integration': 'Intégrations',
  'featureFlagsAdmin.category.maintenance': 'Maintenance',

  // ── Confirmation modale pour flags sensibles ───────────────────────
  'featureFlagsAdmin.confirm.title': 'Confirmer le changement',
  'featureFlagsAdmin.confirm.body':
    'Vous êtes sur le point de changer l’état du flag « {label} » vers {state}. Ce flag est sensible (RGPD ou sécurité).',
  'featureFlagsAdmin.confirm.warning':
    "Cette action sera tracée dans l'audit trail (catégorie « flags »). Confirmez si vous comprenez l'impact.",
  'featureFlagsAdmin.confirm.action': 'Oui, basculer',

  // ── Panneau audit latéral ───────────────────────────────────────────
  'featureFlagsAdmin.audit.title': 'Audit récent',
  'featureFlagsAdmin.audit.subtitle': '20 derniers changements de flags',
  'featureFlagsAdmin.audit.loading': 'Chargement…',
  'featureFlagsAdmin.audit.empty':
    "Aucun changement enregistré pour le moment.",
  'featureFlagsAdmin.audit.unavailable':
    "Le journal d'audit n'est pas encore disponible (endpoint admin/audit.php à livrer).",

  // ── Page admin /admin/maintenance ───────────────────────────────────
  'maintenance.title': 'Mode maintenance',
  'maintenance.subtitle':
    "Active la page maintenance.html pour tous les visiteurs (sauf bypass admin via jeton URL). Le backoffice reste accessible. À utiliser pour les fenêtres de mise à jour en production.",
  'maintenance.banner.active':
    'Maintenance ACTIVE — les visiteurs voient la page maintenance.html.',
  'maintenance.loading': 'Chargement…',
  'maintenance.error.generic':
    'Impossible de charger la configuration. Vérifiez votre session admin et réessayez.',
  'maintenance.field.status': 'Statut',
  'maintenance.status.active': 'Active',
  'maintenance.status.inactive': 'Inactive',
  'maintenance.field.message': 'Message affiché aux visiteurs',
  'maintenance.field.message.placeholder':
    "Ex : « Notre application est temporairement en maintenance. Nous serons de retour vers 18 h. »",
  'maintenance.field.eta': "ETA fin de maintenance",
  'maintenance.field.eta.placeholder':
    "Ex : « Retour prévu vers 18 h » (texte libre, optionnel)",
  'maintenance.field.token': 'Jeton de bypass admin',
  'maintenance.token.notSet': 'Aucun jeton défini',
  'maintenance.token.regen': 'Régénérer un nouveau jeton',
  'maintenance.token.copyNow':
    "Copiez ce jeton dans un endroit sûr — il ne sera plus jamais affiché en clair.",
  'maintenance.token.copy': 'Copier dans le presse-papiers',
  'maintenance.token.copied': 'Jeton copié.',
  'maintenance.field.reason': "Motif (obligatoire pour activer)",
  'maintenance.field.reason.placeholder':
    "Ex : « Migration de la base le 27/05 entre 02 h et 04 h »",
  'maintenance.field.reason.helper':
    "Le motif est consigné dans le journal d'audit (catégorie « maintenance »). Au moins 8 caractères pour activer la maintenance.",
  'maintenance.action.save': 'Enregistrer les changements',
  'maintenance.action.activate': 'Activer la maintenance',
  'maintenance.action.deactivate': 'Désactiver la maintenance',
  'maintenance.save.success': 'Configuration enregistrée.',
  'maintenance.save.error':
    "L'enregistrement a échoué. Vérifiez votre session super-administrateur.",
  'maintenance.error.reasonRequired':
    'Saisissez un motif d’au moins 8 caractères avant d’activer la maintenance.',

  // ── Aperçu iframe ────────────────────────────────────────────────────
  'maintenance.preview.title': 'Aperçu de maintenance.html',
  'maintenance.preview.subtitle':
    "Page rendue telle quelle (les placeholders {{MESSAGE}} / {{ETA}} sont substitués par le serveur lors de l'envoi réel).",
  'maintenance.preview.iframeTitle': 'Aperçu de la page maintenance',

  // FIX 2026-05-20 (Mika Message 8 — chantier C : section rate-limits) ──
  'maintenance.rateLimits.title': 'Compteurs anti-abus (rate-limits)',
  'maintenance.rateLimits.lead':
    'Si un administrateur ou un visiteur est bloqué par « Trop de tentatives », un super_admin peut purger l’ensemble des compteurs ci-dessous. Action immédiate, jamais destructive (les compteurs glissants se reconstruisent normalement).',
  'maintenance.rateLimits.loading': 'Chargement de l’état des compteurs…',
  'maintenance.rateLimits.error':
    'Impossible de charger l’état des compteurs. Vérifiez votre session puis réessayez.',
  'maintenance.rateLimits.counterCount':
    '{n, plural, =0 {Aucun compteur actif} one {1 compteur actif} other {# compteurs actifs}}',
  'maintenance.rateLimits.recentTitle':
    'Voir les 10 compteurs récemment touchés',
  'maintenance.rateLimits.confirm':
    'Purger TOUS les compteurs anti-abus ? Cette action est immédiate et débloque tous les comptes bloqués. Confirmez si vous savez ce que vous faites.',
  'maintenance.rateLimits.btn.purge': 'Purger tous les compteurs',
  'maintenance.rateLimits.btn.refresh': 'Actualiser l’état',
  'maintenance.rateLimits.toast.purged':
    '{deleted, plural, =0 {Aucun compteur à purger} one {1 compteur purgé} other {# compteurs purgés}}.',
  'maintenance.rateLimits.toast.error':
    'Impossible de purger les compteurs. Vérifiez vos droits (super_admin requis).',
  // ── Confirmation activation ──────────────────────────────────────────
  'maintenance.confirm.title': 'Activer le mode maintenance',
  'maintenance.confirm.body':
    "Activer la maintenance va couper l'accès à l'application pour TOUS les visiteurs (hors admins avec jeton de bypass).",
  'maintenance.confirm.instruction':
    "Pour confirmer, tapez {keyword} en majuscules dans le champ ci-dessous.",
  'maintenance.confirm.inputAria': "Champ de confirmation — tapez MAINTENANCE pour activer",
  'maintenance.confirm.action': "Confirmer l'activation",
  'maintenance.confirm.reasonMissing':
    "Le motif d'activation est trop court (minimum 8 caractères). Complétez-le avant de confirmer.",

  // ═════════════════════════════════════════════════════════════════════
  // ───── Sprint S12 — Backoffice rôles + invitations + 2FA UI ──────────
  // ═════════════════════════════════════════════════════════════════════

  // ───── RoleGuard (composant) ─────────────────────────────────────────
  'roleGuard.loading': 'Vérification de votre session…',
  'roleGuard.loading.aria': "Vérification d'accès en cours",
  'roleGuard.denied.title': 'Accès refusé',
  'roleGuard.denied.body':
    "Votre rôle ({role}) ne vous permet pas d'accéder à cette section. Contactez un super administrateur si vous pensez qu'il s'agit d'une erreur.",
  'roleGuard.denied.hint':
    "La protection est appliquée à la fois côté navigateur et côté serveur — aucune donnée sensible ne fuit, l'accès est juste masqué.",
  // FIX 2026-05-18 (bug Mika « je tourne en rond ») — écran serveur HS
  // qui REMPLACE la redirection login en boucle sur erreur 5xx/réseau.
  'roleGuard.serverError.title': 'Service momentanément indisponible',
  'roleGuard.serverError.body':
    "La vérification de votre session a échoué (le serveur n'a pas répondu correctement). Ce n'est pas un problème de connexion : vous reconnecter ne corrigera rien. Réessayez dans quelques instants.",
  'roleGuard.serverError.retry': 'Réessayer',
  'roleGuard.serverError.toLogin': 'Aller à la connexion',
  // FIX 2026-05-18 (bug Mika « /connexion tourne en rond ») — écran
  // serveur HS pour les pages visiteur protégées (RequireVisitor),
  // qui REMPLACE la redirection /connexion en boucle sur 5xx/réseau.
  'requireVisitor.error.title': 'Service momentanément indisponible',
  'requireVisitor.error.body':
    "Votre espace n'a pas pu être chargé car le serveur n'a pas répondu correctement. Ce n'est pas un problème de connexion de votre côté — vous reconnecter ne changerait rien. Réessayez dans quelques instants.",
  'requireVisitor.error.retry': 'Réessayer',
  'requireVisitor.error.toLogin': 'Revenir à la connexion',

  // ───── UsersAdminPage (admin) ────────────────────────────────────────
  'usersAdmin.title': 'Comptes administrateurs',
  'usersAdmin.subtitle':
    'Gestion des comptes du backoffice {eventName} — création, modification de rôle, suspension.',
  'usersAdmin.banner.superAdminOnly':
    "Page réservée au super administrateur. Toutes les actions sont auditées (audit trail RGPD §10.10).",

  'usersAdmin.filter.role': 'Filtrer par rôle',
  'usersAdmin.filter.status': 'Filtrer par statut',
  'usersAdmin.filter.all': 'Tous',

  'usersAdmin.role.super_admin': 'Super admin',
  'usersAdmin.role.admin': 'Administrateur',
  'usersAdmin.role.editeur': 'Éditeur',
  'usersAdmin.role.auteur': 'Auteur',
  'usersAdmin.role.moderateur': 'Modérateur',
  // FIX 2026-05-20 (Mika Message 8) — wording « Organisateur » plus explicite
  // que « Équipe » (cohérent avec V35 seed organisateurs = staff).
  'usersAdmin.role.staff': 'Organisateur',
  // FIX 2026-05-20 (Mika Message 6) — rôle backoffice restreint VIP.
  'usersAdmin.role.vip_manager': 'Gestion VIP',

  'usersAdmin.status.active': 'Actif',
  'usersAdmin.status.blocked': 'Suspendu',
  'usersAdmin.status.pending': 'En attente',
  'usersAdmin.status.archived': 'Archivé',

  'usersAdmin.totp.on': 'Activée',
  'usersAdmin.totp.off': 'Inactive',

  'usersAdmin.action.invite': 'Inviter un nouvel administrateur',
  'usersAdmin.action.suspend': 'Suspendre',
  'usersAdmin.action.reactivate': 'Réactiver',

  'usersAdmin.list.title': 'Liste des comptes',
  'usersAdmin.list.loading': 'Chargement des comptes administrateurs…',
  'usersAdmin.list.error':
    "Impossible de charger la liste. Vérifiez votre session super administrateur.",
  'usersAdmin.list.empty': 'Aucun compte administrateur ne correspond aux filtres.',

  'usersAdmin.col.name': 'Nom',
  'usersAdmin.col.email': 'Email',
  'usersAdmin.col.role': 'Rôle',
  'usersAdmin.col.status': 'Statut',
  'usersAdmin.col.totp': '2FA',
  'usersAdmin.col.lastLogin': 'Dernière connexion',
  'usersAdmin.col.actions': 'Actions',

  'usersAdmin.lastLogin.never': 'Jamais',

  'usersAdmin.confirm.suspend':
    'Suspendre le compte {email} ? Cette action peut être annulée par une réactivation ultérieure.',
  'usersAdmin.suspend.success': 'Compte suspendu.',
  'usersAdmin.suspend.error':
    'Impossible de suspendre le compte. Vérifiez votre session.',
  'usersAdmin.reactivate.success': 'Compte réactivé.',
  'usersAdmin.reactivate.error':
    'Impossible de réactiver le compte. Vérifiez votre session.',

  // ───── Modale d'invitation ──────────────────────────────────────────
  'usersAdmin.invite.title': 'Inviter un nouvel administrateur',
  'usersAdmin.field.email': 'Adresse email',
  'usersAdmin.field.fullName': 'Nom complet (Prénom Nom)',
  'usersAdmin.field.role': 'Rôle attribué',
  'usersAdmin.field.note': "Note interne (visible uniquement aux super administrateurs)",

  'usersAdmin.invite.submit': "Créer le compte et générer l'invitation",
  'usersAdmin.invite.success':
    'Compte créé et invitation générée. Communiquez le lien au destinataire.',
  'usersAdmin.invite.error':
    "Échec de la création. Vérifiez l'adresse email et réessayez.",
  'usersAdmin.invite.created':
    "Invitation créée pour {email}. Communiquez le lien suivant au destinataire (par mail manuel le temps que l'envoi automatique soit branché).",
  'usersAdmin.invite.acceptUrl': "Lien d'acceptation (à transmettre)",
  'usersAdmin.invite.expiresAt': "Expire le {at}.",

  // ───── InvitationsListPage (admin) ───────────────────────────────────
  'invitations.title': 'Invitations administrateurs',
  'invitations.subtitle':
    "Suivi des invitations émises — relance ou révocation possibles tant qu'elles sont en attente.",

  'invitations.filter.status': 'Filtrer par statut',
  'invitations.filter.all': 'Toutes',

  'invitations.status.pending': 'En attente',
  'invitations.status.accepted': 'Acceptée',
  'invitations.status.expired': 'Expirée',
  'invitations.status.revoked': 'Révoquée',

  'invitations.list.title': 'Liste des invitations',
  'invitations.list.loading': 'Chargement des invitations…',
  'invitations.list.error':
    "Impossible de charger la liste. Vérifiez votre session super administrateur.",
  'invitations.list.empty': 'Aucune invitation ne correspond aux filtres.',

  'invitations.col.email': 'Email',
  'invitations.col.role': 'Rôle invité',
  'invitations.col.status': 'Statut',
  'invitations.col.created': 'Créée le',
  'invitations.col.expires': 'Expire le',
  'invitations.col.inviter': 'Émise par',
  'invitations.col.actions': 'Actions',

  'invitations.action.resend': 'Renvoyer le lien',
  'invitations.action.revoke': 'Révoquer',
  'invitations.action.none': '—',

  'invitations.confirm.revoke':
    "Révoquer l'invitation envoyée à {email} ? Cette action est irréversible.",

  'invitations.resend.success':
    'Nouvelle invitation générée. Récupérez le lien dans la modale.',
  'invitations.resend.error':
    "Échec de la régénération. Vérifiez votre session et réessayez.",
  'invitations.revoke.success': 'Invitation révoquée.',
  'invitations.revoke.error':
    "Échec de la révocation. Vérifiez votre session.",

  'invitations.resend.modal.title': "Lien d'acceptation à transmettre",
  'invitations.resend.modal.body':
    'Communiquez le lien suivant à {email} (par mail manuel ou Tchap interne).',
  'invitations.resend.modal.url': "URL d'acceptation",
  'invitations.resend.modal.expires': "Expire le {at}.",

  // ───── AcceptInvitationPage (système — public) ───────────────────────
  'acceptInvitation.title': "Acceptation d'invitation",
  'acceptInvitation.subtitle':
    "Bienvenue sur le backoffice {eventName}. Choisissez un mot de passe robuste pour activer votre compte.",
  'acceptInvitation.field.password': 'Mot de passe',
  'acceptInvitation.field.password.hint':
    "Au moins 12 caractères. Recommandé : un mélange de minuscules, majuscules, chiffres et symboles.",
  'acceptInvitation.field.passwordConfirm': 'Confirmation du mot de passe',
  'acceptInvitation.error.tokenMissing':
    "Lien d'invitation invalide ou incomplet. Demandez à votre super administrateur de vous renvoyer le lien.",
  'acceptInvitation.error.passwordTooShort':
    'Le mot de passe doit comporter au moins 12 caractères.',
  'acceptInvitation.error.passwordMismatch':
    'Les deux mots de passe ne correspondent pas.',
  'acceptInvitation.action.submit': 'Activer mon compte',
  'acceptInvitation.success':
    "Compte activé. Configurez maintenant la double authentification.",
  'acceptInvitation.error':
    "Échec de l'activation. Vérifiez votre lien ou réessayez.",
  // FIX 2026-08-03 (vague 2, constat M3) — c'était le DERNIER « TOTP » visible
  // par un utilisateur, et il se trouvait précisément sur la page qui mène au
  // parcours guidé. Le brief interdit ce jargon dans tout texte affiché : on
  // ne dit pas « TOTP », on dit ce que la personne va faire.
  'acceptInvitation.footnote':
    'Après validation, vous serez guidé pour choisir comment recevoir votre code de connexion : par e-mail, ou dans une application gratuite sur votre téléphone.',

  // ───── Double authentification (lot 2026-08-03) ──────────────────────
  // Les anciennes clés `totpEnroll.*` (parcours en 3 étapes, sans
  // accompagnement) ont été RETIRÉES : le parcours guidé en 5 étapes vit
  // désormais dans le sous-catalogue `fr.twoFactor.ts`, préfixe `twoFactor.`.

  // ═════════════════════════════════════════════════════════════════════
  // ───── Sprint S14 — Module Recommandations IA ────────────────────────
  // ═════════════════════════════════════════════════════════════════════

  // ── Composant AiRecommendationsBlock ─────────────────────────────────
  'ai.reco.title': 'Recommandations pour vous',
  'ai.reco.loading': 'Chargement des recommandations…',
  'ai.reco.empty': 'Aucune recommandation disponible pour le moment.',
  'ai.reco.source.cache': 'Cache',
  'ai.reco.source.live': 'En direct',
  'ai.reco.source.mock': 'Démo',
  'ai.consent.cta.body':
    "Activez les recommandations IA pour recevoir des suggestions {lexWorkshopOfPluralBare} et d'exposants adaptés à votre profil.",
  'ai.consent.cta.action': 'Activer les recommandations IA',

  // ── Page MonQuestionnaireIaPage ──────────────────────────────────────
  'aiQuestionnaire.title': 'Personnaliser mes recommandations IA',
  'aiQuestionnaire.unavailable':
    'Cette fonctionnalité est actuellement indisponible. Réessayez plus tard.',
  'aiQuestionnaire.minor.body':
    "Cette fonctionnalité n'est pas accessible aux mineurs. Le traitement IA des données personnelles d'un mineur est interdit (RGPD art. 8).",
  'aiQuestionnaire.minor.back': 'Retour à mon espace',

  // ── Étape consentement ────────────────────────────────────────────────
  'aiConsent.subtitle': 'Consentement au traitement IA',
  'aiConsent.intro':
    "Pour vous proposer des recommandations personnalisées, l'application transmet à un moteur d'intelligence artificielle votre rôle, vos centres d'intérêt et un historique de navigation anonymisé. Aucune donnée nominative (nom, email, téléphone) n'est envoyée.",
  'aiConsent.bullet.providers':
    "Providers utilisables : Claude (Anthropic), OpenAI, Gemini (Google), Mistral, DeepSeek. L’organisation choisit lequel est actif à un instant donné.",
  'aiConsent.bullet.data':
    "Données envoyées : votre profil professionnel (rôle, niveau, structure), vos thématiques d'intérêt et un agrégat anonyme de votre navigation.",
  'aiConsent.bullet.retention':
    "Conservation : 30 jours pour les majeurs, 7 jours pour les mineurs (RGPD strict). Anonymisation automatique au-delà.",
  'aiConsent.bullet.revocation':
    "Vous pouvez révoquer ce consentement à tout moment. Toutes vos données IA seront alors supprimées immédiatement.",
  'aiConsent.action.grant': "J'accepte le traitement IA de mes données",
  'aiConsent.minorNote':
    'Si vous êtes mineur, cette fonctionnalité ne vous est pas proposée — RGPD art. 8.',

  // ── Étape questionnaire ───────────────────────────────────────────────
  'aiQuestionnaire.questionnaire.subtitle':
    'Quelques informations pour mieux vous orienter',
  'aiQuestionnaire.questionnaire.submit': 'Enregistrer mes réponses',
  'aiQuestionnaire.consent.granted':
    'Consentement enregistré. Vous pouvez maintenant répondre au questionnaire.',
  'aiQuestionnaire.saved':
    'Réponses enregistrées. Vos recommandations seront mises à jour.',

  // ── Étape confirmation ────────────────────────────────────────────────
  'aiQuestionnaire.done.title': 'Configuration terminée',
  'aiQuestionnaire.done.body':
    "Merci ! Vos recommandations IA seront affichées sur votre espace. Vous pouvez modifier vos réponses à tout moment.",

  // ── Révocation ────────────────────────────────────────────────────────
  'aiQuestionnaire.revoke.title': 'Révoquer mon consentement IA',
  'aiQuestionnaire.revoke.body':
    "Cette action désactive immédiatement les recommandations et supprime les événements de navigation associés à votre compte.",
  'aiQuestionnaire.revoke.action': 'Révoquer mon consentement',
  'aiQuestionnaire.revoke.confirm':
    'Confirmer la révocation ? Tous les événements et le cache de recommandations associés à votre compte seront supprimés immédiatement.',
  'aiQuestionnaire.revoked':
    'Consentement révoqué. Vos données IA ont été supprimées.',

  // ── Erreurs ───────────────────────────────────────────────────────────
  'aiQuestionnaire.error.minor':
    "Cette fonctionnalité n'est pas accessible aux mineurs.",
  'aiQuestionnaire.error.consent':
    "Impossible d'enregistrer le consentement. Réessayez dans un instant.",
  'aiQuestionnaire.error.save':
    "Impossible d'enregistrer vos réponses. Réessayez dans un instant.",
  'aiQuestionnaire.error.revoke':
    'La révocation a échoué. Réessayez dans un instant.',

  // ── Page admin AiConfigPage ──────────────────────────────────────────
  'aiConfig.title': 'Configuration IA',
  'aiConfig.banner.masterOff':
    "Module IA désactivé (master switch off). Voir les <link>feature flags</link>.",
  'aiConfig.tab.providers': 'Providers',
  'aiConfig.tab.assignments': 'Assignations',
  'aiConfig.tab.events': 'Événements récents',
  'aiConfig.error.load':
    'Impossible de charger la configuration IA. Vérifiez votre session admin.',

  // ── Liste providers ──────────────────────────────────────────────────
  'aiConfig.providers.empty': 'Aucun provider configuré.',
  'aiConfig.status.active': 'Actif',
  'aiConfig.status.inactive': 'Désactivé',
  'aiConfig.field.providerKey': 'Clé provider',
  'aiConfig.field.model': 'Modèle par défaut',
  'aiConfig.field.apiKey': 'Clé API',
  'aiConfig.apiKey.set': 'Configurée (env {env})',
  'aiConfig.apiKey.unset': 'Non configurée (env {env}) — mode mock activé',
  'aiConfig.field.budget': 'Budget mensuel',
  'aiConfig.budget.summary':
    '{spent} € / {budget} € ({pct} %)',
  'aiConfig.alert.budget80':
    "Seuil d'alerte 80 % atteint — surveillez la consommation.",
  'aiConfig.alert.recorded':
    'Alerte {pct} % enregistrée le {at}.',
  'aiConfig.action.test': 'Tester',
  'aiConfig.action.activate': 'Activer',
  'aiConfig.action.deactivate': 'Désactiver',
  'aiConfig.test.success':
    'Test réussi : {provider} {mock}, latence {ms} ms.',
  'aiConfig.test.error': 'Le test du provider a échoué.',
  'aiConfig.toggle.success': 'Provider mis à jour.',
  'aiConfig.toggle.error': 'Impossible de modifier le provider.',

  // ── Onglet assignations ──────────────────────────────────────────────
  'aiConfig.assignments.empty': 'Aucune assignation feature → provider.',
  'aiConfig.assign.feature': 'Feature',
  'aiConfig.assign.provider': 'Provider',
  'aiConfig.assign.priority': 'Priorité',
  'aiConfig.assign.enabled': 'Activée',

  // ── Onglet events ────────────────────────────────────────────────────
  'aiConfig.events.empty': 'Aucun événement IA enregistré sur la période.',
  'aiConfig.events.when': 'Date',
  'aiConfig.events.action': 'Action',
  'aiConfig.events.actor': 'Utilisateur',
  'aiConfig.events.payload': 'Détail',

  // ───── Sprint S15 — Intégrations OSNum + data.gouv.fr ──────────────────

  // ── Autocomplete UAI (annuaire-education) ────────────────────────────
  'uaiAutocomplete.placeholder':
    'Tapez le nom de votre établissement…',
  'uaiAutocomplete.searching': 'Recherche en cours…',
  'uaiAutocomplete.noResults':
    'Aucun établissement trouvé. Essayez un autre mot-clé.',
  'uaiAutocomplete.resultCount':
    '{count, plural, one {# établissement trouvé} other {# établissements trouvés}}',
  'uaiAutocomplete.unavailable':
    'L’annuaire des établissements est momentanément indisponible. Saisissez librement.',
  'uaiAutocomplete.error':
    'Impossible de joindre l’annuaire. Vous pouvez saisir librement le nom de votre établissement.',
  'uaiAutocomplete.listbox.label':
    'Suggestions d’établissements scolaires',

  // ── Autocomplete commune (COG) ───────────────────────────────────────
  'communeAutocomplete.label': 'Commune',
  'communeAutocomplete.placeholder':
    'Tapez le nom de la commune…',
  'communeAutocomplete.searching': 'Recherche en cours…',
  'communeAutocomplete.noResults':
    'Aucune commune trouvée. Essayez un autre mot-clé.',
  'communeAutocomplete.resultCount':
    '{count, plural, one {# commune trouvée} other {# communes trouvées}}',
  'communeAutocomplete.unavailable':
    'Le répertoire des communes est momentanément indisponible. Saisissez librement.',
  'communeAutocomplete.error':
    'Impossible de joindre le répertoire géographique. Vous pouvez saisir librement le nom de la commune.',
  'communeAutocomplete.listbox.label':
    'Suggestions de communes',

  // ── Page admin État OSNum ────────────────────────────────────────────
  'osnumStatus.title': 'État de l’intégration OSNum 2.0',
  'osnumStatus.subtitle':
    'Vérification automatique toutes les 30 secondes. Vous pouvez aussi forcer une synchronisation manuelle.',
  'osnumStatus.status.healthy': 'En ligne',
  'osnumStatus.status.degraded': 'Partiellement disponible',
  'osnumStatus.status.down': 'Hors ligne',
  'osnumStatus.status.not_configured': 'Non configuré',
  'osnumStatus.status.unknown': 'Vérification en cours…',
  'osnumStatus.metric.event': 'Événement {eventName}',
  'osnumStatus.metric.event.missing': 'Événement non trouvé côté OSNum',
  'osnumStatus.metric.sessions': 'Sessions synchronisées',
  'osnumStatus.metric.sessions.count':
    '{count, plural, one {# session} other {# sessions}}',
  'osnumStatus.metric.cachedAt': 'Dernière mise à jour',
  'osnumStatus.metric.nextPing': 'Prochain rafraîchissement',
  'osnumStatus.metric.nextPing.countdown':
    'dans {seconds, plural, one {# seconde} other {# secondes}}',
  'osnumStatus.metric.lastResync': 'Dernière synchronisation forcée',
  'osnumStatus.error.title': 'Erreur de connexion à OSNum',
  'osnumStatus.error.notConfigured':
    'L’intégration OSNum n’est pas configurée. Renseignez OSNUM_BASE_URL, OSNUM_APP_USER et OSNUM_APP_PASS dans le fichier .env.',
  'osnumStatus.error.unavailable':
    'OSNum ne répond pas. Vérifiez la connectivité du serveur ou l’état du Application Password WordPress.',
  'osnumStatus.action.resync': 'Forcer la synchronisation',
  'osnumStatus.action.refresh': 'Rafraîchir maintenant',
  'osnumStatus.sessions.title': 'Sessions de formation récentes',
  'osnumStatus.sessions.more':
    '… et {count, plural, one {# session de plus} other {# sessions de plus}}',
  'osnumStatus.resync.lastTitle': 'Détails de la dernière synchronisation',
  'osnumStatus.resync.cacheCleared': 'Cache fichier vidé :',
  'osnumStatus.resync.health':
    'Health check : {ok} (latence {ms} ms)',
  'osnumStatus.resync.events':
    'Sessions {eventName} récupérées : {sessions}',
  'osnumStatus.resync.rupn':
    'Équipements RUPN listés : {count}',
  'osnumStatus.resync.errors.title':
    'Étapes en erreur (l’app fonctionne en mode dégradé) :',
  'osnumStatus.resync.success':
    'Synchronisation OSNum effectuée.',
  'osnumStatus.resync.errorOffline':
    'Impossible de forcer la synchronisation hors ligne.',
  'osnumStatus.resync.errorGeneric':
    'La synchronisation a échoué. Consultez le journal d’audit.',

  // ── i18n libellés OSNum générique ─────────────────────────────────────
  'osnum.source.osnum': 'Source : OSNum 2.0',
  'osnum.source.fallback.annuaire':
    'Source : annuaire-education.data.gouv.fr (fallback)',
  'osnum.source.fallback.geo':
    'Source : geo.api.gouv.fr (fallback)',

  // ── Sprint S18 (Agent C) — aria-labels pour boutons icônes ──────────
  // Ajoutés le 2026-05-06. Ces clés couvrent tous les boutons à
  // contenu purement iconographique (caractères Unicode, glyphes
  // typographiques) qui n'ont pas de texte descriptif visible.
  'aria.toast.viewport': 'Notifications',
  'aria.toast.dismiss': 'Fermer la notification',
  'cms.editor.toolbar.bold.aria': 'Mettre en gras',
  'cms.editor.toolbar.italic.aria': 'Mettre en italique',
  'cms.editor.toolbar.strike.aria': 'Mettre en barré',
  'cms.editor.toolbar.h2.aria': 'Insérer un titre de niveau 2',
  'cms.editor.toolbar.h3.aria': 'Insérer un titre de niveau 3',
  'cms.editor.toolbar.bulletList.aria': 'Liste à puces',
  'cms.editor.toolbar.orderedList.aria': 'Liste numérotée',
  'cms.editor.toolbar.codeBlock.aria': 'Bloc de code',
  'cms.editor.toolbar.undo.aria': 'Annuler la dernière action',
  'cms.editor.toolbar.redo.aria': 'Rétablir la dernière action',

  // ───── Sprint S19 — Mode avant / pendant / après l'événement (F12) ──
  // Ajoutés le 2026-05-06 par Mika après livraison des 3 sous-agents
  // S19-A (hook), S19-B (Header pastille + 6 pages adaptées), S19-C
  // (RetrospectivePage + endpoint stats). Cf. CLAUDE.md §F12 + §5
  // entrée 2026-05-06 (S19 finale). Wording français de France strict
  // (cf. §15.5) — pluriels CardinalRule (`{days, plural, one {…} other {…}}`).

  // ── Header — pastille phase (3 clés) ──────────────────────────────
  'header.phase.before': 'J-{days}',
  'header.phase.live': 'LIVE',
  'header.phase.replays': 'Replays',
  // Aperçu intégral admin (2026-06-23) — suffixe + hint de la pastille phase.
  'header.phase.preview': 'aperçu',
  'header.phase.previewHint':
    'Aperçu intégral admin actif : la phase est forcée pour déverrouiller tous les écrans — ne reflète pas la date réelle.',

  // ── MonEspacePage — bandeaux par phase (15 clés) ──────────────────
  'monEspace.phaseBanner.before.countdown': 'Compte à rebours',
  'monEspace.phaseBanner.before.title':
    'J-{days} avant {eventName} — préparez votre venue',
  'monEspace.phaseBanner.before.checklistBadge': 'Téléchargez votre badge',
  'monEspace.phaseBanner.before.checklistProgramme': 'Consultez le programme',
  'monEspace.phaseBanner.before.checklistAteliers': 'Choisissez vos {lexWorkshopPlural}',
  'monEspace.phaseBanner.during.tag': 'En direct',
  'monEspace.phaseBanner.during.title':
    'Événement en cours — bonne journée {eventName} !',
  'monEspace.phaseBanner.during.nextWorkshop':
    'Prochain{lexWorkshopAgr} {lexWorkshopBare} : {title} ({room})',
  'monEspace.phaseBanner.during.noMore':
    'Vous n’avez plus {lexWorkshopOfPluralBare} planifié{lexWorkshopAgrPlural} aujourd’hui.',
  'monEspace.phaseBanner.after.tag': 'Merci !',
  'monEspace.phaseBanner.after.title': 'Merci d’avoir participé',
  'monEspace.phaseBanner.after.body':
    'Découvrez les replays et partagez votre retour.',
  'monEspace.phaseBanner.after.ctaReplays': 'Voir les replays',
  'monEspace.phaseBanner.after.ctaFeedback': 'Donner mon avis',

  // ── CartePage — phase before / after (4 clés) ─────────────────────
  'carte.phase.before.body':
    'Vous pourrez localiser stands et {lexWorkshopPlural}, voir l’affluence en temps réel, et retrouver votre dernier passage scanné.',
  'carte.phase.before.cta': 'Voir le programme',

  // ── ScannerPage — phase before / after (9 clés) ───────────────────
  'scanner.phase.before.title':
    'Le scanner ouvre le {eventDate} à 9 h. J-{days}.',
  'scanner.phase.before.body':
    'Le scanner exposant n’est pas encore actif. Profitez-en pour tester l’autorisation caméra de votre appareil afin d’être prêt·e le jour J.',
  'scanner.phase.before.testCamera.cta': 'Tester l’autorisation caméra',
  'scanner.phase.before.testCamera.ok': 'Autorisation caméra OK ✅',
  'scanner.phase.before.testCamera.denied':
    'Caméra refusée. Autorisez-la dans les réglages avant le {eventDate}.',
  'scanner.phase.before.testCamera.unsupported':
    'Cet appareil ne propose pas de caméra accessible au navigateur.',
  'scanner.phase.after.title': 'Scanner clos depuis le {eventDate} à 17 h.',
  'scanner.phase.after.body':
    'Vos check-ins du jour ont été enregistrés. Découvrez la rétrospective de l’événement.',
  'scanner.phase.after.ctaRetrospective': 'Voir la rétrospective',

  // ── WebradioPage / WebtvPage — teaser before (4 clés) ─────────────
  'webradio.badge.upcoming': 'Bientôt',
  'webradio.phase.before.body':
    'Le direct ouvre le {eventDate} à partir de 9 h. J-{days}. Découvrez le programme webradio ci-dessous.',
  'webtv.phase.before.title': 'WebTV — direct le {eventDate} à 9 h. J-{days}.',
  'webtv.phase.before.body':
    'La WebTV diffusera les conférences, {lexWorkshopPlural} et keynotes en direct le jour J. Replays disponibles dès J+1.',

  // ── MonProgrammePage — ribbons during / after (2 clés) ────────────
  'monProgramme.phase.during.inProgress': 'EN COURS',
  'monProgramme.phase.after.replayAvailable': 'Replay disponible →',

  // ── RetrospectivePage — page complète (30 clés) ───────────────────
  'retrospective.title': 'Rétrospective NOLAS 2026',
  'retrospective.hero.subtitle':
    '9 octobre 2026 — SPOT de Thionville, Place de la Liberté',
  'retrospective.waiting.title':
    'La rétrospective sera disponible après l’événement',
  'retrospective.waiting.subtitle.before':
    'Rendez-vous le 9 octobre 2026 au SPOT de Thionville.',
  'retrospective.waiting.subtitle.during':
    'L’événement est en cours. Retrouvez la rétrospective complète à partir du 28 mai.',
  'retrospective.waiting.cta.programme': 'Voir le programme du jour',
  'retrospective.countdown.hours': 'heures',
  'retrospective.countdown.minutes': 'minutes',
  'retrospective.stats.title': 'Chiffres clés',
  'retrospective.stats.visitors': 'visiteurs accueillis',
  'retrospective.stats.scans': 'scans réalisés',
  'retrospective.stats.workshopsFull': '{lexWorkshopPlural} au complet',
  'retrospective.stats.pushSent': 'notifications envoyées',
  'retrospective.stats.fallback':
    'Statistiques momentanément indisponibles.',
  'retrospective.stats.notReady':
    'Statistiques en cours de finalisation.',
  'retrospective.stats.generatedAt':
    'Statistiques générées il y a {days, plural, =0 {moins d’un jour} one {# jour} other {# jours}}.',
  'retrospective.gallery.title': 'Galerie photos',
  'retrospective.gallery.fallback':
    'La galerie photos est en cours de modération RGPD.',
  'retrospective.gallery.openAria': 'Ouvrir la photo : {title}',
  'retrospective.replays.title': 'Replays — Webradio, WebTV, vidéos courtes',
  'retrospective.replays.fallback':
    'Les rediffusions arrivent bientôt. Repassez dans quelques jours.',
  'retrospective.replays.openAria': 'Lire le replay : {title}',
  'retrospective.testimonials.title': 'Témoignages',
  'retrospective.testimonials.fallback':
    'Les témoignages seront publiés dans les prochains jours.',
  'retrospective.resources.title': 'Ressources distribuées',
  'retrospective.resources.fallback':
    'Les ressources distribuées le 9 octobre seront disponibles ici prochainement.',
  'retrospective.feedback.title': 'Donnez-nous votre avis',
  'retrospective.feedback.body':
    'Votre retour nous aide à améliorer les prochains salons NOLAS. Cela ne prend que 3 minutes.',
  'retrospective.feedback.cta': 'Donner mon avis',
  'retrospective.feedback.disclaimer':
    'Vous quittez l’app pour la plateforme OSNum 2.0.',

  // ───── Sprint S20-B — Connexion backoffice (LoginAdminPage) ──────────
  'admin.login.title': 'Connexion administrateur',
  'admin.login.subtitle':
    'Accédez à l’espace de gestion {eventTypeOf} {eventName}. Une vérification en deux étapes vous sera demandée après cette page.',
  'admin.login.field.email': 'Adresse e-mail professionnelle',
  'admin.login.field.password': 'Mot de passe',
  'admin.login.submit': 'Se connecter',
  'admin.login.hint.lostPassword':
    'En cas de mot de passe oublié, utilisez le lien ci-dessous pour recevoir un courriel de réinitialisation.',
  // FIX 2026-05-07 (S22) — Lien « Mot de passe oublié ? » sous le bouton Connexion.
  'admin.login.forgotLink': 'Mot de passe oublié ?',
  'admin.login.error.email.required': 'Veuillez saisir votre adresse e-mail.',
  'admin.login.error.email.invalid': 'Adresse e-mail invalide.',
  'admin.login.error.email.tooLong':
    'Adresse e-mail trop longue (190 caractères maximum).',
  'admin.login.error.password.required': 'Veuillez saisir votre mot de passe.',
  'admin.login.error.password.tooLong':
    'Mot de passe trop long (255 caractères maximum).',
  'admin.login.error.invalidCredentials':
    'Identifiants incorrects. Veuillez réessayer.',
  'admin.login.error.tooManyAttempts':
    'Trop de tentatives. Veuillez patienter quelques minutes avant de réessayer.',
  'admin.login.error.network':
    'Connexion au serveur impossible. Vérifiez votre réseau puis réessayez.',

  // ───── Sprint S20-B — Vérification 2FA e-mail (EmailTwoFactorPage) ────
  'admin.email2fa.title': 'Vérification en deux étapes',
  'admin.email2fa.subtitle':
    'Saisissez le code à 6 chiffres reçu par e-mail pour finaliser votre connexion.',
  'admin.email2fa.sentTo': 'Code envoyé à : {email}',
  'admin.email2fa.validity':
    'Le code est valable 10 minutes après son envoi.',
  'admin.email2fa.code.label': 'Code de vérification à 6 chiffres',
  // FIX 2026-08-03 (vague 2, constat M6) — l'aide décrit ce que le champ fait
  // VRAIMENT depuis qu'il partage le composant de saisie du lot 2FA : collage
  // depuis n'importe quelle case, et validation dès le 6ᵉ chiffre.
  'admin.email2fa.code.hint':
    'Vous pouvez coller le code complet. La validation se fait dès le 6ᵉ chiffre.',
  // `admin.email2fa.code.digitAria` retirée : l'aria-label des cases vient
  // désormais du composant partagé (`twoFactor.code.digitAria`, « Chiffre 3
  // sur 6 »). Deux libellés pour un même rôle, c'est deux occasions de
  // diverger.
  'admin.email2fa.submit': 'Valider le code',
  'admin.email2fa.resend.button': 'Renvoyer le code par e-mail',
  'admin.email2fa.resend.countdown':
    'Vous pourrez renvoyer un code dans {seconds, plural, one {# seconde} other {# secondes}}.',
  'admin.email2fa.cancel': 'Annuler et revenir à la connexion',
  'admin.email2fa.error.invalid':
    'Code incorrect ou expiré. Veuillez réessayer ou demander un nouveau code.',
  'admin.email2fa.error.invalidWithAttempts':
    'Code incorrect. Il vous reste {attempts, plural, one {# tentative} other {# tentatives}} avant blocage.',
  'admin.email2fa.error.tooManyAttempts':
    'Trop de tentatives. Veuillez patienter quelques minutes avant de réessayer.',
  'admin.email2fa.error.resendTooFast':
    'Vous devez patienter {seconds, plural, one {# seconde} other {# secondes}} avant de pouvoir demander un nouveau code.',
  'admin.email2fa.error.resendFailed':
    'Impossible de renvoyer le code pour le moment. Veuillez réessayer dans quelques instants.',
  'admin.email2fa.error.network':
    'Connexion au serveur impossible. Vérifiez votre réseau puis réessayez.',

  // ───── Sprint S22 — Mot de passe oublié (PasswordResetRequest / Confirm) ─
  // FIX 2026-05-07 (S22). Wording français de France strict (cf. §15.5).
  // Anti-énumération : la page request affiche le même message générique
  // qu'on ait trouvé l'admin ou non — on ne révèle jamais l'existence d'un
  // compte (RGPD + bonne pratique OWASP).
  'admin.passwordReset.request.title': 'Mot de passe oublié',
  'admin.passwordReset.request.subtitle':
    'Saisissez l’adresse e-mail liée à votre compte administrateur. Si elle correspond à un compte actif, un courriel contenant un lien de réinitialisation vous sera envoyé.',
  'admin.passwordReset.request.field.email': 'Adresse e-mail professionnelle',
  'admin.passwordReset.request.submit': 'Envoyer le lien',
  'admin.passwordReset.request.success':
    'Si cette adresse correspond à un compte administrateur, un courriel vient d’être envoyé. Vérifiez votre boîte de réception ainsi que vos courriers indésirables.',
  'admin.passwordReset.request.error.email.required':
    'Veuillez saisir votre adresse e-mail.',
  'admin.passwordReset.request.error.email.invalid':
    'Adresse e-mail invalide.',
  'admin.passwordReset.request.error.email.tooLong':
    'Adresse e-mail trop longue (190 caractères maximum).',
  'admin.passwordReset.request.error.tooManyAttempts':
    'Trop de tentatives. Veuillez patienter quelques minutes avant de réessayer.',
  'admin.passwordReset.request.error.network':
    'Connexion au serveur impossible. Vérifiez votre réseau puis réessayez.',
  'admin.passwordReset.request.backToLogin': 'Retour à la page de connexion',

  // Page « Définir un nouveau mot de passe » (lien depuis le mail)
  'admin.passwordReset.confirm.title': 'Définir un nouveau mot de passe',
  'admin.passwordReset.confirm.subtitle':
    'Choisissez un nouveau mot de passe d’au moins 8 caractères. Vous serez ensuite invité·e à vous reconnecter.',
  'admin.passwordReset.confirm.field.password': 'Nouveau mot de passe',
  'admin.passwordReset.confirm.field.passwordConfirm':
    'Confirmer le nouveau mot de passe',
  'admin.passwordReset.confirm.submit': 'Enregistrer le nouveau mot de passe',
  'admin.passwordReset.confirm.success':
    'Mot de passe mis à jour. Redirection vers la page de connexion…',
  'admin.passwordReset.confirm.error.password.required':
    'Veuillez saisir votre nouveau mot de passe.',
  'admin.passwordReset.confirm.error.password.tooShort':
    'Le mot de passe doit contenir au moins 8 caractères.',
  'admin.passwordReset.confirm.error.password.tooLong':
    'Mot de passe trop long (1024 caractères maximum).',
  'admin.passwordReset.confirm.error.passwordConfirm.required':
    'Veuillez confirmer le nouveau mot de passe.',
  'admin.passwordReset.confirm.error.passwordMismatch':
    'Les deux mots de passe ne correspondent pas.',
  'admin.passwordReset.confirm.error.tokenInvalid':
    'Lien invalide ou expiré. Veuillez demander un nouveau courriel de réinitialisation.',
  'admin.passwordReset.confirm.error.tokenExpired':
    'Ce lien a expiré. Veuillez demander un nouveau courriel de réinitialisation.',
  'admin.passwordReset.confirm.error.tooManyAttempts':
    'Trop de tentatives. Veuillez patienter quelques minutes avant de réessayer.',
  'admin.passwordReset.confirm.error.network':
    'Connexion au serveur impossible. Vérifiez votre réseau puis réessayez.',
  'admin.passwordReset.confirm.requestNewLink':
    'Demander un nouveau courriel de réinitialisation',
  'admin.passwordReset.confirm.missingToken':
    'Lien de réinitialisation invalide ou incomplet. Veuillez retourner à la page de connexion ou demander un nouveau lien.',
  'admin.passwordReset.confirm.backToLogin': 'Retour à la page de connexion',

  // ───── Sprint S21 — Tokens de déploiement à distance (super_admin only) ──
  // Ajoutés le 2026-05-07 lors de la consolidation post-S21. Page accessible
  // via /admin/deploy-tokens. Cf. CLAUDE.md §5 entrée S21 + DEPLOY-TOKEN-
  // SECURITY.md. Wording français de France strict §15.5.

  // ── Page principale + warnings ──────────────────────────────────────
  'admin.deploy.title': 'Tokens de déploiement à distance',
  'admin.deploy.subtitle':
    'Permettez à un opérateur (ou Claude Code) de pousser des fichiers vers la production sans FTP. Chaque token est révocable, audité, et limité par une whitelist de chemins.',
  'admin.deploy.warning.label': 'Attention',
  'admin.deploy.warning.body':
    'Tout token créé donne accès en écriture à la production. Utilisez le scope minimal et l’expiration la plus courte possible.',

  // ── Tableau tokens ──────────────────────────────────────────────────
  'admin.deploy.tokens.title': 'Tokens actifs',
  'admin.deploy.tokens.create': 'Créer un token',
  'admin.deploy.tokens.loading': 'Chargement…',
  'admin.deploy.tokens.error': 'Erreur lors du chargement des tokens.',
  'admin.deploy.tokens.empty': 'Aucun token créé pour le moment.',
  'admin.deploy.col.label': 'Description',
  'admin.deploy.col.prefix': 'Préfixe',
  'admin.deploy.col.allowPhp': 'Autoriser PHP',
  'admin.deploy.col.expiresAt': 'Expire le',
  'admin.deploy.col.lastUsedAt': 'Dernier usage',
  'admin.deploy.col.status': 'Statut',
  'admin.deploy.col.actions': 'Actions',
  'admin.deploy.allowPhp.yes': 'Oui',
  'admin.deploy.allowPhp.no': 'Non',
  'admin.deploy.allowPhp.dangerAria':
    'Attention : ce token autorise le push de fichiers PHP.',
  'admin.deploy.expiresAt.never': 'Jamais',
  'admin.deploy.lastUsedAt.never': 'Jamais utilisé',
  'admin.deploy.status.active': 'Actif',
  'admin.deploy.status.expired': 'Expiré',
  'admin.deploy.status.revoked': 'Révoqué',
  'admin.deploy.action.revoke': 'Révoquer',
  'admin.deploy.action.none': '—',

  // ── Modal création ──────────────────────────────────────────────────
  'admin.deploy.create.modal.title': 'Créer un token de déploiement',
  'admin.deploy.create.field.label': 'Description du token',
  'admin.deploy.create.field.label.helper':
    'Phrase courte pour identifier le token (ex. « Claude Code mai 2026 »).',
  'admin.deploy.create.field.allowPhp':
    'Autoriser le push de fichiers .php',
  'admin.deploy.create.field.allowPhp.helper':
    'Décocher sauf besoin précis. Même activé, les chemins critiques (auth, deploy, config) restent bloqués.',
  'admin.deploy.create.field.allowPaths':
    'Whitelist de chemins (un glob par ligne)',
  'admin.deploy.create.field.allowPaths.helper':
    'Globs autorisés (ex. « app/public_html/dist/** »). Le wildcard global "**" est refusé.',
  'admin.deploy.create.field.expiresInDays': 'Durée de validité (jours)',
  'admin.deploy.create.field.expiresInDays.helper':
    'Entre 1 et 365 jours. 30 j recommandé. Évitez les tokens longue durée.',
  'admin.deploy.create.field.ipAllowlist':
    'Liste IP autorisées (optionnel — une par ligne)',
  'admin.deploy.create.field.ipAllowlist.helper':
    'Si renseigné, seules ces IP pourront utiliser le token. Vide = pas de filtre IP.',
  'admin.deploy.create.submit': 'Créer le token',
  'admin.deploy.create.error.labelRequired':
    'La description est obligatoire.',
  'admin.deploy.create.error.pathsRequired':
    'Au moins un chemin valide est requis dans la whitelist.',
  'admin.deploy.create.error.expiresInvalid':
    'La durée doit être comprise entre 1 et 365 jours.',
  'admin.deploy.create.error.generic': 'Erreur à la création du token.',

  // ── Modal one-time-display ──────────────────────────────────────────
  'admin.deploy.created.modal.title':
    'Token créé — copiez-le maintenant',
  'admin.deploy.created.warning':
    'Ce token ne sera affiché qu’une seule fois. Copiez-le et stockez-le immédiatement dans un endroit sûr (.env.local). Une fois ce dialogue fermé, vous ne pourrez plus le récupérer.',
  'admin.deploy.created.tokenLabel':
    'Token (cliquez pour sélectionner)',
  'admin.deploy.created.copy': 'Copier dans le presse-papiers',
  'admin.deploy.created.copied': 'Token copié.',
  'admin.deploy.created.copyFailed':
    'Impossible de copier dans le presse-papiers.',
  'admin.deploy.created.expiresAt': 'Expire le {at}',

  // ── Révocation ──────────────────────────────────────────────────────
  'admin.deploy.revoke.prompt':
    'Confirmez la révocation du token "{label}" en saisissant la raison :',
  'admin.deploy.revoke.success': 'Token révoqué.',
  'admin.deploy.revoke.error.reasonRequired':
    'La raison de la révocation est obligatoire.',
  'admin.deploy.revoke.error.generic':
    'Erreur lors de la révocation.',

  // ── Historique ──────────────────────────────────────────────────────
  'admin.deploy.history.title':
    'Historique des déploiements (50 derniers)',
  'admin.deploy.history.loading': 'Chargement de l’historique…',
  'admin.deploy.history.empty': 'Aucun déploiement enregistré.',
  'admin.deploy.history.col.when': 'Date',
  'admin.deploy.history.col.token': 'Token',
  'admin.deploy.history.col.action': 'Action',
  'admin.deploy.history.col.path': 'Fichier',
  'admin.deploy.history.col.bytes': 'Octets',
  'admin.deploy.history.col.status': 'Statut',
  'admin.deploy.history.action.push': 'Push',
  'admin.deploy.history.action.rollback': 'Rollback',
  'admin.deploy.history.action.dry_run': 'Dry run',
  'admin.deploy.history.status.ok': 'OK',
  'admin.deploy.history.status.error': 'Erreur',
  'admin.deploy.history.status.skipped': 'Identique',
  'admin.deploy.history.status.rejected': 'Rejeté',

  // ───── S26 OnboardingScannerBadge ──────────────────────────────────
  // FIX 2026-05-07 (S26) — Conformité 100 % Stitch sur les pages
  // Onboarding, Scanner exposant et Mon badge visiteur.
  // Sources Stitch :
  //   - bienvenue_onboarding/code.html  +  bienvenue_onboarding_1/code.html
  //   - inscription_onboarding_2_5/, mon_badge_onboarding_3_5/,
  //     scanner_onboarding_4_5/, programme_onboarding_5_5/
  //   - scanner_exposant/, scanner_exposant_1/, scanner_exposant_2/
  //   - mon_badge_visiteur/code.html
  // -------------------------------------------------------------------
  'header.brand': '{eventName}',
  'scanner.menu.aria': 'Ouvrir le menu',
  'scanner.modal.subtitle':
    'Renseignez le passage du visiteur — niveau d’intérêt et notes facultatives.',
  'badge.validity.label': 'Badge valide pour l’événement',
  // NOLAS Sprint C : ID badge humain « NOLAS-00042 » affiché sous le QR.
  'badge.code.label': 'Identifiant :',

  // ───── S26 ProgrammeCarte (FIX 2026-05-07) ──────────────────────────
  // Mise en conformité 100 % Stitch des pages Programme global, Détail
  // atelier, Mon programme perso et Carte du salon.
  // Sources Stitch :
  //   - programme_global/code.html
  //   - d_tail_de_l_atelier/code.html
  //   - mon_programme_perso/code.html
  //   - carte_du_salon/code.html
  // -------------------------------------------------------------------

  // — Programme global : pastille date, slot, catégories, footer cards
  'programme.dateLong': '{eventDateLong}',
  'programme.dateShort': '{eventDateLong}',
  'programme.axis.all': 'Tous les thèmes',
  'programme.public.ecoles': 'Écoles',
  'programme.slot.range': '{start} — {end}',
  'programme.lunch.label':
    'Pause déjeuner et réseautage — Espace Convivialité',
  'programme.lunch.aria': 'Pause déjeuner — Espace Convivialité',
  'programme.category.inaugurale': 'Conférence inaugurale',
  'programme.category.atelier': '{lexWorkshopCap} pratique',
  'programme.category.tableRonde': 'Table ronde',
  'programme.category.conference': 'Conférence',
  'programme.workshop.lowSeats':
    'Dernières places ! ({count, plural, one {# restante} other {# restantes}})',
  'programme.workshop.addFavorite': 'Ajouter aux favoris',
  'programme.workshop.favorite.add': 'Ajouter {lexWorkshopDem} aux favoris',

  // — Détail atelier : topbar + objectifs + audience + footnote actions
  'atelier.topbar.title': "Détail {lexWorkshopOfDef}",
  'atelier.objectives.title': 'Objectifs pédagogiques',
  'atelier.audience.title': 'Public visé',
  'atelier.actions.favorite': 'Favoris',
  'atelier.actions.replaySoon': 'Replay bientôt',
  'atelier.actions.footnote':
    '* Une confirmation vous sera envoyée par mail après inscription.',

  // — Mon programme perso : titre, compteur, conflit pill, lunch
  'monProgramme.titlePerso': 'Mes {lexWorkshopPlural}',
  'monProgramme.eventsCount':
    '{count, plural, =0 {Aucun événement enregistré} one {# événement enregistré} other {# événements enregistrés}}',
  'monProgramme.conflict.pill': 'Conflit horaire',
  'monProgramme.lunch.time': '12 h 30',
  'monProgramme.lunch.label': 'Pause déjeuner libre — Réseautage',
  'monProgramme.card.favorite.aria':
    "{lexWorkshopDemCap} est dans votre programme personnel",

  // — Carte du salon : topbar + contrôles + last check-in card
  'carte.topbar.back.aria': "Revenir à l'écran précédent",
  'carte.topbar.menu.aria': 'Ouvrir le menu',
  'carte.zoom.in.aria': 'Agrandir le plan',
  'carte.zoom.out.aria': 'Réduire le plan',
  'carte.locate.aria': 'Centrer sur ma position',
  'carte.legend.standPill': 'Stand {standId}',
  'carte.lastCheckin.label': 'Dernier passage',
  'carte.lastCheckin.standName': 'Stand {standId}',
  'carte.lastCheckin.meta': 'Il y a quelques minutes',

  // ───── S26 InstitutionnelAdmin (refonte 100 % Stitch) ────────────────
  // Cohérent §15.5 : français de France strict, glossaire anti-anglicisme.
  // Sources Stitch :
  //   - propos_tne_2026/ (À propos)
  //   - contact_tne_2026/ (Contact)
  //   - aide_installation_ios_tne_2026/ (Aide installation iOS)
  //   - mentions_l_gales_et_rgpd/ (Mentions / RGPD / Accessibilité)
  //   - connexion_administration/ (Login admin + 2FA)
  //   - gestion_des_formulaires/ (Form builder admin)
  //   - notifications_push/ (Push composer admin)
  // FIX 2026-05-07 (S26).

  // ── À propos — partenaires (repli i18n) ─────────────────────────────
  // LOT 5 : la page /a-propos est 100 % éditable au back-office. Sections
  // SUPPRIMÉES ici (résidus TNE Vosges, hors sujet sur NOLAS) : équipe à
  // 3 pôles (about.team.*, about.toc.team), chip « Organisation », bandeau
  // crédits de conception (about.credits.banner.*), cartes partenaires
  // France 2030 / Banque des Territoires / CD88. Ne restent que le libellé de
  // sommaire et le titre de section (repli), la section « Partenaires » étant
  // désormais un simple paragraphe éditable (content.about.partners.intro).
  'about.toc.partners': 'Partenaires institutionnels',
  'about.partners.title': 'Nos partenaires institutionnels',
  // Repli i18n neutre (sans identité en dur) de l'intro « Partenaires ». Le
  // contenu réel (liste des partenaires NOLAS) est éditable au back-office.
  'about.partners.intro':
    'Le salon est rendu possible par l’engagement de ses partenaires.',

  // ── Contact — back button + intro coordonnées ──────────────────────
  'contact.back.aria': 'Revenir à la page précédente',
  'contact.coordinates.intro':
    'Pour toute urgence ou si vous préférez utiliser votre propre client de messagerie, vous pouvez nous écrire directement.',

  // ── Aide installation iOS — pas-à-pas + tips Stitch ────────────────
  'installHelpIos.title': 'Aide à l’installation iOS',
  'installHelpIos.intro':
    'Installez l’application NOLAS 2026 sur l’écran d’accueil de votre iPhone ou iPad pour un accès rapide, même hors-ligne. Une expérience fluide et immersive au cœur de l’éducation numérique.',
  'installHelpIos.step1.title': 'Ouvrez le menu Partager',
  'installHelpIos.step1.description':
    'Dans votre navigateur Safari, appuyez sur l’icône de partage située dans la barre de navigation en bas de l’écran.',
  'installHelpIos.step2.title': 'Choisissez « Sur l’écran d’accueil »',
  'installHelpIos.step2.description':
    'Faites défiler le menu d’options vers le bas jusqu’à trouver « Sur l’écran d’accueil » et appuyez dessus.',
  'installHelpIos.step2.image.alt':
    'Capture iOS — option Sur l’écran d’accueil dans le menu de partage.',
  'installHelpIos.step3.title': 'Validez avec « Ajouter »',
  'installHelpIos.step3.description':
    'Vérifiez les informations puis appuyez sur « Ajouter » en haut à droite de l’écran pour terminer l’installation.',
  'installHelpIos.step3.image.alt':
    'Capture iOS — confirmation Ajouter en haut à droite.',
  'installHelpIos.compatibility.title': 'Note de compatibilité',
  'installHelpIos.compatibility.body':
    'L’installation sur l’écran d’accueil iOS requiert l’utilisation du navigateur Safari. Les navigateurs tiers (Chrome, Firefox) sur iOS ne prennent pas en charge cette fonctionnalité nativement, en raison des restrictions du système.',
  // FIX 2026-05-22 (demande Mika) — clés `installHelpIos.tips.*` retirées
  // (section « Bon à savoir » supprimée — mentionnait App Store / PWA
  // explicitement, contraire à la consigne globale de présenter
  // l'application comme une app classique).

  // ── Login admin — brand footer institutionnel Stitch ───────────────
  'admin.login.brandFooter.line1':
    'Interface d’administration hautement sécurisée.',
  'admin.login.brandFooter.line2':
    'Propulsée par le salon NOLAS 2026.',
  // FIX LOT 1 (2026-07-23) — mention de l'ancien éditeur retirée (organisateur = eln),
  // auteur Mikaël FOURNY conservé. Cohérent avec footer.author +
  // footer.copyright sur le reste de l'app.
  'admin.login.brandFooter.author':
    'Conception : Mikaël FOURNY',

  // ───── S26 Médias — refonte 100 % Stitch (36 clés) ─────────────────
  // FIX 2026-05-07 (S26) — Conformité 100 % Stitch sur les pages
  // MediasPage / WebradioPage / WebtvPage. Sources Stitch :
  //   - app/Stitch Google/m_dias_accueil/code.html
  //   - app/Stitch Google/webradio_tne/code.html
  //   - app/Stitch Google/webtv_tne/code.html
  // Wording strictement français de France — cf. CLAUDE.md §15.5 +
  // glossaire anti-anglicisme.
  // -------------------------------------------------------------------

  // — MediasPage — nouveaux badges hero des 3 tuiles —
  'medias.tile.videos.new': 'Nouveauté',
  'medias.tile.webtv.upcoming': 'Prochaine session à 14 h 30',

  // — WebradioPage — lecteur sticky enrichi —
  'webradio.player.guest': 'Invitée : {host}',
  'webradio.player.playButton': 'Lire la Webradio {eventName}',
  'webradio.player.progressAria': 'Progression du flux Webradio',
  'webradio.player.volume': 'Régler le volume',
  'webradio.player.settings': 'Réglages du lecteur',

  // — WebradioPage — chat live « Posez vos questions » —
  'webradio.chat.aria': 'Chat en direct des questions à l’antenne',
  'webradio.chat.title': 'Posez vos questions',
  'webradio.chat.participants':
    '{count, plural, one {# participant} other {# participants}}',
  'webradio.chat.input.label': 'Votre question pour l’antenne',
  'webradio.chat.input.placeholder': 'Écrivez votre message…',
  'webradio.chat.send': 'Envoyer ma question',
  'webradio.chat.sample.1.author': 'Marie L.',
  'webradio.chat.sample.1.body':
    'Comment le dispositif TNE accompagne-t-il les écoles rurales ?',
  'webradio.chat.sample.2.author': 'Lucas M.',
  'webradio.chat.sample.2.body':
    'L’intelligence artificielle sera-t-elle intégrée aux programmes dès 2026 ?',
  'webradio.chat.sample.3.author': 'Sophie D.',
  'webradio.chat.sample.3.body':
    'Merci pour ces précisions sur le financement !',

  // — WebradioPage — replays cards horizontales —
  'webradio.replay.category.fallback': 'IA & Éducation',

  // — WebradioPage — timeline programme aside droite —
  'webradio.timeline.now': 'En cours',
  'webradio.programme.more': 'Voir tout le programme',

  // — WebradioPage — card gradient « Participez ! » —
  'webradio.participe.title': 'Participez !',
  'webradio.participe.body':
    'Envoyez vos témoignages vocaux par messagerie et passez à l’antenne demain matin.',
  'webradio.participe.cta': 'Déposer un témoignage audio',

  // — WebtvPage — hero player + session info —
  'webtv.badge.live': 'Direct',
  'webtv.session.chip.fallback': 'IA & Inclusion',
  'webtv.session.share': 'Partager',

  // — WebtvPage — chips catégories sur cards (axes thématiques courts) —
  'webtv.session.axis.citoyennete': 'Citoyenneté',
  'webtv.session.axis.ia': 'IA',
  'webtv.session.axis.usages': 'Usages',

  // — WebtvPage — chips filtres courts (Stitch flex-wrap gap-sm) —
  'webtv.filter.ia.short': 'IA',
  'webtv.filter.citoyennete.short': 'Citoyenneté',
  'webtv.filter.usages.short': 'Usages numériques',

  // — WebtvPage — meta footer cards + lien charger plus —
  'webtv.card.views':
    '{count, plural, =0 {— vues} one {# vue} other {# vues}}',
  'webtv.loadMore': 'Charger plus de sessions',

  // ───── S26b OrphansStitch — DashboardLive / Groups / Programs / Press ──
  // FIX 2026-05-07 (S26b) — clés i18n des 4 pages issues des designs
  // Stitch orphelins. Wording français de France strict.

  // — Layout admin partagé (sidebar / top bar / bottom nav) —
  'adminLayout.aria.sidebar': 'Navigation latérale du backoffice',
  'adminLayout.aria.bottomNav': 'Navigation rapide',
  'adminLayout.aria.openMenu': 'Ouvrir le menu',
  'adminLayout.aria.profile': 'Mon profil administrateur',
  'adminLayout.sidebar.profileTitle': 'Espace Admin',
  'adminLayout.sidebar.profileSubtitle': 'Gestion territoriale',
  'adminLayout.sidebar.dashboard': 'Tableau de bord',
  'adminLayout.sidebar.forms': 'Formulaires',
  'adminLayout.sidebar.groups': 'Groupes',
  'adminLayout.sidebar.notifications': 'Notifications',
  'adminLayout.bottomNav.dashboard': 'Tableau de bord',
  'adminLayout.bottomNav.forms': 'Formulaires',
  'adminLayout.bottomNav.groups': 'Groupes',
  'adminLayout.bottomNav.alert': 'Alerte',

  // ───── Chantier A (2026-05-17) — Coquille backoffice unifiée ─────────
  // AdminShell : sidebar groupée + top-bar. Wording français de France.
  'adminShell.brand': '{eventName} — Backoffice',
  'adminShell.aria.sidebar': 'Navigation du backoffice',
  'adminShell.openMenu': 'Ouvrir le menu',
  'adminShell.closeMenu': 'Fermer le menu',
  'adminShell.profileTitle': 'Espace administration',
  'adminShell.profileSubtitle': 'eln',
  'adminShell.viewSite': 'Voir le site',
  'adminShell.logout': 'Déconnexion',
  'adminShell.logout.done': 'Vous êtes déconnecté.',
  'adminShell.role.super_admin': 'Super-administrateur',
  'adminShell.role.admin': 'Administrateur',
  'adminShell.role.editeur': 'Éditeur',
  'adminShell.role.auteur': 'Auteur',
  'adminShell.role.moderateur': 'Modérateur',
  // FIX 2026-05-20 (Mika Message 8) — cohérence avec usersAdmin.role.staff
  'adminShell.role.staff': 'Organisateur',
  // Groupes de la sidebar
  // 2026-07-07 (Mika — cockpit « Étape du salon »)
  'adminNav.group.pilotage': 'Pilotage',
  'adminNav.pilotage': 'Étape du salon',
  // ─── Cockpit « Étape du salon » (2026-07-07) ───────────────────────
  'pilotage.title': 'Étape du salon',
  'pilotage.intro':
    "Pilotez d'un seul endroit ce que vous ouvrez et ce que vous fermez. Choisissez une étape prédéfinie, ou ajustez chaque verrou individuellement. Les réglages sont appliqués immédiatement pour tout le monde.",
  'pilotage.readonly':
    "Lecture seule : seul un super-administrateur peut changer l'étape ou les verrous du salon.",
  'pilotage.banner.kicker': 'Vous êtes à l’étape',
  'pilotage.stages.aria': 'Étapes prédéfinies du salon',
  'pilotage.stage.current': 'Étape actuelle',
  'pilotage.stage.custom.title': 'Configuration personnalisée',
  'pilotage.stage.preparation.title': 'Préparation',
  'pilotage.stage.preparation.desc':
    'Rien de public. Seuls les administrateurs voient le salon (aperçu).',
  'pilotage.stage.exhibitors_open.title': 'Espace exposants ouvert',
  'pilotage.stage.exhibitors_open.desc':
    'Public fermé. Les exposants (et animateurs {lexWorkshopOf}) peuvent s’inscrire et modifier leurs fiches.',
  'pilotage.stage.public_open.title': 'Ouvert au public',
  'pilotage.stage.public_open.desc':
    'Tout est ouvert : visiteurs, inscriptions, scan, carte en direct (jour J).',
  'pilotage.stage.after.title': 'Après l’événement',
  'pilotage.stage.after.desc':
    'Rétrospective et rediffusions. Les inscriptions sont fermées.',
  'pilotage.locks.aria': 'Verrous individuels',
  'pilotage.locks.title': 'Verrous individuels',
  'pilotage.lock.publicOpen.label': 'Salon ouvert au public',
  'pilotage.lock.publicOpen.hint':
    'Activé : tous les visiteurs accèdent au site. Désactivé : réservé aux exposants (public fermé).',
  'pilotage.lock.exhibitorEdit.label': 'Édition des fiches exposants',
  'pilotage.lock.exhibitorEdit.hint':
    'Activé : les exposants peuvent modifier leur fiche et leurs {lexWorkshopPlural}. Désactivé : édition bloquée.',
  'pilotage.lock.exhibitorSignup.label': 'Inscription des exposants',
  'pilotage.lock.exhibitorSignup.hint':
    'Activé : de nouveaux exposants peuvent créer leur compte et leur stand.',
  'pilotage.lock.phase.label': 'Étape du programme',
  'pilotage.lock.phase.hint':
    'Force l’affichage « avant / pendant / après » l’événement, ou laisse le calcul automatique par la date.',
  'pilotage.phase.auto': 'Automatique (selon la date)',
  'pilotage.phase.before': 'Avant l’événement',
  'pilotage.phase.during': 'Pendant l’événement',
  'pilotage.phase.after': 'Après l’événement',
  'pilotage.recap.aria': 'Qui voit quoi selon l’état actuel',
  'pilotage.recap.title': 'Qui voit quoi, en l’état actuel',
  'pilotage.recap.feature': 'Fonction',
  'pilotage.recap.role.visitor': 'Visiteur',
  'pilotage.recap.role.exhibitor': 'Exposant',
  'pilotage.recap.role.admin': 'Admin',
  'pilotage.recap.feat.see_site': 'Voir le site',
  'pilotage.recap.feat.signup': 'S’inscrire',
  'pilotage.recap.feat.edit': 'Éditer sa fiche',
  'pilotage.recap.feat.scan': 'Scanner',
  'pilotage.recap.yes': 'oui',
  'pilotage.recap.no': 'non',
  'pilotage.confirm.stage': 'Appliquer l’étape « {stage} » à tout le salon ?',
  'pilotage.toast.stageApplied': 'Étape appliquée.',
  'pilotage.toast.lockApplied': 'Réglage enregistré.',
  'pilotage.toast.error': 'Action impossible. Réessayez.',
  'pilotage.error.load': 'Impossible de charger le pilotage du salon.',
  // ─── Contrôle d'accès admin par section (2026-07-07, V76) ──────────
  'adminNav.access': 'Accès & permissions',
  'adminAccess.title': 'Accès & permissions',
  'adminAccess.intro':
    "Définissez à quoi chaque rôle a accès (profil par défaut), et surchargez au cas par cas pour un compte précis. Le super-administrateur garde toujours accès à tout.",
  'adminAccess.tab.role': 'Par rôle',
  'adminAccess.tab.user': 'Par compte',
  'adminAccess.role.select': 'Rôle à configurer',
  'adminAccess.user.select': 'Compte à configurer',
  'adminAccess.user.placeholder': '— Choisir un compte —',
  'adminAccess.user.pick': 'Choisissez un compte pour définir ses accès.',
  'adminAccess.save': 'Enregistrer',
  'adminAccess.opt.default': 'Défaut',
  'adminAccess.opt.inherit': 'Hériter du rôle',
  'adminAccess.opt.allow': 'Autorisé',
  'adminAccess.opt.deny': 'Interdit',
  'adminAccess.hint.defaultAllow': 'Défaut du rôle : autorisé',
  'adminAccess.hint.defaultDeny': 'Défaut du rôle : interdit',
  'adminAccess.hint.inheritAllow': 'Hérité du rôle : autorisé',
  // 2026-08-26 — l'ecran dit desormais D'OU vient la valeur effective.
  'adminAccess.hint.fromRole': 'Hérité du profil « {role} » : {etat}',
  'adminAccess.hint.fromCode': 'Défaut du code pour « {role} » : {etat}',
  'adminAccess.state.allowed': 'autorisé',
  'adminAccess.state.denied': 'interdit',
  'adminAccess.hint.inheritDeny': 'Hérité du rôle : interdit',
  'adminAccess.toast.saved': 'Accès enregistrés.',
  'adminAccess.toast.error': 'Enregistrement impossible. Réessayez.',
  'adminAccess.denied.title': 'Accès restreint',
  'adminAccess.denied.body':
    "Votre compte n'a pas accès à cette section. Rapprochez-vous d'un super-administrateur si vous pensez que c'est une erreur.",
  'adminNav.group.dashboard': 'Tableau de bord',
  'adminNav.group.workshops': '{lexWorkshopPluralCap} & programmes',
  'adminNav.group.content': 'Contenu & médias',
  'adminNav.group.users': 'Utilisateurs & rôles',
  'adminNav.group.forms': 'Formulaires & inscriptions',
  'adminNav.group.communication': 'Communication',
  'adminNav.group.config': 'Configuration',
  'adminNav.group.system': 'Système',
  // Lot 2FA (2026-08-03, vague 2) — dernier groupe de la sidebar : ce qu'on
  // y règle ne concerne QUE le compte connecté, pas l'événement.
  'adminNav.group.account': 'Mon compte',
  // Entrées de la sidebar
  'adminNav.dashboard': 'Tableau de bord',
  'adminNav.emploiAnalytics': 'Analytique emploi',
  'adminNav.workshops': '{lexWorkshopPluralCap}',
  'adminNav.workshopAttendance': 'Présence {lexWorkshopPlural}',
  'adminNav.signups': 'Inscriptions {lexWorkshopPlural}',
  'adminNav.programs': 'Programmes',
  'adminNav.pages': 'Pages éditoriales',
  'adminNav.medias': 'Médias',
  'adminNav.mediaStreams': 'Diffusion en direct',
  'adminNav.partners': 'Partenaires',
  'adminNav.tags': 'Étiquettes',
  'adminNav.translations': 'Traductions',
  'adminNav.navigation': 'Menu de navigation',
  'adminNav.usersAdmin': 'Comptes administrateurs',
  'adminNav.invitations': 'Invitations',
  'adminNav.exhibitors': 'Exposants',
  'adminNav.exhibitorsImport': 'Importer (CSV)',
  'adminNav.groups': 'Groupes',
  'adminNav.userJourney': 'Parcours visiteurs',
  'adminNav.roles': 'Rôles d’inscription',
  'adminNav.rolePrograms': 'Programme commun par rôle',
  'adminNav.formBuilder': 'Constructeur de formulaire',
  // Lot CIRFA (2026-08-03) — entrées de menu des trois fonctions socle.
  'adminNav.formStats': 'Statistiques du formulaire',
  'adminNav.group.groupsWelcome': 'Accueil des groupes',
  'adminNav.groupRoster': 'Listes nominatives',
  'adminNav.transport': 'Transport de groupes',
  // FIX 2026-05-18 — page /admin/role-programs.
  'rolePrograms.title': 'Programme commun par rôle',
  'rolePrograms.lead':
    'Composez le programme commun de chaque rôle : cochez {lexWorkshopDefPlural} à proposer automatiquement. Chaque visiteur retrouvera ce programme dans « Mon programme », en plus de ses propres inscriptions.',
  'rolePrograms.save': 'Enregistrer ce rôle',
  'rolePrograms.saved': 'Programme du rôle mis à jour.',
  'rolePrograms.saveError': 'Échec de l’enregistrement. Réessayez.',
  'rolePrograms.loading': 'Chargement…',
  'rolePrograms.error':
    'Impossible de charger les programmes. Vérifiez votre session admin.',
  'rolePrograms.retry': 'Réessayer',
  'rolePrograms.noWorkshops':
    '{lexWorkshopNoneCap} publié. Créez d’abord {lexWorkshopOfPlural} dans « {lexWorkshopPluralCap} ».',
  'rolePrograms.count':
    '{count, plural, =0 {{lexWorkshopNone}} one {# {lexWorkshopBare}} other {# {lexWorkshopPlural}}}',
  // FIX 2026-05-18 — éditeur de texte riche (programme libre) par rôle.
  'rolePrograms.editor.title': 'Programme (texte libre)',
  'rolePrograms.editor.help':
    'Rédigez librement le programme de la journée pour ce rôle (titres, listes, gras…). Ce n’est pas forcément la liste {lexWorkshopOfPlural}. Le programme officiel est pré-rempli par défaut : modifiez-le à votre convenance.',
  'rolePrograms.workshops.title': '{lexWorkshopPluralCap} associé{lexWorkshopAgrPlural} (optionnel)',
  'rolePrograms.workshops.help':
    'En complément du texte ci-dessus, cochez {lexWorkshopDefPlural} à proposer automatiquement à ce rôle dans « Mon programme ».',
  // FIX 2026-05-18 — page /admin/roles (libellé/description/ouverture).
  'rolesAdmin.title': 'Rôles d’inscription',
  'rolesAdmin.lead':
    'Personnalisez le libellé et la description de chaque rôle affiché sur l’écran d’inscription, et ouvrez ou fermez les inscriptions rôle par rôle. Laissez un champ vide pour conserver le texte par défaut.',
  'rolesAdmin.save': 'Enregistrer',
  'rolesAdmin.saved': 'Rôles mis à jour.',
  'rolesAdmin.saveError': 'Échec de l’enregistrement. Réessayez.',
  'rolesAdmin.loading': 'Chargement…',
  'rolesAdmin.error': 'Impossible de charger les rôles. Vérifiez votre session admin.',
  'rolesAdmin.retry': 'Réessayer',
  'rolesAdmin.open': 'Inscription ouverte',
  'rolesAdmin.closed': 'Inscription fermée',
  'rolesAdmin.field.label': 'Libellé du rôle',
  'rolesAdmin.field.desc': 'Description',
  'rolesAdmin.field.default': 'Par défaut : {value}',
  'rolesAdmin.editForm': 'Éditer le formulaire d’inscription de ce rôle →',
  'adminNav.push': 'Notifications push',
  'adminNav.emailComposer': 'E-mails de masse',
  'adminNav.exhibitorReports': 'Bilans exposants',
  'adminNav.settings': 'Paramètres du site',
  'adminNav.featureFlags': 'Activation des modules',
  'adminNav.aiConfig': "Configuration de l'IA",
  'adminNav.eventConfig': "Identité de l'événement",
  'adminNav.activityLog': 'Journal d’activité',
  // FIX 2026-05-20 (journal admin erreurs inscription §5/§24).
  'adminNav.registrationErrors': 'Erreurs d’inscription',
  'adminNav.maintenance': 'Mode maintenance',
  'adminNav.audit': "Journal d'audit",
  'adminNav.deployTokens': 'Jetons de déploiement',
  'adminNav.osnumStatus': 'État OSNum',
  'adminNav.webhooks': 'Webhooks',
  'adminNav.mergeVisitors': 'Fusionner doublons',
  // Lot 2FA (2026-08-03, vague 2) — les deux entrées qui manquaient : sans
  // elles, le parcours guidé, la bascule de méthode et la réinitialisation
  // d'un compte n'étaient joignables qu'en tapant l'URL à la main.
  'adminNav.securite': 'Ma sécurité',
  'adminNav.twoFactorAdmin': 'Double authentification',

  // ───── Chantier B (2026-05-17) — FeatureGate (section coupée) ────────
  'featureGate.title': 'Section indisponible',
  'featureGate.titleNamed': '« {name} » est indisponible',
  'featureGate.body':
    "Cette section a été désactivée temporairement par l'organisation. Merci de réessayer plus tard.",
  'featureGate.backHome': "Retour à l'accueil",

  // — DashboardLivePage —
  'dashboardLive.title': "Vue d'ensemble en temps réel",
  'dashboardLive.subtitle':
    "Suivi de l'activité du salon NOLAS 2026",
  'dashboardLive.live': 'En direct',
  // FIX 2026-05-16 (audit 100 %) — clé référencée par DashboardLivePage
  // ligne 470 (badge mode dégradé) mais jamais définie + sans
  // defaultMessage → affichait le littéral « dashboardLive.fallback ».
  // Pré-existant S26b. Corrigé pendant le contrôle complet de l'app.
  'dashboardLive.fallback': 'Données différées',
  'dashboardLive.kpi.visitorsLive.title': 'Visiteurs présents',
  'dashboardLive.kpi.visitorsLive.subtitle': 'Actuellement sur site',
  'dashboardLive.kpi.visitorsLive.unit': 'personnes',
  'dashboardLive.kpi.visitorsLive.barAria':
    'Taux de remplissage estimé du salon',
  'dashboardLive.kpi.scans.title': 'Scans cumulés',
  'dashboardLive.kpi.scans.subtitle': 'Badges et {lexWorkshopPlural}',
  'dashboardLive.kpi.scans.delta': '+{percent} % vs hier',
  'dashboardLive.alerts.title': '{lexWorkshopPluralCap} saturé{lexWorkshopAgrPlural}',
  'dashboardLive.chart.title': 'Flux de fréquentation',
  'dashboardLive.chart.serieLabel': 'Visiteurs présents',
  'dashboardLive.chart.filter.today': "Aujourd'hui",
  'dashboardLive.chart.filter.week': 'Semaine',
  'dashboardLive.rooms.title': 'Capacité des salles',
  'dashboardLive.rooms.seeAll': 'Voir toutes les salles',

  // — GroupsAdminPage —
  'groupsAdmin.title': 'Gestion des groupes',
  'groupsAdmin.subtitle':
    "Veuillez administrer ci-dessous les cohortes de votre territoire. Vous pouvez structurer les accès, rechercher des profils spécifiques ou intégrer de nouveaux membres en masse.",
  'groupsAdmin.btn.importCsv': 'Importer un CSV',
  'groupsAdmin.btn.create': 'Créer un groupe',
  'groupsAdmin.search.label': "Recherche d'un groupe",
  'groupsAdmin.search.placeholder':
    'Recherchez un groupe, un rôle ou un identifiant…',
  'groupsAdmin.filters.label': 'Filtres rapides :',
  'groupsAdmin.filter.active': 'Actifs',
  'groupsAdmin.filter.pending': 'En attente',
  'groupsAdmin.filter.archived': 'Archivés',
  'groupsAdmin.access.restricted': 'Accès restreint',
  'groupsAdmin.access.standard': 'Accès standard',
  'groupsAdmin.access.limited': 'Accès limité',
  'groupsAdmin.group.elus-territoriaux.title': 'Élus territoriaux',
  'groupsAdmin.group.elus-territoriaux.description':
    'Groupe décisionnel incluant les maires et délégués communautaires en charge du déploiement des infrastructures.',
  'groupsAdmin.group.formateurs-erun.title': 'Formateurs ERUN',
  'groupsAdmin.group.formateurs-erun.description':
    "Enseignants Référents pour les Usages du Numérique. Responsables de l'accompagnement pédagogique local.",
  'groupsAdmin.group.eleves-ambassadeurs.title': 'Élèves ambassadeurs',
  'groupsAdmin.group.eleves-ambassadeurs.description':
    "Cohorte d'élèves volontaires participant aux tests de nouveaux équipements et aux retours d'usage.",
  'groupsAdmin.group.members':
    '{count, plural, =0 {Aucun membre} one {1 membre} other {{count, number} membres}}',
  // FIX 2026-05-18 (audit Cowork Bug #10) : libellé cohérent avec
  // « Gestion des groupes » (était « Nouveau profil », confus).
  'groupsAdmin.empty.title': 'Aucun groupe créé pour le moment',
  // Carte dashed de création (clé dédiée — ne partage plus la clé
  // de l'état vide, cf. audit Bug #10 « doublon »).
  'groupsAdmin.card.newGroup': 'Nouveau groupe',
  'groupsAdmin.empty.description':
    "Cliquez ici pour paramétrer un nouveau groupe d'utilisateurs sur le territoire.",
  'groupsAdmin.partners.title': 'Partenaires institutionnels',
  'groupsAdmin.aria.moreActions': 'Autres actions sur le groupe',
  'groupsAdmin.aria.edit': 'Modifier les droits',
  'groupsAdmin.aria.export': 'Exporter la liste',
  'groupsAdmin.aria.delete': 'Supprimer le groupe',

  // — ProgramsAdminPage —
  'programsAdmin.title': 'Gestion des programmes',
  'programsAdmin.subtitle':
    'Veuillez administrer {lexWorkshopDefPlural}, horaires et intervenants.',
  'programsAdmin.btn.add': 'Ajouter {lexWorkshopIndef}',
  'programsAdmin.btn.filter': 'Filtrer',
  'programsAdmin.btn.editInline': 'Modifier',
  'programsAdmin.btn.defineInline': 'Définir',
  'programsAdmin.filters.search': 'Recherche',
  'programsAdmin.filters.searchPlaceholder': 'Rechercher {lexWorkshopIndef}…',
  'programsAdmin.filters.theme': 'Thématique',
  'programsAdmin.filters.themeAll': 'Toutes les thématiques',
  'programsAdmin.filters.audience': 'Public ciblé',
  'programsAdmin.filters.audienceAll': 'Tous les publics',
  'programsAdmin.theme.ia': 'Intelligence artificielle',
  'programsAdmin.theme.citoyennete': 'Citoyenneté numérique',
  'programsAdmin.theme.usages': 'Usages pédagogiques',
  'programsAdmin.audience.enseignants': 'Enseignants',
  'programsAdmin.audience.directeurs': "Directeurs d'école",
  'programsAdmin.audience.parents': "Parents d'élèves",
  'programsAdmin.audienceFull.enseignants': 'Enseignants',
  'programsAdmin.audienceFull.directeurs': "Directeurs d'école",
  'programsAdmin.audienceFull.parents': "Parents d'élèves",
  'programsAdmin.audienceFull.enseignants-parents':
    "Enseignants et parents d'élèves",
  'programsAdmin.status.published': 'Publié',
  'programsAdmin.status.draft': 'Brouillon',
  'programsAdmin.status.full': 'Complet',
  'programsAdmin.workshop.toDefine': 'À définir',
  'programsAdmin.workshop.audienceLabel': 'Public : {audience}',
  'programsAdmin.workshop.registered':
    'Inscrits : {registered, number}/{capacity, number}',
  'programsAdmin.workshop.intro-ia-classe.title':
    "Introduction à l'IA en classe",
  'programsAdmin.workshop.intro-ia-classe.description':
    "Découvrez comment intégrer les outils d'intelligence artificielle pour personnaliser l'apprentissage.",
  'programsAdmin.workshop.cybersecurite-ecoles.title':
    'Cybersécurité pour les écoles',
  'programsAdmin.workshop.cybersecurite-ecoles.description':
    '{lexWorkshopCap} pratique sur la sécurisation des données scolaires et la sensibilisation des élèves.',
  'programsAdmin.workshop.creation-podcasts.title':
    'Création de podcasts éducatifs',
  'programsAdmin.workshop.creation-podcasts.description':
    "Formation pratique à l'enregistrement et au montage audio pour des projets de classe.",
  'programsAdmin.aria.edit': "Éditer {lexWorkshopDef}",
  'programsAdmin.aria.delete': "Supprimer {lexWorkshopDef}",

  // — PressPage —
  'press.hero.title': 'Espace presse',
  'press.hero.intro':
    "Bienvenue dans l'espace dédié aux professionnels des médias. Vous trouverez ici l'ensemble des ressources nécessaires pour couvrir le salon NOLAS 2026 à Thionville.",
  'press.kit.title': 'Kit presse complet',
  'press.kit.description':
    'Téléchargez le dossier de présentation exhaustif du NOLAS 2026, incluant les chiffres clés, les intervenants majeurs et les enjeux du territoire.',
  'press.kit.download': 'Télécharger (PDF)',
  'press.contact.title': 'Contact relations presse',
  'press.contact.name': 'Service communication NOLAS 2026',
  'press.contact.role': 'Responsable des relations presse',
  'press.contact.cta': 'Nous contacter',
  'press.resources.title': 'Ressources graphiques',
  'press.resources.download': 'Télécharger',
  'press.resources.logos.placeholder': 'Logos officiels',
  'press.resources.logos.title': 'Logos NOLAS 2026',
  'press.resources.logos.formats': 'Formats : EPS, PNG, SVG',
  'press.resources.venue.placeholder': 'Photos du SPOT de Thionville',
  'press.resources.venue.title': 'Photos du lieu',
  'press.resources.venue.formats': 'Haute résolution (300 dpi)',
  'press.resources.posters.placeholder': 'Affiches officielles',
  'press.resources.posters.title': 'Affiches officielles',
  'press.resources.posters.formats': 'Formats : A4, A3 (PDF pour impression)',
  'press.releases.title': 'Communiqués de presse',
  'press.releases.read': 'Consulter',
  'press.releases.archive': 'Voir toutes les archives',
  'press.release.partenaires-majeurs.title':
    "Annonce des partenaires majeurs pour l'édition 2026",
  'press.release.ouverture-inscriptions.title':
    "Ouverture des inscriptions pour les professionnels de l'éducation",

  // ───── PhaseSwitcher (admin — bascule "Mode salon") ──────────────────
  // FIX 2026-05-07 (retour Mika) — Mika veut pouvoir basculer
  // facilement entre les phases avant/pendant/après pour ses tests
  // et démos, sans attendre le 9 octobre 2026.
  'admin.phaseSwitcher.title':  'Mode salon (test / démo)',
  'admin.phaseSwitcher.lead':
    'Basculer la phase affichée par l’application. Utile pour tester ' +
    'le rendu avant l’ouverture, en direct le 9 octobre, ou en mode ' +
    'rétrospective post-événement.',
  'admin.phaseSwitcher.aria': 'Sélecteur de phase événement (test)',
  'admin.phaseSwitcher.auto':   'Automatique (date réelle)',
  'admin.phaseSwitcher.before': 'Avant le salon',
  'admin.phaseSwitcher.during': 'Salon en cours',
  'admin.phaseSwitcher.after':  'Après le salon',
  'admin.phaseSwitcher.warn':
    '⚠ Mode test actif — phase forcée sur « {phase} ». ' +
    'L’app ignore la date réelle de l’événement. Cliquez sur ' +
    '« Automatique » pour revenir au mode normal.',

  // ───── Aperçu intégral admin (2026-06-23) — déverrouille tous les écrans ──
  'admin.phaseReveal.aria': 'Aperçu intégral admin (déverrouiller les écrans)',
  'admin.phaseReveal.title': 'Aperçu intégral (admin)',
  'admin.phaseReveal.lead':
    'Déverrouille TOUS les écrans gardés par la phase (scanner exposant, ' +
    'flux live webtv/webradio, carte temps réel…) en affichant l’expérience ' +
    'comme si le salon était EN COURS — sans changer la date réelle ni le ' +
    'sélecteur ci-dessus. N’affecte que votre navigateur.',
  'admin.phaseReveal.off': 'Normal (respecter la phase)',
  'admin.phaseReveal.on': 'Tout voir (déverrouillé)',
  'admin.phaseReveal.warn':
    '👁️ Aperçu intégral ACTIF — tous les écrans sont déverrouillés et ' +
    'affichés en mode « salon en cours » dans ce navigateur. La pastille ' +
    'de phase indique « · aperçu ». Cliquez sur « Normal » pour revenir.',

  // ───── S27 PhaseAvailabilityToggle (visibilité par phase pour 1 contenu) ──
  'phaseAvailability.legend': 'Disponibilité {entity, select, undefined {de ce contenu} other {de cet « {entity} »}}',
  'phaseAvailability.help':
    'Choisissez ce qui doit être visible AVANT {eventTypeDef}, PENDANT le {eventDate}, ' +
    'et APRÈS. Un statut « masqué » retire le contenu de la vue publique.',
  'phaseAvailability.before.title': 'Avant le salon',
  'phaseAvailability.during.title': 'Pendant le salon',
  'phaseAvailability.after.title':  'Après le salon',
  'phaseAvailability.short.before': 'Avt',
  'phaseAvailability.short.during': 'Pdt',
  'phaseAvailability.short.after':  'Apr',
  'phaseAvailability.status.visible':  'Visible',
  'phaseAvailability.status.readonly': 'Visible (lecture seule)',
  'phaseAvailability.status.hidden':   'Masqué',
  'phaseAvailability.status.live':     'En direct',
  'phaseAvailability.status.archive':  'Archive / Replay',
  'phaseAvailability.autoSwitch':
    'Bascule automatique selon le calendrier (ex : passe en lecture seule 5 min avant {lexWorkshopDef})',
  'phaseAvailability.notes.label': 'Note interne (optionnel)',
  'phaseAvailability.notes.placeholder':
    'Ex : à débloquer après validation de l’organisation le 20 mai…',
  'phaseAvailability.compact.aria':
    'Disponibilité : avant {before}, pendant {during}, après {after}',

  // ───── S28-A WorkshopsAdminPage ─────────────────────────────────────
  'workshopsAdmin.title': '{lexWorkshopPluralCap}',
  'workshopsAdmin.subtitle':
    '{count, plural, =0 {{lexWorkshopNoneCap}} one {# {lexWorkshopBare} au total} other {# {lexWorkshopPlural} au total}}',
  'workshopsAdmin.btn.new': '{lexWorkshopNewCap}',

  // Filtres
  'workshopsAdmin.filters.title': 'Filtres',
  'workshopsAdmin.filter.search': 'Rechercher',
  'workshopsAdmin.filter.search.placeholder': 'Titre, intervenant, identifiant…',
  'workshopsAdmin.filter.date': 'Date',
  'workshopsAdmin.filter.tag': 'Tag',
  'workshopsAdmin.filter.tag.placeholder': 'Ex : IA, citoyenneté…',
  'workshopsAdmin.filter.status': 'Statut',
  'workshopsAdmin.filter.status.all': 'Tous',
  'workshopsAdmin.filter.status.published': 'Publié',
  'workshopsAdmin.filter.status.draft': 'Brouillon',
  'workshopsAdmin.filter.status.full': 'Complet',

  // Liste
  'workshopsAdmin.list.title': 'Liste {lexWorkshopOfPlural}',
  'workshopsAdmin.state.loading': 'Chargement {lexWorkshopOfPlural}…',
  'workshopsAdmin.state.error':
    'Impossible de charger la liste {lexWorkshopOfPlural}. Réessayez dans quelques instants.',
  'workshopsAdmin.state.empty':
    '{lexWorkshopNoneCap} ne correspond aux filtres en cours.',

  // Badges statut
  'workshopsAdmin.status.draft': 'Brouillon',
  'workshopsAdmin.status.published': 'Publié',
  'workshopsAdmin.status.full': 'Complet',

  // Méta
  'workshopsAdmin.meta.speaker': 'Animé par {name}',
  'workshopsAdmin.meta.room': 'Salle : {room}',
  'workshopsAdmin.meta.schedule': 'Du {start} au {end}',
  'workshopsAdmin.capacity.text': 'Inscrits : {registered} / {capacity}',
  'workshopsAdmin.capacity.aria': 'Taux de remplissage {lexWorkshopOfDef}',

  // Actions
  'workshopsAdmin.btn.edit': 'Modifier',
  'workshopsAdmin.btn.archive': 'Archiver',
  'workshopsAdmin.btn.signups':
    '{count, plural, =0 {Inscrits} one {Voir 1 inscrit} other {Voir {count} inscrits}}',

  // Confirmation
  'workshopsAdmin.confirm.archive':
    'Archiver {lexWorkshopDef} « {title} » ? {lexWorkshopPronounCap} ne sera plus visible côté public mais ses inscriptions seront conservées.',

  // Toasts
  'workshopsAdmin.toast.saved': '{lexWorkshopCap} enregistré{lexWorkshopAgr}.',
  'workshopsAdmin.toast.archived': '{lexWorkshopCap} archivé{lexWorkshopAgr}.',
  'workshopsAdmin.toast.queued':
    'Action en attente — sera synchronisée dès le retour en ligne.',

  // Modal édition / création
  'workshopsAdmin.modal.create.title': 'Créer {lexWorkshopIndef}',
  'workshopsAdmin.modal.edit.title': 'Modifier {lexWorkshopDef}',
  'workshopsAdmin.modal.cancel': 'Annuler',
  'workshopsAdmin.modal.save': 'Enregistrer',

  // Champs formulaire
  'workshopsAdmin.form.slug': 'Identifiant (slug)',
  'workshopsAdmin.form.slug.hint':
    'Lettres minuscules, chiffres, tirets uniquement. Non modifiable après création.',
  'workshopsAdmin.form.title': 'Titre',
  'workshopsAdmin.form.description': 'Description',
  'workshopsAdmin.form.speaker': 'Intervenant',
  'workshopsAdmin.form.room': 'Salle',
  'workshopsAdmin.form.starts_at': 'Début',
  'workshopsAdmin.form.ends_at': 'Fin',
  'workshopsAdmin.form.capacity': 'Capacité',
  'workshopsAdmin.form.capacity.hint': '0 = illimitée. Maximum 5000.',
  'workshopsAdmin.form.tags': 'Tags',
  'workshopsAdmin.form.tags.placeholder': 'IA, citoyenneté, école inclusive',
  'workshopsAdmin.form.tags.hint':
    'Séparés par des virgules. Maximum 40 caractères par tag.',
  'workshopsAdmin.form.is_published': 'Publier {lexWorkshopDef} (visible côté public)',

  // Phase availability
  'workshopsAdmin.phase.entityLabel': '{lexWorkshopBare}',

  // Erreurs formulaire (validation client)
  'workshopsAdmin.error.slug_required': 'L’identifiant est obligatoire.',
  'workshopsAdmin.error.slug_format':
    'Identifiant invalide. Lettres minuscules, chiffres et tirets uniquement.',
  'workshopsAdmin.error.title_required': 'Le titre est obligatoire.',
  'workshopsAdmin.error.date_required':
    'Les dates de début et de fin sont obligatoires.',
  'workshopsAdmin.error.date_range':
    'La date de fin doit être postérieure à la date de début.',
  'workshopsAdmin.error.capacity_range':
    'Capacité invalide. Doit être entre 0 (illimitée) et 5000.',

  // Modal liste des inscrits
  'workshopsAdmin.signups.title': 'Inscrits à {lexWorkshopDef}',
  'workshopsAdmin.signups.close': 'Fermer',
  'workshopsAdmin.signups.empty': 'Aucun inscrit pour le moment.',
  'workshopsAdmin.signups.role.visitor': 'Visiteur',
  'workshopsAdmin.signups.role.exhibitor': 'Exposant',
  'workshopsAdmin.signups.role.vip': 'VIP',
  'workshopsAdmin.signups.role.specific_public': 'Public spécifique',
  'workshopsAdmin.signups.role.student': 'Élève',
  'workshopsAdmin.signups.role.child': 'Enfant',
  'workshopsAdmin.signups.role.staff': 'Staff',
  'workshopsAdmin.signups.role.admin': 'Admin',
  'workshopsAdmin.signups.status.confirmed': 'Confirmé',
  'workshopsAdmin.signups.status.waitlist': 'Liste d’attente',
  'workshopsAdmin.signups.status.cancelled': 'Annulé',

  // ───── V22 Workshops kind/world/slots ─────────────────────────────
  // Champs ajoutés (Tableau TNE V2 de Mika) pour piloter le programme
  // depuis le backoffice : type d'élément, Monde thématique, places
  // réservées en amont, créneaux horaires détaillés.
  'workshopsAdmin.field.kind': 'Type d’élément',
  'workshopsAdmin.field.kind.atelier': '{lexWorkshopCap}',
  'workshopsAdmin.field.kind.stand': 'Stand',
  'workshopsAdmin.field.kind.agora': 'Agora',
  'workshopsAdmin.field.kind.webradio': 'Web radio',
  'workshopsAdmin.field.world': 'Monde thématique',
  'workshopsAdmin.field.world.ia': 'Monde IA',
  'workshopsAdmin.field.world.citoyennete': 'Monde Citoyenneté Numérique',
  'workshopsAdmin.field.world.usages': 'Monde des Usages',
  'workshopsAdmin.field.reservedSeats': 'Places déjà réservées en amont',
  'workshopsAdmin.field.reservedSeats.hint':
    'Places occupées par réservation directe (classes, groupes) hors inscription en ligne. Les places libres = Capacité − Réservées − Inscrits en ligne.',
  'workshopsAdmin.field.timeSlots': 'Créneaux horaires',
  'workshopsAdmin.field.timeSlots.empty':
    'Aucun créneau défini. Ajoutez un créneau pour préciser les horaires détaillés.',
  'workshopsAdmin.field.timeSlots.add': '+ Ajouter un créneau',
  'workshopsAdmin.field.timeSlots.remove': 'Supprimer',
  'workshopsAdmin.field.timeSlots.time': 'Créneau (HH:MM-HH:MM)',
  'workshopsAdmin.field.timeSlots.occupied': 'Places occupées',

  // ───── S28-B MediasAdminPage ─────────────────────────────────────
  'mediasAdmin.title': 'Médias',
  'mediasAdmin.subtitle':
    'Webradios, WebTV, vidéos courtes, présentations et albums photo du salon. Les médias contenant des mineurs sans consentement à l’image ne peuvent pas être publiés (RGPD).',
  'mediasAdmin.entityLabel': 'média',
  'mediasAdmin.btn.create': 'Nouveau média',
  'mediasAdmin.btn.edit': 'Modifier',
  'mediasAdmin.btn.delete': 'Supprimer',
  'mediasAdmin.btn.cancel': 'Annuler',
  'mediasAdmin.btn.save': 'Enregistrer',
  'mediasAdmin.btn.createConfirm': 'Créer le média',
  'mediasAdmin.loading': 'Chargement des médias…',
  'mediasAdmin.empty': 'Aucun média pour le moment. Cliquez sur « Nouveau média » pour en créer un.',
  'mediasAdmin.filters.aria': 'Filtres de la liste des médias',
  'mediasAdmin.grid.aria': 'Liste des médias',
  'mediasAdmin.filter.kind': 'Type',
  'mediasAdmin.filter.published': 'Statut',
  'mediasAdmin.filter.partner': 'Partenaire',
  'mediasAdmin.filter.all': 'Tous',
  'mediasAdmin.filter.publishedOnly': 'Publiés uniquement',
  'mediasAdmin.filter.draftOnly': 'Brouillons uniquement',
  'mediasAdmin.modal.titleCreate': 'Créer un média',
  'mediasAdmin.modal.titleEdit': 'Modifier le média',

  'mediasAdmin.kind.webradio_live': 'Webradio (en direct)',
  'mediasAdmin.kind.webradio_replay': 'Webradio (replay)',
  'mediasAdmin.kind.webtv_live': 'WebTV (en direct)',
  'mediasAdmin.kind.webtv_replay': 'WebTV (replay)',
  'mediasAdmin.kind.video_short': 'Vidéo courte',
  'mediasAdmin.kind.presentation_pdf': 'Présentation PDF',
  'mediasAdmin.kind.photo_album': 'Album photo',
  'mediasAdmin.kind.webradio_live.short': 'Webradio',
  'mediasAdmin.kind.webradio_replay.short': 'Replay',
  'mediasAdmin.kind.webtv_live.short': 'WebTV',
  'mediasAdmin.kind.webtv_replay.short': 'Replay',
  'mediasAdmin.kind.video_short.short': 'Vidéo',
  'mediasAdmin.kind.presentation_pdf.short': 'PDF',
  'mediasAdmin.kind.photo_album.short': 'Photos',

  'mediasAdmin.field.kind': 'Type',
  'mediasAdmin.field.title': 'Titre',
  'mediasAdmin.field.description': 'Description',
  'mediasAdmin.field.url': 'URL du média',
  'mediasAdmin.field.thumbnailUrl': 'URL de la vignette',
  'mediasAdmin.field.captionsUrl': 'URL des sous-titres (VTT)',
  'mediasAdmin.field.workshop': '{lexWorkshopCap} lié{lexWorkshopAgr}',
  'mediasAdmin.field.partner': 'Partenaire lié',
  'mediasAdmin.field.speakerName': 'Intervenant',
  'mediasAdmin.field.durationSeconds': 'Durée (secondes)',
  'mediasAdmin.field.recordedAt': 'Enregistré le',
  'mediasAdmin.field.isPublished': 'Publié (visible côté public)',
  'mediasAdmin.field.none': '— Aucun —',

  'mediasAdmin.rgpd.legend': 'Conformité RGPD',
  'mediasAdmin.rgpd.help':
    'Un média contenant des mineurs ne peut être publié qu’avec un consentement à l’image archivé (autorisation parentale signée).',
  'mediasAdmin.rgpd.containsMinors': 'Ce média contient des mineurs identifiables',
  'mediasAdmin.rgpd.consentImage': 'Consentement à l’image archivé pour toutes les personnes filmées',
  'mediasAdmin.rgpd.warning.title': 'Publication impossible :',
  'mediasAdmin.rgpd.warning.body':
    'pour publier ce média qui contient des mineurs, cochez « Consentement à l’image archivé » ou décochez « Publié ».',
  'mediasAdmin.badge.rgpdBlocked': 'RGPD bloqué',

  'mediasAdmin.card.duration': '{seconds, plural, =0 {0 s} one {# s} other {# s}}',
  'mediasAdmin.card.published': 'Publié',
  'mediasAdmin.card.draft': 'Brouillon',

  'mediasAdmin.confirm.delete':
    'Supprimer (dépublier) le média « {title} » ? Cette action peut être annulée en repassant le statut sur « Publié ».',
  'mediasAdmin.toast.created': 'Média créé.',
  'mediasAdmin.toast.updated': 'Média mis à jour.',
  'mediasAdmin.toast.deleted': 'Média supprimé.',
  'mediasAdmin.toast.error': 'Erreur lors de l’opération sur le média.',

  // ───── S28-B PartnersAdminPage ───────────────────────────────────
  'partnersAdmin.title': 'Partenaires',
  'partnersAdmin.subtitle':
    'Liste des partenaires institutionnels, académiques, EdTech, associatifs, médias, financiers et collectivités du salon NOLAS 2026.',
  'partnersAdmin.entityLabel': 'partenaire',
  'partnersAdmin.btn.create': 'Nouveau partenaire',
  'partnersAdmin.btn.edit': 'Modifier',
  'partnersAdmin.btn.delete': 'Supprimer',
  'partnersAdmin.btn.cancel': 'Annuler',
  'partnersAdmin.btn.save': 'Enregistrer',
  'partnersAdmin.btn.createConfirm': 'Créer le partenaire',
  'partnersAdmin.loading': 'Chargement des partenaires…',
  'partnersAdmin.empty': 'Aucun partenaire pour le moment. Cliquez sur « Nouveau partenaire ».',
  'partnersAdmin.filters.aria': 'Filtres de la liste des partenaires',
  'partnersAdmin.filter.category': 'Catégorie',
  'partnersAdmin.filter.level': 'Niveau',
  'partnersAdmin.filter.published': 'Statut',
  'partnersAdmin.filter.all': 'Toutes',
  'partnersAdmin.filter.publishedOnly': 'Publiés uniquement',
  'partnersAdmin.filter.draftOnly': 'Brouillons uniquement',
  'partnersAdmin.modal.titleCreate': 'Créer un partenaire',
  'partnersAdmin.modal.titleEdit': 'Modifier le partenaire',

  'partnersAdmin.category.institutional': 'Institutionnel',
  'partnersAdmin.category.academic': 'Académique',
  'partnersAdmin.category.edtech': 'EdTech',
  'partnersAdmin.category.associative': 'Associatif',
  'partnersAdmin.category.media': 'Média',
  'partnersAdmin.category.financier': 'Financier',
  'partnersAdmin.category.collectivity': 'Collectivité',

  'partnersAdmin.level.platine': 'Platine',
  'partnersAdmin.level.gold': 'Or',
  'partnersAdmin.level.silver': 'Argent',
  'partnersAdmin.level.bronze': 'Bronze',
  'partnersAdmin.level.partner': 'Partenaire',

  'partnersAdmin.field.slug': 'Identifiant (slug)',
  'partnersAdmin.field.slug.helper':
    'Minuscules, chiffres et tirets uniquement. Utilisé dans l’URL publique.',
  'partnersAdmin.field.name': 'Nom officiel',
  'partnersAdmin.field.category': 'Catégorie',
  'partnersAdmin.field.level': 'Niveau de partenariat',
  'partnersAdmin.field.shortDescription': 'Description courte (255 car. max)',
  'partnersAdmin.field.fullDescription': 'Description complète',
  'partnersAdmin.field.logoUrl': 'URL du logo (couleur)',
  'partnersAdmin.field.logoMonoUrl': 'URL du logo (monochrome)',
  'partnersAdmin.field.coverImageUrl': 'URL de l’image de couverture',
  'partnersAdmin.field.websiteUrl': 'Site web',
  'partnersAdmin.field.contactName': 'Nom du contact',
  'partnersAdmin.field.contactEmail': 'Email du contact',
  'partnersAdmin.field.contactPhone': 'Téléphone du contact',
  'partnersAdmin.field.displayOrder': 'Ordre d’affichage (plus petit = plus haut)',
  'partnersAdmin.field.isPublished': 'Publié (visible côté public)',

  'partnersAdmin.card.published': 'Publié',
  'partnersAdmin.card.draft': 'Brouillon',

  'partnersAdmin.confirm.delete':
    'Supprimer (dépublier) le partenaire « {name} » ? Cette action peut être annulée en repassant le statut sur « Publié ».',
  'partnersAdmin.toast.created': 'Partenaire créé.',
  'partnersAdmin.toast.updated': 'Partenaire mis à jour.',
  'partnersAdmin.toast.deleted': 'Partenaire supprimé.',
  'partnersAdmin.toast.error': 'Erreur lors de l’opération sur le partenaire.',

  // ───── S28-C SignupsAdminPage ────────────────────────────────────────
  // Page admin « Inscriptions ateliers » (cancel + promote + export CSV).
  // RGPD : masquage des PII pour les MINEURS si rôle non super_admin/admin.
  'signupsAdmin.title': 'Inscriptions {lexWorkshopPlural}',
  'signupsAdmin.subtitle':
    'Visualisez les inscrits, annulez ou promouvez les personnes en liste d’attente, exportez la liste d’émargement.',

  'signupsAdmin.kpis.aria': 'Statistiques globales des inscriptions',
  'signupsAdmin.kpi.confirmed': 'Inscrits confirmés',
  'signupsAdmin.kpi.waitlist': 'Liste d’attente',
  'signupsAdmin.kpi.cancelled': 'Désinscrits',

  'signupsAdmin.filters.aria': 'Filtres de recherche',
  'signupsAdmin.filter.workshop': '{lexWorkshopCap}',
  'signupsAdmin.filter.workshop.all': '{lexWorkshopAllDefPluralCap}',
  'signupsAdmin.filter.status': 'Statut',
  'signupsAdmin.filter.status.all': 'Tous les statuts',
  'signupsAdmin.filter.search': 'Recherche',
  'signupsAdmin.filter.search.placeholder': 'Prénom ou nom du visiteur…',
  'signupsAdmin.filter.minorsOnly': 'Mineurs uniquement',

  'signupsAdmin.status.confirmed': 'Confirmé',
  'signupsAdmin.status.waitlist': 'Liste d’attente',
  'signupsAdmin.status.cancelled': 'Désinscrit',

  'signupsAdmin.count': '{count, plural, =0 {Aucune inscription} one {# inscription} other {# inscriptions}}',

  'signupsAdmin.loading': 'Chargement des inscriptions…',
  'signupsAdmin.empty':
    'Aucune inscription pour les filtres en cours. Modifiez les critères ou réinitialisez les filtres.',
  'signupsAdmin.error.generic':
    'Impossible de charger les inscriptions. Vérifiez la connexion ou réessayez.',

  'signupsAdmin.col.workshop': '{lexWorkshopCap}',
  'signupsAdmin.col.visitor': 'Visiteur',
  // FIX 2026-05-21 (Bug C — Mika) — colonne « Rôle » dans /admin/signups
  'signupsAdmin.col.role': 'Rôle',
  'signupsAdmin.col.email': 'E-mail',
  'signupsAdmin.col.phone': 'Téléphone',
  'signupsAdmin.col.status': 'Statut',
  'signupsAdmin.col.signedUpAt': 'Inscrit le',
  'signupsAdmin.col.actions': 'Actions',

  'signupsAdmin.badge.minor': 'Mineur',
  'signupsAdmin.badge.adult': 'Majeur',
  'signupsAdmin.badge.masked': 'PII masquées',
  'signupsAdmin.badge.masked.title':
    'Coordonnées masquées (RGPD mineur — visibles uniquement aux rôles super_admin et admin).',

  'signupsAdmin.action.cancel': 'Annuler',
  'signupsAdmin.action.cancel.aria': 'Annuler l’inscription de {name}',
  'signupsAdmin.action.promote': 'Promouvoir',
  'signupsAdmin.action.promote.aria': 'Promouvoir {name} depuis la liste d’attente',
  'signupsAdmin.action.exportCsv': 'Exporter en CSV',
  'signupsAdmin.action.exportCsv.aria': 'Exporter la liste d’émargement au format CSV',

  'signupsAdmin.pagination.aria': 'Pagination des inscriptions',
  'signupsAdmin.pagination.prev': 'Précédent',
  'signupsAdmin.pagination.next': 'Suivant',
  'signupsAdmin.pagination.label': 'Page {page} sur {total}',

  'signupsAdmin.modal.cancel.title': 'Confirmer l’annulation',
  'signupsAdmin.modal.cancel.body':
    'Annuler l’inscription de {name} à {lexWorkshopDef} « {workshop} » ?',
  'signupsAdmin.modal.cancel.hint':
    'Si cette personne était confirmée, la première personne en liste d’attente sera promue automatiquement.',
  'signupsAdmin.modal.cancel.confirm': 'Oui, annuler',
  'signupsAdmin.modal.cancel.dismiss': 'Non, revenir',

  'signupsAdmin.toast.cancel.success': 'Inscription annulée.',
  'signupsAdmin.toast.promote.success': 'Personne promue depuis la liste d’attente.',
  'signupsAdmin.toast.export.needWorkshop':
    'Sélectionnez d’abord {lexWorkshopIndef} pour exporter la liste d’émargement.',
  'signupsAdmin.toast.error.generic':
    'Une erreur est survenue. Veuillez réessayer.',

  // ───── S28-C AuditAdminPage ──────────────────────────────────────────
  // Journal d'audit lecture seule (super_admin uniquement — CDC §10.10).
  'auditAdmin.title': 'Journal d’audit',
  'auditAdmin.subtitle':
    'Historique complet des actions sensibles (connexions, mutations contenu, exports, IA, RGPD). Réservé au super_admin.',

  'auditAdmin.filters.aria': 'Filtres du journal d’audit',
  'auditAdmin.filter.category': 'Catégorie',
  'auditAdmin.filter.action': 'Action exacte',
  'auditAdmin.filter.action.placeholder': 'ex. auth.login.fail',
  'auditAdmin.filter.actor': 'Identifiant admin',
  'auditAdmin.filter.actor.placeholder': 'ID',
  'auditAdmin.filter.since': 'Depuis le',
  'auditAdmin.filter.until': 'Jusqu’au',
  'auditAdmin.filter.search': 'Recherche libre',
  'auditAdmin.filter.search.placeholder': 'Action ou type de cible…',

  // Catégories (whitelist serveur)
  'auditAdmin.category.all': 'Toutes les catégories',
  'auditAdmin.category.auth': 'Authentification',
  'auditAdmin.category.admin': 'Administration',
  'auditAdmin.category.user': 'Utilisateurs',
  'auditAdmin.category.workshop': '{lexWorkshopPluralCap}',
  'auditAdmin.category.push': 'Notifications push',
  'auditAdmin.category.ai': 'Intelligence artificielle',
  'auditAdmin.category.maintenance': 'Maintenance',
  'auditAdmin.category.flags': 'Indicateurs de fonctionnalités',
  'auditAdmin.category.cms': 'Contenus éditoriaux',
  'auditAdmin.category.osnum': 'Intégration OSNum',
  'auditAdmin.category.invitation': 'Invitations admin',
  'auditAdmin.category.export': 'Exports',
  'auditAdmin.category.consent': 'Consentements RGPD',

  'auditAdmin.count': '{count, plural, =0 {Aucune entrée} one {# entrée} other {# entrées}}',

  'auditAdmin.loading': 'Chargement du journal…',
  'auditAdmin.empty': 'Aucune entrée pour les filtres en cours.',
  'auditAdmin.error.generic':
    'Impossible de charger le journal d’audit. Vérifiez votre rôle et la connexion.',

  'auditAdmin.col.when': 'Date',
  'auditAdmin.col.actor': 'Admin',
  'auditAdmin.col.action': 'Action',
  'auditAdmin.col.target': 'Cible',
  'auditAdmin.col.detail': 'Détail',

  'auditAdmin.action.detail': 'Voir',
  'auditAdmin.action.detail.aria': 'Voir le détail de l’entrée d’audit #{id}',
  'auditAdmin.action.exportCsv': 'Exporter en CSV',
  'auditAdmin.action.resetFilters': 'Réinitialiser',

  'auditAdmin.pagination.aria': 'Pagination du journal',
  'auditAdmin.pagination.prev': 'Précédent',
  'auditAdmin.pagination.next': 'Suivant',
  'auditAdmin.pagination.label': 'Page {page} sur {total}',

  'auditAdmin.modal.detail.title': 'Détail de l’entrée d’audit #{id}',
  'auditAdmin.modal.detail.loading': 'Chargement du détail…',
  'auditAdmin.modal.detail.error':
    'Impossible de charger le détail. Vous n’avez peut-être pas les droits suffisants.',
  'auditAdmin.modal.detail.close': 'Fermer',

  'auditAdmin.detail.action': 'Action',
  'auditAdmin.detail.actor': 'Admin',
  'auditAdmin.detail.when': 'Date / heure',
  'auditAdmin.detail.target': 'Cible',
  'auditAdmin.detail.ip': 'IP (anonymisée /24)',
  'auditAdmin.detail.userAgent': 'Agent utilisateur',
  'auditAdmin.detail.payload': 'Détails techniques (JSON)',

  // ───── S28+ BottomNav (refonte nav permanente 2026-05-11 — pattern JANE) ──
  // Libellés COURTS (4-9 caractères) pour tenir dans une icône mobile.
  'bottomNav.home':      'Accueil',
  'bottomNav.programme': 'Programme',
  'bottomNav.map':       'Plan',
  'bottomNav.badge':     'Mon badge',
  'bottomNav.more':      'Plus',

  // ───── S28+ PlusPage (drawer secondaire BottomNav) ──────────────────────
  'plusPage.title': 'Plus',
  'plusPage.lead':
    'Médias en direct, partenaires, presse, rétrospective, contact, profil — ' +
    'tout ce qu’il vous faut au-delà du parcours principal.',
  'plusPage.link.medias': 'Médias',
  'plusPage.link.partners': 'Partenaires',
  'plusPage.link.press': 'Presse',
  'plusPage.link.retrospective': 'Rétrospective',
  'plusPage.link.about': 'À propos',
  'plusPage.link.contact': 'Contact',
  'plusPage.link.questionnaireIa': 'Mon questionnaire IA',
  'plusPage.link.installIos': 'Installer sur iPhone',

  // ───── S30 SettingsAdminPage (édition live tne_site_settings) ───────────
  'settingsAdmin.title': 'Paramètres',
  'settingsAdmin.lead':
    'Configurez 100 % du contenu et des comportements de l\'application : ' +
    'événement, identité, RGPD, notifications, module IA, maintenance.',
  'settingsAdmin.btn.save': 'Enregistrer',
  'settingsAdmin.btn.reset': 'Annuler les modifications',
  'settingsAdmin.dirtyBadge':
    '{count, plural, one {# modification non enregistrée} other {# modifications non enregistrées}}',
  'settingsAdmin.loading': 'Chargement des paramètres…',
  'settingsAdmin.error': 'Impossible de charger les paramètres. Réessayez.',
  'settingsAdmin.toast.saved': 'Paramètres enregistrés avec succès.',
  'settingsAdmin.toast.error': 'Erreur lors de l\'enregistrement. Réessayez.',
  'settingsAdmin.section.event': 'Événement (dates, lieu, capacité)',
  'settingsAdmin.section.site': 'Identité du site',
  // FIX 2026-05-18 — section dédiée au titre/sous-titre de l'écran
  // d'accueil (home.title / home.subtitle) + filet « Divers ».
  'settingsAdmin.section.home': 'Page d’accueil (titre, sous-titre)',
  'settingsAdmin.section.branding': 'Marque (logos du bandeau)',
  // access.mode — menu déroulant clair + explication (au lieu du texte brut).
  'settingsAdmin.accessMode.exhibitor_only': 'Pré-ouverture — exposants seulement (les visiteurs ne peuvent PAS encore s’inscrire)',
  'settingsAdmin.accessMode.open': 'Ouvert à tous — visiteurs ET exposants peuvent s’inscrire',
  'settingsAdmin.accessMode.maintenance': 'Maintenance — site fermé (sauf l’administration)',
  'settingsAdmin.accessMode.help':
    'C’est L’INTERRUPTEUR qui ouvre les inscriptions du salon. Pour ouvrir au public, passez de « Pré-ouverture » à « Ouvert à tous », puis Enregistrez. ⚠️ À ne pas confondre avec le « Mode salon (test/démo) » du tableau de bord, qui ne fait que PRÉVISUALISER les phases (avant / pendant / après le 9 octobre) sans changer qui peut s’inscrire.',
  // Éditeur de logos du bandeau (V73) — upload + ajout/suppression illimités.
  'headerLogos.empty': 'Aucun logo. Cliquez sur « Ajouter un logo » pour en téléverser autant que vous voulez.',
  'headerLogos.add': 'Ajouter un logo',
  'headerLogos.remove': 'Retirer',
  'headerLogos.url.aria': 'Logo {n} — image ou URL',
  'headerLogos.url.placeholder': 'Téléversez une image ou collez une URL',
  'headerLogos.alt.aria': 'Logo {n} — texte alternatif (accessibilité)',
  'headerLogos.alt.placeholder': 'Texte alternatif (ex. « {eventPlace} »)',
  // FIX 2026-05-26 — section dédiée pour `carte.simple_mode` (V50)
  // accessible en 1 clic pour le plan B jour J. Cf. CLAUDE.md §5/§24.
  'settingsAdmin.section.carte': 'Plan / Carte du salon',
  'settingsAdmin.section.other': 'Divers (autres réglages)',
  'settingsAdmin.section.rgpd': 'RGPD et conservation',
  'settingsAdmin.section.notification': 'Notifications',
  'settingsAdmin.section.ai': 'Module Intelligence Artificielle',
  'settingsAdmin.section.maintenance': 'Mode maintenance',
  'settingsAdmin.section.osnum': 'Passerelle OSNum 2.0',
  'settingsAdmin.section.cron': 'Tâches automatisées',
  'settingsAdmin.section.smtp': 'SMTP (verrouillé super-admin)',
  'settingsAdmin.section.vapid': 'Web Push VAPID (verrouillé super-admin)',
  'settingsAdmin.section.db': 'Base de données (verrouillé super-admin)',
  'settingsAdmin.section.deploy': 'Déploiement (verrouillé super-admin)',
  'settingsAdmin.locked.label': 'Verrouillé',
  'settingsAdmin.locked.title':
    'Valeur visible uniquement par le super-administrateur. Modifiable depuis cPanel.',
  'settingsAdmin.public.label': 'Public',
  'settingsAdmin.dirty.label': 'Valeur modifiée non enregistrée',
  'settingsAdmin.bool.on': 'Activé',
  'settingsAdmin.bool.off': 'Désactivé',

  // ───── S30 TagsAdminPage ─────────────────────────────────────────────────
  'tagsAdmin.title': 'Tags thématiques',
  'tagsAdmin.lead': 'Créez et organisez les tags utilisés pour filtrer {lexWorkshopPlural}, partenaires et médias.',
  'tagsAdmin.btn.create': 'Nouveau tag',
  'tagsAdmin.btn.edit': 'Modifier',
  'tagsAdmin.btn.delete': 'Supprimer',
  'tagsAdmin.btn.save': 'Enregistrer',
  'tagsAdmin.btn.cancel': 'Annuler',
  'tagsAdmin.loading': 'Chargement des tags…',
  'tagsAdmin.error': 'Impossible de charger les tags.',
  'tagsAdmin.empty': 'Aucun tag pour le moment. Créez-en un avec le bouton ci-dessus.',
  'tagsAdmin.modal.create.title': 'Créer un tag',
  'tagsAdmin.modal.edit.title': 'Modifier le tag',
  'tagsAdmin.field.slug': 'Identifiant (slug)',
  'tagsAdmin.field.name': 'Nom affiché',
  'tagsAdmin.field.color': 'Couleur',
  'tagsAdmin.phase.entityLabel': 'tag',
  'tagsAdmin.confirm.delete': 'Supprimer le tag « {name} » ? Cette action est irréversible.',
  'tagsAdmin.toast.created': 'Tag créé avec succès.',
  'tagsAdmin.toast.updated': 'Tag mis à jour.',
  'tagsAdmin.toast.deleted': 'Tag supprimé.',
  'tagsAdmin.toast.error': 'Erreur. Réessayez.',

  // ───── M5 / G2 (2026-06-14) — Webhooks (parité admin 2025) ───────────
  'webhooksAdmin.title': 'Webhooks',
  'webhooksAdmin.lead': "Notifiez en temps réel un système tiers (CRM emploi, France Travail, Grist, OSNum…) des événements du salon. Chaque appel HTTP est signé HMAC-SHA256 ; les livraisons sont mises en file, réessayées et journalisées.",
  'webhooksAdmin.btn.create': 'Nouveau webhook',
  'webhooksAdmin.btn.edit': 'Modifier',
  'webhooksAdmin.btn.test': 'Tester',
  'webhooksAdmin.btn.deliveries': 'Livraisons',
  'webhooksAdmin.btn.rotate': 'Régénérer le secret',
  'webhooksAdmin.btn.delete': 'Supprimer',
  'webhooksAdmin.btn.redeliver': 'Renvoyer',
  'webhooksAdmin.btn.save': 'Enregistrer',
  'webhooksAdmin.btn.cancel': 'Annuler',
  'webhooksAdmin.btn.close': 'Fermer',
  'webhooksAdmin.loading': 'Chargement des webhooks…',
  'webhooksAdmin.error': 'Impossible de charger les webhooks.',
  'webhooksAdmin.empty': 'Aucun webhook configuré. Créez-en un avec le bouton ci-dessus.',
  'webhooksAdmin.status.active': 'Actif',
  'webhooksAdmin.status.inactive': 'Inactif',
  'webhooksAdmin.events.all': 'Tous les événements',
  'webhooksAdmin.modal.create.title': 'Créer un webhook',
  'webhooksAdmin.modal.edit.title': 'Modifier le webhook',
  'webhooksAdmin.field.label': 'Libellé',
  'webhooksAdmin.field.url': 'URL de destination (HTTPS)',
  'webhooksAdmin.field.events': 'Événements abonnés',
  'webhooksAdmin.field.events.hint': 'Ne cochez rien pour recevoir TOUS les événements.',
  'webhooksAdmin.field.active': 'Webhook actif',
  'webhooksAdmin.confirm.delete': 'Supprimer le webhook « {label} » et tout son historique de livraison ? Cette action est irréversible.',
  'webhooksAdmin.confirm.rotate': 'Régénérer le secret HMAC ? L’ancien secret cessera immédiatement d’être valide pour le système tiers.',
  'webhooksAdmin.secret.title': 'Secret HMAC',
  'webhooksAdmin.secret.warn': 'Copiez ce secret maintenant : il ne sera plus jamais réaffiché.',
  'webhooksAdmin.secret.help': "Le système tiers doit vérifier l'en-tête X-NOLAS-Signature (sha256=…) calculé sur le corps brut de la requête avec ce secret.",
  'webhooksAdmin.secret.copy': 'Copier',
  'webhooksAdmin.secret.copied': 'Secret copié dans le presse-papiers.',
  'webhooksAdmin.secret.done': "J'ai noté le secret",
  'webhooksAdmin.deliveries.title': 'Livraisons — {label}',
  'webhooksAdmin.deliveries.empty': 'Aucune livraison pour ce webhook.',
  'webhooksAdmin.deliveries.attempts': 'Tentative {n}/{max}',
  'webhooksAdmin.toast.created': 'Webhook créé. Notez bien le secret affiché.',
  'webhooksAdmin.toast.saved': 'Webhook enregistré.',
  'webhooksAdmin.toast.deleted': 'Webhook supprimé.',
  'webhooksAdmin.toast.rotated': 'Secret régénéré.',
  'webhooksAdmin.toast.redelivered': 'Livraison reprogrammée.',
  'webhooksAdmin.toast.testOk': 'Test réussi (HTTP {code}).',
  'webhooksAdmin.toast.testFail': 'Test échoué (HTTP {code}). Voir le journal de livraison.',
  'webhooksAdmin.toast.error': 'Erreur. Réessayez.',

  // ───── M5 / G3 (2026-06-14) — Composeur e-mail admin de masse ─────────
  'emailComposer.title': 'E-mails de masse',
  'emailComposer.lead': "Composez un e-mail et diffusez-le à une audience. L'envoi est mis en file et traité par lots (respect des limites du serveur). Un test peut être envoyé avant la diffusion.",
  'emailComposer.audience.legend': 'Destinataires',
  'emailComposer.audience.all': 'Tous',
  'emailComposer.audience.visitors': 'Candidats',
  'emailComposer.audience.exhibitors': 'Exposants',
  'emailComposer.audience.staff': 'Organisateurs',
  'emailComposer.field.subject': "Sujet de l'e-mail",
  'emailComposer.field.subject.ph': 'Ex. : Votre badge {eventName} est prêt',
  'emailComposer.field.body': 'Message',
  'emailComposer.field.body.ph': 'Rédigez votre message. Les sauts de ligne sont conservés.',
  'emailComposer.field.body.hint': 'Texte simple ou HTML léger. La mise en forme {eventName} (en-tête, pied de page) est ajoutée automatiquement.',
  'emailComposer.test.ph': 'votre.adresse@exemple.fr',
  'emailComposer.btn.test': "Envoyer un test",
  'emailComposer.send.info': "{n, plural, =0 {Aucun destinataire pour cette audience} one {# destinataire} other {# destinataires}}.",
  'emailComposer.btn.send': 'Diffuser à cette audience',
  'emailComposer.confirm.send': "Diffuser cet e-mail à {n, plural, one {# destinataire} other {# destinataires}} ? L'envoi démarrera dans les prochaines minutes.",
  'emailComposer.history.title': 'Campagnes envoyées',
  'emailComposer.history.empty': 'Aucune campagne pour le moment.',
  'emailComposer.loading': 'Chargement…',
  'emailComposer.toast.testOk': 'E-mail de test envoyé à {email}.',
  'emailComposer.toast.testFail': "L'envoi de test a échoué. Vérifiez la configuration SMTP du serveur.",
  'emailComposer.toast.queued': "Campagne créée : {n, plural, one {# destinataire} other {# destinataires}} mis en file.",
  'emailComposer.toast.error': 'Erreur. Réessayez.',
  'emailComposer.err.missingFields':
    'Le sujet, le message et l’adresse de test sont tous les trois obligatoires pour envoyer un test.',
  'emailComposer.err.badEmail':
    'L’adresse e-mail de test n’est pas valide (format attendu : nom@domaine.fr).',
  'emailComposer.err.badSubject': 'Le sujet est vide ou dépasse 255 caractères.',
  'emailComposer.err.emptyBody': 'Le message est vide.',
  'emailComposer.err.badAudience': 'L’audience sélectionnée est invalide.',

  // ───── M5 / G4 (2026-06-14) — Bilan quotidien par exposant ───────────
  'exhibitorReports.title': 'Bilans exposants',
  'exhibitorReports.lead': "Chaque exposant reçoit automatiquement son bilan du jour (candidats rencontrés, entretiens, dépôts de CV…) par e-mail. Vous pouvez prévisualiser et envoyer un test sans attendre l'envoi automatique de 18 h.",
  'exhibitorReports.toggle.label': 'Envoi automatique du bilan quotidien',
  'exhibitorReports.toggle.hint': 'Un e-mail est envoyé à chaque exposant en fin de journée (planifié côté serveur).',
  'exhibitorReports.date.label': 'Date du bilan',
  'exhibitorReports.list.title': "Exposants ({n})",
  'exhibitorReports.list.empty': 'Aucun exposant actif avec une adresse e-mail.',
  'exhibitorReports.loading': 'Chargement…',
  'exhibitorReports.btn.preview': 'Aperçu',
  'exhibitorReports.btn.test': 'Envoyer un test',
  'exhibitorReports.preview.summary': 'Le {date} : {v, plural, =0 {aucun candidat} one {# candidat} other {# candidats}} · {e} entretien(s) · {c} dépôt(s) de CV · {i} information(s).',
  'exhibitorReports.toast.configSaved': 'Réglage enregistré.',
  'exhibitorReports.toast.testOk': "Bilan de test envoyé à l'exposant.",
  'exhibitorReports.toast.testFail': "L'envoi de test a échoué. Vérifiez la configuration SMTP du serveur.",
  'exhibitorReports.toast.error': 'Erreur. Réessayez.',

  // ───── M5 / G5 (2026-06-14) — Badges visiteurs en lot ────────────────
  'visitorBadges.title': 'Badges visiteurs',
  'visitorBadges.lead': "Générez et imprimez des badges en lot (au desk d'accueil). Sélectionnez les visiteurs puis lancez l'impression : chaque badge contient le nom, l'identifiant NOLAS et le QR code à scanner.",
  'visitorBadges.btn.print': 'Imprimer',
  'visitorBadges.btn.search': 'Rechercher',
  'visitorBadges.filters.aria': 'Filtrer les visiteurs',
  'visitorBadges.filter.recent': '20 derniers',
  'visitorBadges.filter.today': "Aujourd'hui",
  'visitorBadges.filter.all': 'Tous',
  'visitorBadges.search.ph': 'Nom ou identifiant (NOLAS-…)',
  'visitorBadges.loading': 'Chargement des badges…',
  'visitorBadges.error': 'Impossible de charger les visiteurs.',
  'visitorBadges.empty': 'Aucun visiteur pour ce filtre.',
  'visitorBadges.count': "{n, plural, =0 {Aucun badge} one {# badge} other {# badges}} à imprimer.",
  'visitorBadges.roleLabel': 'Candidat·e',

  // ═══════════════════════════════════════════════════════════════════════
  // M5 / G7 (Mika 2026-06-14) — Import exposants CSV (mapping flexible).
  // ═══════════════════════════════════════════════════════════════════════
  'exhibitorsImport.title': 'Importer des exposants (CSV)',
  'exhibitorsImport.lead': 'Importez une liste d’exposants depuis un fichier CSV. Un code d’accès EXPO est généré pour chacun, à leur transmettre pour qu’ils complètent leur fiche.',
  'exhibitorsImport.back': 'Retour aux exposants',
  'exhibitorsImport.step1': '1. Choisir le fichier CSV',
  'exhibitorsImport.step2': '2. Associer les colonnes',
  'exhibitorsImport.step2.hint': 'Associez chaque champ à une colonne de votre fichier. La « Structure » (nom de l’entreprise) est obligatoire ; les autres sont facultatifs.',
  'exhibitorsImport.step3': '3. Aperçu et import',
  'exhibitorsImport.fileMeta': '{name} — {n, plural, one {# ligne} other {# lignes}} de données.',
  'exhibitorsImport.reset': 'Changer de fichier',
  'exhibitorsImport.colIgnore': '— Ne pas importer —',
  // Champs canoniques
  'exhibitorsImport.field.structure': 'Structure (entreprise)',
  'exhibitorsImport.field.contact_first_name': 'Prénom du contact',
  'exhibitorsImport.field.contact_last_name': 'Nom du contact',
  'exhibitorsImport.field.email': 'E-mail',
  'exhibitorsImport.field.phone': 'Téléphone',
  'exhibitorsImport.field.function_title': 'Fonction du contact',
  'exhibitorsImport.field.sector': 'Secteur',
  'exhibitorsImport.field.website': 'Site internet',
  'exhibitorsImport.field.booth_number': 'N° de stand',
  'exhibitorsImport.field.description': 'Description',
  'exhibitorsImport.field.address': 'Adresse',
  'exhibitorsImport.field.postal_code': 'Code postal',
  'exhibitorsImport.field.city': 'Ville',
  'exhibitorsImport.field.jobs_url': 'Page des offres d’emploi',
  'exhibitorsImport.field.stand_staff_count': 'Personnes sur le stand',
  'exhibitorsImport.field.spontaneous_application_email': 'E-mail candidature spontanée',
  'exhibitorsImport.field.special_request': 'Demande particulière',
  'exhibitorsImport.field.offer_description': 'Détail des offres',
  'exhibitorsImport.field.job_titles': 'Profils recherchés (séparés par « | »)',
  // Aperçu
  'exhibitorsImport.preview.contact': 'Contact',
  'exhibitorsImport.preview.more': '… et {n, plural, one {# autre ligne} other {# autres lignes}}.',
  'exhibitorsImport.skipDuplicates': 'Ignorer les doublons (même e-mail ou même structure déjà présents).',
  'exhibitorsImport.dryRun': 'Simuler (sans créer)',
  'exhibitorsImport.import': 'Importer',
  // Rapport
  'exhibitorsImport.report.title': 'Import terminé',
  'exhibitorsImport.report.dryTitle': 'Simulation (rien n’a été créé)',
  'exhibitorsImport.report.created': '{n, plural, one {# exposant} other {# exposants}} {n, plural, one {prêt} other {prêts}}',
  'exhibitorsImport.report.duplicates': '{n, plural, one {# doublon ignoré} other {# doublons ignorés}}',
  'exhibitorsImport.report.skipped': '{n, plural, one {# ligne en erreur} other {# lignes en erreur}}',
  'exhibitorsImport.report.dupDetails': 'Voir les {n} doublons ignorés',
  'exhibitorsImport.report.errDetails': 'Voir les {n} lignes en erreur',
  'exhibitorsImport.dupLine': 'Ligne {line} — {structure}',
  'exhibitorsImport.errLine': 'Ligne {line} — {reason}',
  // Codes générés
  'exhibitorsImport.codes.title': 'Codes d’accès générés',
  'exhibitorsImport.codes.warning': 'Notez ou téléchargez ces codes maintenant : ils ne seront plus affichés en clair. Transmettez à chaque exposant son code.',
  'exhibitorsImport.codes.download': '📥 Télécharger les codes (CSV)',
  'exhibitorsImport.codes.code': 'Code d’accès',
  // Toasts / erreurs
  'exhibitorsImport.toast.dryRun': 'Simulation : {n, plural, one {# exposant serait créé} other {# exposants seraient créés}}.',
  'exhibitorsImport.toast.imported': '{n, plural, one {# exposant créé} other {# exposants créés}}.',
  'exhibitorsImport.error.read': 'Impossible de lire le fichier CSV.',
  'exhibitorsImport.error.structureRequired': 'Associez d’abord la colonne « Structure » (obligatoire).',
  'exhibitorsImport.error.empty': 'Aucune ligne à importer.',
  'exhibitorsImport.error.tooMany': 'Trop de lignes (500 maximum par import). Découpez le fichier.',
  'exhibitorsImport.error.server': 'Échec de l’import. Réessayez.',

  // ═══════════════════════════════════════════════════════════════════════
  // M5 / G8 (Mika 2026-06-14) — Fusion de 2 comptes visiteurs doublons.
  // ═══════════════════════════════════════════════════════════════════════
  'mergeVisitors.title': 'Fusionner deux comptes en doublon',
  'mergeVisitors.lead': 'Choisissez le compte à conserver et le doublon à supprimer. Toute la participation du doublon (rencontres, inscriptions, CV, favoris…) est rattachée au compte conservé, puis le doublon est supprimé définitivement.',
  'mergeVisitors.keeper': 'Compte à conserver',
  'mergeVisitors.loser': 'Doublon à supprimer',
  'mergeVisitors.search.ph': 'Nom, e-mail ou structure…',
  'mergeVisitors.search.btn': 'Rechercher',
  'mergeVisitors.searching': 'Recherche…',
  'mergeVisitors.noResult': 'Aucun compte trouvé.',
  'mergeVisitors.change': 'Changer',
  'mergeVisitors.checkins': '{n, plural, one {# rencontre} other {# rencontres}}',
  'mergeVisitors.preview': 'Aperçu de la fusion',
  'mergeVisitors.preview.title': 'Aperçu — ce qui sera déplacé',
  'mergeVisitors.preview.summary': 'Tout sera rattaché à {keeper} ; {loser} sera supprimé. {n, plural, =0 {Aucun élément à déplacer} one {# élément à déplacer} other {# éléments à déplacer}}.',
  'mergeVisitors.preview.nothing': 'Le doublon n’a aucune donnée de participation à déplacer (seul le compte sera supprimé).',
  'mergeVisitors.danger': 'Action IRRÉVERSIBLE : le compte #{id} sera définitivement supprimé après le rattachement.',
  'mergeVisitors.confirm.label': 'Tapez FUSIONNER pour confirmer :',
  'mergeVisitors.merge': 'Fusionner définitivement',
  'mergeVisitors.done': 'Fusion effectuée — {n, plural, one {# élément rattaché} other {# éléments rattachés}}. Doublon supprimé.',
  'mergeVisitors.error': 'Échec de la fusion. Réessayez.',

  // ───── S30 TranslationsAdminPage ─────────────────────────────────────────
  'translationsAdmin.title': 'Libellés et traductions',
  'translationsAdmin.lead':
    'Modifiez en direct les libellés éditoriaux de l\'application (CTA, ' +
    'pages institutionnelles, messages). {total, plural, =0 {Aucune entrée} one {1 entrée} other {# entrées}}.',
  'translationsAdmin.search.label': 'Rechercher',
  'translationsAdmin.search.placeholder': 'Filtrer par clé ou contenu…',
  'translationsAdmin.btn.save': 'Enregistrer toutes les modifications',
  'translationsAdmin.loading': 'Chargement des libellés…',
  'translationsAdmin.error': 'Impossible de charger les libellés.',
  'translationsAdmin.empty':
    'Aucun libellé éditable pour cette recherche. Les libellés techniques ' +
    'restent gérés en code (cf. PLAN-S27-S36 §4.2).',
  'translationsAdmin.dirty':
    '{count, plural, one {# modification} other {# modifications}}',
  'translationsAdmin.dirty.label': 'Valeur modifiée non enregistrée',
  'translationsAdmin.toast.saved': 'Libellés enregistrés.',
  'translationsAdmin.toast.error': 'Erreur d\'enregistrement. Réessayez.',

  // ───── S30 NavigationAdminPage ───────────────────────────────────────────
  'navAdmin.title': 'Navigation principale',
  'navAdmin.lead': 'Organisez les entrées du menu (libellés, URLs, ordre, visibilité par rôle et par phase).',
  'navAdmin.btn.create': 'Nouvelle entrée',
  'navAdmin.btn.edit': 'Modifier',
  'navAdmin.btn.delete': 'Supprimer',
  'navAdmin.btn.save': 'Enregistrer',
  'navAdmin.btn.cancel': 'Annuler',
  'navAdmin.loading': 'Chargement…',
  'navAdmin.error': 'Impossible de charger la navigation.',
  'navAdmin.empty': 'Aucune entrée de menu pour l\'instant.',
  'navAdmin.modal.create.title': 'Créer une entrée de menu',
  'navAdmin.modal.edit.title': 'Modifier l\'entrée',
  'navAdmin.field.label': 'Libellé affiché',
  'navAdmin.field.url': 'URL (chemin ou lien externe)',
  'navAdmin.field.position': 'Position (ordre)',
  'navAdmin.field.parent': 'Entrée parente',
  'navAdmin.field.parent.root': '— (racine)',
  'navAdmin.field.role': 'Visibilité par rôle',
  'navAdmin.phase.entityLabel': 'entrée de menu',
  'navAdmin.role.all': 'Tous',
  'navAdmin.role.visitor': 'Visiteurs',
  'navAdmin.role.exhibitor': 'Exposants',
  'navAdmin.role.admin': 'Administrateurs',
  'navAdmin.confirm.delete':
    'Supprimer l\'entrée « {label} » ? Les éventuels enfants seront remontés au parent.',
  'navAdmin.toast.created': 'Entrée créée.',
  'navAdmin.toast.updated': 'Entrée mise à jour.',
  'navAdmin.toast.deleted': 'Entrée supprimée.',
  'navAdmin.toast.error': 'Erreur. Réessayez.',

  // ───── S31-C LiveAudio/VideoPlayer ──────────────────────────────────────
  'liveAudio.label': 'Lecteur Webradio en direct',
  'liveAudio.player.aria': 'Lecteur audio',
  'liveAudio.banner.aria': 'Diffusion Webradio en direct',
  'liveAudio.live': '● EN DIRECT',
  'liveAudio.ready': 'Prêt à diffuser',
  'liveAudio.nowPlaying': 'En cours :',
  'liveAudio.empty':
    'Pas de diffusion en direct pour le moment. Revenez bientôt !',
  'liveAudio.unsupported':
    'Votre navigateur ne prend pas en charge la lecture audio HTML5.',
  'liveAudio.error.principal': 'Le flux principal est indisponible.',
  'liveAudio.error.tryFallback': 'Réessayer avec le flux de secours',
  'liveAudio.error.noFallback':
    'La diffusion est temporairement interrompue. Revenez dans quelques instants.',

  'liveVideo.label': 'Lecteur WebTV en direct',
  'liveVideo.player.aria': 'Lecteur vidéo',
  'liveVideo.banner.aria': 'Diffusion WebTV en direct',
  'liveVideo.live': 'EN DIRECT',
  'liveVideo.ready': 'Prêt à diffuser',
  'liveVideo.loading': 'Chargement…',
  'liveVideo.nowPlaying': 'En direct :',
  'liveVideo.empty':
    'Pas de diffusion vidéo en direct pour le moment. Revenez bientôt !',
  'liveVideo.unsupported':
    'Votre navigateur ne prend pas en charge la lecture vidéo HTML5.',
  'liveVideo.subtitles.fr': 'Sous-titres en français',
  'liveVideo.error.principal': 'Le flux vidéo principal est indisponible.',
  'liveVideo.error.tryFallback': 'Réessayer avec le flux de secours',
  'liveVideo.error.noFallback':
    'La diffusion vidéo est temporairement interrompue. Revenez dans quelques instants.',

  // ───── S31-C MediaStreamsAdminPage ──────────────────────────────────────
  'mediaStreams.title': 'Diffusion en direct',
  'mediaStreams.lead':
    'Configurez les flux audio (Webradio) et vidéo (WebTV) diffusés en temps réel sur les pages publiques.',
  'mediaStreams.webradio.title': 'Webradio (audio)',
  'mediaStreams.webtv.title': 'WebTV (vidéo HLS)',
  'mediaStreams.toggle.enable': 'Activer le direct',
  'mediaStreams.field.streamUrl':
    'URL du flux audio (HTTPS Icecast/Shoutcast/MP3)',
  'mediaStreams.field.webtvStreamUrl': 'URL du flux HLS (HTTPS .m3u8)',
  'mediaStreams.field.fallbackUrl': 'URL de secours (optionnel)',
  'mediaStreams.field.nowPlayingLabel': 'Libellé affiché',
  'mediaStreams.field.posterUrl': 'Image affichée avant lecture (URL)',
  'mediaStreams.btn.test': 'Tester le flux',
  'mediaStreams.btn.save': 'Enregistrer',
  'mediaStreams.btn.saving': 'Enregistrement…',
  'mediaStreams.dirty': 'Modifications non enregistrées',
  'mediaStreams.saved': 'À jour',
  'mediaStreams.loading': 'Chargement…',
  'mediaStreams.error.load':
    'Impossible de charger la configuration. Réessayez plus tard.',
  'mediaStreams.error.webradioUrl':
    'L’URL principale Webradio doit commencer par https://',
  'mediaStreams.error.webradioFallback':
    'L’URL de secours Webradio doit commencer par https://',
  'mediaStreams.error.webtvUrl':
    'L’URL principale WebTV doit commencer par https://',
  'mediaStreams.error.webtvFallback':
    'L’URL de secours WebTV doit commencer par https://',
  'mediaStreams.errors.title': 'Corrigez les erreurs avant d’enregistrer :',
  'mediaStreams.toast.saved': 'Configuration enregistrée.',
  'mediaStreams.toast.error': 'Échec de l’enregistrement : {msg}',
  'mediaStreams.modal.testWebradio': 'Test du flux Webradio',
  'mediaStreams.modal.testWebtv': 'Test du flux WebTV',
  'mediaStreams.modal.hint':
    'Test en local — n’affecte pas la diffusion publique tant que vous n’avez pas cliqué « Enregistrer ».',
  'mediaStreams.tip.title': 'Comment générer un flux ?',
  'mediaStreams.tip.intro':
    'Pour diffuser en direct, vous avez besoin d’un serveur de streaming. Solutions recommandées :',
  'mediaStreams.tip.icecast':
    'Icecast (auto-hébergé) ou une instance académique. OBS Studio peut diffuser vers Icecast.',
  'mediaStreams.tip.peertube':
    'PeerTube académique (Réseau Canopé) — diffusion HLS native, RGPD-friendly, sans Google.',
  'mediaStreams.tip.https':
    'HTTPS obligatoire — sinon l’application refusera le flux (mixed-content).',
  'mediaStreams.tip.rgpd':
    'Pas de YouTube embed (cookies tiers, profilage). On reste en self-host ou PeerTube académique.',

  // ───── S31-C UserJourneyAdminPage ───────────────────────────────────────
  'userJourney.title': 'Suivi du parcours visiteur',
  'userJourney.lead':
    'Cherchez un visiteur pour reconstituer son parcours détaillé : check-ins, {lexWorkshopPlural}, pages vues, médias consultés.',
  'userJourney.search.label': 'Rechercher un visiteur (nom, email, ID)',
  'userJourney.search.placeholder': 'Tapez 2 caractères ou plus…',
  'userJourney.search.loading': 'Recherche…',
  'userJourney.search.empty': 'Aucun visiteur trouvé.',
  'userJourney.journey.loading': 'Chargement du parcours…',
  'userJourney.journey.error':
    'Impossible de charger le parcours de ce visiteur.',
  'userJourney.profile.minor': 'Mineur',
  'userJourney.profile.createdAt': 'Inscrit le {date}',
  'userJourney.btn.exportCsv': 'Exporter CSV',
  'userJourney.btn.exportPdf': 'Exporter PDF (à venir)',
  'userJourney.stats.totalEvents': 'Événements',
  'userJourney.stats.standsVisited': 'Stands visités',
  'userJourney.stats.workshopsSigned': '{lexWorkshopPluralCap} inscrit{lexWorkshopAgrPlural}',
  'userJourney.stats.hoursOnFloor': 'Heures sur place',
  'userJourney.chart.events': 'Événements',
  'userJourney.chart.heatmap.title': 'Activité par heure',
  'userJourney.top.stands': 'Top 5 stands visités',
  'userJourney.top.pages': 'Top 5 pages vues',
  'userJourney.top.standsEmpty': 'Aucun stand visité pour l’instant.',
  'userJourney.top.pagesEmpty': 'Aucune page suivie pour l’instant.',
  'userJourney.timeline.title': 'Parcours détaillé (du plus récent)',
  'userJourney.timeline.empty': 'Aucun événement enregistré pour ce visiteur.',
  'userJourney.timeline.limitReached':
    'Plus de 200 événements — utilisez l’export CSV pour la suite.',
  'userJourney.emptyState':
    'Saisissez un nom, un email ou un identifiant ci-dessus pour commencer.',
  'userJourney.toast.exportStarted': 'Export CSV en cours de téléchargement.',
  'userJourney.event.checkin':
    'Passage sur le stand #{stand} ({minutes} min)',
  'userJourney.event.signup': 'Inscription à {lexWorkshopDef} #{workshop}',
  'userJourney.event.cancel':
    'Annulation de l’inscription à {lexWorkshopDef} #{workshop}',
  'userJourney.event.generic': '{detail} {resource}',

  // ───── FIX 2026-05-21 Feature B (user-journey cleanup — Mika) ─────
  'userJourney.cleanup.section.title': 'Actions destructives',
  'userJourney.cleanup.section.lead':
    'Réservé aux super administrateurs et administrateurs. Le compte de l’utilisateur ne sera PAS supprimé (utilisez /admin/users-admin pour cela).',
  'userJourney.cleanup.btn.detach': 'Détacher de cet utilisateur',
  'userJourney.cleanup.btn.purge': 'Effacer complètement (irréversible)',
  'userJourney.cleanup.btn.cancel': 'Annuler',
  'userJourney.cleanup.help.detach.label': 'Détacher :',
  'userJourney.cleanup.help.detach.body':
    'anonymise les scans/checkins/inscriptions de cet utilisateur. Les enregistrements restent dans les statistiques globales mais ne lui sont plus liés.',
  'userJourney.cleanup.help.purge.label': 'Effacer :',
  'userJourney.cleanup.help.purge.body':
    'supprime DÉFINITIVEMENT tous les scans, check-ins, inscriptions {lexWorkshopPlural}, événements, favoris et cartes de contact de cet utilisateur. Action irréversible.',
  'userJourney.cleanup.detach.title': 'Détacher de cet utilisateur ?',
  'userJourney.cleanup.detach.lead':
    'Les enregistrements suivants seront anonymisés (visitor_id mis à NULL pour les scans/check-ins, status=« cancelled » pour les inscriptions). Ils resteront dans les statistiques globales :',
  'userJourney.cleanup.detach.note':
    'Cette action n’efface PAS les données — elle les délie. Pour une suppression définitive, utilisez « Effacer complètement ».',
  'userJourney.cleanup.detach.confirm': 'Confirmer le détachement',
  'userJourney.cleanup.purge.title': 'Effacer définitivement les statistiques ?',
  'userJourney.cleanup.purge.warning':
    '⚠ Cette action est définitive. Les données suivantes seront DÉFINITIVEMENT supprimées :',
  'userJourney.cleanup.purge.totalAffected':
    'Total : {n} enregistrements seront supprimés.',
  'userJourney.cleanup.purge.note':
    'Le compte utilisateur tne_users sera CONSERVÉ. Pour le supprimer, utilisez /admin/users-admin après cette action.',
  'userJourney.cleanup.purge.checkbox':
    'Je comprends que cette action est irréversible.',
  'userJourney.cleanup.purge.confirmInputLabel':
    'Pour confirmer, saisissez le prénom de l’utilisateur : « {first_name} »',
  'userJourney.cleanup.purge.confirm': 'Effacer définitivement',
  'userJourney.cleanup.loadingCounts': 'Chargement des compteurs…',
  'userJourney.cleanup.error': 'Échec de l’opération de nettoyage.',

  // ───── S31-A ExhibitorsAdminPage ─────
  'exhibitorsAdmin.title': 'Exposants',
  // B6 corrige le 2026-08-26 : la phrase decrivait le login par code, abandonne
  // le 2026-05-19. Elle envoyait donc les exposants vers un chemin ferme.
  'exhibitorsAdmin.subtitle':
    'Créez, modifiez et gérez les exposants. Ils se connectent avec leur adresse e-mail et un code à 6 chiffres reçu par e-mail.',
  'exhibitorsAdmin.kpi.aria': 'Indicateurs exposants',
  'exhibitorsAdmin.kpi.total': 'Exposants au total',
  'exhibitorsAdmin.kpi.active': 'Exposants actifs',
  'exhibitorsAdmin.kpi.withCheckins': 'Avec scans enregistrés',
  'exhibitorsAdmin.action.create': '＋ Nouvel exposant',
  'exhibitorsAdmin.action.import': '📥 Importer (CSV)',
  'exhibitorsAdmin.action.exportCsv': 'Exporter en CSV',
  'exhibitorsAdmin.action.sendConnexionInvites': '✉️ Rappel de connexion',
  'exhibitorsAdmin.invite.title': 'Envoyer le rappel de connexion',
  'exhibitorsAdmin.invite.intro':
    'Un e-mail sera envoyé à chaque exposant pour lui rappeler qu’il se connecte sur la page « Connexion » avec son adresse e-mail (il recevra alors un code à 6 chiffres). Aucun code EXPO n’est communiqué. Vous pouvez le renvoyer autant de fois que nécessaire.',
  'exhibitorsAdmin.invite.confirm': 'Envoyer à tous les exposants',
  'exhibitorsAdmin.invite.sending': 'Envoi en cours…',
  'exhibitorsAdmin.invite.result':
    '{sent, plural, =0 {Aucun e-mail envoyé} one {1 e-mail envoyé} other {# e-mails envoyés}}{failed, plural, =0 {} other { — # échec(s)}}.',
  'exhibitorsAdmin.invite.error': 'L’envoi des rappels a échoué. Réessayez.',
  'exhibitorsAdmin.action.edit': 'Modifier',
  'exhibitorsAdmin.action.print': 'Imprimer carte',
  'exhibitorsAdmin.action.regen': 'Régénérer code',
  'exhibitorsAdmin.action.regenConfirm': 'Oui, régénérer maintenant',
  'exhibitorsAdmin.action.archive': 'Archiver',
  'exhibitorsAdmin.action.reveal': 'Afficher (5 sec)',
  'exhibitorsAdmin.action.copy': 'Copier',
  'exhibitorsAdmin.action.cancel': 'Annuler',
  'exhibitorsAdmin.action.save': 'Enregistrer',
  'exhibitorsAdmin.action.createSubmit': 'Créer l’exposant',
  'exhibitorsAdmin.action.close': 'Fermer',
  'exhibitorsAdmin.action.printNow': 'Lancer l’impression',
  'exhibitorsAdmin.filter.search': 'Recherche',
  'exhibitorsAdmin.filter.search.placeholder':
    'Structure, nom, e-mail…',
  'exhibitorsAdmin.filter.stand': 'Stand',
  'exhibitorsAdmin.filter.stand.placeholder': 'Ex. A12',
  'exhibitorsAdmin.filter.status': 'Statut',
  'exhibitorsAdmin.filter.status.all': 'Tous',
  'exhibitorsAdmin.filter.status.active': 'Actifs',
  'exhibitorsAdmin.filter.status.archived': 'Archivés',
  'exhibitorsAdmin.filter.hasCheckins': 'Avec scans uniquement',
  'exhibitorsAdmin.col.structure': 'Structure',
  'exhibitorsAdmin.col.referent': 'Référent',
  'exhibitorsAdmin.col.email': 'E-mail',
  'exhibitorsAdmin.col.stand': 'Stand',
  'exhibitorsAdmin.col.code': 'Code d’accès',
  // Postes proposes + relance (Mika, 2026-08-26).
  'exhibitorsAdmin.col.jobs': 'Postes',
  'exhibitorsAdmin.col.jobs.sort': 'Trier par nombre de postes',
  'exhibitorsAdmin.jobs.none': 'Aucun poste',
  'exhibitorsAdmin.jobs.positions': '{n, plural, one {# poste} other {# postes}}',
  'exhibitorsAdmin.jobs.offers': '({n, plural, one {# offre} other {# offres}})',
  'exhibitorsAdmin.jobs.draft': 'Brouillon',
  'exhibitorsAdmin.jobs.loading': 'Chargement des postes…',
  'exhibitorsAdmin.jobs.error': 'Impossible de charger les postes.',
  'exhibitorsAdmin.kpi.withoutJobs': 'Sans aucun poste',
  'exhibitorsAdmin.filter.jobs': 'Postes',
  'exhibitorsAdmin.filter.jobs.all': 'Tous',
  'exhibitorsAdmin.filter.jobs.without': 'Sans aucun poste',
  'exhibitorsAdmin.filter.jobs.with': 'Avec au moins un poste',
  'exhibitorsAdmin.relance.intro':
    '{n, plural, one {# exposant n’a publié aucun poste} other {# exposants n’ont publié aucun poste}}. Vous pouvez les relancer en un envoi.',
  'exhibitorsAdmin.relance.send': 'Relancer {n, plural, one {# exposant} other {les # exposants}}',
  'exhibitorsAdmin.relance.confirm':
    'Envoyer la relance à {n, plural, one {# exposant} other {# exposants}} ? Le message est celui du modèle « Relance — aucun poste à pourvoir ».',
  'exhibitorsAdmin.relance.editTemplate': 'Modifier le message',
  'exhibitorsAdmin.relance.showList': 'Voir la liste',
  // Fenetre « Postes proposes » (Mika, 2026-08-26).
  'exhibitorsAdmin.jobsModal.title': 'Postes proposés — {societe}',
  'exhibitorsAdmin.jobsModal.close': 'Fermer',
  // Distinction animateur / exposant dans la liste (Mika, 2026-08-31).
  'exhibitorsAdmin.badge.workshopHost': 'Animateur',
  // Entite de l'animateur sur /admin/workshops (Mika, 2026-09-01).
  'workshopsAdmin.meta.noEntity': 'entité non renseignée',
  'exhibitorsAdmin.relance.done': '{n, plural, =0 {Aucun envoi : plus personne n’était concerné.} one {Relance envoyée à # exposant.} other {Relance envoyée à # exposants.}}',
  'exhibitorsAdmin.relance.partial': 'Relance envoyée à {envoyes}, {echecs} en échec.',
  'exhibitorsAdmin.relance.error': 'Impossible d’envoyer la relance.',
  'exhibitorsAdmin.col.scans': 'Scans',
  'exhibitorsAdmin.col.actions': 'Actions',
  'exhibitorsAdmin.reveal.aria': 'Afficher le code d’accès de {name}',
  'exhibitorsAdmin.reveal.notAllowed':
    'Vous n’avez pas l’autorisation d’afficher le code en clair.',
  'exhibitorsAdmin.reveal.error':
    'Impossible d’afficher le code. Réessayez dans un instant.',
  'exhibitorsAdmin.copy.success': 'Code copié dans le presse-papiers.',
  'exhibitorsAdmin.copy.error':
    'Impossible de copier le code. Sélectionnez-le manuellement.',
  'exhibitorsAdmin.loading': 'Chargement des exposants…',
  'exhibitorsAdmin.error':
    'Impossible de charger la liste des exposants. Vérifiez votre connexion.',
  'exhibitorsAdmin.empty':
    'Aucun exposant ne correspond à votre recherche.',
  'exhibitorsAdmin.modal.create': 'Nouvel exposant',
  'exhibitorsAdmin.modal.edit': 'Modifier l’exposant',
  'exhibitorsAdmin.modal.codeShowcase':
    'Accès de l’exposant',
  'exhibitorsAdmin.modal.regen': 'Régénérer le code d’accès',
  'exhibitorsAdmin.modal.print': 'Imprimer la carte exposant',
  'exhibitorsAdmin.modal.currentCode': 'Code d’accès actuel :',
  'exhibitorsAdmin.field.firstName': 'Prénom du référent',
  'exhibitorsAdmin.field.lastName': 'Nom du référent',
  'exhibitorsAdmin.field.email': 'Adresse e-mail',
  'exhibitorsAdmin.field.phone': 'Téléphone',
  'exhibitorsAdmin.field.structure': 'Nom de la structure',
  'exhibitorsAdmin.field.functionTitle': 'Fonction du référent',
  'exhibitorsAdmin.field.booth': 'Numéro de stand',
  'exhibitorsAdmin.field.sector': 'Secteur d’activité',
  'exhibitorsAdmin.field.website': 'Site internet',
  'exhibitorsAdmin.field.description': 'Description courte',
  'exhibitorsAdmin.form.required':
    'Les champs Structure, Prénom et Nom du référent sont obligatoires.',
  'exhibitorsAdmin.form.error':
    'Une erreur est survenue lors de l’enregistrement.',
  'exhibitorsAdmin.create.success':
    'Exposant créé. Communiquez le code d’accès maintenant.',
  'exhibitorsAdmin.update.success': 'Exposant mis à jour.',
  'exhibitorsAdmin.confirm.archive':
    'Archiver l’exposant « {name} » ? Cette action peut être annulée.',
  'exhibitorsAdmin.archive.success': 'Exposant archivé.',
  'exhibitorsAdmin.archive.error':
    'Impossible d’archiver l’exposant. Réessayez dans un instant.',
  'exhibitorsAdmin.regen.confirm':
    'Régénérer le code d’accès de « {name} » ?',
  'exhibitorsAdmin.regen.warning':
    'Attention : l’ancien code ne fonctionnera plus immédiatement. L’exposant devra utiliser le nouveau dès maintenant.',
  'exhibitorsAdmin.regen.success': 'Nouveau code d’accès généré.',
  'exhibitorsAdmin.regen.error':
    'Impossible de régénérer le code. Réessayez dans un instant.',
  'exhibitorsAdmin.showcase.intro':
    'Le compte de « {name} » est créé. Voici comment cette personne se connecte.',
  'exhibitorsAdmin.showcase.qrAlt':
    'QR code vers la page de connexion',
  'exhibitorsAdmin.showcase.warning':
    'Elle recevra un code à 6 chiffres par e-mail à chaque connexion. Le bouton « Rappel de connexion » lui renvoie ces instructions.',
  'exhibitorsAdmin.print.noCode':
    'Aucun code d’accès à imprimer pour cet exposant.',
  'exhibitorsAdmin.print.error':
    'Impossible de préparer la carte exposant.',
  'exhibitorsAdmin.print.event': 'Salon {eventName}',
  'exhibitorsAdmin.print.stand': 'Stand {stand}',
  'exhibitorsAdmin.print.codeLabel': 'Code d’accès :',
  // 2026-08-26 — la carte porte le VRAI mode d'entree, plus un code mort.
  'exhibitorsAdmin.print.emailLabel': 'Identifiant :',
  'exhibitorsAdmin.print.howTo':
    'Un code à 6 chiffres vous sera envoyé par e-mail à la connexion.',
  'exhibitorsAdmin.login.byEmail': 'E-mail + code à 6 chiffres',
  // Fusion Demandes / Exposants (2026-08-26).
  'exhibitorsAdmin.tab.exhibitors': 'Exposants',
  'exhibitorsAdmin.tab.requests': 'Demandes de code',
  'exhibitorsAdmin.print.qrAlt':
    'QR code pour se connecter rapidement à l’espace exposant.',

  // ───── S31-A ExhibitorLoginPage ──────
  'exhibitorLogin.title': 'Espace exposant',
  'exhibitorLogin.subtitle':
    'Connectez-vous avec le code à 6 caractères qui vous a été remis.',
  'exhibitorLogin.codeLabel': 'Votre code d’accès',
  'exhibitorLogin.charLabel': 'Caractère {n} du code',
  'exhibitorLogin.submit': 'Se connecter',
  'exhibitorLogin.help': 'J’ai perdu mon code',
  'exhibitorLogin.help.title': 'Code d’accès oublié ou perdu',
  'exhibitorLogin.help.body1':
    'Votre code d’accès vous a été remis par e-mail ou directement par l’équipe d’organisation lors de votre inscription.',
  'exhibitorLogin.help.body2':
    'Le code est au format EXPO suivi de 6 caractères, par exemple EXPO-ABCD23.',
  'exhibitorLogin.help.contact':
    'Si vous ne le retrouvez pas, contactez l’organisation à l’adresse mikael.fourny@labriquerie.net — nous régénérerons votre code dans la journée.',
  'exhibitorLogin.help.close': 'J’ai compris',
  'exhibitorLogin.backHome': 'Retour à l’accueil',
  'exhibitorLogin.success':
    'Bienvenue {name} ! Vous êtes connecté pour 12 heures.',
  'exhibitorLogin.error.invalid':
    'Code d’accès invalide ou expiré. Vérifiez vos caractères.',
  'exhibitorLogin.error.format':
    'Code d’accès incomplet. Saisissez les 6 caractères.',
  'exhibitorLogin.error.tooMany':
    'Trop de tentatives. Patientez quelques minutes avant de réessayer.',
  'exhibitorLogin.error.network':
    'Connexion impossible. Vérifiez votre réseau et réessayez.',

  // ───── S31-A ExhibitorScannerPage ────
  'exhibitorScanner.back': 'Retour',
  'exhibitorScanner.logout': 'Déconnexion',
  // FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — ST2 ❤️)
  'exhibitorScanner.dashboardLink': 'Voir mon tableau de bord',
  'exhibitorScanner.checking': 'Vérification de votre session…',
  'exhibitorScanner.cameraLabel':
    'Zone de scan caméra. Touchez pour mettre au point.',
  'exhibitorScanner.tapToFocus':
    'Toucher l’écran pour mettre au point la caméra.',
  'exhibitorScanner.cameraNotSupported':
    'Votre navigateur ne prend pas en charge la caméra. Utilisez Chrome ou Safari récent.',
  'exhibitorScanner.cameraDenied':
    'L’accès à la caméra a été refusé. Autorisez-le dans les réglages de votre navigateur.',
  'exhibitorScanner.cameraError':
    'Impossible de démarrer la caméra. Vérifiez qu’elle n’est pas utilisée par une autre application.',
  'exhibitorScanner.retry': 'Réessayer',
  'exhibitorScanner.ready': 'Prêt à scanner',
  'exhibitorScanner.torch': 'Activer la lampe',
  'exhibitorScanner.torchError':
    'La lampe n’est pas disponible sur cet appareil.',
  'exhibitorScanner.sessionCount': 'Cette session',
  'exhibitorScanner.lastScan': 'Dernier : {name} à {time}',
  'exhibitorScanner.status.online': 'En ligne',
  'exhibitorScanner.status.offline': 'Hors ligne',
  'exhibitorScanner.status.syncing': 'Synchronisation…',
  'exhibitorScanner.offlineBanner':
    'En attente de réseau — {n, plural, one {# scan en file} other {# scans en file}}.',
  'exhibitorScanner.offlineQueued':
    'Scan enregistré hors ligne. Il sera synchronisé au retour du réseau.',
  'exhibitorScanner.offlinePlaceholder': 'Scan en file…',
  // FIX 2026-05-11 (consolidation S31) — distinction "enregistrées
  // serveur" (notesSaved) vs "conservées en local seulement" (fallback).
  'exhibitorScanner.notesSaved':
    'Notes enregistrées. Visiteur suivant !',
  'exhibitorScanner.notesSavedLocal':
    'Notes conservées (synchronisation différée).',

  // ───── S31 consolidation — LiveDashboardSection (SSE temps réel) ─────────
  'liveDashboard.title': 'Tableau de bord temps réel',
  'liveDashboard.connecting': 'Connexion au flux en cours…',
  'liveDashboard.loading': 'Chargement des données live…',
  'liveDashboard.lastUpdate': 'Dernière mise à jour : {time}',
  'liveDashboard.time.justNow': 'à l’instant',
  'liveDashboard.time.sec': 'il y a {n}s',
  'liveDashboard.time.min': 'il y a {n} min',
  'liveDashboard.time.hour': 'il y a {n} h',
  'liveDashboard.kpi.visitorsPresent': 'Visiteurs présents',
  'liveDashboard.kpi.windowHint': 'Sur les 20 dernières minutes',
  'liveDashboard.kpi.scansToday': 'Scans aujourd’hui',
  'liveDashboard.kpi.totalCumul': 'Total cumulé : {n}',
  'liveDashboard.kpi.usersActive': 'Inscrits actifs',
  'liveDashboard.kpi.workshopsFull': '{lexWorkshopPluralCap} au complet',
  'liveDashboard.kpi.signupsRecap':
    '{confirmed} inscriptions, {waitlist} liste d’attente',
  'liveDashboard.topStands.title': 'Qui est où maintenant',
  'liveDashboard.topStands.subtitle':
    'Cliquez sur un stand pour voir la liste détaillée des présents',
  'liveDashboard.topStands.empty':
    'Aucun stand actif sur la fenêtre courante. L’activité va apparaître dès les premiers scans.',
  'liveDashboard.topStands.openDrill':
    'Ouvrir le détail du stand {stand} ({count} présents)',
  'liveDashboard.activity.title': 'Qui fait quoi',
  'liveDashboard.activity.subtitle':
    'Derniers passages et événements de navigation, anonymisés',
  'liveDashboard.activity.empty':
    'Aucun événement récent. Les scans, vues {lexWorkshopOfPluralBare} et lectures de médias apparaîtront ici en direct.',
  'liveDashboard.activity.checkinOn': 'a scanné le stand {stand} ({exhibitor})',
  'liveDashboard.activity.checkinStand': 'a scanné le stand {stand}',
  'liveDashboard.activity.workshopView': 'a consulté {lexWorkshopDef} {slug}',
  'liveDashboard.activity.workshopSignup': 's’est inscrit à {lexWorkshopDef} {slug}',
  'liveDashboard.activity.mediaPlay': 'a lancé le média {slug}',
  'liveDashboard.activity.partnerView': 'a consulté le partenaire {slug}',
  'liveDashboard.activity.badgeShow': 'a affiché son badge',
  'liveDashboard.activity.search': 'a effectué une recherche',
  'liveDashboard.btn.userJourney': 'Recherche détaillée par visiteur',
  'liveDashboard.btn.openMap': 'Ouvrir le plan temps réel',
  'liveDashboard.viewJourney': 'Voir le parcours',
  'liveDashboard.minorBadge': 'Mineur',
  'liveDashboard.modal.title': 'Stand « {stand} » — présents',
  'liveDashboard.modal.windowInfo':
    'Liste des visiteurs ayant scanné le QR de ce stand dans les {minutes} dernières minutes.',
  'liveDashboard.modal.loading': 'Chargement…',
  'liveDashboard.modal.error': 'Impossible de charger la liste.',
  'liveDashboard.modal.empty':
    'Personne actuellement présent sur ce stand selon la fenêtre courante.',
  'liveDashboard.modal.count':
    '{count, plural, one {# visiteur présent} other {# visiteurs présents}}',
  'exhibitorScanner.alreadySeen': 'Déjà scanné aujourd’hui',
  'exhibitorScanner.modal.title': 'Détails du passage',
  'exhibitorScanner.interest': 'Niveau d’intérêt',
  'exhibitorScanner.interest.level': '{level} étoile{level, plural, one {} other {s}}',
  'exhibitorScanner.notes': 'Notes (optionnel)',
  'exhibitorScanner.notesPlaceholder':
    'Ex : très intéressé par PANDA, souhaite un rappel après le salon.',
  'exhibitorScanner.skip': 'Ignorer',
  'exhibitorScanner.saveAndNext': 'Enregistrer et scanner le suivant',
  'exhibitorScanner.error.visitorNotFound':
    'QR visiteur invalide ou compte désactivé.',
  'exhibitorScanner.error.sessionExpired':
    'Votre session a expiré. Reconnectez-vous.',
  'exhibitorScanner.error.rateLimited':
    'Trop de scans en peu de temps. Patientez quelques secondes.',
  'exhibitorScanner.error.generic':
    'Une erreur est survenue. Réessayez dans un instant.',

  // ───── Sprint S31-B — LoginVisiteurPage (magic code) ──────────────────
  'visitorLogin.title.email': 'Bienvenue sur {eventName}',
  'visitorLogin.title.code': 'Saisissez votre code',
  'visitorLogin.subtitle.email':
    'Entrez votre adresse email pour recevoir un code de connexion à 6 chiffres.',
  'visitorLogin.subtitle.code':
    'Saisissez le code que vous venez de recevoir par email.',
  'visitorLogin.sentTo': 'Code envoyé à {email}.',
  'visitorLogin.validity':
    'Ce code est valable 10 minutes et ne peut être utilisé qu’une seule fois.',
  'visitorLogin.field.email': 'Adresse email',
  'visitorLogin.submit.email': 'Recevoir mon code',
  'visitorLogin.submit.code': 'Me connecter',
  'visitorLogin.code.legend': 'Code à 6 chiffres reçu par email',
  'visitorLogin.code.digit': 'Chiffre {n}',
  'visitorLogin.code.hint':
    'Vous pouvez aussi coller le code directement dans la première case.',
  'visitorLogin.resend.now': 'Renvoyer un code',
  'visitorLogin.resend.countdown': 'Renvoyer un code dans {seconds} s',
  // FIX 2026-05-18 — affiché quand un code vient déjà d'être envoyé
  // (cooldown actif) : on invite à saisir le code reçu plutôt que de
  // re-demander en boucle.
  'visitorLogin.cooldown.enterExisting':
    'Un code vous a déjà été envoyé. Saisissez-le ci-dessous (valable 10 minutes).',
  'visitorLogin.back.email': 'Modifier mon adresse email',
  'visitorLogin.link.signup': 'Je n’ai pas encore de compte — m’inscrire',
  'visitorLogin.link.haveBadge':
    'J’ai déjà mon badge — accéder à mon espace',
  'visitorLogin.error.email.required':
    'Veuillez saisir votre adresse email.',
  'visitorLogin.error.email.invalid':
    'Cette adresse email n’est pas valide.',
  'visitorLogin.error.email.tooLong':
    'Adresse email trop longue (190 caractères maximum).',
  'visitorLogin.error.codeInvalid':
    'Code incorrect ou expiré. Vérifiez ou demandez un nouveau code.',
  'visitorLogin.error.badRequest':
    'Requête invalide. Vérifiez votre saisie.',
  'visitorLogin.error.tooManyAttempts':
    'Trop de tentatives. Veuillez patienter quelques minutes.',
  'visitorLogin.error.tooManyAttempts.withDelay':
    'Trop de tentatives. Réessayez dans {seconds} s.',
  'visitorLogin.error.network':
    'Erreur de connexion. Vérifiez votre réseau et réessayez.',
  // FIX 2026-05-18 — code validé mais le navigateur ne conserve pas le
  // cookie de session (typiquement Safari en navigation privée / ITP).
  'visitorLogin.error.sessionNotPersisted':
    'Votre code est validé, mais votre navigateur ne conserve pas la session (souvent le cas en navigation privée sur Safari). Réessayez en navigation normale, ou autorisez les cookies pour ce site.',

  // ───── Sprint S31-B — MonBadgePage enrichi ────────────────────────────
  'badge.action.printA6': 'Imprimer ma carte (A6)',
  'badge.action.printA6.aria':
    'Ouvrir la page d’impression A6 de mon badge dans un nouvel onglet',
  'badge.action.downloadPdf': 'Télécharger en PDF',
  'badge.action.downloadPdf.error': 'Impossible de generer le PDF. Utilisez « Imprimer ma carte » en attendant.',
  'badge.action.downloadPdf.aria': 'Télécharger mon badge au format PDF',
  'badge.action.downloadPdf.soon':
    'L’export PDF arrive très bientôt — utilisez « Imprimer ma carte (A6) » en attendant.',
  'badge.action.wallet.soon':
    'L’ajout à Apple Wallet et Google Wallet sera disponible prochainement.',
  'badge.recentCheckin.message':
    'Vous venez d’être enregistré au {stand}, {ago}.',
  'badge.recentCheckin.standDefault': 'stand',
  'badge.recentCheckin.justNow': 'à l’instant',
  'badge.recentCheckin.oneMinuteAgo': 'il y a 1 minute',
  'badge.recentCheckin.minutesAgo': 'il y a {minutes} minutes',

  // ───── Sprint S31-B — MonBadgePrintPage ───────────────────────────────
  'badgePrint.confirm.prompt':
    'Lancer l’impression de votre badge au format A6 ?',
  'badgePrint.action.print': 'Imprimer maintenant',
  'badgePrint.action.close': 'Fermer cet onglet',
  'badgePrint.actions.aria': 'Actions d’impression',
  'badgePrint.aria.label': 'Badge {role} de {name} à imprimer',
  'badgePrint.brand.subtitle':
    'Salon NOLAS 2026 — Recrutement, Emploi et Formation',
  'badgePrint.qr.alt': 'Code QR à scanner par les exposants',
  'badgePrint.qr.error': 'Erreur de génération du code QR.',
  'badgePrint.helpText':
    'Cette page est conçue pour l’impression au format A6 (148×105 mm). Si vous n’imprimez pas tout de suite, utilisez « Fermer » pour revenir à votre badge.',
  'badgePrint.unavailable.title': 'Badge indisponible',
  'badgePrint.unavailable.message':
    'Connectez-vous à votre espace {eventName} pour accéder à votre badge.',

  // ───── Sprint S31-B — MonParcoursPage ─────────────────────────────────
  'monParcours.title': 'Mon parcours',
  'monParcours.subtitle.during':
    'Retrouvez les stands où vous avez été enregistré, en temps réel.',
  'monParcours.subtitle.after':
    'Voici le récapitulatif de votre passage au salon.',
  'monParcours.phaseBefore.subtitle':
    'Votre parcours sera disponible le jour du salon.',
  'monParcours.phaseBefore.message':
    '{eventTypeDefCap} {eventName} n’a pas encore commencé. Découvrez en attendant le programme et {lexWorkshopDefPlural}.',
  'monParcours.phaseBefore.cta': 'Voir le programme',
  'monParcours.kpi.standsVisited': 'Stands visités',
  'monParcours.kpi.totalTime': 'Temps total',
  'monParcours.kpi.lastStand': 'Dernier stand',
  'monParcours.kpi.lastStand.none': 'Aucun stand',
  'monParcours.refresh.label': 'Actualiser',
  'monParcours.refresh.aria': 'Actualiser mon parcours',
  'monParcours.export.label': 'Exporter en PDF',
  'monParcours.export.soon':
    'L’export PDF de votre parcours arrive bientôt.',
  'monParcours.timeline.aria': 'Chronologie de mes passages sur les stands',
  'monParcours.timeline.duration': 'Durée sur le stand : {duration}',
  'monParcours.unknownStand': 'Stand inconnu',
  'monParcours.empty.title': 'Aucun stand visité pour le moment',
  'monParcours.empty.message':
    'Présentez votre badge à un exposant ou scannez le QR code de son stand pour commencer votre parcours.',
  'monParcours.empty.cta': 'Scanner un stand',
  'monParcours.error.load':
    'Impossible de charger votre parcours. Vérifiez votre connexion.',
  'monParcours.duration.zero': 'moins d’une minute',
  'monParcours.duration.minutes': '{minutes} min',
  'monParcours.duration.hours': '{hours} h',
  'monParcours.duration.hoursMinutes': '{hours} h {minutes} min',
  'monParcours.phaseAfter.message':
    'Merci d’avoir participé {eventTypeAt} {eventName} ! Revivez les temps forts dans la rétrospective.',
  'monParcours.phaseAfter.linkLabel': 'Voir la rétrospective',

  // ───── Sprint contrôle d'accès atelier (2026-05-16) ───────────────────
  // a) Mode contrôle d'accès dans la douchette exposant
  'exhibitorScanner.mode.aria': 'Choisir le mode de scan',
  'exhibitorScanner.mode.stand': 'Check-in stand',
  'exhibitorScanner.mode.access': 'Contrôle d’accès {lexWorkshopBare}',
  'exhibitorScanner.access.selectLabel': 'Quel {lexWorkshopBare} contrôlez-vous ?',
  'exhibitorScanner.access.loading': 'Chargement de vos {lexWorkshopPlural}…',
  'exhibitorScanner.access.noWorkshopAssigned':
    '{lexWorkshopNoneCap} ne vous est attribué{lexWorkshopAgr}. Demandez à l’organisation de vous désigner comme animateur.',
  'exhibitorScanner.access.selectPlaceholder': 'Sélectionnez {lexWorkshopIndef}…',
  'exhibitorScanner.access.counter':
    'Présents : {attended} / {confirmed} · Restantes : {places}',
  'exhibitorScanner.access.noWorkshop':
    'Choisissez d’abord {lexWorkshopDef} que vous contrôlez.',
  'exhibitorScanner.access.offline':
    'Le contrôle d’accès nécessite une connexion. Réessayez quand le réseau revient.',
  'exhibitorScanner.access.notHost':
    'Vous n’animez pas {lexWorkshopDem}. Contrôle d’accès refusé.',
  'exhibitorScanner.access.loadError':
    'Impossible de charger vos {lexWorkshopPlural}. Vérifiez votre connexion.',
  'exhibitorScanner.access.verdictTitle': 'Contrôle d’accès',
  'exhibitorScanner.access.minor': 'Mineur',
  'exhibitorScanner.access.confirmedAttended':
    'Inscrit confirmé — déjà pointé présent ✓',
  'exhibitorScanner.access.confirmed':
    'Inscrit confirmé. Vous pouvez le marquer présent.',
  'exhibitorScanner.access.waitlist':
    'En liste d’attente. À vous de décider de l’admettre ou non.',
  'exhibitorScanner.access.notRegistered':
    'Non inscrit à {lexWorkshopDem}. Vous pouvez l’inscrire sur place s’il reste de la place.',
  'exhibitorScanner.access.notRegisteredFull':
    'Non inscrit — {lexWorkshopBare} au complet. Inscription sur place impossible.',
  'exhibitorScanner.access.registeredOnSite':
    'Inscrit sur place et pointé présent ✓',
  'exhibitorScanner.access.markAttended': 'Marquer présent',
  'exhibitorScanner.access.admit': 'Admettre quand même',
  'exhibitorScanner.access.registerOnSite': 'Inscrire sur place',
  'exhibitorScanner.access.next': 'Suivant',

  // b) Backoffice ateliers — assignation des exposants animateurs
  'workshopsAdmin.btn.hosts': 'Animateurs',
  'workshopsAdmin.hosts.title': 'Animateurs — {title}',
  'workshopsAdmin.hosts.help':
    'Cochez les exposants qui animent et contrôlent l’accès de {lexWorkshopDem}. Eux seuls pourront vérifier les inscriptions au point d’accès.',
  // Creation d'un animateur sans quitter la modale (demande Mika 26/08).
  // « atelier » est INTERDIT en dur : le jeton rend « activite » sur CIRFA.
  'workshopsAdmin.hosts.createOpen': 'Créer un animateur',
  'workshopsAdmin.hosts.createHelp':
    'Créez la personne si elle n’est pas encore inscrite. Elle sera ajoutée aux animateurs {lexWorkshopDem} dès l’enregistrement. L’adresse e-mail est facultative, mais c’est elle qui lui permettra de se connecter.',
  'workshopsAdmin.hosts.field.firstName': 'Prénom',
  'workshopsAdmin.hosts.field.lastName': 'Nom',
  'workshopsAdmin.hosts.field.structure': 'Structure',
  'workshopsAdmin.hosts.field.email': 'Adresse e-mail (facultative)',
  'workshopsAdmin.hosts.createCancel': 'Annuler',
  'workshopsAdmin.hosts.createSubmit': 'Créer et sélectionner',
  'workshopsAdmin.hosts.created':
    'Animateur créé et sélectionné. Enregistrez pour valider le rattachement.',
  'workshopsAdmin.hosts.createError':
    'Impossible de créer cet animateur. Réessayez.',
  'workshopsAdmin.hosts.cancel': 'Annuler',
  'workshopsAdmin.hosts.save': 'Enregistrer',
  'workshopsAdmin.hosts.saved': 'Animateurs enregistrés.',
  'workshopsAdmin.hosts.queued':
    'Enregistrement différé (hors ligne). Il sera synchronisé au retour du réseau.',
  'workshopsAdmin.hosts.error':
    'Impossible d’enregistrer les animateurs. Réessayez.',
  'workshopsAdmin.hosts.forbidden':
    'Seul un administrateur peut modifier les animateurs.',
  'workshopsAdmin.hosts.noExhibitors':
    'Aucun exposant actif. Créez d’abord des comptes exposants.',

  // c) Vue présence temps réel par atelier (/admin/workshop-attendance)
  'workshopAttendance.title': 'Présence par {lexWorkshopBare}',
  'workshopAttendance.subtitle':
    'Suivi en temps réel des inscrits, des présents et des places restantes.',
  'workshopAttendance.live': 'En direct',
  'workshopAttendance.totals.aria': 'Totaux toutes activités confondues',
  'workshopAttendance.totals.confirmed': 'Inscrits',
  'workshopAttendance.totals.attended': 'Présents',
  'workshopAttendance.totals.noShow': 'Absents',
  'workshopAttendance.totals.waitlist': 'Liste d’attente',
  'workshopAttendance.filter.search': 'Rechercher',
  'workshopAttendance.filter.searchPlaceholder': 'Titre ou salle…',
  'workshopAttendance.filter.kind': 'Type',
  'workshopAttendance.filter.kindAll': 'Tous les types',
  'workshopAttendance.kind.atelier': '{lexWorkshopCap}',
  'workshopAttendance.kind.stand': 'Stand',
  'workshopAttendance.kind.agora': 'Agora',
  'workshopAttendance.kind.webradio': 'Webradio',
  'workshopAttendance.state.loading': 'Chargement des présences…',
  // FIX 2026-05-18 (audit Cowork Bug #3) : ne plus blâmer la
  // connexion de l'admin sur une erreur serveur (faisait perdre du
  // temps à vérifier le wifi alors que la cause est côté serveur).
  'workshopAttendance.state.error':
    'Le serveur n’a pas pu charger les présences (erreur interne). Réessayez dans quelques instants.',
  'workshopAttendance.state.empty': 'Aucune activité ne correspond.',
  'workshopAttendance.ratio.aria': 'Taux de présence : {ratio} %',
  'workshopAttendance.ratio.label': 'de présence',
  'workshopAttendance.stat.confirmed': 'Inscrits',
  'workshopAttendance.stat.attended': 'Présents',
  'workshopAttendance.stat.noShow': 'Absents',
  'workshopAttendance.stat.waitlist': 'Liste d’attente',
  'workshopAttendance.stat.places': 'Restantes',
  'workshopAttendance.hosts.label': 'Animateurs',
  'workshopAttendance.hosts.none': 'aucun animateur assigné',

  // ───── 2026-05-16 — Mise en conformité 100 % CDC + fondation multi-événement
  // a) F12 — Mes favoris
  'mesFavoris.title': 'Mes favoris',
  'mesFavoris.subtitle':
    'Retrouvez ici {lexWorkshopDefPlural}, partenaires et médias que vous avez mis en favori.',
  'mesFavoris.section.workshops': '{lexWorkshopPluralCap}',
  'mesFavoris.section.partners': 'Partenaires',
  'mesFavoris.section.medias': 'Médias',
  'mesFavoris.remove.label': 'Retirer',
  'mesFavoris.remove.aria': 'Retirer « {title} » de mes favoris',
  'mesFavoris.item.untitled': 'Élément sans titre',
  'mesFavoris.empty.title': 'Aucun favori pour le moment',
  'mesFavoris.empty.message':
    'Parcourez le programme et touchez le cœur pour ajouter {lexWorkshopIndef}, un partenaire ou un média à vos favoris.',
  'mesFavoris.empty.cta': 'Découvrir le programme',
  'mesFavoris.error.load': 'Impossible de charger vos favoris.',
  'mesFavoris.error.retry': 'Réessayer',
  // Boutons favori partagés (fiche partenaire / média / carte)
  'favoris.action.label': 'Favori',
  'favoris.action.add.aria': 'Ajouter « {title} » à mes favoris',
  'favoris.action.remove.aria': 'Retirer « {title} » de mes favoris',
  'favoris.action.added': 'Ajouté à vos favoris.',
  'favoris.action.removed': 'Retiré de vos favoris.',
  'favoris.action.error': 'Action impossible, réessayez.',
  'favoris.action.viewMine': 'Mes favoris',

  // b) F7 — Import CSV d'une classe d'élèves (backoffice Groupes)
  'importCsv.btn.open': 'Importer une classe (CSV)',
  'importCsv.modal.title': 'Importer une classe d’élèves (CSV)',
  'importCsv.btn.cancel': 'Annuler',
  'importCsv.btn.check': 'Vérifier (sans enregistrer)',
  'importCsv.btn.import':
    'Importer {count, plural, =0 {les élèves} one {# élève} other {# élèves}}',
  'importCsv.help.format':
    'Format attendu : une ligne par élève, colonnes « prénom, nom, classe, établissement » (la colonne « email » est facultative — souvent vide pour un mineur). Séparateur virgule ou point-virgule. Encodage UTF-8.',
  'importCsv.field.group': 'Classe (groupe) de destination',
  'importCsv.field.groupPlaceholder': '— Choisir une classe —',
  'importCsv.field.noGroups':
    'Aucun groupe disponible. Créez d’abord une classe (groupe).',
  'importCsv.field.file': 'Fichier CSV (ou collez le contenu ci-dessous)',
  'importCsv.field.paste': 'Contenu CSV',
  'importCsv.preview.summary':
    '{rows} ligne(s) valide(s) détectée(s), {errors} ligne(s) en erreur.',
  'importCsv.preview.errorLine': 'Ligne {line} ignorée : champ(s) manquant(s).',
  'importCsv.preview.errorMore': '… et {n} autre(s).',
  'importCsv.preview.tooMany':
    'Maximum {max} élèves par import — découpez le fichier.',
  'importCsv.error.fileRead': 'Impossible de lire le fichier sélectionné.',
  'importCsv.error.groupRequired':
    'Sélectionnez la classe (groupe) de destination.',
  'importCsv.error.rowsEmpty': 'Aucune ligne d’élève valide à importer.',
  'importCsv.toast.queued': 'Import mis en file (hors ligne).',
  'importCsv.toast.checked': 'Vérification terminée.',
  'importCsv.toast.imported':
    '{count, plural, =0 {Aucun élève importé} one {# élève importé} other {# élèves importés}}.',
  'importCsv.report.dryRunTitle': 'Vérification — aucun élève créé',
  'importCsv.report.doneTitle': 'Import terminé',
  'importCsv.report.counts':
    'Classe « {group} » — {created} créé(s), {skipped} ignoré(s).',
  'importCsv.report.errorLine': 'Ligne {line} : {reason}',
  // Clé pré-existante S26b sans définition (avait un defaultMessage) —
  // ajoutée proprement pendant la mise en conformité.
  // FIX 2026-05-18 (audit Cowork Bug #10) : « pousser » → « proposer ».
  'groupsAdmin.empty.hint':
    'Créez des groupes pour organiser visiteurs, exposants ou élèves (par classe, école, équipe) et leur proposer un programme dédié.',

  // c) F12 — Mon groupe
  'monGroupe.title': 'Mon groupe',
  'monGroupe.subtitle':
    'Le ou les groupes (classe, école, équipe…) auxquels vous êtes rattaché·e.',
  'monGroupe.loading': 'Chargement de votre groupe…',
  'monGroupe.needLogin': 'Connectez-vous pour voir votre groupe.',
  'monGroupe.loginCta': 'Se connecter',
  'monGroupe.error':
    'Impossible de charger votre groupe. Vérifiez votre connexion.',
  'monGroupe.empty.title': 'Vous n’êtes rattaché·e à aucun groupe',
  'monGroupe.empty.hint':
    'Si vous êtes venu·e en classe ou en équipe, votre référent vous rattachera à un groupe.',
  'monGroupe.members':
    '{count, plural, =0 {aucun membre} one {# membre} other {# membres}}',
  'monGroupe.members.aria':
    'Effectif du groupe : {count, plural, one {# membre} other {# membres}}',
  'monGroupe.joinedAt': 'Rattaché·e depuis le {date}',

  // d) §13.1 — Ma carte de contact (Attendee Connect)
  'maCarte.title': 'Ma carte de contact',
  'maCarte.subtitle':
    'Présentez ce QR pour qu’un autre participant garde le contact après le salon.',
  'maCarte.loading': 'Chargement de votre carte…',
  'maCarte.needLogin': 'Connectez-vous pour afficher votre carte de contact.',
  'maCarte.loginCta': 'Se connecter',
  'maCarte.error': 'Impossible de charger votre carte. Réessayez.',
  'maCarte.noPayload':
    'Votre carte n’est pas encore disponible. Revenez après votre inscription complète.',
  'maCarte.hint':
    'Vos coordonnées (e-mail, téléphone) ne sont jamais partagées : ce QR ne montre que votre prénom, votre rôle et votre structure.',
  'maCarte.minorNote':
    'Compte mineur : seul le prénom est affiché, conformément au RGPD.',

  // e) Fondation multi-événement — backoffice « Configuration de l’événement »
  'eventConfig.title': 'Configuration de l’événement',
  'eventConfig.subtitle':
    'Identité et apparence de l’événement actif. Modifiable à chaud — utilisé pour NOLAS aujourd’hui, un autre salon demain.',
  'eventConfig.loading': 'Chargement de la configuration…',
  'eventConfig.error':
    'Impossible de charger la configuration de l’événement.',
  'eventConfig.forbidden':
    'Seul un administrateur peut modifier l’événement.',
  'eventConfig.queued': 'Modification mise en file (hors ligne).',
  'eventConfig.saved': 'Configuration enregistrée.',
  'eventConfig.save': 'Enregistrer',
  'eventConfig.section.identity': 'Identité',
  'eventConfig.section.palette': 'Palette de marque',
  'eventConfig.f.name': 'Nom de l’événement',
  // LOT 7 — sélecteur de TYPE d'événement (pilote le vocabulaire {eventType}).
  'eventConfig.f.type': 'Type d’événement',
  'eventConfig.f.type.help':
    'Détermine le vocabulaire employé partout dans l’application (« au salon », « au séminaire », « aux portes ouvertes »…). Change uniquement les mots, pas les fonctionnalités.',
  'eventConfig.type.salon': 'Salon',
  'eventConfig.type.portesOuvertes': 'Portes ouvertes',
  'eventConfig.type.seminaire': 'Séminaire',
  'eventConfig.type.symposium': 'Symposium',
  'eventConfig.type.cycleConferences': 'Cycle de conférences',
  'eventConfig.type.biennale': 'Biennale',
  // CIRFA 2026 — 7e type. Libellé LONG au back-office (« Forum des métiers »)
  // pour lever l'ambiguïté avec un forum de discussion ; le nom nu injecté dans
  // les phrases reste court (« au forum »), cf. utils/formatEventType.ts.
  'eventConfig.type.forum': 'Forum des métiers',
  // ── LEXIQUE (lot CIRFA) — renommage des concepts par événement ─────────
  // Ces libellés décrivent le RÉGLAGE ; le vocabulaire lui-même est produit
  // par utils/eventLexicon.ts (formes fléchies), pas par ces chaînes.
  'eventConfig.section.lexicon': 'Vocabulaire',
  'eventConfig.lexicon.help':
    'Choisissez les mots employés partout dans l’application. Les accords, les articles et les élisions suivent automatiquement (« une activité », « aux activités », « de la session »). Ne change que les mots, jamais les fonctionnalités.',
  'eventConfig.lexicon.workshop': 'Ce que proposent les stands',
  'eventConfig.lexicon.exhibitor': 'Ceux qui tiennent un stand',
  'eventConfig.lexicon.visitor': 'Ceux qui viennent',
  'eventConfig.lexicon.auto': 'Automatique ({word})',
  'eventConfig.lexicon.preview': 'Exemple : « inscrivez-vous {sample} »',
  'eventConfig.lexicon.variant.atelier': 'Ateliers',
  'eventConfig.lexicon.variant.activite': 'Activités',
  'eventConfig.lexicon.variant.demonstration': 'Démonstrations',
  'eventConfig.lexicon.variant.session': 'Sessions',
  'eventConfig.lexicon.variant.conference': 'Conférences',
  'eventConfig.lexicon.variant.visite': 'Visites',
  'eventConfig.lexicon.variant.exposant': 'Exposants',
  'eventConfig.lexicon.variant.unite': 'Unités',
  'eventConfig.lexicon.variant.organisme': 'Organismes',
  'eventConfig.lexicon.variant.etablissement': 'Établissements',
  'eventConfig.lexicon.variant.entreprise': 'Entreprises',
  'eventConfig.lexicon.variant.partenaire': 'Partenaires',
  'eventConfig.lexicon.variant.visiteur': 'Visiteurs',
  'eventConfig.lexicon.variant.participant': 'Participants',
  'eventConfig.lexicon.variant.eleve': 'Élèves',
  'eventConfig.lexicon.variant.invite': 'Invités',
  'eventConfig.f.tagline': 'Accroche',
  'eventConfig.f.startsAt': 'Début',
  'eventConfig.f.endsAt': 'Fin',
  'eventConfig.f.venueName': 'Lieu',
  'eventConfig.f.venueAddress': 'Adresse',
  'eventConfig.f.organizer': 'Organisateur',
  // LOT 2 — préfixe de badge configurable (partie B). Le libellé d'aide cite
  // le format « NOLAS-00042 » (exemple technique) : clé répertoriée dans les
  // EXCEPTIONS_TECHNIQUES du garde-fou no-hardcoded-identity.test.ts.
  'eventConfig.f.badgePrefix': 'Préfixe des identifiants de badge',
  'eventConfig.f.badgePrefix.help':
    'Ex. « NOLAS-00042 ». Laissez vide pour dériver automatiquement du nom court de l’événement. Les badges déjà émis ne changent pas.',
  'eventConfig.palette.help':
    'Couleurs de marque au format #RRGGBB. Laissez vide pour conserver le thème par défaut ({eventName}).',
  'eventConfig.palette.invalid':
    'Une ou plusieurs couleurs sont invalides (#RRGGBB attendu).',

  // ───── Feature tuiles accueil + zone Organisateur (2026-05-18) ────────
  // Demande Mika : page d'accueil = 6 tuiles configurables au backoffice ;
  // zone « Organisateur » = clone fonctionnel de « Partenaires » (V29).
  // Cf. CLAUDE.md §5 2026-05-18. Français de France strict (§15.5).

  // Tuile « Organisateur » de /mon-espace
  'monEspace.tile.organisateur.title': 'Organisateur',
  'monEspace.tile.organisateur.aria':
    'Voir l’organisateur du salon et ses partenaires institutionnels',

  // Tuile « Mon profil » de /mon-espace (BUG P2 §9 — 2026-05-19).
  'monEspace.tile.profil.title': 'Mon profil',
  'monEspace.tile.profil.aria':
    'Consulter et modifier mon profil (prénom, nom, e-mail, structure)',

  // Page publique /organisateur
  'organisateur.title': 'L’organisateur',
  'organisateur.subtitle':
    'Salon NOLAS 2026 — organisé par eln',
  'organisateur.count':
    '{count, plural, =0 {Aucun organisateur} one {1 organisateur} other {{count} organisateurs}}',
  'organisateur.empty':
    'Aucun organisateur publié pour le moment.',
  'organisateur.disclaimer':
    'L’organisation du salon est assurée par eln.',
  'organisateur.card.visit': 'Site officiel',

  // Backoffice — CRUD zone Organisateur
  'organisateursAdmin.title': 'Organisateur',
  'organisateursAdmin.subtitle':
    'Logos et liens de l’organisateur du salon (zone distincte des partenaires).',
  'organisateursAdmin.entityLabel': 'organisateur',
  'organisateursAdmin.btn.create': 'Ajouter un organisateur',
  'organisateursAdmin.filters.aria': 'Filtres de la liste des organisateurs',
  'organisateursAdmin.filter.published': 'Statut de publication',
  'organisateursAdmin.filter.all': 'Tous',
  'organisateursAdmin.filter.publishedOnly': 'Publiés uniquement',
  'organisateursAdmin.filter.draftOnly': 'Brouillons uniquement',
  'organisateursAdmin.loading': 'Chargement des organisateurs…',
  'organisateursAdmin.empty':
    'Aucun organisateur enregistré pour le moment.',
  'organisateursAdmin.card.published': 'Publié',
  'organisateursAdmin.card.draft': 'Brouillon',
  'organisateursAdmin.btn.edit': 'Modifier',
  'organisateursAdmin.btn.delete': 'Supprimer',
  'organisateursAdmin.btn.cancel': 'Annuler',
  'organisateursAdmin.btn.save': 'Enregistrer',
  'organisateursAdmin.btn.createConfirm': 'Créer l’organisateur',
  'organisateursAdmin.modal.titleEdit': 'Modifier l’organisateur',
  'organisateursAdmin.modal.titleCreate': 'Nouvel organisateur',
  'organisateursAdmin.field.slug': 'Identifiant (slug)',
  'organisateursAdmin.field.slug.helper':
    'Lettres minuscules, chiffres et tirets uniquement. Exemple : eln-thionville.',
  'organisateursAdmin.field.name': 'Nom',
  'organisateursAdmin.field.shortDescription': 'Description courte',
  'organisateursAdmin.field.logoUrl': 'Logo',
  'organisateursAdmin.field.logoUrl.helper':
    'Téléversez une image (PNG, JPEG, WEBP, GIF ou SVG, 5 Mo max) ou saisissez le chemin d’un logo déjà déposé (ex. /logos/mon-logo.png).',

  // Composant réutilisable <LogoUploadField> (upload direct des logos au
  // back-office — demande Mika 2026-06-23). Cf. components/admin/LogoUploadField.tsx.
  'logoUpload.button': 'Téléverser un logo',
  'logoUpload.uploading': 'Envoi…',
  'logoUpload.previewAlt': 'Aperçu du logo',
  'logoUpload.toast.success': 'Logo téléversé.',
  'logoUpload.toast.error': 'Échec du téléversement du logo.',
  'logoUpload.error.type':
    'Format non autorisé. Formats acceptés : PNG, JPEG, WEBP, GIF ou SVG.',
  'logoUpload.error.size': 'Le fichier dépasse la taille maximale de 5 Mo.',
  'organisateursAdmin.field.websiteUrl': 'Site web',
  'organisateursAdmin.field.displayOrder': 'Ordre d’affichage',
  'organisateursAdmin.field.isPublished': 'Publié (visible du public)',
  'organisateursAdmin.confirm.delete':
    'Supprimer (dépublier) l’organisateur « {name} » ? Cette action peut être annulée en repassant le statut sur « Publié ».',
  'organisateursAdmin.toast.created': 'Organisateur créé.',
  'organisateursAdmin.toast.updated': 'Organisateur mis à jour.',
  'organisateursAdmin.toast.deleted': 'Organisateur masqué (dépublié).',
  'organisateursAdmin.toast.error':
    'Une erreur est survenue. Veuillez réessayer.',

  // Backoffice — choix des 6 tuiles de la page d'accueil
  'homeTiles.title': 'Tuiles de la page d’accueil',
  'homeTiles.subtitle':
    'Choisissez les 6 tuiles affichées sur la page d’accueil (Mon Espace) et leur ordre. Par défaut, la 5ᵉ tuile est « Organisateur ».',
  'homeTiles.loading': 'Chargement de la configuration…',
  'homeTiles.aria.list': 'Liste ordonnée des 6 tuiles de l’accueil',
  'homeTiles.slot.label': 'Tuile n° {position}',
  'homeTiles.warning.duplicate':
    'Deux emplacements pointent vers la même tuile. Choisissez 6 tuiles différentes avant d’enregistrer.',
  'homeTiles.hint':
    'Astuce : pour remplacer « Partenaires » par « Organisateur », sélectionnez « Organisateur » dans l’emplacement souhaité.',
  'homeTiles.btn.reset': 'Réinitialiser par défaut',
  'homeTiles.btn.save': 'Enregistrer',
  'homeTiles.toast.saved': 'Tuiles de l’accueil enregistrées.',
  'homeTiles.toast.error':
    'Une erreur est survenue. Veuillez réessayer.',

  // Navigation backoffice (AdminShell)
  'adminNav.organisateurs': 'Organisateur',
  'adminNav.homeTiles': 'Tuiles d’accueil',

  // ───── Inscription PAR SESSION (2026-05-18) ───────────────────────────
  // Demande Mika : « on s'inscrit sur 9h->17h, c'est faux ; il faut
  // choisir une session à chaque fois ». Français de France strict (§15.5).
  'atelier.session.choose': 'Choisissez votre session :',
  'atelier.session.required':
    'Veuillez choisir une session (créneau horaire) avant de vous inscrire.',
  'atelier.session.alreadyOther':
    'Vous êtes déjà inscrit·e à un autre créneau de {lexWorkshopDem}. Désinscrivez-vous d’abord pour en choisir un autre.',
  'atelier.session.conflict':
    'Cette session chevauche un autre créneau auquel vous êtes déjà inscrit·e.',
  'atelier.session.mine': 'Votre créneau',

  // Backoffice — créneaux types (WorkshopsAdminPage)
  'workshopsAdmin.field.timeSlots.presetsLabel':
    'Ajout rapide d’une session (créneaux types du salon, 9 h → 17 h) :',

  // ───── Invite d'installation PWA (2026-05-18) ─────────────────────────
  // Demande Mika : message d'installation Android/iOS/desktop.
  // `btn.installApp` (« Installer l'application ») existe déjà.
  'pwa.install.aria': 'Installer l’application {eventName}',
  'pwa.install.text':
    'Installez l’application {eventName} sur votre appareil pour un accès rapide, même hors-ligne.',
  'pwa.install.iosText':
    'Ajoutez l’application {eventName} à votre écran d’accueil pour un accès rapide, même hors-ligne.',
  'pwa.install.iosCta': 'Comment installer ?',
  'pwa.install.dismiss': 'Masquer le message d’installation',
  'pwa.install.accepted': 'Installation lancée. Merci !',
  'pwa.install.unavailable':
    'L’installation n’est pas disponible sur ce navigateur. Utilisez le menu du navigateur pour « Installer l’application ».',

  // ───── Sprint codes EXPO + module Contrôle (2026-05-19) ───────────────
  // Spec verrouillée CLAUDE.md §5 2026-05-19. Français de France strict.

  // Navigation backoffice
  'adminNav.exhibitorCodes': 'Codes exposant / organisateur',
  'adminNav.emailTemplates': 'Modèles d’e-mail',
  'adminNav.controlTypes': 'Types de contrôle',
  // FIX 2026-05-20 (Option A — flux complet invitation VIP §5/§24).
  'adminNav.vipInvitations': 'Invitations VIP',
  // FIX 2026-05-20 (Message 7 Mika) — templates de mail VIP éditables.
  'adminNav.vipEmailTemplates': 'Modèles de mail VIP',

  // Tuiles « Plus » conditionnées par rôle
  'plusPage.link.exhibitorScanner': 'Scanner les badges',
  'plusPage.link.controle': 'Contrôle / émargement',

  // Garde scanner exposant (rôle ≠ exposant)
  'exhibitorScanner.notExhibitor':
    'Cet espace est réservé aux exposants. Vous avez été redirigé vers votre espace.',

  // ── Backoffice — Codes EXPO (ExhibitorCodesAdminPage) ────────────────
  'exhibitorCodes.title': 'Codes exposant / organisateur',
  'exhibitorCodes.lead':
    'Générez des codes à transmettre aux exposants et à l’équipe organisatrice. Chaque code autorise une seule inscription et porte le rôle choisi.',
  'exhibitorCodes.btn.create': 'Générer des codes',
  'exhibitorCodes.kpi.total': 'Total',
  'exhibitorCodes.kpi.pending': 'En attente',
  'exhibitorCodes.kpi.used': 'Utilisés',
  'exhibitorCodes.kpi.revoked': 'Révoqués',
  'exhibitorCodes.loading': 'Chargement des codes…',
  'exhibitorCodes.error':
    'Impossible de charger les codes. Vérifiez votre session puis réessayez.',
  'exhibitorCodes.empty': 'Aucun code généré pour le moment.',

  // ── FIX 2026-05-20 (Option A §5/§24) — Page invitations VIP
  // Wording français de France strict §15.5. Mirror exhibitorCodes.*.
  'vipInvitations.title': 'Invitations VIP',
  'vipInvitations.lead':
    'Invitez les VIP (recteur, IA-IPR, élus, partenaires institutionnels). L’invité reçoit un e-mail avec un lien personnel qui active son badge VIP.',
  // KPI
  'vipInvitations.kpi.total': 'Total',
  'vipInvitations.kpi.pending': 'En attente',
  'vipInvitations.kpi.active': 'Actifs',
  'vipInvitations.kpi.expired': 'Liens expirés',
  'vipInvitations.kpi.blocked': 'Révoqués',
  // Form de création
  'vipInvitations.form.title': 'Nouvelle invitation',
  'vipInvitations.field.firstName': 'Prénom',
  'vipInvitations.field.lastName': 'Nom',
  'vipInvitations.field.email': 'Adresse e-mail',
  'vipInvitations.field.function': 'Fonction officielle',
  'vipInvitations.field.functionPlaceholder':
    'ex. maire, élu, directeur, responsable RH…',
  'vipInvitations.field.structure': 'Structure (optionnel)',
  'vipInvitations.field.structurePlaceholder':
    'ex. entreprise, collectivité, organisme de formation…',
  'vipInvitations.field.expiresDays': 'Validité du lien (jours, 1 à 90)',
  'vipInvitations.btn.create': 'Envoyer l’invitation',
  // Liste
  'vipInvitations.list.title': 'Invitations envoyées',
  'vipInvitations.list.expiresAt': 'Lien valable jusqu’au {date}',
  'vipInvitations.btn.resend': 'Renvoyer le lien',
  'vipInvitations.btn.revoke': 'Révoquer',
  'vipInvitations.confirm.revoke':
    'Révoquer cette invitation ? Le lien deviendra immédiatement inopérant. Cette action est réversible (création d’une nouvelle invitation).',
  'vipInvitations.confirm.revokeActive':
    'Ce compte VIP est ACTIF (la personne a déjà activé son badge). Révoquer va bloquer son accès. Continuer ?',
  // Status badges
  'vipInvitations.status.pending': 'En attente',
  'vipInvitations.status.active': 'Actif',
  'vipInvitations.status.expired': 'Expiré',
  'vipInvitations.status.blocked': 'Révoqué',
  'vipInvitations.status.archived': 'Archivé',
  // Modale post-création / post-renvoi
  'vipInvitations.modal.titleCreated': 'Invitation envoyée',
  'vipInvitations.modal.titleResent': 'Lien régénéré et renvoyé',
  'vipInvitations.modal.emailSent':
    'Un e-mail d’invitation a été envoyé à {email}.',
  'vipInvitations.modal.emailNotSent':
    'L’envoi de l’e-mail à {email} a échoué (à vérifier côté serveur). Vous pouvez copier le lien ci-dessous et le transmettre par un autre canal.',
  'vipInvitations.modal.urlHelp':
    'Lien personnel d’invitation (utilisable une seule fois pour activer le compte) :',
  'vipInvitations.btn.copy': 'Copier le lien',
  'vipInvitations.btn.close': 'Fermer',
  // États / toasts
  'vipInvitations.loading': 'Chargement des invitations…',
  'vipInvitations.error':
    'Impossible de charger les invitations. Vérifiez votre session puis réessayez.',
  'vipInvitations.empty': 'Aucune invitation VIP envoyée pour le moment.',
  'vipInvitations.toast.duplicate':
    'Un compte VIP existe déjà pour cet e-mail. Utilisez « Renvoyer le lien » dans la liste.',
  'vipInvitations.toast.error':
    'Une erreur est survenue. Réessayez ou contactez le support.',
  'vipInvitations.toast.revoked': 'Invitation révoquée.',
  'vipInvitations.toast.urlCopied': 'Lien copié dans le presse-papiers.',
  'vipInvitations.toast.copyError':
    'Copie automatique impossible. Sélectionnez le lien manuellement.',
  // FIX 2026-05-20 (Message 7 Mika — selector de template global) ──────
  'vipInvitations.field.template': 'Modèle de mail',
  'vipInvitations.template.default':
    'Modèle par défaut (paramétré par défaut au backoffice)',
  // FIX 2026-05-20 (Message 7 Mika — page CRUD templates) ──────────────
  'vipEmailTemplates.title': 'Modèles de mail d’invitation VIP',
  'vipEmailTemplates.lead':
    'Créez plusieurs modèles d’invitation (sobre, protocolaire, partenaire…) et choisissez celui à utiliser au moment de l’envoi (formulaire, renvoi, import CSV).',
  'vipEmailTemplates.btn.create': 'Nouveau modèle',
  'vipEmailTemplates.btn.edit': 'Éditer',
  'vipEmailTemplates.btn.preview': 'Aperçu',
  'vipEmailTemplates.btn.duplicate': 'Dupliquer',
  'vipEmailTemplates.btn.setDefault': 'Définir par défaut',
  'vipEmailTemplates.btn.delete': 'Supprimer',
  'vipEmailTemplates.btn.save': 'Enregistrer',
  'vipEmailTemplates.btn.cancel': 'Annuler',
  'vipEmailTemplates.btn.close': 'Fermer',
  'vipEmailTemplates.badge.default': 'Modèle par défaut',
  'vipEmailTemplates.loading': 'Chargement…',
  'vipEmailTemplates.error':
    'Impossible de charger les modèles de mail. Vérifiez votre session admin puis réessayez.',
  'vipEmailTemplates.empty':
    'Aucun modèle disponible. Créez-en un pour commencer.',
  'vipEmailTemplates.list.title':
    '{count, plural, =0 {Aucun modèle} one {1 modèle} other {# modèles}}',
  'vipEmailTemplates.confirm.delete':
    'Supprimer ce modèle ? Les futurs envois utiliseront le modèle par défaut.',
  'vipEmailTemplates.modal.createTitle': 'Nouveau modèle',
  'vipEmailTemplates.modal.editTitle': 'Modifier le modèle',
  'vipEmailTemplates.modal.previewTitle': 'Aperçu du modèle',
  'vipEmailTemplates.field.name': 'Libellé (affiché côté admin)',
  'vipEmailTemplates.field.slug':
    'Identifiant technique (3-80 caractères, minuscules / chiffres / - / _)',
  'vipEmailTemplates.field.subject': 'Sujet du mail',
  'vipEmailTemplates.field.bodyHtml':
    'Corps HTML (les variables {{...}} seront remplacées à l’envoi)',
  'vipEmailTemplates.field.bodyText':
    'Corps texte (fallback pour clients mail sans HTML)',
  'vipEmailTemplates.field.notes': 'Notes internes (optionnelles)',
  'vipEmailTemplates.variables.title': 'Variables disponibles',
  'vipEmailTemplates.variables.help':
    'Vous pouvez insérer ces variables dans le sujet et les corps. Elles seront remplacées par les vraies valeurs au moment de l’envoi.',
  'vipEmailTemplates.preview.help':
    'Aperçu avec des valeurs d’exemple (Marie Dupont, Rectrice…). Les vraies valeurs seront utilisées au moment de l’envoi.',
  'vipEmailTemplates.preview.subject': 'Sujet :',
  'vipEmailTemplates.preview.text': 'Aperçu version texte (fallback)',
  'vipEmailTemplates.toast.saved': 'Modèle enregistré.',
  'vipEmailTemplates.toast.deleted': 'Modèle supprimé.',
  'vipEmailTemplates.toast.duplicated': 'Modèle dupliqué.',
  'vipEmailTemplates.toast.defaultSet': 'Modèle défini par défaut.',
  'vipEmailTemplates.toast.error':
    'Une erreur est survenue. Réessayez ou contactez le support.',
  // FIX 2026-05-20 (Message 6 Mika — code 6 chiffres + import CSV) ─────
  'vipInvitations.modal.codeHelp':
    'Code à 6 chiffres alternatif (à transmettre au VIP si le lien ne fonctionne pas — il pourra s’inscrire directement sur /inscription/vip en saisissant son e-mail + ce code) :',
  'vipInvitations.btn.importCsv': 'Importer un fichier (CSV)',
  'vipInvitations.importCsv.title': 'Import en lot — invitations VIP',
  'vipInvitations.importCsv.help':
    'Importez une liste de personnes à inviter en une seule fois. Chaque ligne crée un compte VIP en attente et envoie un e-mail d’invitation (lien + code 6 chiffres).',
  'vipInvitations.importCsv.format':
    'Format attendu : fichier CSV (séparateur « ; » ou « , »), avec un en-tête de colonnes. Colonnes minimales : prenom, nom, email. Optionnelles : fonction, structure. Maximum 200 lignes par import.',
  'vipInvitations.importCsv.file': 'Fichier à importer (.csv)',
  'vipInvitations.importCsv.preview':
    '{count, plural, =0 {Aucune ligne exploitable détectée.} one {1 ligne détectée} other {# lignes détectées}} dans « {name} ».',
  'vipInvitations.importCsv.error.read':
    'Lecture du fichier impossible. Vérifiez qu’il s’agit bien d’un fichier texte.',
  'vipInvitations.importCsv.btn.send':
    '{count, plural, =0 {Envoyer les invitations} one {Envoyer (1 invitation)} other {Envoyer ({count} invitations)}}',
  'vipInvitations.importCsv.summary.title': 'Résumé de l’import',
  'vipInvitations.importCsv.summary.total':
    '{n, plural, =0 {Aucune ligne traitée} one {1 ligne traitée} other {# lignes traitées}}',
  'vipInvitations.importCsv.summary.created':
    '{n, plural, =0 {Aucune invitation créée} one {1 invitation créée} other {# invitations créées}}',
  'vipInvitations.importCsv.summary.skipped':
    '{n, plural, =0 {Aucun doublon ignoré} one {1 doublon ignoré (e-mail déjà inscrit)} other {# doublons ignorés (e-mails déjà inscrits)}}',
  'vipInvitations.importCsv.summary.errors':
    '{n, plural, =0 {Aucune erreur} one {1 ligne en erreur} other {# lignes en erreur}}',
  // Mini-formulaire « VIP par code » (InscriptionPage)
  'inscription.vip.code.title': 'Activer mon invitation VIP',
  'inscription.vip.code.lead':
    'Saisissez votre adresse e-mail et le code à 6 chiffres reçus dans votre invitation.',
  'inscription.vip.code.email.label': 'Adresse e-mail',
  'inscription.vip.code.email.placeholder': 'prenom.nom@exemple.fr',
  'inscription.vip.code.input.label': 'Code à 6 chiffres',
  'inscription.vip.code.input.help':
    'Ce code figure dans le mail d’invitation reçu, à côté du lien sécurisé.',
  'inscription.vip.code.submit': 'Activer mon badge VIP',
  'inscription.vip.code.fallback.help':
    'Vous pouvez aussi cliquer le lien sécurisé reçu dans votre mail d’invitation — les deux méthodes sont équivalentes.',
  'inscription.vip.code.back': 'Retour au choix du profil',
  'inscription.vip.code.success':
    'Bienvenue. Votre badge VIP est activé.',
  'inscription.vip.code.error.format':
    'Le code doit comporter exactement 6 chiffres.',
  'inscription.vip.code.error.emailRequired':
    'Adresse e-mail requise.',
  'inscription.vip.code.error.network':
    'Connexion au serveur impossible. Vérifiez votre réseau et réessayez.',
  'exhibitorCodes.role.exhibitor': 'Exposant',
  'exhibitorCodes.role.staff': 'Organisateur',
  'exhibitorCodes.status.pending': 'En attente',
  'exhibitorCodes.status.used': 'Utilisé',
  'exhibitorCodes.status.revoked': 'Révoqué',
  'exhibitorCodes.usedBy': 'Compte créé : {name} — {email}',
  'exhibitorCodes.confirm.revoke':
    'Révoquer ce code ? Il ne pourra plus servir à créer un compte.',
  'exhibitorCodes.btn.revoke': 'Révoquer',
  'exhibitorCodes.modal.title': 'Générer des codes d’accès',
  'exhibitorCodes.field.role': 'Rôle attribué par le code',
  'exhibitorCodes.field.quantity': 'Nombre de codes (1 à 200)',
  'exhibitorCodes.field.label': 'Libellé (note interne, facultatif)',
  'exhibitorCodes.field.label.placeholder':
    'Exemple : stand Réseau Canopé, équipe vestiaire…',
  'exhibitorCodes.btn.cancel': 'Annuler',
  'exhibitorCodes.btn.generate': 'Générer',
  'exhibitorCodes.btn.done': 'J’ai noté les codes',
  'exhibitorCodes.generated.intro':
    'Codes générés. Transmettez-les maintenant aux personnes concernées :',
  'exhibitorCodes.generated.warn':
    'Ces codes ne seront plus affichés en clair après la fermeture de cette fenêtre.',
  'exhibitorCodes.toast.error':
    'Une erreur est survenue. Réessayez dans un instant.',
  'exhibitorCodes.toast.revoked': 'Code révoqué.',
  'exhibitorCodes.toast.alreadyUsed':
    'Ce code a déjà été utilisé : il ne peut plus être révoqué.',

  // ── FIX 2026-05-21 Feature D (export PDF publipostage — Mika) ───────
  'exhibitorCodes.btn.exportPdf':
    'Exporter en PDF ({n} sélectionné{plural})',
  'exhibitorCodes.btn.exportBatch':
    'Télécharger le lot en PDF ({n})',
  'exhibitorCodes.selectAll':
    'Sélectionner tous les codes « en attente » ({n})',
  'exhibitorCodes.selectAll.aria':
    'Sélectionner tous les codes en attente',
  'exhibitorCodes.select.aria': 'Sélectionner le code {code}',
  'exhibitorCodes.exportPdf.success':
    'Document prêt — utilisez Cmd+P / Imprimer pour enregistrer en PDF.',
  'exhibitorCodes.exportPdf.error':
    'Échec de l’export PDF : {msg}',
  'exhibitorCodes.exportPdf.errorEmpty':
    'Sélectionnez au moins un code à exporter.',

  // ── Backoffice — Types de contrôle (ControlTypesAdminPage) ───────────
  'controlTypes.title': 'Types de contrôle',
  'controlTypes.lead':
    'Configurez les points de contrôle de l’émargement : entrée et sortie du salon, repas, dépôt au vestiaire. Le vestiaire attribue un numéro de casier.',
  'controlTypes.btn.create': 'Ajouter un type',
  'controlTypes.loading': 'Chargement des types de contrôle…',
  'controlTypes.error':
    'Impossible de charger les types de contrôle. Réessayez.',
  'controlTypes.empty': 'Aucun type de contrôle pour le moment.',
  'controlTypes.active': 'Actif',
  'controlTypes.inactive': 'Inactif',
  'controlTypes.locker.tag': 'Attribue un casier',
  'controlTypes.btn.up': 'Monter dans la liste',
  'controlTypes.btn.down': 'Descendre dans la liste',
  'controlTypes.btn.edit': 'Modifier',
  'controlTypes.btn.delete': 'Supprimer',
  'controlTypes.confirm.delete':
    'Supprimer le type de contrôle « {label} » ?',
  'controlTypes.modal.create': 'Nouveau type de contrôle',
  'controlTypes.modal.edit': 'Modifier le type de contrôle',
  'controlTypes.field.slug': 'Identifiant (slug, non modifiable ensuite)',
  'controlTypes.field.label': 'Libellé',
  'controlTypes.field.label.placeholder':
    'Exemple : Entrée au salon, Repas 1…',
  'controlTypes.field.generatesLocker':
    'Attribuer un numéro de casier (vestiaire)',
  'controlTypes.field.isActive': 'Actif (proposé aux organisateurs)',
  'controlTypes.btn.cancel': 'Annuler',
  'controlTypes.btn.save': 'Enregistrer',
  'controlTypes.toast.created': 'Type de contrôle créé.',
  'controlTypes.toast.updated': 'Type de contrôle mis à jour.',
  'controlTypes.toast.deleted': 'Type de contrôle supprimé.',
  'controlTypes.toast.duplicate':
    'Un type de contrôle avec cet identifiant existe déjà.',
  'controlTypes.toast.hasScans':
    'Ce type a déjà des émargements : désactivez-le plutôt que de le supprimer.',
  'controlTypes.toast.error':
    'Une erreur est survenue. Réessayez dans un instant.',

  // ── Page organisateur — Contrôle / émargement (ControlePage) ─────────
  'controle.checking': 'Vérification de votre session…',
  'controle.title': 'Contrôle / émargement',
  'controle.notOrganiser': 'Accès réservé à l’équipe organisatrice.',
  'controle.typesError':
    'Impossible de charger les types de contrôle. Réessayez.',
  'controle.selectLabel': 'Quel contrôle effectuez-vous ?',
  'controle.loading': 'Chargement…',
  'controle.noTypes': 'Aucun type de contrôle configuré.',
  'controle.selectPlaceholder': 'Choisir un type de contrôle…',
  'controle.locker.tag': 'casier',
  'controle.noType': 'Choisissez d’abord le type de contrôle.',
  'controle.offlineQueued':
    'Hors ligne : l’émargement sera synchronisé au retour du réseau.',
  'controle.sessionCount': 'Cette session',
  'controle.activeType': 'Contrôle en cours : {label}',
  'controle.verdictTitle': 'Émargement',
  'controle.alreadySeen': 'Déjà enregistré',
  'controle.recorded': 'Émargement enregistré',
  'controle.lockerNumber': 'Casier n° {n}',
  'controle.next': 'Suivant',
  'controle.error.visitorNotFound':
    'Participant introuvable. Code QR invalide ou compte désactivé.',
  'controle.error.rateLimited':
    'Trop de scans en peu de temps. Patientez quelques secondes.',
  'controle.error.generic': 'Une erreur est survenue. Réessayez.',
  // FIX 2026-05-19 (BUG /controle scan iOS) — message clair quand le
  // fetch échoue vraiment (offline:false → pas de queue, verdict immédiat
  // requis). Mirror d'`exhibitorScanner.access.offline`.
  'controle.error.offline':
    'Connexion réseau indisponible. Le contrôle exige un verdict immédiat — vérifiez votre Wi-Fi puis réessayez.',

  // ── Inscription gatée par code EXPO (InscriptionPage) ────────────────
  'inscription.withCode.success':
    'Compte créé. Connectez-vous avec votre adresse e-mail pour récupérer votre badge.',
  'inscription.withCode.error.generic':
    'L’inscription a échoué. Vérifiez votre code et réessayez.',
  'inscription.withCode.error.unknown':
    'Code d’accès inconnu. Vérifiez le code communiqué par l’organisation.',
  'inscription.withCode.error.used':
    'Ce code a déjà été utilisé. Si vous avez déjà créé votre compte, connectez-vous avec votre e-mail.',
  'inscription.withCode.error.revoked':
    'Ce code a été révoqué par l’organisation. Demandez un nouveau code.',

  // ───── Mon profil + Admin comptes inscrits (2026-05-19) ───────────────
  // Demande Mika : auto-correction du profil après connexion + liste
  // admin de tous les comptes par rôle/type avec révocation d'accès.

  // Libellés de rôle (réutilisés MonProfilPage + UsersListAdminPage)
  'profil.role.visitor': 'Visiteur',
  'profil.role.exhibitor': 'Exposant',
  'profil.role.vip': 'VIP',
  'profil.role.specific_public': 'Public spécifique',
  'profil.role.student': 'Élève',
  'profil.role.child': 'Enfant',
  'profil.role.staff': 'Organisateur',
  'profil.role.admin': 'Administrateur',

  // Page « Mon profil »
  'plusPage.link.profile': 'Mon profil',
  'profil.title': 'Mon profil',
  'profil.intro':
    'Complétez ou corrigez vos informations. Elles servent à l’organisation du salon.',
  'profil.optional': '(facultatif)',
  'profil.loading': 'Chargement de votre profil…',
  'profil.error.load':
    'Impossible de charger votre profil. Réessayez dans un instant.',
  'profil.field.firstName': 'Prénom',
  'profil.field.lastName': 'Nom',
  'profil.field.email': 'Adresse e-mail',
  'profil.field.email.help':
    'Sert à vous reconnecter (un code à 6 chiffres vous est envoyé).',
  'profil.field.email.minor':
    'Adresse non modifiable pour un compte mineur (gérée par l’organisation).',
  'profil.field.phone': 'Téléphone',
  'profil.field.structure': 'Structure / organisme',
  'profil.field.functionTitle': 'Fonction',
  'profil.save': 'Enregistrer',
  'profil.saved': 'Profil mis à jour.',
  'profil.error.nameRequired': 'Le prénom et le nom sont obligatoires.',
  'profil.error.validation':
    'Certaines informations sont invalides. Vérifiez les champs.',
  'profil.error.session':
    'Votre session a expiré. Reconnectez-vous pour enregistrer.',
  'profil.error.generic':
    'Une erreur est survenue. Réessayez dans un instant.',

  // Admin — Comptes inscrits (UsersListAdminPage)
  'adminNav.usersList': 'Comptes inscrits',
  'adminNav.visitorBadges': 'Badges en lot',
  // FIX 2026-05-26 (Mika valorisation pro) — export CSV consolidé.
  'adminNav.attendanceExport': 'Export participations (carrière)',
  'attendanceExport.title': 'Export consolidé des participations',
  'attendanceExport.subtitle':
    'Téléchargez en un fichier CSV unique l’ensemble des participations ' +
    '(passages stand, émargements contrôle, inscriptions {lexWorkshopPlural} ' +
    'confirmées) de TOUS les inscrits, pour produire les attestations ' +
    'de participation et alimenter les dossiers de carrière.',
  'attendanceExport.intro':
    'Cet export combine trois sources de vérité : les passages sur les ' +
    'stands (scan exposant), les émargements de contrôle (entrée, ' +
    'sortie, repas, vestiaire) et les inscriptions {lexWorkshopAtPlural} ' +
    'confirmées. Une ligne par participation, triée chronologiquement. ' +
    'Robuste : si une source de données est temporairement indisponible, ' +
    'les autres restent exportées (aucune perte silencieuse).',
  'attendanceExport.field.dateFrom': 'Date de début (inclusive)',
  'attendanceExport.field.dateTo':   'Date de fin (inclusive)',
  'attendanceExport.field.role':     'Rôle du participant',
  'attendanceExport.role.all':       'Tous les rôles',
  'attendanceExport.toggle.includeMinors':
    'Inclure les participants mineurs (élèves, enfants accompagnés).',
  'attendanceExport.toggle.includeTest':
    'Inclure les comptes plastron / démonstration (par défaut exclus).',
  'attendanceExport.btn.download': 'Télécharger l’export CSV',
  'attendanceExport.columns.title': 'Colonnes incluses dans le fichier',
  'attendanceExport.columns.list':
    'user_id ; email ; nom_complet ; role ; structure ; fonction ; ' +
    'mineur (oui/non) ; type_evenement (stand/controle/{lexWorkshopBare}) ; ' +
    'sous_type ; libelle ; date ; heure ; lieu ; source_table ; ' +
    'scanner_email ; scanner_nom ; record_id',
  'attendanceExport.note.rgpd':
    'Données nominatives en clair (e-mail, nom complet) destinées aux ' +
    'attestations professionnelles. Conservez le fichier dans un ' +
    'espace sécurisé. RGPD article 6 — finalité administrative ' +
    'légitime (valorisation de la participation). Chaque export ' +
    'est tracé dans le journal d’audit.',
  'usersList.title': 'Comptes inscrits',
  'usersList.lead':
    'Tous les comptes par type. Vous pouvez révoquer un accès (le compte est immédiatement bloqué) ou le rétablir.',
  // ── « Voir en tant que » (B1, 2026-08-25) ───────────────────────────────
  // Vérifier ce qu'un compte voit RÉELLEMENT, au lieu de le déduire d'une
  // table de permissions. Le serveur impose la lecture seule et 1 h maximum.
  'impersonation.banner.text':
    'Vous naviguez en tant que {who}',
  'impersonation.banner.readOnly': 'lecture seule, aucune modification possible',
  // 2026-08-26 — l'ecriture est desormais autorisee : le bandeau doit le
  // dire, ET rappeler la contrepartie (tout est attribue a l'auteur reel).
  'impersonation.banner.tracked':
    'vos actions sont possibles et enregistrées à VOTRE nom',
  'impersonation.banner.remaining': 'fin automatique dans {time}',
  // Boutons de deplacement pendant l'emprunt (demande Mika 26/08) : sans eux,
  // on atterrit sur un ecran qui refuse et il faut deviner l'adresse.
  'impersonation.banner.goSpace': 'Son espace',
  'impersonation.banner.goBackoffice': 'Son back-office',
  'impersonation.banner.stop': 'Revenir à mon compte',
  'impersonation.action.view': 'Voir en tant que',
  'impersonation.action.viewAria': 'Voir l’application en tant que {name}',
  'impersonation.confirm.title': 'Voir en tant que {name} ?',
  'impersonation.confirm.body':
    'Vous verrez l’application exactement comme ce compte la voit. Tout est en lecture seule, et vous reviendrez à votre compte par le bandeau, ou automatiquement au bout d’une heure.',
  'impersonation.confirm.cancel': 'Annuler',
  'impersonation.confirm.start': 'Voir en tant que',
  'impersonation.error.start':
    'Impossible de démarrer. Ce compte n’est peut-être pas usurpable (un autre super-administrateur, par exemple).',
  'usersList.type.workshopHost': 'Animateur ({lexWorkshopBare})',
  'usersList.badge.workshopHost':
    '{n, plural, one {# {lexWorkshopBare} animé{lexWorkshopAgr}} other {# {lexWorkshopDefPlural} animé{lexWorkshopAgrPlural}}}',
  'usersList.loading': 'Chargement des comptes…',
  'usersList.error':
    'Impossible de charger les comptes. Vérifiez votre session puis réessayez.',
  'usersList.empty': 'Aucun compte ne correspond à ces critères.',
  'usersList.filter.role': 'Type',
  'usersList.filter.allRoles': 'Tous les types',
  'usersList.filter.status': 'Statut',
  'usersList.filter.allStatuses': 'Tous les statuts',
  'usersList.filter.search': 'Recherche',
  'usersList.filter.searchPlaceholder': 'Nom, e-mail, structure…',
  'usersList.status.active': 'Actif',
  'usersList.status.blocked': 'Révoqué',
  'usersList.status.pending': 'En attente',
  'usersList.status.archived': 'Archivé',
  'usersList.btn.revoke': 'Révoquer l’accès',
  'usersList.btn.restore': 'Rétablir l’accès',
  'usersList.confirm.revoke':
    'Révoquer l’accès de {name} ? Son compte sera immédiatement bloqué (réversible).',
  'usersList.toast.revoked': 'Accès révoqué.',
  'usersList.toast.restored': 'Accès rétabli.',
  'usersList.toast.protected':
    'Action refusée : compte protégé (administrateur ou votre propre compte).',
  'usersList.toast.error':
    'Une erreur est survenue. Réessayez dans un instant.',

  // ───── Journal d’activité (organisateurs + exposants) — 2026-05-19 ─────
  'activityLog.title': 'Journal d’activité',
  'activityLog.lead':
    'Connexions et actions des organisateurs et des exposants, pour le suivi le jour de l’événement.',
  'activityLog.kpi.staff': 'Actions organisateurs',
  'activityLog.kpi.exhibitor': 'Actions exposants',
  'activityLog.family.all': 'Toutes les actions',
  'activityLog.family.connexion': 'Connexions',
  'activityLog.family.scan': 'Scans',
  'activityLog.family.controle': 'Contrôle / émargement',
  'activityLog.family.acces_atelier': 'Accès {lexWorkshopBare}',
  'activityLog.family.inscription': 'Inscriptions par code',
  'activityLog.who.all': 'Organisateurs et exposants',
  'activityLog.who.staff': 'Organisateurs',
  'activityLog.who.exhibitor': 'Exposants',
  'activityLog.role.staff': 'Organisateur',
  'activityLog.role.exhibitor': 'Exposant',
  'activityLog.filter.who': 'Qui',
  'activityLog.filter.family': 'Type d’action',
  'activityLog.filter.actor': 'Rechercher une personne',
  'activityLog.filter.actorPlaceholder': 'Nom, prénom ou e-mail…',
  'activityLog.filter.dateFrom': 'À partir du',
  'activityLog.filter.dateTo': 'Jusqu’au',
  'activityLog.btn.export': 'Exporter en CSV',
  'activityLog.loading': 'Chargement du journal…',
  'activityLog.error':
    'Le serveur n’a pas pu charger le journal. Réessayez dans un instant.',
  'activityLog.empty':
    'Aucune activité ne correspond à ces filtres pour le moment.',

  // ──────────────────────────────────────────────────────────────────
  // FIX 2026-05-20 (BUG VIP + audit register §5/§24) — JOURNAL DES
  // ÉCHECS D'INSCRIPTION /admin/registration-errors.
  // Wording français de France strict §15.5. Mirror activityLog.*.
  // ──────────────────────────────────────────────────────────────────
  'registrationErrors.title': 'Erreurs d’inscription',
  'registrationErrors.lead':
    'Échecs récents des inscriptions (validation, e-mail déjà utilisé, lien VIP expiré, code EXPO inconnu, etc.). Utile pour repérer les soucis le jour J et accompagner les utilisateurs.',
  // KPI total + par rôle.
  'registrationErrors.kpi.total': 'Total des échecs',
  'registrationErrors.role.visitor': 'Visiteur',
  'registrationErrors.role.exhibitor': 'Exposant',
  'registrationErrors.role.vip': 'VIP',
  'registrationErrors.role.student': 'Élève',
  'registrationErrors.role.child': 'Enfant',
  'registrationErrors.role.specific_public': 'Public spécifique',
  'registrationErrors.role.with_code': 'Code EXPO',
  'registrationErrors.role.unknown': 'Inconnu',
  // Options du filtre rôle.
  'registrationErrors.roleOption.all': 'Tous les rôles',
  'registrationErrors.roleOption.visitor': 'Visiteur',
  'registrationErrors.roleOption.exhibitor': 'Exposant',
  'registrationErrors.roleOption.vip': 'VIP',
  'registrationErrors.roleOption.student': 'Élève',
  'registrationErrors.roleOption.child': 'Enfant',
  'registrationErrors.roleOption.specific_public':
    'Public spécifique (presse / formateur / intervenant…)',
  'registrationErrors.roleOption.with_code': 'Code EXPO (exposant / orga)',
  // Filtres.
  'registrationErrors.filter.role': 'Rôle',
  'registrationErrors.filter.reason': 'Motif',
  'registrationErrors.filter.reasonPlaceholder':
    'ex. validation, email_already_used, token_expired',
  'registrationErrors.filter.dateFrom': 'À partir du',
  'registrationErrors.filter.dateTo': 'Jusqu’au',
  // Composition de la ligne de résumé.
  'registrationErrors.reasonLabel': 'Motif : {reason}',
  'registrationErrors.fieldsLabel': 'Champs : {fields}',
  // États.
  'registrationErrors.btn.export': 'Exporter en CSV',
  'registrationErrors.loading': 'Chargement du journal…',
  'registrationErrors.error':
    'Le serveur n’a pas pu charger le journal. Réessayez dans un instant.',
  'registrationErrors.empty':
    'Aucune erreur d’inscription ne correspond à ces filtres pour le moment.',

  // ────────────────────────────────────────────────────────────────────
  // FIX 2026-05-20 (compte plastron de démo) — CLAUDE.md §5/§24 2026-05-20.
  // Wording FR strict §15.5 (français de France, pas d'anglicisme, pas
  // d'abréviation anglo, ponctuation française).
  // ────────────────────────────────────────────────────────────────────

  // Rôle backoffice — affiché dans /admin/users-admin (cohérent ROLES).
  'usersAdmin.role.plastron': 'Plastron (démo)',

  // Champ password — visible UNIQUEMENT pour le rôle plastron lors
  // de la création d'un compte dans /admin/users-admin.
  'usersAdmin.field.password': 'Mot de passe initial',
  'usersAdmin.field.password.help':
    '8 caractères minimum, au moins une lettre et un chiffre. ' +
    'À communiquer au destinataire par un canal sûr (jamais en clair par e-mail).',
  'usersAdmin.field.password.show': 'Afficher le mot de passe',
  'usersAdmin.field.password.hide': 'Masquer le mot de passe',
  'usersAdmin.field.password.invalid':
    'Le mot de passe doit faire au moins 8 caractères et contenir au moins ' +
    'une lettre et un chiffre.',

  // Bouton submit alternatif pour le rôle plastron (workflow distinct).
  'usersAdmin.invite.submitPlastron': 'Créer le compte de démonstration',

  // Réponse post-création (workflow plastron, pas d'invitation HMAC).
  'usersAdmin.invite.createdPlastron':
    'Compte plastron créé pour {email}. Il est actif immédiatement.',
  'usersAdmin.invite.plastronHelp':
    'Communiquez l’adresse e-mail et le mot de passe au destinataire ' +
    'par un canal sûr (oralement, messagerie sécurisée). Le compte se ' +
    'connecte ensuite via /connexion en saisissant son e-mail puis ' +
    'son mot de passe (pas de 2FA).',

  // FIX 2026-05-25 — Modale « Reset mot de passe plastron » (Mika signale
  // l'absence de bouton dans /admin/users-admin ; le backend
  // handle_update_admin_user SUPPORTE déjà ce reset depuis 2026-05-20).
  'usersAdmin.action.resetPlastronPassword': 'Réinitialiser le mot de passe',
  // 2026-08-26 — definition du mot de passe, ouverte a tous les comptes.
  'usersAdmin.action.setPassword': 'Mot de passe',
  'usersAdmin.action.changeEmail': 'Adresse e-mail',
  'usersAdmin.changeEmail.title': 'Adresse e-mail de {name}',
  'usersAdmin.changeEmail.intro':
    'C’est l’identifiant de connexion de ce compte. Une adresse déjà utilisée par un autre compte sera refusée.',
  'usersAdmin.changeEmail.invalid': 'Adresse e-mail invalide.',
  'usersAdmin.changeEmail.submit': 'Enregistrer l’adresse',
  'usersAdmin.changeEmail.success': 'Adresse e-mail modifiée.',
  'usersAdmin.changeEmail.error': 'Modification impossible.',
  'usersAdmin.setPassword.sendEmail':
    'Envoyer ce mot de passe par e-mail à {email}',
  'usersAdmin.resetPassword.title':
    'Réinitialiser le mot de passe — {name}',
  'usersAdmin.resetPassword.intro':
    'Définissez un nouveau mot de passe pour le compte plastron de {email}. ' +
    'Il remplace immédiatement l’ancien. Transmettez-le au destinataire par ' +
    'un canal sûr (oralement, messagerie sécurisée).',
  'usersAdmin.resetPassword.submit': 'Enregistrer le nouveau mot de passe',
  'usersAdmin.resetPassword.success': 'Mot de passe mis à jour.',
  'usersAdmin.resetPassword.error':
    'La mise à jour du mot de passe a échoué. Réessayez ou contactez le ' +
    'support technique.',

  // FIX 2026-05-26 — suppression définitive (hard delete) d'un compte
  // admin. Distinct de « Suspendre » (soft delete réversible). Backend
  // handle_hard_delete_admin_user dans users-admin.php.
  'usersAdmin.action.hardDelete': 'Supprimer définitivement',
  'usersAdmin.hardDelete.title': 'Supprimer définitivement — {name}',
  'usersAdmin.hardDelete.warningTitle':
    'Action irréversible',
  'usersAdmin.hardDelete.warningBody':
    'Vous êtes sur le point de supprimer DÉFINITIVEMENT le compte ' +
    '{email} (rôle : {role}). Cette opération ne peut pas être annulée.',
  'usersAdmin.hardDelete.item.irreversible':
    'La ligne sera effacée de la base — le compte disparaît de la ' +
    'liste et ne peut plus se reconnecter.',
  'usersAdmin.hardDelete.item.audit':
    'L\'historique d\'audit (qui a fait quoi) est conservé : un snapshot ' +
    'du compte est enregistré avant suppression à des fins de ' +
    'traçabilité RGPD.',
  'usersAdmin.hardDelete.item.fallback':
    'Si le compte a déjà produit des données protégées (connexions ' +
    'tracées par exemple), la suppression sera refusée par sécurité ; ' +
    'utilisez alors « Suspendre » à la place.',
  'usersAdmin.hardDelete.checkbox':
    'Je confirme avoir compris que cette suppression est définitive.',
  'usersAdmin.hardDelete.typeLabel':
    'Pour valider, saisissez le mot SUPPRIMER en majuscules :',
  'usersAdmin.hardDelete.submit': 'Supprimer définitivement',
  'usersAdmin.hardDelete.success': 'Compte supprimé définitivement.',
  'usersAdmin.hardDelete.error':
    'La suppression a échoué. Réessayez ou utilisez « Suspendre » à ' +
    'la place si le problème persiste.',

  // Modale pédagogique (PlastronGuardModal).
  'plastron.modal.title': 'Compte plastron de démonstration',
  'plastron.modal.bodyGeneric':
    'Vous utilisez un compte de démonstration en lecture seule. Cette ' +
    'action est désactivée. Pour vous inscrire et profiter pleinement ' +
    'du salon NOLAS 2026, créez un vrai compte.',
  'plastron.modal.bodyWithReason':
    'Vous utilisez un compte de démonstration en lecture seule. ' +
    'L’action « {reason} » est désactivée. Pour vous inscrire et ' +
    'profiter pleinement du salon NOLAS 2026, créez un vrai compte.',
  'plastron.modal.cta.help':
    'En mode démonstration, vous pouvez consulter le programme, les ' +
    'partenaires, les médias et la carte du salon. Les inscriptions, ' +
    'favoris et modifications de profil ne sont pas enregistrés.',
  'plastron.modal.btn.continueDemo': 'Continuer en démonstration',
  'plastron.modal.btn.createReal': 'Se créer un vrai compte',

  // Libellés des actions sensibles interceptées (insérés dans la
  // raison de la modale via {reason}).
  'plastron.action.signupWorkshop': 'S’inscrire à {lexWorkshopIndef}',
  'plastron.action.cancelWorkshop': 'Se désinscrire d’{lexWorkshopIndef}',
  'plastron.action.saveProfile': 'Enregistrer les modifications du profil',
  'plastron.action.favoriteToggle': 'Ajouter ou retirer un favori',

  // Bannière en haut de /mon-badge mode démo.
  'plastron.badge.banner':
    'Aperçu de démonstration : un vrai visiteur verra ici son badge ' +
    'personnel scannable par les exposants.',

  // Étape password de /connexion (compte plastron uniquement).
  'plastron.login.title': 'Connexion à la démonstration',
  'plastron.login.subtitle':
    'Saisissez le mot de passe communiqué par l’organisateur du salon.',
  'plastron.login.helper':
    'Le compte de démonstration utilise un mot de passe (pas de code ' +
    'par e-mail). Le mot de passe vous a été remis par l’organisateur.',
  'plastron.login.passwordLabel': 'Mot de passe',
  'plastron.login.passwordPlaceholder': 'Votre mot de passe de démonstration',
  'plastron.login.showPassword': 'Afficher',
  'plastron.login.hidePassword': 'Masquer',
  'plastron.login.submit': 'Accéder à la démonstration',
  'plastron.login.error.invalid':
    'Identifiants invalides. Vérifiez votre adresse e-mail et votre ' +
    'mot de passe.',

  // ────────────────────────────────────────────────────────────────────
  // FIX 2026-05-20 #3 (Mika — message push plein écran au login démo).
  // Modale d'accueil PlastronWelcomeModal affichée UNE FOIS par session
  // au login d'un compte plastron. Recommande d'utiliser un vrai compte.
  // ────────────────────────────────────────────────────────────────────

  'plastron.welcome.title': 'Bienvenue sur la démonstration {eventName}',
  'plastron.welcome.banner':
    '⚠ Vous utilisez un compte de DÉMONSTRATION en lecture seule.',
  'plastron.welcome.body1':
    'Ce compte vous permet de découvrir l’application NOLAS 2026 comme un ' +
    'visiteur du salon (programme, partenaires, plan, médias, fiches ' +
    '{lexWorkshopOfPluralBare}). Vous pouvez naviguer librement dans toute l’app.',
  'plastron.welcome.body2':
    'En revanche, les inscriptions {lexWorkshopAtPlural}, les favoris et les ' +
    'modifications de profil NE SONT PAS enregistrées. Toute tentative ' +
    'sera signalée par un message pédagogique.',
  'plastron.welcome.recommendation.title':
    'Recommandation : utilisez votre compte personnel',
  'plastron.welcome.recommendation.body':
    'Pour profiter pleinement du salon du 9 octobre 2026 — vous inscrire ' +
    '{lexWorkshopAtPlural}, recevoir votre badge personnel, gérer vos favoris — ' +
    'il est FORTEMENT RECOMMANDÉ de vous créer un vrai compte ' +
    'personnel ou de vous connecter avec votre compte existant.',
  'plastron.welcome.btn.continueDemo': 'Continuer en démonstration',
  'plastron.welcome.btn.createReal': 'Se créer un vrai compte',

  // ────────────────────────────────────────────────────────────────────
  // FIX 2026-05-20 #2 (Mika — selector template VIP ne s'affiche pas) :
  // avertissement explicite sur /admin/vip-invitations si les
  // templates ne sont pas chargeables (cause probable : V37 SQL pas
  // importée). Évite la cause silencieuse.
  // ────────────────────────────────────────────────────────────────────

  'vipInvitations.templates.warning.title':
    'Aucun modèle de mail disponible',
  'vipInvitations.templates.warning.error':
    'Les modèles de mail ne peuvent pas être chargés. ' +
    'Vérifiez que la migration SQL V37 a bien été importée en base.',
  'vipInvitations.templates.warning.empty':
    'Aucun modèle de mail n’est encore créé. Sans modèle, les ' +
    'invitations utiliseront le wording par défaut.',
  'vipInvitations.templates.warning.cta':
    'Créer un modèle →',

  // ──────────────────────────────────────────────────────────────────────
  // Sprint 2026-05-20 — Plan Interactif Niveau 3 wow.
  // Refonte CartePage + StandsAdmin + nouvelle entrée adminNav.
  // ──────────────────────────────────────────────────────────────────────

  // CartePage (plan visiteur)
  'plan.title': 'Plan du salon',
  // FIX 2026-05-26 — Mode simple (carte.simple_mode V50) plan B safe jour J.
  // Cf. CLAUDE.md §5 2026-05-26.
  'plan.simple.title': 'Plan du salon — Mode simplifié',
  'plan.simple.banner':
    'Mode plan simplifié activé. Pan / zoom uniquement, sans interactivité. ' +
    'Pour repasser en mode interactif complet : /admin/settings → Plan / Carte.',
  'plan.before.title': 'Plan du salon',
  'plan.before.body':
    'Le plan interactif du {eventPlace} sera disponible ' +
    'le {eventDate} à 9 h.',
  'plan.loading': 'Chargement du plan…',
  'plan.loadError':
    'Impossible de charger le plan. Vérifiez votre connexion.',
  'plan.canvas.aria': 'Plan interactif du {eventPlace}',
  'plan.search.placeholder': 'Rechercher un stand, un partenaire…',
  'plan.search.aria': 'Rechercher dans le plan',
  'plan.filters.worlds': 'Filtres par monde thématique',
  'plan.filter.favorites': 'Mes favoris',
  'plan.btn.zoomIn': 'Zoomer',
  'plan.btn.zoomOut': 'Dézoomer',
  'plan.btn.reset': 'Recentrer le plan',
  'plan.btn.fullscreen': 'Plein écran',
  'plan.btn.legend': 'Afficher la légende',
  'plan.legend.title': 'Légende',
  'plan.legend.worlds': 'Mondes',
  'plan.legend.density': 'Densité temps réel',
  'plan.results.count':
    '{n, plural, =0 {Aucun résultat} one {1 résultat} other {# résultats}}',
  'plan.panel.code': 'Code',
  'plan.panel.partner': 'Partenaire',
  'plan.panel.partnerWebsite': 'Site web',
  'plan.panel.workshops':
    '{n, plural, one {1 {lexWorkshopBare}} other {# {lexWorkshopPlural}}}',
  'plan.panel.density': 'Affluence temps réel',
  'plan.panel.densityPeople': 'personnes',
  'plan.panel.favorite': 'Ajouter aux favoris',
  'plan.panel.unfavorite': 'Retirer des favoris',
  'plan.panel.center': 'Centrer sur le plan',
  'plan.toast.favoriteError':
    "Impossible d'enregistrer le favori pour ce stand.",

  // Backoffice — adminNav
  'adminNav.stands': 'Stands & zones du plan',

  // StandsAdminPage (backoffice CRUD stands)
  'standsAdmin.title': 'Stands & zones du plan',
  'standsAdmin.lead':
    'Référentiel des zones du plan du salon (consommé par la carte ' +
    'interactive). Les positions géométriques sont dérivées ' +
    'automatiquement du PDF officiel — ici vous modifiez les libellés, ' +
    'capacités et liens partenaires.',
  'standsAdmin.btn.create': 'Créer un stand',
  'standsAdmin.btn.edit': 'Éditer',
  'standsAdmin.btn.delete': 'Supprimer',
  'standsAdmin.btn.save': 'Enregistrer',
  'standsAdmin.filter.placeholder': 'Filtrer par code ou libellé…',
  'standsAdmin.filter.aria': 'Filtrer la liste des stands',
  'standsAdmin.counter':
    '{n, plural, =0 {Aucun stand} one {1 stand} other {# stands}}',
  'standsAdmin.loading': 'Chargement…',
  'standsAdmin.loadError': 'Impossible de charger les stands.',
  'standsAdmin.empty': 'Aucun stand ne correspond au filtre.',
  'standsAdmin.col.code': 'Code',
  'standsAdmin.col.label': 'Libellé',
  'standsAdmin.col.capacity': 'Capacité',
  'standsAdmin.col.svgNode': 'ID SVG',
  'standsAdmin.col.actions': 'Actions',
  'standsAdmin.confirm.delete':
    'Supprimer le stand « {label} » (code {code}) ?',
  'standsAdmin.modal.create': 'Créer un stand',
  'standsAdmin.modal.edit': 'Modifier le stand',
  'standsAdmin.field.code': 'Code (unique)',
  'standsAdmin.field.code.help':
    'A-Z, 0-9, tiret, underscore (max 40)',
  'standsAdmin.field.label': 'Libellé',
  'standsAdmin.field.capacity': 'Capacité maximale',
  'standsAdmin.field.capacity.help':
    'Utilisée pour le calcul de densité temps réel. Laisser vide si ' +
    'pas de limite.',
  'standsAdmin.field.partnerId': 'ID partenaire (optionnel)',
  'standsAdmin.field.partnerId.help':
    "ID du partenaire dans /admin/partners (associe la fiche stand à " +
    'un partenaire).',
  'standsAdmin.field.svgNodeId': 'ID SVG (avancé)',
  'standsAdmin.field.svgNodeId.help':
    'Identifiant du nœud dans le SVG du plan. Par défaut : stand-CODE.',
  'standsAdmin.field.floor': 'Étage / niveau',
  'standsAdmin.toast.created': 'Stand créé.',
  'standsAdmin.toast.updated': 'Stand mis à jour.',
  'standsAdmin.toast.deleted': 'Stand supprimé.',
  'standsAdmin.toast.duplicate': 'Ce code de stand existe déjà.',
  'standsAdmin.toast.inUse':
    "Ce stand est référencé par {lexWorkshopOfPlural}. Délier d'abord.",
  'standsAdmin.toast.error': 'Erreur enregistrement.',

  // ───────────────────────────────────────────────────────────────────────
  // FIX 2026-05-21 (V40) — Sprint « Limites + Stats par type de contrôle »
  // ───────────────────────────────────────────────────────────────────────

  // /admin/control-types — extension des champs limite + alerte
  'controlTypes.section.limits': 'Limites',
  'controlTypes.section.alert': 'Alerte en cas de dépassement',
  'controlTypes.field.maxPerQr': 'Scans maximum par badge',
  'controlTypes.field.maxPerQr.help':
    'Nombre maximum de scans autorisés pour un même badge (ex. 1 = un seul scan par personne). Laisser vide = illimité.',
  'controlTypes.field.maxTotal': 'Plafond global sur l’événement',
  'controlTypes.field.maxTotal.help':
    'Nombre maximum de scans pour ce type sur tout l’événement (ex. 600 casiers vestiaire). Laisser vide = illimité.',
  'controlTypes.field.windowMinutes': 'Fenêtre anti-double-scan (min)',
  'controlTypes.field.windowMinutes.help':
    'Durée pendant laquelle un même badge ne peut pas être re-scanné. Laisser vide = pas de fenêtre.',
  'controlTypes.field.requiresRole': 'Rôle requis',
  'controlTypes.field.requiresRole.help':
    'Restreint ce contrôle à un rôle précis (ex. repas-élèves réservé aux élèves). « Tous » = aucun filtre.',
  'controlTypes.field.behavior': 'Comportement en cas de dépassement',
  'controlTypes.field.behavior.help':
    'Alerter mais autoriser (warn) / Refuser le scan (block) / Autoriser silencieusement (allow).',
  'controlTypes.field.color': 'Couleur d’alerte',
  'controlTypes.field.color.help':
    'Couleur affichée sur la modale de l’organisateur quand la limite est dépassée.',
  'controlTypes.field.sound': 'Son d’alerte',
  'controlTypes.field.sound.help':
    'Son joué sur le scanner organisateur en cas d’alerte. Bouton « Écouter » pour pré-écouter.',
  'controlTypes.placeholder.unlimited': 'Illimité',
  'controlTypes.placeholder.noWindow': 'Pas de fenêtre',
  'controlTypes.btn.testSound': 'Écouter',
  // Rôles dropdown
  'controlTypes.role.any': 'Tous les rôles',
  'controlTypes.role.visitor': 'Visiteur',
  'controlTypes.role.exhibitor': 'Exposant',
  'controlTypes.role.vip': 'VIP',
  'controlTypes.role.specific_public': 'Public spécifique',
  'controlTypes.role.student': 'Élève',
  'controlTypes.role.child': 'Enfant',
  'controlTypes.role.staff': 'Organisateur',
  // Behaviors
  'controlTypes.behavior.warn': 'Alerter mais autoriser',
  'controlTypes.behavior.block': 'Refuser',
  'controlTypes.behavior.allow': 'Autoriser silencieusement',
  // Sons
  'controlTypes.sound.none': 'Aucun',
  'controlTypes.sound.beep': 'Bip simple',
  'controlTypes.sound.double_beep': 'Double bip',
  'controlTypes.sound.siren': 'Sirène',

  // ControlePage (organisateur) — modale alerte
  'controle.alert.blocked': 'Refusé',
  'controle.alert.warn': 'Admis avec alerte',
  'controle.alert.reason.role_required':
    'Ce contrôle est réservé à « {expected} ».',
  'controle.alert.reason.window':
    'Déjà scanné il y a moins de {max} min.',
  'controle.alert.reason.per_qr':
    'Limite atteinte : {current}/{max} scans pour ce participant.',
  'controle.alert.reason.total':
    'Plafond global atteint : {current}/{max} sur ce contrôle.',

  // /admin/control-stats — page dédiée détaillée
  'adminNav.controlStats': 'Stats contrôle (live)',
  // ───── G1 — Dashboard analytique emploi (Sprint I, parité 2025) ─────
  'emploiAnalytics.title': 'Analytique emploi',
  'emploiAnalytics.subtitle':
    'Indicateurs du salon de l’emploi : conversion, parcours candidats, exposants et tendances.',
  'emploiAnalytics.export.csv': 'Exporter (CSV)',
  'emploiAnalytics.export.pdf': 'Imprimer / PDF',
  'emploiAnalytics.loading': 'Chargement des analyses…',
  'emploiAnalytics.error':
    'Les analyses emploi n’ont pas pu être chargées. Réessayez dans un instant.',
  'emploiAnalytics.kpi.aria': 'Indicateurs clés emploi',
  'emploiAnalytics.kpi.conversion': 'Taux de conversion',
  'emploiAnalytics.kpi.conversion.hint':
    '{present} présent·e·s sur {total} inscrit·e·s',
  'emploiAnalytics.kpi.time': 'Temps moyen sur le salon',
  'emploiAnalytics.kpi.time.value':
    '{min, plural, =0 {moins d’une minute} one {# minute} other {# minutes}}',
  'emploiAnalytics.kpi.interest': 'Intérêt moyen des exposants',
  'emploiAnalytics.kpi.stands': 'Stands par visiteur',
  'emploiAnalytics.funnel.title': 'Entonnoir de conversion',
  'emploiAnalytics.dist.type': 'Profil des candidats',
  'emploiAnalytics.dist.sector': 'Secteur recherché',
  'emploiAnalytics.dist.search': 'Objet de la recherche',
  'emploiAnalytics.dist.experience': 'Niveau d’expérience',
  'emploiAnalytics.dist.source': 'Canal de connaissance',
  'emploiAnalytics.dist.empty': 'Aucune donnée pour le moment.',
  'emploiAnalytics.topExhibitors.title': 'Top exposants par candidats rencontrés',
  'emploiAnalytics.topExhibitors.company': 'Exposant',
  'emploiAnalytics.topExhibitors.candidates': 'Candidats',
  'emploiAnalytics.topExhibitors.interviews': 'Entretiens',
  'emploiAnalytics.topExhibitors.cv': 'Dépôts de CV',
  'emploiAnalytics.topExhibitors.empty':
    'Aucune rencontre exposant ↔ candidat enregistrée pour le moment.',
  'emploiAnalytics.trends.title': 'Tendances (7 jours)',
  'emploiAnalytics.trends.signups': 'Inscriptions',
  'emploiAnalytics.trends.scans': 'Passages sur stands',
  'emploiAnalytics.trends.j1': 'Hier (J-1)',
  'emploiAnalytics.trends.j7': 'Il y a 7 jours (J-7)',
  'emploiAnalytics.hourly.title': 'Affluence horaire (aujourd’hui)',
  'emploiAnalytics.generatedAt': 'Données générées le {when}',
  'controlStats.title': 'Statistiques contrôle / émargement (temps réel)',
  'controlStats.lead':
    'Suivi en direct des scans par type de contrôle. Cliquez une carte pour le détail.',
  'controlStats.loading': 'Chargement des statistiques…',
  'controlStats.error':
    'Impossible de charger les statistiques. Veuillez réessayer dans quelques secondes.',
  'controlStats.empty':
    'Aucun type de contrôle actif. Créez-en un depuis /admin/control-types.',
  'controlStats.kpi.total': 'Total',
  'controlStats.kpi.last15': '15 min',
  'controlStats.kpi.unique': 'Participants',
  'controlStats.kpi.lockers': 'Casiers attribués',
  'controlStats.kpi.warns15': 'Alertes (15 min)',
  'controlStats.kpi.peak': 'Minute de pointe',
  'controlStats.lastScan': 'Dernier scan : {when}',
  'controlStats.never': 'aucun scan',
  'controlStats.secondsAgo':
    '{n, plural, one {il y a # seconde} other {il y a # secondes}}',
  'controlStats.minutesAgo':
    '{n, plural, one {il y a # minute} other {il y a # minutes}}',
  'controlStats.hoursAgo':
    '{n, plural, one {il y a # heure} other {il y a # heures}}',
  'controlStats.detail.title': 'Détail : {label}',
  'controlStats.detail.close': 'Fermer le détail',
  'controlStats.timeline.title': 'Activité des 60 dernières minutes',
  'controlStats.topScanners.title': 'Top organisateurs (scans)',

  // Widget compact dashboard
  'controlStatsCompact.title': 'Contrôle & émargement',
  'controlStatsCompact.detail': 'Voir détails →',
  'controlStatsCompact.loading': 'Chargement…',
  'controlStatsCompact.last15': 'dans les 15 min',
  'controlStatsCompact.total': 'au total',
  // V40 phase 4 — libellés courts pour layout compact horizontal
  'controlStatsCompact.last15Short': '15 min',
  'controlStatsCompact.totalShort': 'total',

  // ───── Sprint SCANS-TRACKING-STATS 2026-05-22 ─────────────────────────
  // T1 — Heatmap densité cumulée + page /admin/heatmap
  'adminNav.heatmap': 'Carte d’affluence',
  'adminNav.dashboardWall': 'Affichage TV',
  'heatmap.svg.aria': 'Plan du salon colorisé par densité de visites',
  'heatmap.zone.aria':
    '{code} {label} — {visits, plural, =0 {aucune visite} one {# visite} other {# visites}}',
  'heatmap.legend.aria': 'Légende des niveaux d’affluence',
  'heatmap.legend.q0': 'Aucune visite',
  'heatmap.legend.q1': 'Peu visité',
  'heatmap.legend.q2': 'Moyennement visité',
  'heatmap.legend.q3': 'Très visité',
  'heatmap.legend.total':
    'Total : {count, plural, =0 {aucun passage} one {# passage} other {# passages}}',
  'heatmap.error': 'Impossible de charger les données d’affluence.',
  'heatmapPage.title': 'Carte d’affluence',
  'heatmapPage.subtitle':
    'Densité cumulée des passages sur chaque stand depuis le début du salon.',
  'heatmapPage.mode.aria': 'Choix du mode d’affichage',
  'heatmapPage.mode.cumulative': 'Cumulé (depuis ce matin)',
  'heatmapPage.mode.live': 'En direct (20 dernières min)',
  'heatmapPage.fullscreen': 'Plein écran',
  'heatmapPage.detail.close': 'Fermer le détail',
  'heatmapPage.detail.visits': 'Visites cumulées',
  'heatmapPage.detail.quartile': 'Niveau d’affluence',

  // ST1 — Mode TV grand écran (à venir, clés réservées)
  'dashboardWall.title': '{eventName}',
  'dashboardWall.subtitle': '{eventDate} — {eventPlace}, {eventPlaceAddress}',
  'dashboardWall.kpi.visitorsPresent': 'Visiteurs présents',
  'dashboardWall.kpi.scansToday': 'Scans aujourd’hui',
  'dashboardWall.kpi.scansTotal': 'Scans cumulés',
  'dashboardWall.kpi.workshopsFull': '{lexWorkshopPluralCap} au complet',
  'dashboardWall.kpi.topStand': 'Stand le plus visité',
  'dashboardWall.kpi.signupsConfirmed': 'Inscriptions confirmées',
  'dashboardWall.heatmap.title': 'Affluence sur le plan',
  'dashboardWall.activity.title': 'Derniers événements',
  'dashboardWall.activity.empty': 'En attente d’activité…',
  'dashboardWall.countdown.before': 'Ouverture dans {h}h {m}min',
  'dashboardWall.countdown.during': 'Fin de salon dans {h}h {m}min',
  'dashboardWall.countdown.after': 'Salon terminé — bilan à venir',
  'dashboardWall.exit': 'Quitter',
  'dashboardWall.fullscreen': 'Plein écran',
  'dashboardWall.lastUpdate': 'Mis à jour à {time}',

  // S1 — Mode rafale scan (toggle organisateur/exposant)
  'scanner.rapidMode.toggle': 'Basculer le mode rafale',
  'scanner.rapidMode.on':
    'Mode rafale actif : modale désactivée pour les scans valides. Les alertes restent affichées.',
  'scanner.rapidMode.off':
    'Mode standard : modale verdict après chaque scan.',

  // S9 — Mode kiosque iPad d'accueil (FIX 2026-05-22 sprint « finir 100 % »).
  'kiosque.title': 'Bienvenue {eventTypeAt} {eventName}',
  'kiosque.instruction': 'Présentez votre badge devant la caméra',
  'kiosque.hint':
    'Approchez votre badge à 15-20 cm de l\'écran et patientez quelques instants.',
  'kiosque.cameraAria': 'Caméra de scan du badge',
  'kiosque.welcome.kicker': 'Bienvenue',
  'kiosque.welcome.badgeCode': 'Votre code de badge',
  'kiosque.welcome.next': 'Suivant',
  'kiosque.error.generic':
    'Une erreur est survenue. Vérifiez votre badge ou présentez-vous à l\'accueil.',
  'kiosque.error.network':
    'Connexion au serveur impossible. Veuillez prévenir l\'équipe d\'accueil.',
  'kiosque.error.retry': 'Réessayer',
  'kiosque.footer': 'Borne d\'accueil — {eventName}, {eventDate}, {eventPlace}',
  'kiosque.exit': 'Quitter',

  // S3 — Anti-double-scan stand configurable par exposant (FIX 2026-05-22 sprint « finir 100 % »).
  'monStand.settings.title': 'Réglages de mon stand',
  'monStand.settings.intro':
    'Évitez les doubles scans accidentels en limitant la fréquence de scan d\'un même visiteur sur votre stand.',
  'monStand.settings.unavailable':
    'Réglages indisponibles. Importez la migration V43 puis rechargez la page.',
  'monStand.settings.scanWindow.label':
    'Fenêtre anti-double-scan (en minutes)',
  'monStand.settings.scanWindow.placeholder': 'Vide = désactivé',
  'monStand.settings.scanWindow.hint':
    'Laissez vide ou indiquez 0 pour désactiver. Sinon entre 1 et 1440 (24 h). Pendant cette fenêtre, le même visiteur ne pourra pas être re-scanné par votre stand.',
  'monStand.settings.save': 'Enregistrer',
  'monStand.settings.saved': 'Réglages enregistrés.',
  'monStand.settings.error':
    'Impossible d\'enregistrer les réglages. Réessayez dans quelques instants.',
  'monStand.settings.invalid':
    'Valeur invalide. La fenêtre doit être entre 0 et 1440 minutes.',
  // Message d'erreur scanner exposant — anti-double-scan déclenché.
  'exhibitorScanner.error.alreadyCheckedInRecently':
    '{name} a déjà été scanné par votre stand il y a moins de {minutes, plural, =1 {1 min} other {# min}}. Patientez {remainingMin, plural, =1 {1 min} other {# min}}.',

  // S6 — Last-known sticky en bas du scanner (FIX 2026-05-22 sprint « finir 100 % »).
  // Chrono LIVE rassurant pour l'organisateur/exposant pendant les pics.
  'lastScan.label': 'Dernier scan',
  'lastScan.ago.seconds': '{n} sec',
  'lastScan.ago.minutes': '{n, plural, =1 {1 min} other {# min}}',
  'lastScan.ago.both': '{m, plural, =1 {1 min} other {# min}} {s} sec',
  'lastScan.agoPrefix': 'il y a {ago}',
  'lastScan.aria': 'Dernier scan : {name}, il y a {ago}.',

  // S5 — Saisie manuelle du code visiteur (FIX 2026-05-22 sprint « finir 100 % »).
  // Préfixe `scanner.manualEntry.*` pour éviter collision avec les clés
  // `scanner.manual.*` historiques de la page NOLAS /scanner (cf. lignes
  // 410, 439 — sens différent : ancienne UX, pas la même page).
  'scanner.manualEntry.toggle': 'Saisir le code à la main',
  'scanner.manualEntry.title': 'Saisie manuelle du code',
  'scanner.manualEntry.intro':
    'Si la caméra est indisponible ou si le QR est illisible, saisissez le code de 12 caractères imprimé sur le badge.',
  'scanner.manualEntry.label': 'Code du badge',
  'scanner.manualEntry.placeholder': 'AB12CD34EF56',
  'scanner.manualEntry.hint':
    '12 caractères : chiffres et lettres majuscules (sans I, L, O, U).',
  'scanner.manualEntry.submit': 'Valider',
  'scanner.manualEntry.errorFormat':
    'Format invalide : 12 caractères attendus, alphabet 0-9 et A-Z sans I/L/O/U.',

  // T3+T4 — Alertes staff temps réel
  'adminNav.staffAlerts': 'Alertes opérationnelles',
  'adminShell.alertsBadge.aria':
    '{count, plural, one {# alerte ouverte} other {# alertes ouvertes}}',
  'staffAlerts.title': 'Alertes opérationnelles',
  'staffAlerts.subtitle':
    'Surveillance temps réel : mineurs perdus, saturation salon, {lexWorkshopPlural} à plein. Mise à jour toutes les 30 secondes.',
  'staffAlerts.loading': 'Chargement des alertes…',
  'staffAlerts.error': 'Impossible de charger les alertes. Réessayez.',
  'staffAlerts.empty': 'Aucune alerte pour le filtre sélectionné.',
  'staffAlerts.tableMissing':
    'Module d’alertes non encore installé : importez la migration V42 puis rechargez la page.',
  'staffAlerts.toast.updated': 'Alerte mise à jour.',
  'staffAlerts.toast.error': 'Impossible de mettre à jour l’alerte.',
  'staffAlerts.filter.status': 'Statut',
  'staffAlerts.filter.kind': 'Type',
  'staffAlerts.filter.severity': 'Gravité',
  'staffAlerts.filter.all': 'Toutes',
  'staffAlerts.status.open': 'Ouvertes',
  'staffAlerts.status.acknowledged': 'Acquittées',
  'staffAlerts.status.resolved': 'Résolues',
  'staffAlerts.severity.info': 'Info',
  'staffAlerts.severity.warning': 'Avertissement',
  'staffAlerts.severity.critical': 'Critique',
  'staffAlerts.kind.minorLost': 'Mineur sans contact',
  'staffAlerts.kind.roomSaturated': 'Saturation salon',
  'staffAlerts.kind.workshopSaturated': '{lexWorkshopCap} saturé{lexWorkshopAgr}',
  'staffAlerts.kind.custom': 'Personnalisée',
  'staffAlerts.kpi.critical': 'critiques',
  'staffAlerts.kpi.warning': 'avertissements',
  'staffAlerts.kpi.info': 'infos',
  'staffAlerts.action.acknowledge': 'Prendre en charge',
  'staffAlerts.action.resolve': 'Marquer résolue',
  'staffAlerts.acknowledged.by': 'Prise en charge par {name} à {time}.',
  'staffAlerts.resolved.at': 'Résolue à {time}.',

  // ST8 — Bilan PDF jour J (HTML imprimable)
  'adminNav.dailyReport': 'Bilan jour J',
  'dailyReport.title': 'Bilan {eventName} — vue imprimable',
  'dailyReport.loading': 'Chargement du bilan…',
  'dailyReport.error': 'Impossible de charger le bilan. Réessayez ultérieurement.',
  'dailyReport.empty': 'Aucune donnée pour cette section.',
  'dailyReport.btn.print': '🖨 Imprimer / Enregistrer en PDF',
  'dailyReport.cover.title': 'Bilan {eventName}',
  'dailyReport.cover.date': 'Journée du {date}',
  'dailyReport.cover.subtitle':
    'Salon « Votre avenir commence ICI ! »',
  'dailyReport.cover.generated': 'Bilan généré le {when}.',
  'dailyReport.section.kpis': 'Indicateurs clés',
  'dailyReport.section.heatmap': 'Carte d’affluence cumulée',
  'dailyReport.section.hourly': 'Affluence horaire (24h)',
  'dailyReport.section.topStands': 'Top 10 des stands',
  'dailyReport.section.topWorkshops': 'Top 10 {lexWorkshopOfPlural}',
  'dailyReport.section.demographics': 'Démographie',
  'dailyReport.section.alerts': 'Alertes opérationnelles',
  'dailyReport.kpi.uniqueVisitorsDay': 'Visiteurs uniques (jour)',
  'dailyReport.kpi.scansDay': 'Scans du jour',
  'dailyReport.kpi.scansTotal': 'Scans cumulés (total)',
  'dailyReport.kpi.signupsConfirmed': 'Inscriptions confirmées',
  'dailyReport.kpi.workshopsFull': '{lexWorkshopPluralCap} au complet',
  'dailyReport.kpi.exhibitorsActive': 'Exposants actifs',
  'dailyReport.col.standCode': 'Code',
  'dailyReport.col.standLabel': 'Stand',
  'dailyReport.col.visits': 'Visites',
  'dailyReport.col.workshopTitle': '{lexWorkshopCap}',
  'dailyReport.col.confirmed': 'Confirmés',
  'dailyReport.col.capacity': 'Capacité',
  'dailyReport.col.fillRate': 'Taux',
  'dailyReport.col.waitlist': 'Liste d’attente',
  'dailyReport.demo.byRole': 'Par rôle',
  'dailyReport.demo.minors': 'Mineurs / adultes',
  'dailyReport.demo.topStructures': 'Top 10 des établissements',
  'dailyReport.demo.minor': 'Mineurs',
  'dailyReport.demo.adult': 'Adultes',
  'dailyReport.alerts.openTotal': 'alerte(s) ouverte(s) ou prise(s) en charge',
  'dailyReport.alerts.critical': 'critiques',
  'dailyReport.alerts.warning': 'avertissements',
  'dailyReport.footer':
    '© 2026 — Mikaël FOURNY. Bilan généré automatiquement par l’app NOLAS 2026.',

  // ST2 ❤️ — Dashboard exposant /exposant/mon-stand
  'monStand.title': 'Mon stand',
  'monStand.back': 'Retour à mon espace',
  'monStand.checking': 'Vérification de votre session…',
  'monStand.loading': 'Chargement de votre tableau de bord…',
  'monStand.error': 'Impossible de charger votre tableau de bord. Réessayez.',
  'monStand.notExhibitor': 'Cette page est réservée aux exposants.',
  'monStand.scanner': 'Scanner',
  // NOLAS 2026 LOT 2 — accès « Ma fiche » + bannière d’échéance fiche exposant.
  'monStand.maFiche': 'Ma fiche',
  'monStand.mesAteliers': 'Mes {lexWorkshopPlural}',
  'monStand.ficheDeadline.text': 'Pensez à compléter votre fiche exposant.',
  'monStand.ficheDeadline.textWithDate': 'Pensez à compléter votre fiche exposant avant le {date}.',
  'monStand.ficheDeadline.cta': 'Compléter ma fiche',
  'monStand.meetings': 'Mes rencontres',
  'monStand.export': '📥 Exporter mes scans (CSV)',
  'monStand.export.done': 'Export CSV téléchargé.',
  'monStand.kpis.aria': 'Indicateurs clés',
  'monStand.kpi.scansToday': 'Scans aujourd’hui',
  'monStand.kpi.scansLastHour': 'Scans dans la dernière heure',
  'monStand.kpi.uniqueVisitors': 'Visiteurs uniques',
  'monStand.kpi.interestAverage':
    '{count, plural, =0 {Note moyenne (aucune)} one {Note moyenne (# avis)} other {Note moyenne (# avis)}}',
  'monStand.topRoles.title': 'Top 3 des profils intéressés',
  'monStand.timeline.title': 'Activité par heure (aujourd’hui)',
  'monStand.timeline.aria': 'Histogramme des scans par heure',
  'monStand.recent.title': '20 derniers scans',
  'monStand.recent.empty':
    'Aucun scan enregistré pour l’instant. Allez scanner des badges depuis « Scanner ».',
  'monStand.col.time': 'Heure',
  'monStand.col.name': 'Visiteur',
  'monStand.col.role': 'Profil',
  'monStand.col.interest': 'Intérêt',
  'monStand.col.notes': 'Notes',

  // ───── Sprint « finir 100 % » — S10 Géo coordinates (2026-05-23) ─────
  'adminNav.standPositions': 'Positions GPS des stands',
  'standPositions.title': 'Positions GPS des stands',
  'standPositions.subtitle':
    'Recalibrage automatique de la position de chaque stand à partir des scans géolocalisés (capture opt-in via Réglages → Capture GPS au scan).',
  'standPositions.lead':
    'Affiche les positions proposées pour chaque stand ayant au moins {minScans, plural, one {# scan} other {# scans}} géolocalisé(s). Outliers > {maxMeters} m exclus du calcul.',
  'standPositions.loading': 'Chargement des propositions…',
  'standPositions.empty':
    'Aucun stand pour l’instant. Importez la migration V45 et activez « Capture GPS au scan » dans les Réglages.',
  'standPositions.error.load':
    'Impossible de charger les positions. Vérifiez votre session et réessayez.',
  'standPositions.error.v45Missing':
    'La capture GPS n’est pas encore disponible. Importez la migration V45 via phpMyAdmin (sélectionnez la base AVANT).',
  'standPositions.col.selectAll': 'Tout sélectionner',
  'standPositions.col.select': 'Sélectionner {code}',
  'standPositions.col.stand': 'Stand',
  'standPositions.col.scans': 'Scans GPS',
  'standPositions.col.current': 'Position actuelle',
  'standPositions.col.proposed': 'Position proposée',
  'standPositions.col.delta': 'Écart',
  'standPositions.col.calibratedAt': 'Dernier recalibrage',
  'standPositions.delta.meters': '{m} m',
  'standPositions.btn.applySelected':
    '{count, plural, =0 {Appliquer la sélection} one {Appliquer (# stand)} other {Appliquer (# stands)}}',
  'standPositions.btn.applyAll':
    '{count, plural, =0 {Tout recalibrer} one {Recalibrer les # stand} other {Recalibrer les # stands}}',
  'standPositions.confirm.selected':
    'Recalibrer {count, plural, one {# stand} other {# stands}} ? Les positions actuelles seront écrasées.',
  'standPositions.confirm.all':
    'Recalibrer les {count, plural, one {# stand prêt} other {# stands prêts}} (≥ 5 scans GPS) ? Les positions actuelles seront écrasées.',
  'standPositions.toast.success':
    '{count, plural, =0 {Aucun stand recalibré (pas assez de scans).} one {# stand recalibré avec succès.} other {# stands recalibrés avec succès.}}',
  'standPositions.toast.error':
    'Erreur lors du recalibrage. Vérifiez votre session et réessayez.',

  // ───── Sprint « finir 100 % » — S8 Attendee Connect (2026-05-23) ─────
  'maCarte.btn.scanContact': 'Scanner un contact',
  'maCarte.btn.myContacts': 'Mes contacts',
  'maCarte.scan.modalTitle': 'Scanner le badge d’un participant',
  'maCarte.scan.modalHint':
    'Demandez à votre interlocuteur de présenter son badge {eventName}. Vous pourrez retrouver son contact dans « Mes contacts ».',
  'maCarte.scan.success.title': 'Contact ajouté',
  'maCarte.scan.success.hint':
    'Vous le retrouverez dans « Mes contacts » après le salon.',
  'maCarte.scan.error.generic':
    'Impossible d’ajouter ce contact. Réessayez ou demandez à votre interlocuteur de vérifier ses réglages de confidentialité.',
  'mesContacts.title': 'Mes contacts',
  'mesContacts.subtitle':
    'Les personnes que vous avez ajoutées via QR pendant le salon, et celles qui vous ont ajouté(e).',
  'mesContacts.btn.scanNew': 'Scanner un nouveau contact',
  'mesContacts.loading': 'Chargement de vos contacts…',
  'mesContacts.needLogin':
    'Connectez-vous pour retrouver vos contacts.',
  'mesContacts.loginCta': 'Se connecter',
  'mesContacts.error':
    'Impossible de charger vos contacts. Vérifiez votre connexion.',
  'mesContacts.scannedByMe.title':
    '{count, plural, =0 {Mes contacts} one {Mes contacts (#)} other {Mes contacts (#)}}',
  'mesContacts.scannedByMe.empty':
    'Vous n’avez encore ajouté aucun contact. Allez sur « Ma carte » puis « Scanner un contact ».',
  'mesContacts.scannedMyCard.title':
    '{count, plural, =0 {Personnes qui m’ont ajouté(e)} one {Personnes qui m’ont ajouté(e) (#)} other {Personnes qui m’ont ajouté(e) (#)}}',
  'mesContacts.scannedMyCard.empty':
    'Personne ne vous a encore ajouté(e). Présentez votre carte aux autres participants pour qu’ils puissent vous scanner.',

  'common.cancel': 'Annuler',
  'common.close': 'Fermer',

  // ───── Sprint « finir 100 % » — S2 Photo profil (2026-05-23) ─────
  'profil.photo.title': 'Photo de profil',
  'profil.photo.altMe': 'Votre photo de profil',
  'profil.photo.uploadAria': 'Choisir une photo (JPEG ou PNG, max 5 Mo)',
  'profil.photo.btn.delete': 'Supprimer ma photo',
  'profil.photo.confirm.delete':
    'Voulez-vous vraiment supprimer votre photo de profil ?',
  'profil.photo.hint':
    'JPEG ou PNG, max 5 Mo. Recadrée et redimensionnée automatiquement en 256 × 256 pixels.',
  'profil.photo.minorNote':
    'Les mineurs ne peuvent ajouter une photo qu’avec l’accord parental signé. Contactez l’organisation : mikael.fourny@labriquerie.net.',
  'profil.photo.uploaded': 'Photo enregistrée.',
  'profil.photo.deleted': 'Photo supprimée.',
  'profil.photo.error.upload':
    'Impossible d’enregistrer la photo. Réessayez ou contactez le support.',
  'profil.photo.error.delete':
    'Impossible de supprimer la photo. Réessayez.',
  'profil.photo.error.tooLarge':
    'Fichier trop volumineux (5 Mo max).',
  'profil.photo.error.invalidType':
    'Format non supporté. Utilisez JPEG ou PNG uniquement.',
  'profil.photo.error.minorConsent':
    'Les mineurs ne peuvent ajouter une photo qu’avec l’accord parental.',

  // ───── Sprint « finir 100 % » — S2 Photo admin (2026-05-23) ─────
  'usersList.col.photo': 'Photo',
  'usersList.btn.deletePhoto': 'Supprimer photo',
  'usersList.confirm.deletePhoto':
    'Supprimer la photo de profil de ce participant ? Cette action est irréversible.',
  'usersList.toast.photoDeleted': 'Photo supprimée.',
  'usersList.toast.photoError':
    'Erreur lors de la suppression de la photo.',
  'usersList.btn.deleteAllPhotos': 'Supprimer toutes les photos',
  'usersList.confirm.deleteAllPhotos':
    'Supprimer la photo de profil de TOUS les participants ayant uploadé une photo ? Cette action est IRRÉVERSIBLE. Tapez « SUPPRIMER » pour confirmer.',
  'usersList.confirm.deleteAllPhotosWord': 'SUPPRIMER',
  'usersList.toast.deleteAllPhotos':
    '{count, plural, =0 {Aucune photo à supprimer} one {# photo supprimée} other {# photos supprimées}}.',

  // ───── Contact-prefs (2026-05-25, sprint « Reprendre contact ») ─────
  // CLAUDE.md §5 entrée 2026-05-25, §10.10 RGPD opt-in, §10.16.7 mineurs,
  // §13.1 Attendee Connect. Wording français de France strict §15.5.
  'profil.contactPrefs.title': 'Mes contacts au salon',
  'profil.contactPrefs.intro':
    'Choisissez comment vous souhaitez être retrouvé·e par les personnes que vous avez rencontrées au salon. Vos choix s’appliquent immédiatement aux contacts déjà échangés.',
  'profil.contactPrefs.allowScan':
    'Permettre aux autres de me scanner pour échanger nos contacts',
  'profil.contactPrefs.shareEmail':
    'Partager mon adresse e-mail avec mes contacts',
  'profil.contactPrefs.sharePhone':
    'Partager mon téléphone avec mes contacts',
  'profil.contactPrefs.minorNote':
    'Vous êtes mineur·e : vos coordonnées (e-mail, téléphone) ne sont JAMAIS partagées, quelle que soit votre préférence. Cette protection est définitive.',
  'profil.contactPrefs.v47Missing':
    'Cette fonctionnalité n’est pas encore disponible côté serveur. Contactez l’équipe organisatrice si le problème persiste.',
  'profil.contactPrefs.save': 'Enregistrer mes préférences',
  'profil.contactPrefs.saved': 'Préférences enregistrées.',
  'profil.contactPrefs.error':
    'Impossible d’enregistrer vos préférences. Réessayez dans un instant.',
  'common.saving': 'Enregistrement…',

  // Boutons mailto / tel sur /mes-contacts (rendus uniquement si la
  // personne ciblée a opt-in dans son profil ET n\'est pas mineure).
  'mesContacts.btn.email': '📧 Envoyer un e-mail',
  'mesContacts.btn.phone': '📞 Appeler',
  'mesContacts.noShared':
    'Cette personne n’a pas partagé ses coordonnées.',

  // ───── Borne plastron / mode kiosque (2026-05-25, sprint borne) ─────
  // Scripts kiosque téléchargeables + instructions Android/iOS.
  // CLAUDE.md §5 entrée 2026-05-25, §10.10 RGPD, §15.5 wording FR strict.
  'adminNav.kioskLaunchers': 'Bornes — scripts kiosque',

  'kioskLaunchers.title': 'Scripts pour bornes en mode kiosque',
  'kioskLaunchers.subtitle':
    'Téléchargez les scripts prêts à l’emploi pour lancer la borne NOLAS 2026 en mode kiosque plein écran sur n’importe quel grand écran tactile (Windows, macOS, Linux). Pour les tablettes Android et iOS, suivez les instructions natives ci-dessous.',

  // Bandeau de prérequis (avant de télécharger).
  'kioskLaunchers.requirements.title': 'Avant de démarrer une borne',
  'kioskLaunchers.requirements.step1':
    'Créez un compte dédié borne dans <link>/admin/users-admin</link> avec le rôle « Plastron (démo) ». Choisissez un mot de passe simple à retenir mais robuste (8+ caractères, lettre + chiffre).',
  'kioskLaunchers.requirements.step2':
    'Notez l’adresse e-mail et le mot de passe sur un papier confidentiel — vous en aurez besoin au premier lancement de chaque borne.',
  'kioskLaunchers.requirements.step3':
    'Connectez le PC ou la tablette à l’écran tactile, vérifiez qu’il y a une connexion internet stable, puis lancez le script correspondant à votre système.',

  // Section PC (Windows / macOS / Linux).
  'kioskLaunchers.pc.title': 'Ordinateurs (Windows, macOS, Linux)',
  'kioskLaunchers.pc.intro':
    'Téléchargez le script correspondant à votre système d’exploitation, puis double-cliquez dessus pour lancer la borne. Au premier lancement, saisissez l’e-mail et le mot de passe du compte plastron. La session restera active pendant 30 jours.',

  'kioskLaunchers.windows.title': 'Windows 10 / 11',
  'kioskLaunchers.windows.description':
    'Pour PC Windows avec Google Chrome installé. Double-clic sur le fichier .cmd pour lancer la borne en mode plein écran.',
  'kioskLaunchers.windows.steps':
    'Téléchargez → Double-clic → Connexion → C’est parti.',

  'kioskLaunchers.macos.title': 'macOS (10.15 Catalina et +)',
  'kioskLaunchers.macos.description':
    'Pour Mac avec Google Chrome installé. Double-clic sur le fichier .command depuis le Finder. Au 1ᵉʳ lancement, si macOS bloque (Gatekeeper), faites Ctrl+clic → Ouvrir → Confirmer.',
  'kioskLaunchers.macos.steps':
    'Téléchargez → Ctrl+clic puis Ouvrir → Connexion → C’est parti.',

  'kioskLaunchers.linux.title': 'Linux (Ubuntu, Debian, Raspberry Pi…)',
  'kioskLaunchers.linux.description':
    'Pour distributions Linux avec Chrome ou Chromium installé. Rendez le script exécutable avec « chmod +x » puis lancez-le depuis un terminal ou un double-clic.',
  'kioskLaunchers.linux.steps':
    'Téléchargez → chmod +x → ./borne-tne-linux.sh → Connexion → C’est parti.',

  'kioskLaunchers.btn.download': 'Télécharger {fileName}',
  'kioskLaunchers.btn.downloadAria': 'Télécharger le script {fileName}',

  // Section tablettes (Android + iOS).
  'kioskLaunchers.mobile.title': 'Tablettes (Android, iPad / iOS)',

  'kioskLaunchers.android.title': 'Android (tablettes et téléphones)',
  'kioskLaunchers.android.description':
    'Les systèmes Android ne permettent pas le téléchargement de scripts. Utilisez l’une des deux méthodes natives ci-dessous pour verrouiller votre appareil sur la borne NOLAS 2026.',
  'kioskLaunchers.android.method1.title':
    'Méthode 1 — Mode épinglage d’écran (intégré, gratuit)',
  'kioskLaunchers.android.method1.step1':
    'Ouvrez Chrome et allez sur https://nolas.labriquerie.net/connexion?kiosk=1',
  'kioskLaunchers.android.method1.step2':
    'Connectez-vous avec le compte plastron (e-mail + mot de passe).',
  'kioskLaunchers.android.method1.step3':
    'Activez le mode épinglage : Paramètres → Sécurité → Avancé → Épingler l’écran (ou « App pinning » sur certaines marques).',
  'kioskLaunchers.android.method1.step4':
    'Dans l’aperçu des applications, appuyez longuement sur l’onglet Chrome et choisissez « Épingler ». L’écran est verrouillé sur la borne.',
  'kioskLaunchers.android.method2.title':
    'Méthode 2 — Fully Kiosk Browser (recommandé pour usage permanent)',
  'kioskLaunchers.android.method2.description':
    'Application gratuite « Fully Kiosk Browser » sur le Play Store. Permet de verrouiller totalement la tablette sur l’URL https://nolas.labriquerie.net/connexion?kiosk=1, désactive les boutons système, et redémarre automatiquement si l’app est fermée. Idéal pour une borne laissée sans surveillance toute la journée.',

  'kioskLaunchers.ios.title': 'iPad / iPhone (iOS, iPadOS)',
  'kioskLaunchers.ios.description':
    'Les systèmes iOS ne permettent pas le téléchargement de scripts. Utilisez le mode « Accès Guidé » natif d’Apple pour verrouiller Safari sur la borne NOLAS 2026.',
  'kioskLaunchers.ios.method.title':
    'Méthode — Accès Guidé Safari (intégré, gratuit)',
  'kioskLaunchers.ios.method.step1':
    'Activez l’Accès Guidé : Réglages → Accessibilité → Accès Guidé → Activé.',
  'kioskLaunchers.ios.method.step2':
    'Définissez un code de sortie dans Réglages d’Accès Guidé → Code d’accès → choisissez un code à 4 chiffres.',
  'kioskLaunchers.ios.method.step3':
    'Ouvrez Safari et allez sur https://nolas.labriquerie.net/connexion?kiosk=1 — connectez-vous avec le compte plastron.',
  'kioskLaunchers.ios.method.step4':
    'Triple-clic sur la touche latérale (iPhone X+) ou le bouton principal → Accès Guidé. Désactivez les options Sommeil / Toucher si besoin, puis appuyez sur « Démarrer ».',
  'kioskLaunchers.ios.method.step5':
    'L’iPad est verrouillé sur Safari. Pour sortir, triple-clic + code à 4 chiffres + « Terminer ».',

  // Section transparence sécurité.
  'kioskLaunchers.security.title': 'Sécurité — que font ces scripts ?',
  'kioskLaunchers.security.body':
    'Les scripts ne contiennent AUCUN mot de passe en clair. Ils se contentent de lancer votre navigateur Chrome en mode kiosque plein écran sur l’adresse https://nolas.labriquerie.net/connexion?kiosk=1. Vous êtes invité à saisir vos identifiants au premier lancement uniquement, comme pour n’importe quel site web.',
  'kioskLaunchers.security.body2':
    'Chaque script utilise un profil Chrome dédié à la borne (dossier « TNE-Kiosk-Profile »), isolé de votre profil personnel. Une fois logué, votre session reste active pendant 30 jours grâce au cookie sécurisé TNEVSID (HttpOnly, Secure, SameSite). Tant que personne ne clique « Se déconnecter » — bouton masqué automatiquement en mode kiosque grâce au paramètre ?kiosk=1 — la borne fonctionne sans aucune action supplémentaire.',

  // ── Hash SHA-256 + transparence (FIX 2026-05-25 antivirus) ───────
  'kioskLaunchers.integrity.size': 'Taille :',
  'kioskLaunchers.integrity.modified': 'Modifié le :',
  'kioskLaunchers.integrity.sha256': 'SHA-256 :',
  'kioskLaunchers.integrity.copyHash': 'Copier',
  'kioskLaunchers.integrity.copyHashAria': 'Copier le hash SHA-256 complet dans le presse-papiers',
  'kioskLaunchers.integrity.missing':
    'Ce script n’est pas disponible sur le serveur. Contactez Mikaël.',

  // ── Bouton « Voir le contenu » + modale d’inspection ─────────────
  'kioskLaunchers.btn.inspect': 'Voir le contenu du script',
  'kioskLaunchers.inspect.title': 'Contenu de {fileName}',
  'kioskLaunchers.inspect.loading': 'Chargement du contenu…',
  'kioskLaunchers.inspect.error':
    'Impossible de charger le contenu du script. Réessayez plus tard.',

  // ── Section antivirus & SmartScreen/Gatekeeper ───────────────────
  'kioskLaunchers.antivirus.title':
    '⚠️ Antivirus, SmartScreen, Gatekeeper : quoi faire ?',
  'kioskLaunchers.antivirus.intro':
    'Les scripts que vous téléchargez ne sont pas signés numériquement (un certificat de signature de code coûte 200 à 500 € par an, ce n’est pas raisonnable pour un événement ponctuel). Votre système d’exploitation va donc afficher un avertissement la première fois — c’est NORMAL et attendu. Voici comment confirmer l’ouverture sans contourner la sécurité.',

  'kioskLaunchers.antivirus.windows.title': 'Windows 10 / 11 (SmartScreen)',
  'kioskLaunchers.antivirus.windows.step1':
    'Double-cliquez sur le fichier borne-tne-windows.cmd. Windows Defender SmartScreen va afficher un encadré bleu « Windows a protégé votre PC ».',
  'kioskLaunchers.antivirus.windows.step2':
    'Cliquez sur « Informations complémentaires » dans cet encadré.',
  'kioskLaunchers.antivirus.windows.step3':
    'Cliquez sur « Exécuter quand même ». Cette action sera mémorisée pour ce script, vous n’aurez plus à le faire lors des lancements suivants.',

  'kioskLaunchers.antivirus.macos.title': 'macOS (Gatekeeper)',
  'kioskLaunchers.antivirus.macos.step1':
    'Au premier double-clic, macOS peut afficher « impossible d’ouvrir car son intégrité ne peut pas être vérifiée ».',
  'kioskLaunchers.antivirus.macos.step2':
    'Faites Ctrl+clic (ou clic droit) sur le fichier borne-tne-macos.command dans le Finder, puis choisissez « Ouvrir » dans le menu. macOS affiche une boîte de dialogue avec un bouton « Ouvrir » qui n’apparaît pas au double-clic normal. Confirmez.',
  'kioskLaunchers.antivirus.macos.step3':
    'Alternative en Terminal : <code>xattr -d com.apple.quarantine borne-tne-macos.command</code> puis <code>chmod +x borne-tne-macos.command</code> puis <code>./borne-tne-macos.command</code> — cela retire définitivement le marqueur de quarantaine.',

  'kioskLaunchers.antivirus.linux.title': 'Linux (permissions exécutables)',
  'kioskLaunchers.antivirus.linux.step1':
    'Ouvrez un terminal dans le dossier de téléchargement et rendez le script exécutable : <code>chmod +x borne-tne-linux.sh</code>',
  'kioskLaunchers.antivirus.linux.step2':
    'Lancez ensuite le script : ./borne-tne-linux.sh — Linux ne pose pas de question (pas d’équivalent SmartScreen ou Gatekeeper sur la plupart des distributions).',

  'kioskLaunchers.antivirus.guarantee':
    '🛡️ Garantie de transparence : le contenu du script est inspectable ci-dessus via le bouton « Voir le contenu du script ». Il ne fait STRICTEMENT QUE lancer votre navigateur Chrome sur l’adresse https://nolas.labriquerie.net/connexion?kiosk=1. Aucune connexion à un autre serveur, aucun fichier créé en dehors du profil Chrome dédié (TNE-Kiosk-Profile), aucune télémétrie. Vérifiez le hash SHA-256 affiché plus haut si vous voulez être absolument certain que le fichier téléchargé n’a pas été altéré pendant le transfert.',

  // ── Section MÉTHODE RAPIDE (FIX 2026-05-25, Gatekeeper Sequoia 15+) ──
  // Commandes one-liner Terminal / PowerShell qui contournent les
  // blocages Gatekeeper / SmartScreen sans contourner la sécurité.
  'kioskLaunchers.fast.title':
    '🚀 Méthode rapide — sans warning antivirus (recommandée)',
  'kioskLaunchers.fast.intro':
    'Copiez-collez la commande correspondant à votre système d’exploitation dans un Terminal (macOS / Linux) ou PowerShell (Windows). Cette méthode ÉVITE les avertissements SmartScreen et Gatekeeper : aucun fichier exécutable n’est téléchargé via le navigateur (donc pas marqué « provient d’Internet »). C’est la méthode standard utilisée par Homebrew, Docker, Ollama, etc.',

  'kioskLaunchers.fast.windows.title': 'Windows — PowerShell',
  'kioskLaunchers.fast.windows.howto':
    'Ouvrez PowerShell (clic droit sur le menu Démarrer → « Windows PowerShell » ou « Terminal »), collez la commande ci-dessous avec Ctrl+V, puis appuyez sur Entrée. Chrome se lance immédiatement en mode kiosque. Aucun fichier n’est créé sur votre disque, donc aucun warning antivirus.',

  'kioskLaunchers.fast.macos.title': 'macOS — Terminal',
  'kioskLaunchers.fast.macos.howto':
    'Ouvrez Terminal (Cmd+Espace → tapez « Terminal » → Entrée), collez la commande ci-dessous avec Cmd+V, puis appuyez sur Entrée. Le script est téléchargé via curl (qui NE pose PAS la quarantaine Gatekeeper, contrairement à Safari), rendu exécutable, puis lancé. Chrome s’ouvre en kiosque, aucun blocage.',

  'kioskLaunchers.fast.linux.title': 'Linux — Terminal',
  'kioskLaunchers.fast.linux.howto':
    'Ouvrez un terminal (Ctrl+Alt+T sur la plupart des distributions), collez la commande ci-dessous, puis appuyez sur Entrée. Le script est téléchargé via curl, rendu exécutable, puis lancé. Chrome / Chromium s’ouvre en kiosque.',

  'kioskLaunchers.fast.copy': '📋 Copier',
  'kioskLaunchers.fast.copied': '✓ Copié !',
  'kioskLaunchers.fast.copyAria':
    'Copier la commande dans le presse-papiers',

  'kioskLaunchers.fast.whyWorks':
    '💡 Pourquoi cette méthode passe sans warning ? Pour Windows, PowerShell lance Chrome directement sans créer aucun fichier — Defender n’a rien à analyser. Pour macOS et Linux, l’outil curl ne pose pas l’attribut « com.apple.quarantine » sur le fichier téléchargé (contrairement à Safari/Chrome), donc Gatekeeper ne le considère pas comme « provenant d’Internet ». La sécurité du système n’est PAS contournée : c’est juste un canal de téléchargement différent, parfaitement légitime et standard dans l’industrie (Homebrew, Docker, et de nombreux installeurs pro utilisent cette approche).',

  // ═══════════════════════════════════════════════════════════════════════
  // 2026-06-03 (Mika — Sprint 1) — Mode d'accès / parcours exposant.
  // ═══════════════════════════════════════════════════════════════════════

  // ───── Back-office : section réglages « Accès » ──────────────────────
  'settingsAdmin.section.access': 'Accès & ouverture du salon',

  // ───── Écran de pré-lancement (AccessModeGuard) ──────────────────────
  'prelaunch.poster.alt': 'Affiche {eventTypeOf} {eventName}',
  'prelaunch.countdown.label': 'Compte à rebours avant l’ouverture du salon',
  'prelaunch.countdown.days': 'jours',
  'prelaunch.countdown.hours': 'heures',
  'prelaunch.countdown.minutes': 'minutes',
  'prelaunch.countdown.seconds': 'secondes',
  'prelaunch.cta.exhibitor': 'Espace exposant',
  // 2026-08-26 — l'espace animateur a desormais sa porte d'entree.
  'prelaunch.cta.workshopHost': 'Espace animateur',

  // ───── Inscription exposant (code EXPO) ──────────────────────────────
  'exhibitorSignup.title': 'Inscription exposant',
  'exhibitorSignup.subtitle': 'Saisissez le code reçu par l’organisateur pour créer votre compte.',
  'exhibitorSignup.closed': 'Les inscriptions exposants sont actuellement fermées.',
  'exhibitorSignup.field.code': 'Code exposant',
  'exhibitorSignup.field.email': 'Adresse e-mail',
  'exhibitorSignup.field.firstName': 'Prénom',
  'exhibitorSignup.field.lastName': 'Nom',
  'exhibitorSignup.field.consent': 'J’accepte que mes données soient traitées pour la gestion de mon inscription au salon (RGPD).',
  'exhibitorSignup.submit': 'Créer mon compte',
  'exhibitorSignup.success': 'Votre compte a bien été créé. Connectez-vous avec votre adresse e-mail pour accéder à votre espace.',
  'exhibitorSignup.success.cta': 'Aller à la connexion',
  'exhibitorSignup.link.noCode': 'Je n’ai pas de code → demander un accès',
  'exhibitorSignup.link.alreadyAccount': 'J’ai déjà un compte → me connecter',
  'exhibitorSignup.error.code.required': 'Le code exposant est obligatoire.',
  'exhibitorSignup.error.code.format': 'Le code doit être au format EXPO suivi de 6 caractères.',
  'exhibitorSignup.error.code.unknown': 'Ce code exposant est inconnu.',
  'exhibitorSignup.error.code.used': 'Ce code exposant a déjà été utilisé.',
  'exhibitorSignup.error.email.required': 'L’adresse e-mail est obligatoire.',
  'exhibitorSignup.error.email.invalid': 'Cette adresse e-mail n’est pas valide.',
  'exhibitorSignup.error.email.tooLong': 'Cette adresse e-mail est trop longue.',
  'exhibitorSignup.error.firstName.required': 'Le prénom est obligatoire.',
  'exhibitorSignup.error.firstName.tooLong': 'Le prénom est trop long.',
  'exhibitorSignup.error.lastName.required': 'Le nom est obligatoire.',
  'exhibitorSignup.error.lastName.tooLong': 'Le nom est trop long.',
  'exhibitorSignup.error.consent.required': 'Vous devez accepter le traitement de vos données pour continuer.',
  'exhibitorSignup.error.validation': 'Certains champs sont invalides. Vérifiez votre saisie.',
  'exhibitorSignup.error.network': 'Une erreur est survenue. Veuillez réessayer.',

  // ───── Wizard d’inscription exposant tout-en-un (LOT 2 2026-06-24) ─────
  // Phase « code » (validate-code.php)
  'exhibitorWizard.code.title': 'Inscription exposant',
  'exhibitorWizard.code.subtitle': 'Saisissez le code « EXPO » communiqué par l’organisation pour démarrer votre inscription.',
  'exhibitorWizard.code.label': 'Code d’accès exposant',
  'exhibitorWizard.code.submit': 'Continuer',
  'exhibitorWizard.code.error.format': 'Le code doit être au format EXPO suivi de 6 caractères.',
  'exhibitorWizard.code.error.network': 'Connexion impossible. Vérifiez votre réseau et réessayez.',
  'exhibitorWizard.code.error.invalid': 'Code d’accès invalide. Vérifiez le code communiqué par l’organisation.',
  'exhibitorWizard.code.error.used': 'Ce code a déjà été utilisé. Si vous avez déjà créé votre compte, connectez-vous avec votre adresse e-mail.',
  // FIX 2026-07-05 (LOT 4) : ancienne adresse de contact (résidu de l'ancien
  // salon) retirée ici — on renvoie vers la demande d'accès intégrée.
  'exhibitorWizard.code.error.revoked': 'Ce code a été révoqué par l’organisation. Refaites une demande d’accès depuis la page d’inscription.',
  // LOT 4 — champs additionnels publiés au form-builder (étape Identité)
  'exhibitorWizard.custom.title': 'Informations complémentaires demandées par l’organisation',
  'exhibitorWizard.custom.required': 'Ce champ est obligatoire.',
  // Identité du compte (réutilise exhibitorSignup.field.firstName/lastName/email/consent)
  'exhibitorWizard.field.code': 'Votre code d’accès',
  'exhibitorWizard.field.phone': 'Téléphone (facultatif)',
  'exhibitorWizard.field.structure': 'Structure / entreprise (facultatif)',
  'exhibitorWizard.field.consentTracking': 'J’accepte de recevoir des informations utiles pendant le salon (facultatif).',
  // Progression + intitulés d’étape
  'exhibitorWizard.progress': 'Étape {current} / {total} — {label}',
  'exhibitorWizard.step.identity': 'Identité',
  'exhibitorWizard.step.company': 'Entreprise',
  'exhibitorWizard.step.offer': 'Offre & réseaux',
  'exhibitorWizard.step.jobs': 'Postes à pourvoir',
  'exhibitorWizard.step.stand': 'Stand & logistique',
  'exhibitorWizard.step.visibility': 'Visibilité & récapitulatif',
  'exhibitorWizard.stepIntro.identity': 'Ces informations créent votre compte. Vous vous connecterez ensuite par e-mail.',
  'exhibitorWizard.stepIntro.company': 'Présentez votre entreprise. Le nom et le logo sont obligatoires.',
  'exhibitorWizard.stepIntro.offer': 'Décrivez votre offre et ajoutez vos réseaux sociaux (facultatif).',
  'exhibitorWizard.stepIntro.jobs': 'Ajoutez les postes que vous recherchez (facultatif — vous pourrez en ajouter plus tard).',
  'exhibitorWizard.stepIntro.stand': 'Informations sur votre présence le jour du salon (facultatif).',
  'exhibitorWizard.stepIntro.visibility': 'Choisissez vos options de visibilité, puis vérifiez et validez votre inscription.',
  // Logo (étape entreprise)
  'exhibitorWizard.logo.label': 'Logo de l’entreprise',
  'exhibitorWizard.logo.hint': 'Format PNG, JPEG ou WebP, 2 Mo maximum. Le logo est obligatoire.',
  // Contact référent (sous-titre, étape entreprise)
  'exhibitorWizard.contact.title': 'Contact référent du stand',
  // Postes répétables
  'exhibitorWizard.jobs.item': 'Poste {n}',
  'exhibitorWizard.jobs.remove': 'Retirer ce poste',
  'exhibitorWizard.jobs.add': '+ Ajouter un poste',
  // Récapitulatif (dernière étape)
  'exhibitorWizard.recap.title': 'Récapitulatif',
  'exhibitorWizard.recap.company': 'Entreprise',
  'exhibitorWizard.recap.email': 'E-mail du compte',
  'exhibitorWizard.recap.jobs': 'Postes saisis',
  'exhibitorWizard.recap.intro': 'En validant, votre fiche exposant sera créée et complète. Vous pourrez la modifier à tout moment depuis « Ma fiche ».',
  // Secteurs (chargement public)
  'exhibitorWizard.sectors.error': 'Les secteurs d’activité n’ont pas pu être chargés. Vous pourrez les renseigner plus tard depuis « Ma fiche ».',
  // Navigation du wizard
  'exhibitorWizard.nav.back': 'Précédent',
  'exhibitorWizard.nav.next': 'Suivant',
  'exhibitorWizard.nav.submit': 'Valider mon inscription',
  // Erreurs de validation par étape / submit
  'exhibitorWizard.error.firstNameRequired': 'Le prénom est obligatoire.',
  'exhibitorWizard.error.lastNameRequired': 'Le nom est obligatoire.',
  'exhibitorWizard.error.emailInvalid': 'Une adresse e-mail valide est obligatoire.',
  'exhibitorWizard.error.consentRequired': 'Vous devez accepter le traitement de vos données pour continuer.',
  'exhibitorWizard.error.companyRequired': 'Le nom de l’entreprise est obligatoire.',
  'exhibitorWizard.error.logoRequired': 'Le logo de l’entreprise est obligatoire.',
  'exhibitorWizard.error.submitFailed': 'Votre inscription n’a pas pu être finalisée. Vérifiez les informations saisies.',
  'exhibitorWizard.error.network': 'Connexion impossible. Vérifiez votre réseau et réessayez.',
  // Bientôt disponible (code « animateur d’atelier » — espace construit au LOT 3)
  'exhibitorWizard.comingSoon.title': 'Espace animateur bientôt disponible',
  'exhibitorWizard.comingSoon.body': 'Votre code correspond à {lexWorkshopIndef}. L’inscription des animateurs {lexWorkshopOf} sera ouverte prochainement. Merci de votre patience.',
  'exhibitorWizard.comingSoon.cta': 'Revenir à la saisie du code',
  // Écran de succès (pas d’auto-login — connexion par e-mail)
  'exhibitorWizard.success.title': 'Votre fiche est complète ✓',
  'exhibitorWizard.success.body': 'Votre compte et votre fiche exposant ont été créés. Connectez-vous par e-mail pour la modifier à tout moment.',
  'exhibitorWizard.success.cta': 'Se connecter',

  // ───── LOT 3 (2026-07-05) — WIZARD ANIMATEUR d’atelier ───────────────
  // (Les clés exhibitorWizard.comingSoon.* ci-dessus sont désormais
  //  inutilisées — conservées, inoffensif, précédent 2026-05-18.)
  'exhibitorWizard.hostStep.identity': 'Identité',
  'exhibitorWizard.hostStep.workshops': 'Vos {lexWorkshopPlural}',
  'exhibitorWizard.hostStep.recap': 'Récapitulatif',
  'exhibitorWizard.hostStepIntro.identity': 'Créez votre compte animateur : ces informations servent à vous connecter et à vous contacter.',
  'exhibitorWizard.hostStepIntro.workshops': 'Décrivez {lexWorkshopDefPlural} que vous animerez : titre, public visé, créneaux et jauge par créneau.',
  'exhibitorWizard.hostStepIntro.recap': 'Vérifiez ces informations avant de valider votre inscription.',
  'exhibitorWizard.host.workshops.item': '{lexWorkshopCap} {n}',
  'exhibitorWizard.host.workshops.add': '+ Ajouter {lexWorkshopIndef}',
  'exhibitorWizard.host.workshops.remove': 'Retirer {lexWorkshopDem}',
  'exhibitorWizard.host.error.titleRequired': 'Le titre {lexWorkshopOfDef} est obligatoire.',
  'exhibitorWizard.host.error.slotIncomplete': 'Un créneau est incomplet : renseignez l’heure de début ET l’heure de fin.',
  'exhibitorWizard.host.error.slotOrder': 'L’heure de fin d’un créneau doit être postérieure à son heure de début.',
  'exhibitorWizard.host.recap.workshops': '{lexWorkshopPluralCap}',
  'exhibitorWizard.host.recap.workshopN': '{lexWorkshopCap} {n}',
  'exhibitorWizard.host.recap.slotsSuffix': 'créneau(x)',
  'exhibitorWizard.host.recap.intro': 'En validant, votre compte animateur et vos {lexWorkshopPlural} seront créé{lexWorkshopAgrPlural}. Vous pourrez ensuite les modifier à tout moment depuis votre espace « Mes {lexWorkshopPlural} ».',
  'exhibitorWizard.successHost.title': 'Votre espace animateur est prêt ✓',
  'exhibitorWizard.successHost.body': 'Votre compte et vos {lexWorkshopPlural} ont été créé{lexWorkshopAgrPlural}. Connectez-vous par e-mail pour les modifier à tout moment (créneaux, jauges, publication).',

  // Champs d’un atelier (composant partagé WorkshopFields — wizard + éditeur)
  'workshopFields.title': 'Titre {lexWorkshopOfDef}',
  'workshopFields.description': 'Description',
  'workshopFields.audience': 'Public visé',
  'workshopFields.audience.hint': 'Exemple : collégiens, lycéens, demandeurs d’emploi, tout public…',
  'workshopFields.room': 'Salle / zone',
  'workshopFields.slots': 'Créneaux horaires',
  'workshopFields.slots.hint': 'Ajoutez un créneau par session de votre {lexWorkshopBare} (horaire + jauge).',
  'workshopFields.slot.start': 'Début',
  'workshopFields.slot.end': 'Fin',
  'workshopFields.slot.capacity': 'Jauge (places)',
  'workshopFields.slot.add': '+ Ajouter un créneau',
  'workshopFields.slot.remove': 'Retirer',
  'workshopFields.slot.capacityHint': 'La jauge s’applique par créneau. 0 ou vide = nombre de places non limité.',

  // Espace animateur /exposant/mes-ateliers (LOT 3)
  'mesAteliers.title': 'Mes {lexWorkshopPlural}',
  'mesAteliers.subtitle': 'Créez et modifiez vos {lexWorkshopPlural} : informations, créneaux, jauge par créneau et publication.',
  'mesAteliers.back': 'Mon stand',
  'mesAteliers.loading': 'Chargement de vos {lexWorkshopPlural}…',
  'mesAteliers.notExhibitor': 'Cet espace est réservé aux animateurs et aux exposants.',
  'mesAteliers.error.load': 'Impossible de charger vos {lexWorkshopPlural}. Réessayez dans quelques instants.',
  'mesAteliers.error.save': 'Impossible d’enregistrer {lexWorkshopDef}. Réessayez.',
  'mesAteliers.error.delete': 'Impossible de supprimer {lexWorkshopDef}.',
  'mesAteliers.error.titleRequired': 'Le titre {lexWorkshopOfDef} est obligatoire.',
  'mesAteliers.empty': 'Vous n’avez pas encore {lexWorkshopOf}. Cliquez sur « + Ajouter {lexWorkshopIndef} » pour commencer.',
  'mesAteliers.action.add': '+ Ajouter {lexWorkshopIndef}',
  'mesAteliers.action.save': 'Enregistrer',
  'mesAteliers.action.delete': 'Supprimer',
  'mesAteliers.create.title': '{lexWorkshopNewCap}',
  'mesAteliers.create.submit': 'Créer {lexWorkshopDef}',
  'mesAteliers.create.cancel': 'Annuler',
  'mesAteliers.status.published': 'Publié',
  'mesAteliers.status.draft': 'Brouillon',
  'mesAteliers.field.published': 'Publier {lexWorkshopDem} (visible du public)',
  'mesAteliers.gauge.title': 'Inscriptions par créneau',
  'mesAteliers.gauge.count': '{confirmed, plural, =0 {Aucun inscrit} one {# inscrit·e} other {# inscrit·e·s}} / {capacity} places',
  'mesAteliers.gauge.countUnlimited': '{confirmed, plural, =0 {Aucun inscrit} one {# inscrit·e} other {# inscrit·e·s}} — places non limitées',
  'mesAteliers.gauge.waitlist': '(+{n} en liste d’attente)',
  'mesAteliers.delete.confirm': 'Supprimer définitivement {lexWorkshopDem} ? Cette action est irréversible.',
  'mesAteliers.delete.blockedHint': 'Suppression impossible : des inscriptions existent. Dépubliez plutôt {lexWorkshopDem}.',
  'mesAteliers.toast.created': '{lexWorkshopCap} créé{lexWorkshopAgr}.',
  'mesAteliers.toast.saved': '{lexWorkshopCap} enregistré{lexWorkshopAgr}.',
  'mesAteliers.toast.deleted': '{lexWorkshopCap} supprimé{lexWorkshopAgr}.',

  // ───── Demande d’accès exposant (sans code) ──────────────────────────
  'exhibitorRequest.title': 'Demander un accès exposant',
  'exhibitorRequest.subtitle': 'Laissez vos coordonnées : un code vous sera envoyé par e-mail après validation.',
  'exhibitorRequest.field.company': 'Nom de la structure',
  'exhibitorRequest.field.firstName': 'Prénom du contact',
  'exhibitorRequest.field.lastName': 'Nom du contact',
  'exhibitorRequest.field.email': 'Adresse e-mail',
  'exhibitorRequest.field.phone': 'Téléphone (facultatif)',
  'exhibitorRequest.field.message': 'Message (facultatif)',
  'exhibitorRequest.field.consent': 'J’accepte que mes données soient traitées pour l’étude de ma demande d’accès (RGPD).',
  'exhibitorRequest.submit': 'Envoyer ma demande',
  'exhibitorRequest.success': 'Votre demande a bien été envoyée. Vous recevrez votre code par e-mail après validation par l’organisateur.',
  'exhibitorRequest.success.backHome': 'Retour à l’accueil',
  'exhibitorRequest.link.haveCode': 'J’ai déjà un code → m’inscrire',
  'exhibitorRequest.error.company.required': 'Le nom de la structure est obligatoire.',
  'exhibitorRequest.error.firstName.required': 'Le prénom du contact est obligatoire.',
  'exhibitorRequest.error.lastName.required': 'Le nom du contact est obligatoire.',
  'exhibitorRequest.error.email.required': 'L’adresse e-mail est obligatoire.',
  'exhibitorRequest.error.email.invalid': 'Cette adresse e-mail n’est pas valide.',
  'exhibitorRequest.error.message.tooLong': 'Votre message est trop long.',
  'exhibitorRequest.error.tooLong': 'Cette valeur est trop longue.',
  'exhibitorRequest.error.consent.required': 'Vous devez accepter le traitement de vos données pour continuer.',
  'exhibitorRequest.error.tooManyRequests': 'Trop de demandes envoyées. Veuillez réessayer plus tard.',
  'exhibitorRequest.error.validation': 'Certains champs sont invalides. Vérifiez votre saisie.',
  'exhibitorRequest.error.network': 'Une erreur est survenue. Veuillez réessayer.',
  // Sélecteur de type de demande — point d'entrée dual (2026-06-23).
  'exhibitorRequest.type.legend': 'Quel accès souhaitez-vous demander ?',
  'exhibitorRequest.type.exhibitor': 'Exposant',
  'exhibitorRequest.type.exhibitor.desc':
    'Je représente une structure qui recrute et tiendra un stand.',
  'exhibitorRequest.type.workshopHost': 'Animateur {lexWorkshopOf}',
  'exhibitorRequest.type.workshopHost.desc':
    'J’anime un{lexWorkshopAgr} ou plusieurs {lexWorkshopPlural} avec des créneaux et des inscriptions.',

  // ───── Fiche exposant (MaFichePage) ──────────────────────────────────
  'maFiche.title': 'Ma fiche exposant',
  'maFiche.subtitle': 'Renseignez les informations vues par les visiteurs du salon.',
  'maFiche.back': 'Retour à mon stand',
  'maFiche.checking': 'Vérification de votre session…',
  'maFiche.loading': 'Chargement de votre fiche…',
  'maFiche.error': 'Impossible de charger votre fiche. Réessayez.',
  'maFiche.notExhibitor': 'Cette page est réservée aux exposants.',
  'maFiche.saved': 'Fiche enregistrée.',
  'maFiche.saveError': 'Échec de l’enregistrement. Vérifiez les champs.',
  'maFiche.company.required': 'Le nom de l’entreprise est obligatoire.',
  'maFiche.status.published': 'Publiée',
  'maFiche.status.draft': 'Brouillon',
  'maFiche.action.publish': 'Publier',
  'maFiche.action.unpublish': 'Repasser en brouillon',
  'maFiche.action.save': 'Enregistrer ma fiche',
  'maFiche.section.company': 'Entreprise',
  'maFiche.section.contact': 'Contact référent',
  'maFiche.section.stand': 'Stand',
  'maFiche.section.visibility': 'Visibilité (options)',
  'maFiche.section.specialRequest': 'Demande particulière',
  'maFiche.section.jobs': 'Postes à pourvoir',
  'maFiche.section.offer': 'Votre offre & vos réseaux',
  'maFiche.field.offerDescription': 'Description de votre offre / ce que vous proposez',
  'maFiche.field.applyUrl': 'Lien pour candidater en direct',
  'maFiche.field.linkedin': 'LinkedIn',
  'maFiche.field.instagram': 'Instagram',
  'maFiche.field.facebook': 'Facebook',
  'maFiche.field.x': 'X (Twitter)',
  'maFiche.field.bluesky': 'Bluesky',
  'maFiche.field.logisticsNeeds': 'Besoins logistiques du stand',
  'maFiche.field.logisticsNeeds.hint': 'Visible uniquement par l’organisation (ex. prise électrique, écran, table supplémentaire).',
  'maFiche.field.companyName': 'Nom de l’entreprise',
  'maFiche.field.address': 'Adresse',
  'maFiche.field.postalCode': 'Code postal',
  'maFiche.field.city': 'Ville',
  'maFiche.field.website': 'Site internet',
  'maFiche.field.jobsUrl': 'Page de nos offres d’emploi',
  'maFiche.field.sector': 'Secteur principal',
  'maFiche.field.sector.none': '— Aucun —',
  'maFiche.field.description': 'Présentation de l’entreprise',
  'maFiche.field.logo': 'Logo',
  'maFiche.field.contactFirstName': 'Prénom du référent',
  'maFiche.field.contactLastName': 'Nom du référent',
  'maFiche.field.contactPhone': 'Téléphone',
  'maFiche.field.contactEmail': 'Adresse e-mail',
  'maFiche.field.spontaneousEmail': 'E-mail pour les candidatures spontanées',
  'maFiche.field.standStaffCount': 'Personnes présentes sur le stand',
  'maFiche.field.standStaffCount.preview':
    '{n, plural, =0 {Aucune personne sur le stand} one {# personne sur le stand} other {# personnes sur le stand}}',
  'maFiche.field.standStaffCount.decrease': 'Retirer une personne',
  'maFiche.field.standStaffCount.increase': 'Ajouter une personne',
  'maFiche.field.specialRequest': 'Votre demande',
  'maFiche.logo.alt': 'Logo de l’entreprise',
  'maFiche.logo.none': 'Aucun logo',
  'maFiche.logo.upload': 'Téléverser un logo',
  'maFiche.logo.delete': 'Supprimer le logo',
  'maFiche.logo.uploaded': 'Logo mis à jour.',
  'maFiche.logo.deleted': 'Logo supprimé.',
  'maFiche.logo.error': 'Échec sur le logo. Réessayez.',
  'maFiche.logo.badType': 'Format non accepté (PNG, JPG, SVG ou WebP uniquement).',
  'maFiche.logo.tooBig': 'Le fichier dépasse 2 Mo.',
  'maFiche.visibility.intro': 'Options de mise en avant (sur demande, susceptibles d’une facturation par l’organisation).',
  'maFiche.opt.homeBanner': 'Bannière sur la page d’accueil',
  'maFiche.opt.featuredLogo': 'Logo mis en avant',
  'maFiche.opt.giantScreenAd': 'Publicité sur l’écran géant',
  // Pieces des options de visibilite (Mika, 2026-08-26).
  'visibilityAssets.intro':
    'Vous avez souscrit des options de mise en avant. Merci de déposer les fichiers correspondants, aux formats indiqués : sans eux, l’organisation ne pourra pas les mettre en place.',
  'visibilityAssets.provided': 'Fichier fourni',
  'visibilityAssets.missing': 'Fichier attendu',
  'visibilityAssets.choose': 'Choisir un fichier',
  'visibilityAssets.replace': 'Remplacer',
  'visibilityAssets.remove': 'Retirer',
  'visibilityAssets.uploaded': 'Fichier enregistré.',
  'visibilityAssets.removed': 'Fichier retiré.',
  'visibilityAssets.uploadError': 'Impossible d’enregistrer ce fichier.',
  'visibilityAssets.removeError': 'Impossible de retirer ce fichier.',
  // Renvoi depuis « Votre offre » vers la section des postes (Mika 26/08) :
  // sans lui, l'exposant croit avoir fini et n'ouvre jamais la derniere section.
  'maFiche.offer.jumpToJobs':
    'Cette rubrique présente votre offre en général. Le détail de chaque poste — intitulé, contrat, secteur, nombre de postes — se saisit plus bas, dans <lien>Postes à pourvoir</lien>. Au moins un poste complet y est requis.',
  // Postes a pourvoir — lisibilite + exigence (demande Mika 2026-08-26).
  'maFiche.jobs.chip.required': 'Au moins un poste complet est requis',
  'maFiche.jobs.chip.ok': '{n, plural, one {# poste complet} other {# postes complets}}',
  'maFiche.job.complete': 'Complet',
  'maFiche.job.missing': 'À compléter : {champs}',
  'maFiche.job.field.title': 'intitulé',
  'maFiche.job.field.description': 'description',
  'maFiche.job.field.contract_type': 'type de contrat',
  'maFiche.job.field.sector_id': 'secteur',
  'maFiche.job.field.positions_count': 'nombre de postes',
  'maFiche.jobs.intro': 'Ajoutez les postes que vous proposez. Chaque poste peut être publié ou gardé en brouillon.',
  'maFiche.jobs.loading': 'Chargement des postes…',
  'maFiche.jobs.error': 'Impossible de charger les postes.',
  'maFiche.jobs.empty': 'Aucun poste pour le moment.',
  'maFiche.job.addTitle': 'Ajouter un poste',
  'maFiche.job.add': 'Ajouter le poste',
  'maFiche.job.save': 'Enregistrer',
  'maFiche.job.delete': 'Supprimer',
  'maFiche.job.created': 'Poste ajouté.',
  'maFiche.job.saved': 'Poste enregistré.',
  'maFiche.job.deleted': 'Poste supprimé.',
  'maFiche.job.error': 'Échec sur le poste. Réessayez.',
  'maFiche.job.titleRequired': 'L’intitulé du poste est obligatoire.',
  'maFiche.job.confirmDelete': 'Supprimer définitivement ce poste ?',
  'maFiche.job.untitled': 'Poste sans intitulé',
  'maFiche.job.moveUp': 'Monter ce poste',
  'maFiche.job.moveDown': 'Descendre ce poste',
  'maFiche.job.title': 'Intitulé du poste',
  'maFiche.job.contractType': 'Type de contrat',
  'maFiche.job.positionsCount': 'Nombre de postes',
  'maFiche.job.sector': 'Secteur',
  'maFiche.job.experienceLevel': 'Expérience',
  'maFiche.job.location': 'Lieu',
  'maFiche.job.description': 'Description',
  'maFiche.job.isPublished': 'Publier ce poste',

  // ───── Admin — demandes de code exposant (ExhibitorCodeRequestsPage) ──
  'adminNav.exhibitorCodeRequests': 'Demandes de code',
  // Écran « Animateurs » (2026-08-26) — pendant de l'écran Exposants.
  'adminNav.workshopHosts': 'Animateurs',
  'adminNav.visibility': 'Options de visibilité',
  // Recherche dans le menu (Mika, 2026-08-26) — 69 entrees, 14 groupes.
  // Creation d'un code ANIMATEUR depuis l'admin (Mika, 2026-08-30).
  'exhibitorCodes.field.profileKind': 'Profil',
  'exhibitorCodes.profile.exhibitor': 'Exposant / recruteur',
  'exhibitorCodes.profile.workshopHost': 'Animateur {lexWorkshopDefPlural}',
  'adminShell.search.placeholder': 'Rechercher une page…  (Ctrl+K)',
  'adminShell.search.label': 'Rechercher une page du menu d’administration',
  'adminShell.search.empty': 'Aucune page ne correspond.',
  // Ecran de recuperation des pieces (Mika, 2026-08-26).
  'visibilityAdmin.title': 'Options de visibilité',
  'visibilityAdmin.subtitle': 'Les fichiers fournis par les exposants pour les options de mise en avant qu’ils ont souscrites.',
  'visibilityAdmin.filter.state': 'État',
  'visibilityAdmin.filter.all': 'Tous',
  'visibilityAdmin.filter.missing': 'Fichier manquant',
  'visibilityAdmin.filter.provided': 'Fichier fourni',
  'visibilityAdmin.filter.option': 'Option',
  'visibilityAdmin.filter.allOptions': 'Toutes les options',
  'visibilityAdmin.export': 'Exporter (CSV)',
  'visibilityAdmin.summary': '{total, plural, one {# option souscrite} other {# options souscrites}} — {manquants, plural, =0 {aucun fichier manquant} one {# fichier manquant} other {# fichiers manquants}}.',
  'visibilityAdmin.provided': 'Fourni',
  'visibilityAdmin.missing': 'Manquant',
  'visibilityAdmin.loading': 'Chargement…',
  'visibilityAdmin.error': 'Impossible de charger la liste.',
  'visibilityAdmin.empty': 'Aucune option de visibilité souscrite pour l’instant.',
  'workshopHosts.title': 'Animateurs',
  'workshopHosts.subtitle':
    'Les personnes qui animent {lexWorkshopDefPlural}. Rattacher quelqu’un à {lexWorkshopIndef} suffit à en faire un animateur.',
  'workshopHosts.filter.search': 'Recherche',
  'workshopHosts.filter.searchPlaceholder': 'Nom, e-mail, structure',
  'workshopHosts.filter.workshop': '{lexWorkshopCap}',
  'workshopHosts.filter.allWorkshops': 'Tous',
  'workshopHosts.loading': 'Chargement des animateurs…',
  'workshopHosts.error': 'Impossible de charger la liste des animateurs.',
  'workshopHosts.empty': 'Aucun animateur ne correspond à ces critères.',
  'workshopHosts.badge.count':
    '{n, plural, one {# {lexWorkshopBare} animé{lexWorkshopAgr}} other {# {lexWorkshopDefPlural} animé{lexWorkshopAgrPlural}}}',
  'workshopHosts.notPublished': 'non publié{lexWorkshopAgr}',
  'workshopHosts.action.add': '+ Rattacher un animateur',
  'workshopHosts.action.unassign': 'Détacher',
  'workshopHosts.unassign.confirm':
    'Détacher {name} de « {workshop} » ? Le compte reste intact, seul le rattachement est retiré.',
  'workshopHosts.assign.success': 'Animateur rattaché.',
  'workshopHosts.unassign.success': 'Animateur détaché.',
  'workshopHosts.assign.error': 'Opération impossible.',
  'workshopHosts.add.title': 'Rattacher un animateur',
  'workshopHosts.add.intro':
    'Choisissez une personne déjà inscrite et {lexWorkshopDef} qu’elle animera. Aucun compte n’est créé : on relie l’existant.',
  'workshopHosts.add.filter': 'Filtrer la liste',
  'workshopHosts.add.person': 'Personne',
  'workshopHosts.add.choosePerson': '— Choisir une personne —',
  'workshopHosts.add.workshop': '{lexWorkshopCap}',
  'workshopHosts.add.chooseWorkshop': '— Choisir —',
  'workshopHosts.add.submit': 'Rattacher',
  // Droits VIRTUELS (2026-08-26) : des capacites attribuables dans
  // /admin/access, a un role ET a une personne, sans page derriere.
  'adminNav.group.rights': 'Droits particuliers',
  'adminNav.right.impersonate': 'Voir en tant que',
  // Groupe de menu ajoute le 2026-08-26 : « Modifier son mot de passe » etait
  // un droit sans ecran ; il devient une vraie section, donc il lui faut une
  // place dans le menu — c'est precisement ce que Mika ne trouvait pas.
  'adminNav.group.myAccount': 'Mon compte',

  // Ecran « Mon mot de passe » (admin/self-password).
  'selfPassword.title': 'Mon mot de passe',
  'selfPassword.subtitle':
    'Changez le mot de passe de votre propre compte. Vous devez connaître le mot de passe actuel.',
  'selfPassword.current': 'Mot de passe actuel',
  'selfPassword.new': 'Nouveau mot de passe',
  'selfPassword.confirm': 'Confirmer le nouveau mot de passe',
  'selfPassword.rule.length': 'Au moins {n} caractères',
  'selfPassword.rule.families':
    'Au moins {n} familles parmi : minuscules, majuscules, chiffres, symboles',
  'selfPassword.mismatch': 'Les deux saisies ne correspondent pas.',
  'selfPassword.note2fa':
    'La vérification en deux étapes reste demandée à la connexion : ce mot de passe n’en est que le premier facteur.',
  'selfPassword.submit': 'Modifier mon mot de passe',
  'selfPassword.error': 'Impossible de modifier le mot de passe. Réessayez.',

  'adminNav.right.selfPassword': 'Modifier son mot de passe',
  'codeRequests.title': 'Demandes de code exposant',
  'codeRequests.subtitle': 'Approuvez ou rejetez les demandes de code d’accès des structures.',
  'codeRequests.loading': 'Chargement des demandes…',
  'codeRequests.error': 'Impossible de charger les demandes.',
  'codeRequests.empty': 'Aucune demande pour ce filtre.',
  'codeRequests.filter.pending': 'En attente',
  'codeRequests.filter.approved': 'Approuvées',
  'codeRequests.filter.rejected': 'Rejetées',
  'codeRequests.filter.all': 'Toutes',
  'codeRequests.status.pending': 'En attente',
  'codeRequests.status.approved': 'Approuvée',
  'codeRequests.status.rejected': 'Rejetée',
  'codeRequests.col.company': 'Structure',
  'codeRequests.col.contact': 'Contact',
  'codeRequests.col.message': 'Message',
  'codeRequests.col.date': 'Demandée le',
  'codeRequests.col.status': 'Statut',
  'codeRequests.col.actions': 'Actions',
  'codeRequests.action.approve': 'Approuver',
  'codeRequests.action.reject': 'Rejeter',
  'codeRequests.action.confirmReject': 'Confirmer le rejet',
  'codeRequests.action.cancel': 'Annuler',
  'codeRequests.action.close': 'Fermer',
  'codeRequests.action.copy': 'Copier le code',
  'codeRequests.approve.success': 'Demande approuvée, code généré.',
  'codeRequests.approve.error': 'Échec de l’approbation. Réessayez.',
  'codeRequests.reject.success': 'Demande rejetée.',
  'codeRequests.reject.error': 'Échec du rejet. Réessayez.',
  'codeRequests.reject.modalTitle': 'Rejeter la demande',
  'codeRequests.reject.intro': 'Rejeter la demande de « {name} » ?',
  'codeRequests.reject.reasonLabel': 'Motif (facultatif)',
  'codeRequests.reject.reasonPlaceholder': 'Expliquez le motif du rejet…',
  'codeRequests.copy.success': 'Code copié.',
  'codeRequests.copy.error': 'Impossible de copier le code.',
  'codeRequests.codeModal.title': 'Code généré',
  'codeRequests.codeModal.intro': 'Code d’accès pour « {name} » :',
  'codeRequests.codeModal.emailSent': 'Le code a été envoyé par e-mail à la structure.',
  'codeRequests.codeModal.emailNotSent': 'L’e-mail n’a pas pu être envoyé — communiquez le code manuellement.',

  // ───── Admin — modèles d'e-mail transactionnels (EmailTemplatesAdminPage) ──
  // Phase 1 onboarding dual (2026-06-23) — édition de exhibitor_code /
  // workshop_host_code (clés fixes, seedées V68).
  'emailTemplates.title': 'Modèles d’e-mail',
  'emailTemplates.lead':
    'Personnalisez l’objet et le corps des e-mails envoyés automatiquement lors de l’approbation d’une demande de code (exposant ou animateur {lexWorkshopOf}).',
  'emailTemplates.variables.title': 'Variables disponibles',
  'emailTemplates.variables.help':
    'Insérez ces variables dans l’objet ou le corps : elles sont remplacées à l’envoi par les valeurs réelles du destinataire.',
  'emailTemplates.list.title': 'Modèles ({count})',
  'emailTemplates.loading': 'Chargement des modèles…',
  'emailTemplates.error': 'Impossible de charger les modèles.',
  'emailTemplates.empty': 'Aucun modèle d’e-mail.',
  'emailTemplates.badge.inactive': 'Inactif',
  'emailTemplates.btn.edit': 'Modifier',
  'emailTemplates.btn.preview': 'Aperçu',
  'emailTemplates.btn.save': 'Enregistrer',
  'emailTemplates.btn.cancel': 'Annuler',
  'emailTemplates.btn.close': 'Fermer',
  'emailTemplates.modal.editTitle': 'Modifier le modèle d’e-mail',
  'emailTemplates.modal.previewTitle': 'Aperçu du modèle',
  'emailTemplates.field.name': 'Nom du modèle',
  'emailTemplates.field.subject': 'Objet de l’e-mail',
  'emailTemplates.field.bodyHtml': 'Corps HTML',
  'emailTemplates.field.bodyText': 'Corps texte (repli sans HTML)',
  'emailTemplates.field.active': 'Modèle actif (utilisé à l’envoi)',
  'emailTemplates.preview.help':
    'Aperçu du corps HTML enregistré. Les variables {{…}} sont affichées telles quelles ; elles seront remplacées à l’envoi.',
  'emailTemplates.preview.subject': 'Objet :',
  'emailTemplates.preview.text': 'Voir le corps texte',
  'emailTemplates.toast.saved': 'Modèle enregistré.',
  'emailTemplates.toast.error': 'Échec de l’enregistrement. Réessayez.',

  // ═══════════════════════════════════════════════════════════════════════
  // 2026-06-03 (Mika — Sprint D) — Dépôt de CV candidats.
  // ═══════════════════════════════════════════════════════════════════════
  'settingsAdmin.section.cv': 'CV candidats',

  // ───── Visiteur : « Mon CV » (MonProfilPage) ─────────────────────────
  'monProfil.cv.title': 'Mon CV',
  'monProfil.cv.intro': 'Déposez votre CV pour le partager avec les exposants que vous rencontrez.',
  'monProfil.cv.loading': 'Chargement de votre CV…',
  'monProfil.cv.disabled': 'Le dépôt de CV est désactivé pour cet évènement.',
  'monProfil.cv.none': 'Aucun CV déposé pour le moment.',
  'monProfil.cv.depositedOn': 'déposé le {when}',
  'monProfil.cv.consent': "J'accepte que mon CV soit partagé avec les exposants que je rencontre.",
  'monProfil.cv.upload': 'Déposer mon CV',
  'monProfil.cv.replace': 'Remplacer mon CV',
  'monProfil.cv.delete': 'Supprimer',
  'monProfil.cv.constraints': 'Formats acceptés : {ext}. Taille maximale : {max} Mo.',
  'monProfil.cv.shareOnScan': 'Partager automatiquement mon CV avec les exposants qui scannent mon badge',
  // 2026-09-09 — libelle affiche lorsque l'instance ouvre les CV a TOUS les
  // exposants (reglage cv.visibility_scope). Le consentement doit decrire ce
  // qui se passe reellement, sinon il n'en est pas un.
  'monProfil.cv.shareAll': 'Partager mon CV avec l’ensemble des exposants {eventTypeOf}, y compris ceux que je ne rencontrerai pas',
  'monProfil.cv.uploaded': 'CV déposé.',
  'monProfil.cv.deleted': 'CV supprimé.',
  'monProfil.cv.shareSaved': 'Préférence de partage enregistrée.',
  'monProfil.cv.confirm.delete': 'Supprimer définitivement votre CV ?',
  'monProfil.cv.error.consentRequired': 'Veuillez cocher la case de consentement avant de déposer votre CV.',
  'monProfil.cv.error.tooLarge': 'Fichier trop volumineux.',
  'monProfil.cv.error.invalidType': 'Format de fichier non autorisé.',
  'monProfil.cv.error.upload': 'Échec du dépôt du CV.',
  'monProfil.cv.error.delete': 'Échec de la suppression du CV.',
  'monProfil.cv.error.generic': 'Une erreur est survenue.',

  // ───── Exposant : « CV reçus » (CvRecusPage) ─────────────────────────
  'cvRecus.title': 'CV reçus',
  'cvRecus.subtitle': 'Les CV partagés par les candidats que vous avez scannés.',
  'cvRecus.back': 'Retour à mon stand',
  'cvRecus.checking': 'Vérification de votre accès…',
  'cvRecus.loading': 'Chargement des CV…',
  'cvRecus.error': 'Impossible de charger les CV reçus.',
  'cvRecus.empty': 'Aucun CV partagé pour l\'instant.',
  'cvRecus.scannedAt': 'Scanné le {when}',
  'cvRecus.download': 'Télécharger le CV',
  'cvRecus.notExhibitor': 'Accès réservé aux exposants.',
  'monStand.cvRecus': 'CV reçus',

  // ═══════════════════════════════════════════════════════════════════════
  // M5 / G6 (Mika 2026-06-14) — Page « Mes rencontres » exposant éditable.
  // ═══════════════════════════════════════════════════════════════════════
  'mesRencontres.title': 'Mes rencontres',
  'mesRencontres.lead': 'Toutes les personnes que vous avez scannées. Modifiez vos notes, votre niveau d’intérêt et le type d’échange pour préparer votre suivi.',
  'mesRencontres.back': 'Retour à mon stand',
  'mesRencontres.checking': 'Vérification de votre session…',
  'mesRencontres.loading': 'Chargement de vos rencontres…',
  'mesRencontres.error': 'Impossible de charger vos rencontres. Réessayez.',
  'mesRencontres.empty': 'Aucune rencontre pour ce filtre. Scannez les badges des candidats depuis le scanner.',
  'mesRencontres.notExhibitor': 'Cette page est réservée aux exposants.',
  'mesRencontres.export': '📥 Exporter (CSV)',
  'mesRencontres.count': '{n, plural, one {# rencontre} other {# rencontres}}',
  // Filtres
  'mesRencontres.filters.aria': 'Filtrer par type d’échange',
  'mesRencontres.filter.all': 'Toutes',
  'mesRencontres.filter.entretien': 'Entretien',
  'mesRencontres.filter.depot_cv': 'Dépôt de CV',
  'mesRencontres.filter.information': 'Information',
  'mesRencontres.filter.autre': 'Autre',
  'mesRencontres.filter.none': 'Non qualifié',
  // Recherche
  'mesRencontres.search.ph': 'Rechercher un nom, un établissement…',
  'mesRencontres.search.btn': 'Rechercher',
  // Colonnes
  'mesRencontres.col.when': 'Quand',
  'mesRencontres.col.candidate': 'Candidat·e',
  'mesRencontres.col.interest': 'Intérêt',
  'mesRencontres.col.interaction': 'Type d’échange',
  'mesRencontres.col.notes': 'Notes',
  'mesRencontres.col.action': 'Action',
  // Type d'interaction (édition)
  'mesRencontres.interaction.none': '— Non précisé —',
  'mesRencontres.interaction.entretien': 'Entretien',
  'mesRencontres.interaction.depot_cv': 'Dépôt de CV',
  'mesRencontres.interaction.information': 'Information',
  'mesRencontres.interaction.autre': 'Autre',
  // Métadonnées candidat
  'mesRencontres.cv': 'CV',
  'mesRencontres.cvShared': 'Ce candidat a partagé son CV.',
  'mesRencontres.jobSearch': 'Recherche : {v}',
  // Édition
  'mesRencontres.notes.placeholder': 'Vos notes sur cette rencontre…',
  'mesRencontres.save': 'Enregistrer',
  'mesRencontres.saved': 'Rencontre enregistrée.',
  'mesRencontres.saveError': 'Échec de l’enregistrement. Réessayez.',

  // ═══════════════════════════════════════════════════════════════════════
  // 2026-06-03 (Mika — Sprint E) — Annuaire public des exposants.
  // ═══════════════════════════════════════════════════════════════════════
  'exposants.eyebrow': '{eventName} · Salon du recrutement et de l’emploi',
  'exposants.title': 'Les exposants',
  'exposants.subtitle': 'Découvrez les entreprises qui recrutent et leurs postes à pourvoir.',
  'exposants.stats.exhibitors': 'entreprises présentes',
  'exposants.stats.jobs': 'postes à pourvoir',
  'exposants.stats.sectors': 'secteurs représentés',
  'exposants.search.placeholder': 'Rechercher une entreprise, un poste, un secteur…',
  'exposants.search.label': 'Rechercher un exposant',
  'exposants.sectors.label': 'Filtrer par secteur',
  'exposants.sectors.all': 'Tous',
  'exposants.count': '{count, plural, =0 {Aucun exposant} one {# exposant affiché} other {# exposants affichés}}',
  'exposants.empty': 'Aucun exposant ne correspond à votre recherche.',
  'exposants.card.logoAlt': 'Logo {name}',
  'exposants.card.jobs': '{count, plural, one {# poste à pourvoir} other {# postes à pourvoir}}',
  'exposants.card.info': 'Orientation / information',
  'exposants.card.viewDetail': 'Voir la fiche',
  'exposants.card.website': 'Site',
  'exposants.card.offers': 'Offres',
  'exposants.detail.back': 'Retour',
  'exposants.detail.backToList': 'Revenir à l’annuaire',
  'exposants.detail.errorTitle': 'Exposant introuvable',
  'exposants.detail.notFound': 'Cet exposant n’existe pas ou n’est pas publié.',
  'exposants.detail.invalidId': 'Identifiant d’exposant invalide.',
  'exposants.detail.about': 'À propos',
  'exposants.detail.offer': 'Notre offre',
  'exposants.detail.links': 'Liens utiles',
  'exposants.detail.spontaneous': 'Candidature spontanée',
  'exposants.detail.apply': 'Postuler',
  'exposants.detail.jobs': '{count, plural, =0 {Postes à pourvoir} one {# poste à pourvoir} other {# postes à pourvoir}}',
  'exposants.detail.noJobs': 'Aucun poste publié pour le moment — passez sur le stand le jour J.',

  // ═══════════════════════════════════════════════════════════════════════
  // 2026-06-03 (Mika — Sprint F) — Type d'interaction du check-in (emploi).
  // ═══════════════════════════════════════════════════════════════════════
  'checkin.interaction.label': "Type d'interaction",
  'checkin.interaction.none': '— Non précisé —',
  'checkin.interaction.entretien': 'Entretien',
  'checkin.interaction.depot_cv': 'Dépôt de CV',
  'checkin.interaction.information': 'Information',
  'checkin.interaction.autre': 'Autre',
  'monStand.col.interaction': 'Type',
  'monStand.scan.interaction.entretien': 'Entretien',
  'monStand.scan.interaction.depot_cv': 'Dépôt de CV',
  'monStand.scan.interaction.information': 'Information',
  'monStand.scan.interaction.autre': 'Autre',

  // ═══════════════════════════════════════════════════════════════════════
  // 2026-06-03 (Mika — Sprint AI) — Clé API IA chiffrée éditable au back-office.
  // ═══════════════════════════════════════════════════════════════════════
  'aiConfig.apiKey.label': 'Clé API',
  'aiConfig.apiKey.placeholder': 'Saisir une nouvelle clé (laisser vide pour ne pas changer)',
  'aiConfig.apiKey.setOn': 'Clé définie le {date}',
  'aiConfig.apiKey.none': 'Aucune clé enregistrée',
  'aiConfig.apiKey.clear': 'Supprimer la clé',
  'aiConfig.apiKey.save': 'Enregistrer la clé',
  'aiConfig.apiKey.save.success': 'Clé API enregistrée.',
  'aiConfig.apiKey.save.error': 'Échec de l’enregistrement de la clé API.',
  'aiConfig.apiKey.help': 'La clé est chiffrée et n’est jamais réaffichée. Source de clé : réglage ai.key_source (db par défaut).',

  // ═══════════════════════════════════════════════════════════════════════
  // NOLAS 2026 — Vague 1 / LOT-G (Sondages, M8) — intégration chef 2026-06-16.
  // Préfixes : surveys.* (admin) · survey.* (commun) · mesSondages.* (candidat).
  // ═══════════════════════════════════════════════════════════════════════
  'adminNav.surveys': 'Sondages',
  'survey.boolean.yes': 'Oui',
  'survey.boolean.no': 'Non',
  // Admin (surveys.*)
  'surveys.title': 'Sondages',
  'surveys.lead': 'Créez des sondages ciblés, suivez les résultats et exportez les réponses.',
  'surveys.loading': 'Chargement…',
  'surveys.error': 'Impossible de charger les sondages.',
  'surveys.empty': 'Aucun sondage pour le moment. Créez le premier !',
  'surveys.btn.create': 'Nouveau sondage',
  'surveys.btn.edit': 'Éditer',
  'surveys.btn.results': 'Résultats',
  'surveys.btn.export': 'Exporter CSV',
  'surveys.btn.delete': 'Supprimer',
  'surveys.btn.cancel': 'Annuler',
  'surveys.btn.save': 'Enregistrer',
  'surveys.anon': 'Anonyme',
  'surveys.counts': '{q} questions · {r} réponses',
  'surveys.confirm.delete': 'Supprimer le sondage « {title} » ?',
  'surveys.modal.create': 'Nouveau sondage',
  'surveys.modal.edit': 'Éditer le sondage',
  'surveys.status.draft': 'Brouillon',
  'surveys.status.published': 'Publié',
  'surveys.status.closed': 'Clos',
  'surveys.phase.any': 'Toujours',
  'surveys.phase.before': 'Avant',
  'surveys.phase.during': 'Pendant',
  'surveys.phase.after': 'Après',
  'surveys.field.title': 'Titre',
  'surveys.field.slug': 'Identifiant (slug)',
  'surveys.field.description': 'Description',
  'surveys.field.phase': 'Phase',
  'surveys.field.status': 'Statut',
  'surveys.field.anonymous': 'Sondage anonyme (aucune identité dans l’export)',
  'surveys.field.onePerUser': 'Une seule réponse par personne',
  'surveys.field.allAudience': 'Tout le monde',
  'surveys.field.roles': 'Rôles (séparés par des virgules)',
  'surveys.field.visitorType': 'Types de visiteur',
  'surveys.field.sectorSlug': 'Secteurs',
  'surveys.field.jobSearch': 'Recherche d’emploi',
  'surveys.section.audience': 'Ciblage',
  'surveys.section.questions': 'Questions',
  'surveys.lockedNote': 'Ce sondage a déjà des réponses : les questions ne sont plus modifiables (les métadonnées le restent).',
  'surveys.q.type': 'Type de question',
  'surveys.q.label': 'Libellé',
  'surveys.q.help': 'Aide (optionnel)',
  'surveys.q.options': 'Options (une par ligne)',
  'surveys.q.options.ph': 'Option 1\nOption 2',
  'surveys.q.required': 'Réponse obligatoire',
  'surveys.q.add': '+ Ajouter une question',
  'surveys.q.up': 'Monter',
  'surveys.q.down': 'Descendre',
  'surveys.q.remove': 'Supprimer la question',
  'surveys.qtype.text': 'Texte court',
  'surveys.qtype.textarea': 'Texte long',
  'surveys.qtype.single': 'Choix unique',
  'surveys.qtype.multiple': 'Choix multiple',
  'surveys.qtype.boolean': 'Oui / Non',
  'surveys.qtype.scale': 'Échelle',
  'surveys.qtype.nps': 'Recommandation (0-10)',
  'surveys.qtype.rating': 'Note (étoiles)',
  'surveys.results.title': 'Résultats du sondage',
  'surveys.results.error': 'Impossible de charger les résultats.',
  'surveys.results.total': '{n, plural, one {# réponse} other {# réponses}}',
  'surveys.results.average': 'Moyenne : {avg} ({n} réponses)',
  'surveys.results.noText': 'Aucune réponse.',
  'surveys.toast.saved': 'Sondage enregistré.',
  'surveys.toast.deleted': 'Sondage supprimé.',
  'surveys.toast.duplicateSlug': 'Cet identifiant (slug) existe déjà.',
  'surveys.toast.locked': 'Ce sondage a déjà des réponses : ses questions ne sont plus modifiables.',
  'surveys.toast.hasResponses': 'Impossible de supprimer : ce sondage a déjà des réponses.',
  'surveys.toast.error': 'Une erreur est survenue.',
  // Candidat (mesSondages.*)
  'mesSondages.title': 'Sondages',
  'mesSondages.lead': 'Donnez votre avis — quelques minutes suffisent pour nous aider à améliorer le salon.',
  'mesSondages.loading': 'Chargement…',
  'mesSondages.listError': 'Impossible de charger les sondages. Réessayez dans un instant.',
  'mesSondages.detailError': 'Ce sondage n’est plus disponible.',
  'mesSondages.empty': 'Aucun sondage ne vous est proposé pour le moment. Revenez plus tard !',
  'mesSondages.questionsCount': '{count, plural, one {# question} other {# questions}}',
  'mesSondages.doneBadge': '✓ Répondu',
  'mesSondages.answer': 'Répondre',
  'mesSondages.answered': 'Déjà répondu',
  'mesSondages.back': '← Retour aux sondages',
  'mesSondages.anonymous': '🔒 Sondage anonyme : vos réponses ne sont pas associées à votre nom.',
  'mesSondages.alreadyAnswered': 'Vous avez déjà répondu à ce sondage. Merci !',
  'mesSondages.submit': 'Envoyer mes réponses',
  'mesSondages.error.required': 'Cette réponse est obligatoire.',
  'mesSondages.toast.submitted': 'Merci, votre réponse a bien été enregistrée.',
  'mesSondages.toast.already': 'Vous avez déjà répondu à ce sondage.',
  'mesSondages.toast.error': 'Échec de l’envoi. Réessayez dans un instant.',

  // ═══════════════════════════════════════════════════════════════════════
  // NOLAS 2026 — Vague 1 / LOT-J (Postes + matching, M9) — intégration chef
  // 2026-06-16. Préfixes : jobs.* (page + matching) · monEspace.tile.jobs.*.
  // ⚠ jobs.matching.reason.* sont accentués (construits depuis le token de
  // raison renvoyé par l'API : secteur|expérience|contrat|profil).
  // ═══════════════════════════════════════════════════════════════════════
  'jobs.eyebrow': 'Salon {eventName}',
  'jobs.title': 'Postes disponibles',
  'jobs.subtitle': 'Découvrez les postes à pourvoir des exposants du salon et trouvez ceux qui vous correspondent.',
  'jobs.stats.jobs': 'postes',
  'jobs.stats.positions': 'opportunités',
  'jobs.stats.sectors': 'secteurs',
  'jobs.search.placeholder': 'Rechercher un poste, une entreprise, une ville…',
  'jobs.search.label': 'Rechercher un poste',
  'jobs.filter.contract.label': 'Type de contrat',
  'jobs.filter.contract.all': 'Tous les contrats',
  'jobs.filter.level.label': "Niveau d'expérience",
  'jobs.filter.level.all': 'Tous les niveaux',
  'jobs.sectors.label': 'Filtrer par secteur',
  'jobs.sectors.all': 'Tous',
  'jobs.count': '{count, plural, =0 {Aucun poste} one {# poste} other {# postes}}',
  'jobs.empty': 'Aucun poste ne correspond à votre recherche pour le moment.',
  'jobs.card.logoAlt': 'Logo de {name}',
  'jobs.card.matchBadge': 'Correspond à votre profil',
  'jobs.card.positions': '{count, plural, one {# poste} other {# postes}}',
  'jobs.card.viewDetail': 'Voir le poste',
  // Détail poste
  'jobs.detail.eyebrow': 'Poste à pourvoir',
  'jobs.detail.location': 'Lieu',
  'jobs.detail.positions': 'Postes ouverts',
  'jobs.detail.about': 'Le poste',
  'jobs.detail.company': "L'entreprise",
  'jobs.detail.viewExhibitor': "Voir la fiche de l'entreprise",
  'jobs.detail.website': 'Site web',
  'jobs.detail.offers': 'Toutes ses offres',
  'jobs.detail.back': 'Retour aux postes',
  'jobs.detail.invalidId': 'Identifiant de poste invalide.',
  'jobs.detail.notFound': "Ce poste n'est plus disponible.",
  'jobs.detail.errorTitle': 'Poste indisponible',
  'jobs.detail.backToList': 'Voir tous les postes',
  // MatchingPanel
  'jobs.matching.title': 'Postes qui vous correspondent',
  'jobs.matching.subtitle': 'Sélectionnés selon votre secteur, votre expérience et votre recherche.',
  'jobs.matching.subtitleGeneric': 'Quelques postes à découvrir au salon.',
  'jobs.matching.notConnected': 'Connectez-vous pour voir les postes qui correspondent à votre profil.',
  'jobs.matching.connect': 'Se connecter',
  'jobs.matching.viewAll': 'Voir tous les postes',
  'jobs.matching.error': 'Impossible de charger vos correspondances pour le moment.',
  'jobs.matching.empty': "Aucune correspondance pour l'instant. Complétez votre profil pour des recommandations plus précises.",
  'jobs.matching.reason.secteur': 'Secteur',
  'jobs.matching.reason.expérience': 'Expérience',
  'jobs.matching.reason.contrat': 'Contrat',
  'jobs.matching.reason.profil': 'Profil',
  // Tuile MonEspace (clés RÉSERVÉES par le contrat)
  'monEspace.tile.jobs.title': 'Postes',
  'monEspace.tile.jobs.aria': 'Voir les postes disponibles et ceux qui vous correspondent',

  // ═══════════════════════════════════════════════════════════════════════
  // NOLAS 2026 — Vague 1 / LOT-H (Contenu éditorial configurable, M7) —
  // intégration chef 2026-06-16. Préfixe : contentAdmin.* (page admin) +
  // adminNav.content. Les blocs publics (<ContentBlock>) réutilisent les
  // clés i18n existantes comme fallbackId (ex. about.title/about.intro) —
  // pas de nouvelles clés content.* tant que seul l'À propos est câblé.
  // ═══════════════════════════════════════════════════════════════════════
  'adminNav.content': 'Contenu éditorial',
  'contentAdmin.title': 'Contenu éditorial',
  'contentAdmin.lead': 'Modifiez le contenu de la page À propos, l’accroche d’accueil et le footer. Un champ vide retombe sur le texte par défaut de l’application.',
  'contentAdmin.btn.back': 'Retour',
  'contentAdmin.btn.save': 'Enregistrer ({count})',
  'contentAdmin.loading': 'Chargement du contenu…',
  'contentAdmin.error': 'Impossible de charger le contenu. Vérifiez votre session.',
  'contentAdmin.empty': 'Aucun bloc de contenu. Appliquez la migration V67.',
  'contentAdmin.field.placeholder': 'Texte par défaut de l’application',
  'contentAdmin.section.home': 'Accueil',
  'contentAdmin.section.about': 'À propos',
  'contentAdmin.section.footer': 'Footer',
  'contentAdmin.toast.saved': 'Contenu enregistré.',
  'contentAdmin.toast.error': 'Erreur lors de l’enregistrement. Réessayez.',
};

/**
 * Catalogue COMPLET servi à react-intl : le noyau ci-dessus + les
 * sous-catalogues de chantier. L'ordre compte : en cas de clé dupliquée, le
 * sous-catalogue gagnerait — c'est volontairement interdit par un test
 * (`fr.subcatalogs.test.ts`), une clé ne doit avoir qu'une seule définition.
 */
export const messagesFr: Record<string, string> = {
  ...messagesCoreFr,
  ...messagesFormStatsFr,
  ...messagesRosterFr,
  ...messagesTransportFr,
  ...messagesTwoFactorFr,
  ...messagesTwoFactorParticipantFr,
};

// ──────────────────────────────────────────────────────────────────────────
// Idées d'évolution (Mika) :
//  - Découper en sous-fichiers par domaine (auth.fr.ts, workshop.fr.ts, …)
//    si la liste dépasse 500 entrées.
//  - Générer ce fichier depuis une feuille Google Sheet partagée avec
//    l'organisation pour validation linguistique.
// Notes pour rétro-port NOLAS :
//  - NOLAS a beaucoup de chaînes hardcodées dans les .html — c'est un gros
//    chantier de migration, mais ce catalogue donne la grille de wording
//    officielle pour le faire proprement.
// ──────────────────────────────────────────────────────────────────────────
